/* eslint-disable no-await-in-loop */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { uploadLargeFile } from 'Utils/uploadWrapper';
import uploadManagerSecure from 'Utils/uploadManagerSecure';
import { generateS3Url } from 'Api/awsConfig';
import { updateDraftLink, registerDocument, createGiftUploadSession, uploadPartWithPresignedUrl, completeGiftUploadSession } from 'Api/uploaderApiFunctions';
import { getUploaderToken } from 'Api/tokenManager';
import axios from 'axios';
import Tooltip from '@material-ui/core/Tooltip';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { Logo, Snackbar } from 'Components';
import { CLOUDFLARE_TURNSTILE_SITE_KEY } from 'Commons';
import useSWR from 'swr';
import { STRIPE_PUBLIC_KEY, baseUrl, getToken, axios as apiAxios, dataFetcher, endpoints, cloudBaseUrl, publicAxios } from 'Api';
import { saveGiftUser, updateGiftUser, getAllGiftPlans, createPaymentIntent, registerGiftUser, subscribeToGiftUploadStatus, getGiftStatus, sendGiftEmail, sendGiftLinkToPhone, previewGiftEmail, getVideosDetail, updateGiftStatus, createShortLink, addGiftAction } from 'Api/apiFunctions';
import { logAppEvent } from 'Api/errorLogger';
import SuccessPopUp from 'Components/VideoPopup/SuccessPopUp';
import QRCodeComponent from 'Utils/QRCodeComponent';
import CloudUploadModal from 'Components/UploadDropdown/CloudUploadModal';
import heroBackground from 'Assets/AdobeStock_175697403.png';
import image01 from 'Assets/birthday.png';
import image02 from 'Assets/heading-bg.png';
import image03 from 'Assets/thumbnail.png';
import image04 from 'Assets/noThumbImage.png';
import logoRound from 'Assets/logo_round.png';
import fireTvIcon from 'Assets/fire-tv.png';
import styles from './index.module.css';

// Upload status constants
const UPLOAD_STATUS = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  PAUSED: 'paused',
  NETWORK_ERROR: 'network_error',
  COMPLETED: 'completed',
  S3_COMPLETE: 's3_complete', // Gift flow: S3 upload done, awaiting payment for registration
  FAILED: 'failed',
  CANCELLING: 'cancelling',
  CANCELLED: 'cancelled',
};

// Supported file types
const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi', '.mkv', '.webm', '.flv', '.wmv', '.m4v', '.mpeg', '.mpg'];
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.gif'];

// File upload limits
const MAX_TOTAL_SIZE_BYTES = 10 * 1024 * 1024 * 1024; // 10GB in bytes
const MAX_CONCURRENT_UPLOADS = 3; // Match main app's upload concurrency limit

// Helper to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
};

// Helper to format time remaining
const formatTimeRemaining = (seconds) => {
  if (!seconds || seconds <= 0) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Helper to detect mobile device
const isMobileDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
};

// Use MIME wildcards + extensions for native Photos picker (enables swipe-to-select on iOS)
// Note: iOS may transcode HEIC/HEVC but native picker UX is better
const FILE_ACCEPT = 'video/*,image/*,.mp4,.mov,.avi,.mkv,.webm,.flv,.wmv,.m4v,.mpeg,.mpg,.jpg,.jpeg,.png,.webp,.heic,.heif,.gif';

// Helper to get a unique vector icon for each plan feature
const getFeatureIcon = (text) => {
  const t = (text || '').toLowerCase();
  const svgProps = { width: '22', height: '22', viewBox: '0 0 24 24', fill: 'none', stroke: '#fff', strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (t.includes('subscription') || t.includes('lifetime') || t.includes('2 year')) {
    return (
      <svg {...svgProps}>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    );
  }
  if (t.includes('uploads')) {
    return (
      <svg {...svgProps}>
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>
    );
  }
  if (t.includes('sharing')) {
    return (
      <svg {...svgProps}>
        <circle cx="18" cy="5" r="3" />
        <circle cx="6" cy="12" r="3" />
        <circle cx="18" cy="19" r="3" />
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
      </svg>
    );
  }
  if (t.includes('streaming')) {
    return (
      <svg {...svgProps}>
        <polygon points="5 3 19 12 5 21 5 3" />
      </svg>
    );
  }
  if (t.includes('viewer')) {
    return (
      <svg {...svgProps}>
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87" />
        <path d="M16 3.13a4 4 0 010 7.75" />
      </svg>
    );
  }
  if (t.includes('sign-in') || t.includes('account') || t.includes('tv') || t.includes('mobile apps')) {
    return (
      <svg {...svgProps}>
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    );
  }
  if (t.includes('firestick') || t.includes('fire stick') || t.includes('amazon fire')) {
    return (
      <img src={fireTvIcon} alt="Fire TV" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
    );
  }
  if (t.includes('storage')) {
    return (
      <svg {...svgProps}>
        <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
      </svg>
    );
  }
  if (t.includes('device')) {
    return (
      <svg {...svgProps}>
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </svg>
    );
  }
  // Default: checkmark
  return (
    <svg {...svgProps}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
};

// Helper to generate unique session ID
const generateSessionId = () => `gift-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Encode file name with unique ID for push messages to distinguish duplicate file names
// Format: "fileName|uniqueId" — pipe separator is used throughout the push system
const encodeFileId = (name, id) => (id ? `${name}|${id}` : name);

// Decode file name and unique ID from push message (backward compatible: no '|' means old format)
const decodeFileId = (encoded) => {
  if (!encoded || typeof encoded !== 'string') return { name: encoded || '', fileId: null };
  const idx = encoded.lastIndexOf('|');
  if (idx <= 0) return { name: encoded, fileId: null };
  return { name: encoded.substring(0, idx), fileId: encoded.substring(idx + 1) };
};

// Helper to convert file size to MB for API
const changeSize = (size) => {
  const newSize = size / 1024 / 1024;
  return newSize.toFixed(2);
};

// Helper to convert base64 string to binary blob for API
const base64ToBinary = (base64String) => {
  const [, base64Data] = base64String.split(',');
  const binaryString = atob(base64Data);
  const len = binaryString.length;
  const binaryData = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    binaryData[i] = binaryString.charCodeAt(i);
  }
  return new Blob([binaryData], { type: 'image/jpeg' });
};

// Helper to convert blob URL to base64
const convertBlobUrlToBase64 = (blobUrl) => new Promise((resolve, reject) => {
  fetch(blobUrl)
    .then((response) => response.blob())
    .then((blob) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    })
    .catch(reject);
});

// Downscale a thumbnail data URL to a small version for push transmission (~3-8KB)
// Compress a data URL thumbnail to a smaller size for push metadata.
// Used by buildMobileUploadMetadata during registration (post-upload).
const compressThumbnailForPush = (dataUrl) => {
  if (!dataUrl || (!dataUrl.startsWith('data:') && !dataUrl.startsWith('blob:'))) return null;
  try {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    return new Promise((resolve) => {
      img.onload = () => {
        const maxWidth = 240;
        const scale = Math.min(1, maxWidth / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const compressed = canvas.toDataURL('image/jpeg', 0.7);
        canvas.remove();
        resolve(compressed);
      };
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    });
  } catch (e) {
    return null;
  }
};

// Capture a video thumbnail from a RENDERED <video> element already in the DOM.
// On mobile, creating a new off-DOM video and using canvas.drawImage produces black
// because GPU-decoded frames aren't accessible to canvas/WebGL from detached elements.
// By capturing from the actual visible <video> element that the browser is already
// rendering on screen, the frame data should be accessible.
// blobUrl is used to find the matching <video> element via querySelector.
const captureVideoFromRenderedElement = (blobUrl) => {
  try {
    // Find the rendered <video> element with this src
    const videoEl = document.querySelector(`video[src="${CSS.escape(blobUrl)}"]`);
    if (!videoEl || videoEl.videoWidth === 0 || videoEl.videoHeight === 0) return null;

    const maxWidth = 160;
    const scale = Math.min(1, maxWidth / videoEl.videoWidth);
    const w = Math.round(videoEl.videoWidth * scale);
    const h = Math.round(videoEl.videoHeight * scale);

    // Method 1: WebGL texImage2D — different GPU pathway, may access video frames
    try {
      const glCanvas = document.createElement('canvas');
      glCanvas.width = w;
      glCanvas.height = h;
      const gl = glCanvas.getContext('webgl', { preserveDrawingBuffer: true });
      if (gl) {
        const texture = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, videoEl);
        const vs = gl.createShader(gl.VERTEX_SHADER);
        gl.shaderSource(vs, 'attribute vec2 a_pos;varying vec2 v_tc;void main(){gl_Position=vec4(a_pos,0,1);v_tc=(a_pos+1.0)/2.0;v_tc.y=1.0-v_tc.y;}');
        gl.compileShader(vs);
        const fs = gl.createShader(gl.FRAGMENT_SHADER);
        gl.shaderSource(fs, 'precision mediump float;varying vec2 v_tc;uniform sampler2D u_tex;void main(){gl_FragColor=texture2D(u_tex,v_tc);}');
        gl.compileShader(fs);
        const prog = gl.createProgram();
        gl.attachShader(prog, vs);
        gl.attachShader(prog, fs);
        gl.linkProgram(prog);
        gl.useProgram(prog);
        const buf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
        const loc = gl.getAttribLocation(prog, 'a_pos');
        gl.enableVertexAttribArray(loc);
        gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
        gl.viewport(0, 0, w, h);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        const dataUrl = glCanvas.toDataURL('image/jpeg', 0.5);
        gl.deleteTexture(texture);
        gl.deleteBuffer(buf);
        gl.deleteProgram(prog);
        gl.deleteShader(vs);
        gl.deleteShader(fs);
        glCanvas.remove();
        if (dataUrl && dataUrl.length > 1000) return dataUrl;
      }
    } catch (e) { /* WebGL failed */ }

    // Method 2: 2D canvas drawImage
    try {
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(videoEl, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.5);
        canvas.remove();
        if (dataUrl && dataUrl.length > 1000) return dataUrl;
      }
    } catch (e) { /* 2D canvas failed */ }

    // Method 3: createPattern — uses a different internal rendering path
    try {
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const pattern = ctx.createPattern(videoEl, 'no-repeat');
        if (pattern) {
          ctx.scale(w / videoEl.videoWidth, h / videoEl.videoHeight);
          ctx.fillStyle = pattern;
          ctx.fillRect(0, 0, videoEl.videoWidth, videoEl.videoHeight);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.5);
          canvas.remove();
          if (dataUrl && dataUrl.length > 1000) return dataUrl;
        }
      }
    } catch (e) { /* pattern failed */ }

    return null;
  } catch (e) {
    return null;
  }
};

// Generate a small thumbnail data URL directly from a File/Blob (image or JPEG blob).
// Uses FileReader (reliable on mobile) → Image → single Canvas pass.
const generateImageThumbnailForPush = (file) => new Promise((resolve) => {
  const timeout = setTimeout(() => resolve(null), 5000);
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      try {
        const maxWidth = 160;
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d');
        if (!ctx) { clearTimeout(timeout); resolve(null); return; }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.4);
        canvas.remove();
        clearTimeout(timeout);
        resolve(dataUrl);
      } catch (e) { clearTimeout(timeout); resolve(null); }
    };
    img.onerror = () => { clearTimeout(timeout); resolve(null); };
    img.src = reader.result;
  };
  reader.onerror = () => { clearTimeout(timeout); resolve(null); };
  reader.readAsDataURL(file);
});

// Generate a MICRO thumbnail for push payloads.
// Much smaller than generateImageThumbnailForPush to keep push string within API/DB limits.
const generateMicroThumbnail = (file) => new Promise((resolve) => {
  const timeout = setTimeout(() => resolve(null), 2000);
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      try {
        const maxWidth = 160;
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx) { clearTimeout(timeout); resolve(null); return; }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.4);
        canvas.remove();
        clearTimeout(timeout);
        resolve(dataUrl);
      } catch (e) { clearTimeout(timeout); resolve(null); }
    };
    img.onerror = () => { clearTimeout(timeout); resolve(null); };
    img.src = reader.result;
  };
  reader.onerror = () => { clearTimeout(timeout); resolve(null); };
  reader.readAsDataURL(file);
});

// Capture a single frame from a video file as an image data URL (iOS compatible).
// Used to generate poster thumbnails since iOS Safari won't auto-render <video> frames.
const captureVideoFrame = (file) => new Promise((resolve) => {
  console.log('[THUMB_CAPTURE] START - file:', file?.name, 'size:', file?.size, 'type:', file?.type);
  const timeout = setTimeout(() => {
    console.warn('[THUMB_CAPTURE] TIMEOUT after 6s - file:', file?.name);
    resolve(null);
  }, 6000);
  try {
    const video = document.createElement('video');
    const videoUrl = URL.createObjectURL(file);
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    const cleanup = () => {
      try { URL.revokeObjectURL(videoUrl); } catch (e) { /* ignore */ }
      try { video.remove(); } catch (e) { /* ignore */ }
    };

    video.onloadedmetadata = () => {
      console.log('[THUMB_CAPTURE] METADATA LOADED - file:', file?.name, 'duration:', video.duration, 'dimensions:', video.videoWidth, 'x', video.videoHeight);
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        console.warn('[THUMB_CAPTURE] FAILED - video dimensions are 0x0, cannot capture frame, file:', file?.name);
        clearTimeout(timeout); cleanup(); resolve(null);
        return;
      }
      video.currentTime = 0.5;
    };

    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 320;
        canvas.height = video.videoHeight || 180;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          console.error('[THUMB_CAPTURE] FAILED - no canvas context, file:', file?.name);
          clearTimeout(timeout); cleanup(); resolve(null); return;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
        console.log('[THUMB_CAPTURE] SUCCESS - file:', file?.name, 'canvas:', canvas.width, 'x', canvas.height, 'dataUrl size:', dataUrl.length);
        canvas.remove();
        clearTimeout(timeout);
        cleanup();
        resolve(dataUrl);
      } catch (e) {
        console.error('[THUMB_CAPTURE] SEEKED ERROR - file:', file?.name, 'error:', e.message);
        clearTimeout(timeout); cleanup(); resolve(null);
      }
    };

    video.onerror = () => {
      const errMsg = video?.error?.message || '';
      const isAudioOnly = errMsg.includes('audio') || errMsg.includes('PIPELINE_ERROR_DECODE');
      if (isAudioOnly) {
        console.warn('[THUMB_CAPTURE] Audio-only error (non-fatal):', errMsg, 'file:', file?.name);
      } else {
        console.error('[THUMB_CAPTURE] VIDEO ERROR - file:', file?.name, 'error:', errMsg);
        clearTimeout(timeout); cleanup(); resolve(null);
      }
    };
    video.src = videoUrl;
    video.load();
  } catch (e) {
    console.error('[THUMB_CAPTURE] EXCEPTION - file:', file?.name, 'error:', e.message);
    clearTimeout(timeout); resolve(null);
  }
});

// Generate thumbnails from video file.
const generateThumbnailsFromVideo = (file) => new Promise((resolve) => {
  console.log('[THUMB_GEN] START - file:', file?.name, 'size:', file?.size, 'type:', file?.type);
  if (!file) {
    console.log('[THUMB_GEN] No file provided, using fallback');
    resolve({ thumbnails: [image04], videoHeight: 0, videoWidth: 0, videoDuration: 0 });
    return;
  }

  let resolved = false;
  const fallback = {
    thumbnails: [image04],
    videoHeight: 0,
    videoWidth: 0,
    videoDuration: 0,
  };

  const video = document.createElement('video');
  const videoUrl = URL.createObjectURL(file);
  console.log('[THUMB_GEN] Created blob URL:', videoUrl.substring(0, 50));

  const cleanup = () => {
    try { URL.revokeObjectURL(videoUrl); } catch (e) { /* ignore */ }
    try { video.remove(); } catch (e) { /* ignore */ }
  };

  const timeout = setTimeout(() => {
    if (!resolved) {
      console.warn('[THUMB_GEN] TIMEOUT after 15s - video never loaded or seeking stuck');
      resolved = true;
      cleanup();
      resolve(fallback);
    }
  }, 15000);

  video.preload = 'metadata';
  video.muted = true;
  video.playsInline = true;
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  video.src = videoUrl;
  video.load();

  video.onerror = (e) => {
    const errMsg = e?.target?.error?.message || '';
    const isAudioOnly = errMsg.includes('audio') || errMsg.includes('PIPELINE_ERROR_DECODE');
    if (isAudioOnly) {
      // Audio decode error — video frames may still be accessible via metadata loading.
      console.warn('[THUMB_GEN] Audio-only error (non-fatal, waiting for metadata):', errMsg);
    } else {
      // Real video error — fail fast
      console.error('[THUMB_GEN] VIDEO ERROR:', errMsg);
      if (!resolved) {
        resolved = true;
        clearTimeout(timeout);
        cleanup();
        resolve(fallback);
      }
    }
  };

  video.onloadedmetadata = () => {
    if (resolved) return;
    const { duration, videoHeight, videoWidth } = video;
    console.log('[THUMB_GEN] VIDEO LOADED - duration:', duration, 'dimensions:', videoWidth, 'x', videoHeight);
    if (videoWidth === 0 || videoHeight === 0) {
      console.warn('[THUMB_GEN] FAILED - video dimensions are 0x0, cannot generate thumbnails, file:', file?.name);
      resolved = true;
      clearTimeout(timeout);
      cleanup();
      resolve(fallback);
      return;
    }
    const thumbnailCount = 6;
    const interval = duration / (thumbnailCount + 1);
    const tempThumbnails = [];

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const maxWidth = 640;
    const scale = Math.min(1, maxWidth / videoWidth);
    canvas.width = videoWidth * scale;
    canvas.height = videoHeight * scale;
    console.log('[THUMB_GEN] Canvas size:', canvas.width, 'x', canvas.height, 'scale:', scale);

    let currentIndex = 0;

    const captureThumbnail = () => {
      if (resolved) return;
      if (currentIndex >= thumbnailCount) {
        console.log('[THUMB_GEN] DONE - captured', tempThumbnails.length, 'of', thumbnailCount, 'frames');
        resolved = true;
        clearTimeout(timeout);
        canvas.remove();
        cleanup();
        resolve({
          thumbnails: tempThumbnails.length > 0 ? tempThumbnails : [image04],
          videoHeight,
          videoWidth,
          videoDuration: duration,
        });
        return;
      }

      const time = Math.max(0.5, interval * (currentIndex + 1));
      video.currentTime = Math.min(time, duration - 0.1);
      console.log('[THUMB_GEN] Seeking to frame', currentIndex + 1, '/', thumbnailCount, 'time:', time.toFixed(2));
    };

    video.onseeked = () => {
      if (resolved) return;
      requestAnimationFrame(() => {
        if (resolved) return;
        try {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
          tempThumbnails.push(thumbnailUrl);
          console.log('[THUMB_GEN] Frame', currentIndex + 1, 'captured OK - size:', thumbnailUrl.length, 'chars');
        } catch (e) {
          console.warn('[THUMB_GEN] Frame', currentIndex + 1, 'FAILED:', e.message);
        }
        currentIndex += 1;
        captureThumbnail();
      });
    };

    captureThumbnail();
  };
});

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

// Inline Stripe Payment Form Component
const InlinePaymentForm = ({ clientSecret, giftUserUid, planId, onSuccess, onError, fromName, fromEmail, deliveryDate, promoCode, onValidationError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [isPaymentReady, setIsPaymentReady] = useState(false);
  // Email validation
  const isEmailValid = fromEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fromEmail);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!fromName || !fromName.trim() || !deliveryDate) {
      if (onValidationError) {
        onValidationError({
          cardholderName: !fromName || !fromName.trim(),
          deliveryDate: !deliveryDate,
        });
      }
      return;
    }

    if (!stripe || !elements) {
      console.error('[GIFT_FLOW] stripe or elements not ready:', { stripe: !!stripe, elements: !!elements });
      return;
    }

    setProcessing(true);
    setPaymentError('');

    try {
      const isSetupIntent = clientSecret?.startsWith('seti_');
      console.log('[GIFT_FLOW] Calling', isSetupIntent ? 'confirmSetup' : 'confirmPayment', 'clientSecret:', `${clientSecret?.substring(0, 20)}...`);

      const result = isSetupIntent
        ? await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: 'if_required',
        })
        : await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: 'if_required',
        });

      if (result.error) {
        setPaymentError(result.error.message);
        setTimeout(() => setPaymentError(''), 5000);
        setProcessing(false);
        if (onError) onError(result.error.message);
      } else {
        // Payment succeeded - register gift user
        try {
          // Extract payment method ID from the successful result
          const paymentMethodId = isSetupIntent
            ? result.setupIntent?.payment_method
            : result.paymentIntent?.payment_method;

          if (!paymentMethodId) {
            throw new Error('Payment method ID not found');
          }

          // Convert deliveryDate to ISO format with local timezone offset
          let deliveryDateWithTimezone = null;
          if (deliveryDate) {
            const date = new Date(`${deliveryDate}T00:00:00`);
            const tzOffset = -date.getTimezoneOffset(); // in minutes
            const offsetHours = Math.floor(Math.abs(tzOffset) / 60);
            const offsetMinutes = Math.abs(tzOffset) % 60;
            const offsetSign = tzOffset >= 0 ? '+' : '-';
            const formattedOffset = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;
            deliveryDateWithTimezone = `${deliveryDate}T00:00:00${formattedOffset}`;
          }

          const response = await registerGiftUser({
            uid: giftUserUid,
            checkout_id: clientSecret,
            plan_id: parseInt(planId, 10),
            payment_method: paymentMethodId,
            fromName: localStorage.getItem('senderName') || fromName,
            fromEmail: localStorage.getItem('senderEmail') || fromEmail,
            cardholderName: fromName,
            cardholderEmail: fromEmail,
            deliveryDate: deliveryDateWithTimezone,
            promo_code: promoCode || '',
            v2: true,
          });

          if (response.data?.success) {
            // Store the token from response for API calls
            const userToken = response.data?.data?.userDetails?.[0]?.token;
            const accessToken = response.data?.api_response?.access;
            if (userToken) {
              localStorage.setItem('token', userToken);
              console.log('[GIFT_FLOW] Token stored after payment success');
            }
            if (accessToken) {
              localStorage.setItem('uploader_token', accessToken);
              // Parse and store JWT expiry so tokenManager doesn't think it's expired
              try {
                const payload = JSON.parse(atob(accessToken.split('.')[1]));
                if (payload.exp) {
                  localStorage.setItem('uploader_token_expiry', (payload.exp * 1000).toString());
                }
              } catch (e) { console.warn('[GIFT_FLOW] Failed to parse uploader token expiry:', e); }
              console.log('[GIFT_FLOW] Uploader token stored after payment success');
            } else {
              // Fallback: fetch uploader token from API if not in response
              try { await getUploaderToken(); } catch (err) { console.warn('[GIFT_FLOW] Failed to pre-fetch uploader token:', err); }
            }
            onSuccess(response.data);
          } else {
            const errorMsg = 'Payment successful but registration failed. Please contact support.';
            logAppEvent({
              userName: 'Gift User',
              functionAffected: 'Gift Registration',
              description: `registerGiftUser returned success=false — giftUuid: ${giftUserUid}, planId: ${planId}`,
              severity: 'high',
              errorMessage: response.data?.message || 'Registration returned unsuccessful',
              responseStatus: response?.status || null,
              component: 'giftFlowPayment',
              userAction: 'registering gift user after payment',
            });
            setPaymentError(errorMsg);
            setTimeout(() => setPaymentError(''), 5000);
            setProcessing(false);
            if (onError) onError(errorMsg);
          }
        } catch (err) {
          const errorMsg = 'Payment successful but registration failed. Please contact support.';
          logAppEvent({
            userName: 'Gift User',
            functionAffected: 'Gift Registration',
            description: `registerGiftUser threw exception — giftUuid: ${giftUserUid}, planId: ${planId}`,
            severity: 'high',
            errorMessage: err?.response?.data?.message || err?.message || 'Registration exception',
            errorStack: err?.stack || null,
            responseStatus: err?.response?.status || null,
            component: 'giftFlowPayment',
            userAction: 'registering gift user after payment',
          });
          setPaymentError(errorMsg);
          setTimeout(() => setPaymentError(''), 5000);
          setProcessing(false);
          if (onError) onError(errorMsg);
        }
      }
    } catch (err) {
      console.error('[GIFT_FLOW] Payment confirmPayment exception:', err);
      const errMsg = err?.message || 'Payment failed. Please try again.';
      setPaymentError(errMsg);
      setTimeout(() => setPaymentError(''), 5000);
      setProcessing(false);
      if (onError) onError(errMsg);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.inlinePaymentForm}>
      <div className={styles.paymentElementWrapper}>
        <PaymentElement
          onChange={(event) => setIsPaymentReady(event.complete)}
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['apple_pay', 'google_pay', 'card'],
            wallets: { applePay: 'auto', googlePay: 'auto' },
          }}
        />
      </div>

      {paymentError && (
        <div className={styles.paymentError}>{paymentError}</div>
      )}

      <button
        type="submit"
        disabled={!stripe || !isPaymentReady || processing || !isEmailValid}
        className={styles.completePaymentButton}
        style={{ opacity: (!stripe || !isPaymentReady || processing || !isEmailValid) ? 0.5 : 1, cursor: (!stripe || !isPaymentReady || processing || !isEmailValid) ? 'not-allowed' : 'pointer' }}
      >
        {processing ? 'Processing...' : 'Complete Payment'}
      </button>
    </form>
  );
};

// Debug logger with timestamp for sync debugging - filter console by "[GIFT_SYNC]"
const syncLog = (location, message, data = null) => {
  const now = new Date();
  const timestamp = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}.${now.getMilliseconds().toString().padStart(3, '0')}`;
  const logData = data ? ` | ${JSON.stringify(data)}` : '';
  // eslint-disable-next-line no-console
  console.log(`[GIFT_SYNC] ${timestamp} [${location}] ${message}${logData}`);
};

export const GiftFlowIntro = () => {
  const navigate = useNavigate();

  // Check URL params for mobile upload mode and session
  const getUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      testParam: params.get('test'),
      mobileUpload: params.get('mobile') === 'true',
      sessionId: params.get('session'),
      giftUid: params.get('giftUid'),
      token: params.get('token'),
      access: params.get('access'),
      share: params.get('share') === 'true',
      statusPage: params.get('status') === 'true',
      contributorName: params.get('contributorName') || '',
      recipientName: params.get('recipientName') || '',
      referral: params.get('referral') || '',
    };
  };

  const urlParams = getUrlParams();

  // Clear existing tokens on first load of plain /gift route only
  // Skip clearing if returning from OAuth redirect (cloud service auth)
  const hasNoParams = !urlParams.testParam && !urlParams.mobileUpload && !urlParams.giftUid && !urlParams.token && !urlParams.access && !urlParams.statusPage;
  const isReturningFromOAuth = !!sessionStorage.getItem('autoOpenCloudModal') || !!sessionStorage.getItem('giftFlowState');
  useState(() => {
    if (hasNoParams && !isReturningFromOAuth) {
      localStorage.removeItem('token');
      localStorage.removeItem('uploader_token');
      localStorage.removeItem('uploader_token_expiry');
      localStorage.removeItem('senderName');
      localStorage.removeItem('senderEmail');
      localStorage.removeItem('contributorsName');
      localStorage.removeItem('existingUser');
      localStorage.removeItem('giftUserId');
    }
    // Clear stale contributor name on fresh share link visits (new contributor)
    if (urlParams.share && !isReturningFromOAuth) {
      localStorage.removeItem('contributorsName');
    }
  });

  // Detect if user is on mobile device AND accessing via QR code (mobile=true param)
  // Mobile upload mode: Based on URL parameter (mobile=true)
  const [isMobileUploadMode] = useState(() => urlParams.mobileUpload);
  // UI-only: show mobile-style upload UI when contributor opens share link on mobile/tablet
  // UI-only: show mobile-style upload UI when contributor opens share link on mobile/tablet
  const isShareMobileUI = !isMobileUploadMode && !!urlParams.share && window.innerWidth <= 1024;

  // Warn user before refresh in mobile upload mode (all data will be lost)
  useEffect(() => {
    if (!isMobileUploadMode) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isMobileUploadMode]);

  // Scroll to top and reset zoom on initial load (helps mobile devices)
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
    // Reset mobile browser zoom to 1x
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const originalViewport = viewportMeta?.getAttribute('content');
    if (viewportMeta) {
      viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
    }
    // Fallback for browsers that restore scroll after paint
    const timeout = setTimeout(() => window.scrollTo(0, 0), 100);
    return () => {
      clearTimeout(timeout);
      // Restore original viewport when leaving this page
      if (viewportMeta && originalViewport) {
        viewportMeta.setAttribute('content', originalViewport);
      }
    };
  }, []);

  // Session ID for linking mobile uploads to desktop session
  const [sessionId] = useState(() => urlParams.sessionId || generateSessionId());

  // Check for ?test query param to go directly to specific steps
  const getInitialStep = () => {
    // If mobile upload mode, go directly to upload section (step 3)
    if (urlParams.mobileUpload) return 3;

    const { testParam } = urlParams;
    if (testParam === 'upload') return 3; // Step 3 - Choose content source
    if (testParam === 'progress') return 5; // Step 4 (internal step 5) - Upload progress with sidebar
    if (testParam === 'choice') return 2;
    if (testParam === 'step1') return 1;
    if (testParam === 'note') return 1; // Step 1 sub-step 2 - Note section (temp)
    if (testParam === 'payment') return 4;
    if (testParam === 'projector') return 5; // Projector view
    return 1;
  };

  const getInitialSubStep = () => {
    const { testParam } = urlParams;
    if (testParam === 'note') return 2;
    return 1;
  };

  const [currentStep, setCurrentStep] = useState(getInitialStep()); // 0 = intro, 1 = step1, 2 = step2 choice, 3 = upload box, 4 = invite
  const currentStepRef = useRef(currentStep);
  useEffect(() => { currentStepRef.current = currentStep; }, [currentStep]);

  const [step1SubStep, setStep1SubStep] = useState(getInitialSubStep()); // 1 = To/From section, 2 = Short note section
  const [showSenderInline, setShowSenderInline] = useState(false); // Normal flow: show sender fields inline in substep 1 after recipient saved
  const [selectedChoice, setSelectedChoice] = useState(() => (urlParams.mobileUpload ? 'you-help-build' : null)); // 'they-build' or 'you-help-build'
  const [cloudflareToken, setCloudflareToken] = useState('');
  const [cloudflareVerifying, setCloudflareVerifying] = useState(true);
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  // Animation state - controls content visibility during step transitions
  const [isContentVisible, setIsContentVisible] = useState(true);
  const [noteAnimating, setNoteAnimating] = useState(false);
  const [gridAnimationComplete, setGridAnimationComplete] = useState(false);
  const noteTextareaRef = useRef(null);

  // Occasion carousel state
  const [occasionScrollIndex, setOccasionScrollIndex] = useState(0);
  const occasionCarouselRef = useRef(null);

  // Plan carousel state
  const [planCarouselIndex, setPlanCarouselIndex] = useState(0);
  const planCarouselRef = useRef(null);
  const [selectedPlan, setSelectedPlan] = useState(null); // Track selected plan

  // Scroll animation refs
  const imageGalleryRef = useRef(null);
  const featuresSectionRef = useRef(null);
  const [galleryVisible, setGalleryVisible] = useState(false);
  const [featuresVisible, setFeaturesVisible] = useState(false);

  // Bottom section slide state
  const [bottomSectionUp, setBottomSectionUp] = useState(false);
  const heroSectionRef = useRef(null);
  const bottomSectionRef = useRef(null);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.2
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (entry.target === imageGalleryRef.current) {
            setGalleryVisible(true);
          }
          if (entry.target === featuresSectionRef.current) {
            setFeaturesVisible(true);
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    if (imageGalleryRef.current) {
      observer.observe(imageGalleryRef.current);
    }
    if (featuresSectionRef.current) {
      observer.observe(featuresSectionRef.current);
    }

    return () => observer.disconnect();
  }, [currentStep]);

  // Handle wheel/scroll for bottom section slide animation (only on intro step)
  useEffect(() => {
    if (currentStep !== 0) return;

    const handleWheel = (e) => {
      // Scrolling down (positive deltaY) - slide bottom section up
      if (e.deltaY > 0 && !bottomSectionUp) {
        setBottomSectionUp(true);
      } else if (e.deltaY < 0 && bottomSectionUp) {
        // Scrolling up (negative deltaY) - slide bottom section down
        setBottomSectionUp(false);
      }
    };

    // Handle click on bottom section to toggle
    const handleBottomClick = () => {
      setBottomSectionUp((prev) => !prev);
    };

    const bottomSection = bottomSectionRef.current;

    window.addEventListener('wheel', handleWheel, { passive: true });
    if (bottomSection) {
      bottomSection.addEventListener('click', handleBottomClick);
    }

    return () => {
      window.removeEventListener('wheel', handleWheel);
      if (bottomSection) {
        bottomSection.removeEventListener('click', handleBottomClick);
      }
    };
  }, [currentStep, bottomSectionUp]);

  // Trigger card animations when bottom section slides up
  useEffect(() => {
    if (bottomSectionUp) {
      // Delay the animations slightly to let the slide animation start first
      const timer = setTimeout(() => {
        setGalleryVisible(true);
        setFeaturesVisible(true);
      }, 300);
      return () => clearTimeout(timer);
    }
    // Reset animations when sliding down
    setGalleryVisible(false);
    setFeaturesVisible(false);
    return undefined;
  }, [bottomSectionUp]);

  // Animated step transition (memoized to prevent SSE re-render loop)
  const animateToStep = useCallback((newStep) => {
    setIsContentVisible(false); // Hide content immediately

    // Wait for fade out, then change step and fade in
    setTimeout(() => {
      setCurrentStep(newStep);
      // Scroll to top for Finalize Projector step
      if (newStep === 5) {
        const giftPage = document.querySelector('[class*="giftPage"]');
        if (giftPage) giftPage.scrollTop = 0;
        window.scrollTo(0, 0);
      }
      // Small delay then show new content with enter animation
      setTimeout(() => {
        setIsContentVisible(true);
      }, 50);
    }, 400);
  }, []);

  // Get animation class based on state
  const getAnimationClass = () => {
    if (!isContentVisible) return styles.stepHidden;
    return styles.stepEnter;
  };

  // Form data state
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    senderEmail: '',
    note: '',
    occasions: [], // Changed to array for multiple selection
    otherReason: '',
    cardholderName: '',
    cardholderEmail: ''
  });

  // Track if name field has been blurred and typing completed (for showing validation error)
  const [nameFieldTouched, setNameFieldTouched] = useState(false);
  const [nameValidationReady, setNameValidationReady] = useState(false);

  // Debounce name validation - show error 500ms after user stops typing (if field was touched)
  useEffect(() => {
    if (!nameFieldTouched) return;

    setNameValidationReady(false);
    const timer = setTimeout(() => {
      setNameValidationReady(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.firstname, nameFieldTouched]);

  // Helper: get album grid CSS class based on photo count (2x2 for <=4, 3x2 for <=6, 3x3 for >6)
  const getAlbumGridClass = (count) => {
    if (count <= 4) return `${styles.albumFolderGrid} ${styles.albumFolderGrid2x2}`;
    if (count <= 6) return `${styles.albumFolderGrid} ${styles.albumFolderGrid3x2}`;
    return styles.albumFolderGrid;
  };

  // Derived name validation helpers — minimum 4 non-space characters with a space in between
  const trimmedName = formData.firstname ? formData.firstname.trim() : '';
  const nameWithoutSpaces = trimmedName.replace(/\s/g, '');
  const hasSpace = trimmedName.includes(' ');
  const fullNameValid = nameWithoutSpaces.length >= 4 && hasSpace;

  // Track if cardholder email field has been blurred and typing completed (for showing validation error)
  const [emailFieldTouched, setEmailFieldTouched] = useState(false);
  const [emailValidationReady, setEmailValidationReady] = useState(false);

  // Debounce email validation - show error 500ms after user stops typing (if field was touched)
  useEffect(() => {
    if (!emailFieldTouched) return;

    setEmailValidationReady(false);
    const timer = setTimeout(() => {
      setEmailValidationReady(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.cardholderEmail, emailFieldTouched]);

  // Contributor popup state - shown when share=true and contributorName not already in URL or localStorage
  const [showContributorPopup, setShowContributorPopup] = useState(urlParams.share && !urlParams.contributorName && !localStorage.getItem('contributorsName'));
  const [contributorName, setContributorName] = useState(urlParams.contributorName || localStorage.getItem('contributorsName') || '');
  const [contributorEmail, setContributorEmail] = useState('');
  const [contNameTouched, setContNameTouched] = useState(false);
  const [contNameValidReady, setContNameValidReady] = useState(false);
  const [contEmailTouched, setContEmailTouched] = useState(false);
  const [contEmailValidReady, setContEmailValidReady] = useState(false);

  // Contributor name validation — same as step 1: min 4 non-space chars with a space
  const contTrimmedName = contributorName.trim();
  const contNameNoSpaces = contTrimmedName.replace(/\s/g, '');
  const contHasSpace = contTrimmedName.includes(' ');
  const contFullNameValid = contNameNoSpaces.length >= 4 && contHasSpace;

  // Contributor email validation — same regex as step 1
  const contEmailValid = !contributorEmail || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contributorEmail);

  // Debounce contributor name validation - 500ms after stop typing
  useEffect(() => {
    if (!contNameTouched) return;
    setContNameValidReady(false);
    const timer = setTimeout(() => setContNameValidReady(true), 1200);
    return () => clearTimeout(timer);
  }, [contributorName, contNameTouched]);

  // Debounce contributor email validation - 1200ms after stop typing
  useEffect(() => {
    if (!contEmailTouched) return;
    setContEmailValidReady(false);
    const timer = setTimeout(() => setContEmailValidReady(true), 1200);
    return () => clearTimeout(timer);
  }, [contributorEmail, contEmailTouched]);

  // Sender popup state - shown in normal /gift flow when on step 3 and senderName not yet saved
  const [showSenderPopup, setShowSenderPopup] = useState(false);
  const [senderName, setSenderName] = useState(() => localStorage.getItem('senderName') || '');
  const [senderEmail, setSenderEmail] = useState(() => localStorage.getItem('senderEmail') || '');
  const [senderNameTouched, setSenderNameTouched] = useState(false);
  const [senderNameValidReady, setSenderNameValidReady] = useState(false);
  const [senderEmailTouched, setSenderEmailTouched] = useState(false);
  const [senderEmailValidReady, setSenderEmailValidReady] = useState(false);

  // Sender name validation — same as contributor: min 4 non-space chars with a space
  const senderTrimmedName = senderName.trim();
  const senderNameNoSpaces = senderTrimmedName.replace(/\s/g, '');
  const senderHasSpace = senderTrimmedName.includes(' ');
  const senderFullNameValid = senderNameNoSpaces.length >= 4 && senderHasSpace;

  // Sender email validation — required, must be valid
  const senderEmailValid = senderEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(senderEmail);

  // Sender popup is no longer needed for normal flow — sender info is collected inline in step 1
  // Keep popup only for flows that might need it (e.g., restored sessions without sender name)

  // Debounce sender name validation - 500ms after stop typing
  useEffect(() => {
    if (!senderNameTouched) return;
    setSenderNameValidReady(false);
    const timer = setTimeout(() => setSenderNameValidReady(true), 500);
    return () => clearTimeout(timer);
  }, [senderName, senderNameTouched]);

  // Debounce sender email validation - 1200ms after stop typing
  useEffect(() => {
    if (!senderEmailTouched) return;
    setSenderEmailValidReady(false);
    const timer = setTimeout(() => setSenderEmailValidReady(true), 1200);
    return () => clearTimeout(timer);
  }, [senderEmail, senderEmailTouched]);

  // Gift user state - initialize from URL param if in mobile mode
  const [giftUserUid, setGiftUserUid] = useState(() => urlParams.giftUid || '');
  const [giftPlans, setGiftPlans] = useState([]);
  const [planToggle, setPlanToggle] = useState('2 Years'); // '2 Years' or 'Lifetime'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Inline payment state
  const [showPayment, setShowPayment] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [giftExpressData, setGiftExpressData] = useState(null);

  // Upload item preview modal (step 5)
  const [uploadPreviewOpen, setUploadPreviewOpen] = useState(false);
  const [uploadPreviewLoading, setUploadPreviewLoading] = useState(false);
  const [uploadPreviewVideo, setUploadPreviewVideo] = useState(null);
  const [uploadAlbumOpen, setUploadAlbumOpen] = useState(false);
  const [uploadAlbumLoading, setUploadAlbumLoading] = useState(false);
  const [uploadAlbumDetail, setUploadAlbumDetail] = useState(null);

  // Upload item delete confirm (step 5)
  const [uploadDeletePending, setUploadDeletePending] = useState(null); // { uploadId, type: 'video'|'album', id, fileName }
  const [uploadDeleting, setUploadDeleting] = useState(false);

  // Step 3 delete confirm — stores the action to execute on confirm (cancel uploads only)
  // { type: 'video'|'album'|'photo'|'failed', fileName, onConfirm: () => void, isCancel: true }
  const [step3DeletePending, setStep3DeletePending] = useState(null);

  // Soft-deleted items waiting for restore or permanent deletion
  const [deletedItems, setDeletedItems] = useState([]);

  // Album photos modal for grid view — { upload, albumMatch, photos }
  const [step3AlbumModal, setStep3AlbumModal] = useState(null);

  // Section expansion state for payment step
  const [paymentSectionExpanded, setPaymentSectionExpanded] = useState(false);

  // Promo code state
  const [promoCodeNew, setPromoCodeNew] = useState('');
  const [promoResponse, setPromoResponse] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoErrMsg, setPromoErrMsg] = useState('');
  const [promoActive, setPromoActive] = useState(false);
  const [discountedPrice, setDiscountedPrice] = useState('');
  const [calculatedDiscount, setCalculatedDiscount] = useState('');
  const [promoFieldTouched, setPromoFieldTouched] = useState(false);
  const [promoInputVisible, setPromoInputVisible] = useState(false);
  const promoInputRef = useRef(null);
  const promoDebounceRef = useRef(null);
  const promoCancelToken = useRef(null);

  // Upload state for Step 3
  const [selectedFiles, setSelectedFiles] = useState([]);
  // Initialize uploads with test data if ?test=progress
  const [uploads, setUploads] = useState(() => {
    if (urlParams.testParam === 'progress') {
      return [
        {
          id: 'test-1',
          fileName: 'test_video_1.mp4',
          fileSize: 125000000,
          status: UPLOAD_STATUS.COMPLETED,
          progress: 100,
          bytesUploaded: 125000000,
          speed: 0,
          timeRemaining: 0,
        },
        {
          id: 'test-2',
          fileName: 'test_photo_2.jpg',
          fileSize: 5000000,
          status: UPLOAD_STATUS.COMPLETED,
          progress: 100,
          bytesUploaded: 5000000,
          speed: 0,
          timeRemaining: 0,
        },
      ];
    }
    return [];
  }); // Array of upload progress objects
  const [isUploading, setIsUploading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [showAddMore, setShowAddMore] = useState(false); // Show upload options while keeping selected files
  const [showAddMoreOptions, setShowAddMoreOptions] = useState(false); // Show additional cloud options (Google Drive, Apple Photos)
  const [contentFilter, setContentFilter] = useState('all'); // 'all' | 'videos' | 'photos' - filter file list display
  const videoInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const filesInputRef = useRef(null); // Combined video + photo input
  const cardholderNameRef = useRef(null);
  const paymentNoteRef = useRef(null); // Payment section note textarea

  // Ref for handleStartUpload to avoid circular dependency in SSE useEffect
  const handleStartUploadRef = useRef(null);
  // Ref for registerS3CompleteUploads to avoid use-before-define in subscription effect
  const registerS3CompleteUploadsRef = useRef(null);
  // Ref for sendMobilePushMessage to avoid use-before-define in file selection handlers
  const sendMobilePushMessageRef = useRef(null);

  // Track uploads state for pause/resume functionality
  const uploadsRef = useRef([]);
  const queuedFileIdsRef = useRef(new Set());
  // Store File objects separately (File objects don't serialize well in React state)
  const fileDataRef = useRef(new Map());
  // Store video metadata per draftId for post-payment registration (thumbnails, dimensions)
  const videoMetadataRef = useRef(new Map());
  // Store photo album metadata for post-payment createAndAttachAlbum call
  const photoMetadataRef = useRef(new Map());
  // Track if payment is done so S3-complete uploads can be registered
  const paymentDoneRef = useRef(false);
  // Track if "Pay from Web" was clicked so the flag persists in all subsequent pushes
  const paywebRequestedRef = useRef(false);
  // Prevent concurrent registration calls
  const isRegisteringRef = useRef(false);
  // Store upload metadata received from mobile for desktop-side registration
  // when mobile tab is closed and cannot run registerS3CompleteUploads itself
  const mobileUploadMetadataRef = useRef(null); // { videos: [...], albums: [...] }
  const registeredMobileIdsRef = useRef(new Set()); // Track registered mobile draftIds/albumUploadIds to prevent duplicates
  // Track deleted file names so they are not registered even if metadata was already received
  const deletedFileNamesRef = useRef(new Set());
  const selectedFilesRef = useRef([]);
  // Batch mobile cancel requests to avoid push overwrites
  const pendingCancelsRef = useRef([]);
  const cancelBatchTimerRef = useRef(null);
  // Track all completed files cumulatively - sent with each fileComplete so desktop doesn't miss any
  const completedFilesRef = useRef([]);
  // Note: fileProgress interval removed - mobile uploads only show "uploading"/"completed" status

  // Keep uploadsRef in sync with uploads state
  useEffect(() => {
    uploadsRef.current = uploads;
  }, [uploads]);

  // Keep selectedFilesRef in sync with selectedFiles state
  useEffect(() => {
    selectedFilesRef.current = selectedFiles;
  }, [selectedFiles]);

  // Mobile: Progress updates removed - desktop only shows "uploading" and "completed" status
  // Previously sent fileProgress every 5 seconds, but percentage display not needed for mobile uploads
  // Status changes come from fileUpload (start) and fileComplete (done) push messages

  // Mobile upload mode: auto-open file picker and handle cancel to go back
  useEffect(() => {
    if (!isMobileUploadMode || currentStep !== 3 || selectedFiles.length > 0) return;

    const fileInput = filesInputRef.current;
    if (!fileInput) return;

    const handleCancel = () => {
      window.history.back();
    };

    fileInput.addEventListener('cancel', handleCancel);

    // Auto-open file picker (works on desktop; mobile falls back to tap overlay)
    const timer = setTimeout(() => {
      fileInput.click();
    }, 500);

    return () => {
      clearTimeout(timer);
      fileInput.removeEventListener('cancel', handleCancel);
    };
  }, [isMobileUploadMode, currentStep, selectedFiles.length]);

  // Network monitoring state
  const [networkStatus, setNetworkStatus] = useState('online');

  // Network monitoring effect (UI display only — secure manager handles pause/resume internally)
  useEffect(() => {
    const handleOnline = () => {
      console.log('[GIFT_FLOW] Network: ONLINE');
      setNetworkStatus('online');
    };

    const handleOffline = () => {
      console.log('[GIFT_FLOW] Network: OFFLINE');
      setNetworkStatus('offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Subscribe to secure upload manager for video upload status/progress
  // This matches the main app's UploadPanel subscription pattern
  useEffect(() => {
    const unsubscribe = uploadManagerSecure.subscribe(({ uploads: managerUploads, networkStatus: managerNetworkStatus }) => {
      if (managerUploads.length === 0) return;

      setUploads((prev) => {
        let changed = false;
        const updated = prev.map((upload) => {
          // Only sync uploads that have a secureUploadId (videos going through secure manager)
          if (!upload.secureUploadId) return upload;
          // Never downgrade a COMPLETED upload
          if (upload.status === UPLOAD_STATUS.COMPLETED) return upload;

          const managerUpload = managerUploads.find((u) => u.id === upload.secureUploadId);
          if (!managerUpload) return upload;

          // Map secure manager status to gift flow status
          let mappedStatus = upload.status;
          if (managerUpload.status === 'pending') mappedStatus = UPLOAD_STATUS.PENDING;
          else if (managerUpload.status === 'initializing' || managerUpload.status === 'uploading' || managerUpload.status === 'finishing') mappedStatus = UPLOAD_STATUS.UPLOADING;
          else if (managerUpload.status === 'paused') mappedStatus = UPLOAD_STATUS.PAUSED;
          else if (managerUpload.status === 'network_error') mappedStatus = UPLOAD_STATUS.NETWORK_ERROR;
          else if (managerUpload.status === 'failed') mappedStatus = UPLOAD_STATUS.FAILED;
          else if (managerUpload.status === 'cancelled') mappedStatus = UPLOAD_STATUS.CANCELLED;
          else if (managerUpload.status === 's3_complete' || managerUpload.status === 'completed') mappedStatus = UPLOAD_STATUS.COMPLETED;

          // Only update if something actually changed
          if (
            mappedStatus === upload.status
            && managerUpload.progress === upload.progress
            && managerUpload.speed === upload.speed
          ) {
            return upload;
          }

          changed = true;
          // S3 upload done shows 100%, otherwise reserve 10% for registration
          const managerProgress = managerUpload.status === 's3_complete' || mappedStatus === UPLOAD_STATUS.COMPLETED
            ? 100
            : Math.round(managerUpload.progress * 0.9);
          const result = {
            ...upload,
            status: mappedStatus,
            progress: Math.max(managerProgress, upload.progress || 0), // Never go backward
            bytesUploaded: Math.max(managerUpload.bytesUploaded || 0, upload.bytesUploaded || 0),
            speed: managerUpload.speed || upload.speed,
            timeRemaining: managerUpload.timeRemaining || upload.timeRemaining,
            isPaused: managerUpload.isPaused || false,
          };

          // Store S3 URL for video preview when upload completes
          if (managerUpload.s3Key && !upload.s3Url) {
            result.s3Url = `${generateS3Url()}${managerUpload.s3Key}`;
          }

          return result;
        });

        return changed ? updated : prev;
      });

      // Sync network status from secure manager
      if (managerNetworkStatus) {
        setNetworkStatus(managerNetworkStatus);
      }

      // Auto-register S3-complete uploads if payment is done (videos via manager + photo albums via photoMetadataRef)
      // Skip on mobile — mobile only uploads to S3, desktop handles registration via metadata push
      if (paymentDoneRef.current && !isMobileUploadMode) {
        const hasS3Complete = managerUploads.some((u) => u.status === 's3_complete' && u.giftUuid);
        const hasPhotoAlbums = photoMetadataRef.current.size > 0;
        if ((hasS3Complete || hasPhotoAlbums) && registerS3CompleteUploadsRef.current) {
          registerS3CompleteUploadsRef.current();
        }
      }

      // Check if all uploads are done (s3_complete/completed/failed) — enable Continue button
      const allManagerDone = managerUploads.every(
        (u) => u.status === 's3_complete' || u.status === 'completed' || u.status === 'failed' || u.status === 'cancelled'
      );
      if (allManagerDone) {
        // Also verify no non-manager uploads (albums) are still active
        const hasActiveNonManager = uploadsRef.current.some(
          (u) => !u.secureUploadId && !u.isMobileUpload
            && (u.status === UPLOAD_STATUS.UPLOADING || u.status === UPLOAD_STATUS.PENDING || u.status === UPLOAD_STATUS.PAUSED)
        );
        if (!hasActiveNonManager) {
          setIsUploading(false);
        }
      }
    });

    return unsubscribe;
  }, []);

  // Callback ref for plan carousel — attaches swipe + drag listeners when div mounts
  const planCarouselCleanupRef = useRef(null);
  const planCarouselCallbackRef = useCallback((node) => {
    // Cleanup previous listeners
    if (planCarouselCleanupRef.current) {
      planCarouselCleanupRef.current();
      planCarouselCleanupRef.current = null;
    }

    planCarouselRef.current = node;
    if (!node) return;

    let startX = 0;
    let isDragging = false;

    const handleSwipe = (endX) => {
      const swipeThreshold = 50;
      const diff = startX - endX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          setPlanCarouselIndex((prev) => Math.min(prev + 1, giftPlans.length - 1));
        } else {
          setPlanCarouselIndex((prev) => Math.max(prev - 1, 0));
        }
      }
    };

    // Touch events
    const handleTouchStart = (e) => { startX = e.changedTouches[0].screenX; };
    const handleTouchEnd = (e) => { handleSwipe(e.changedTouches[0].screenX); };

    // Mouse events (for emulator / desktop drag)
    const handleMouseDown = (e) => { isDragging = true; startX = e.screenX; e.preventDefault(); };
    const handleMouseUp = (e) => { if (isDragging) { isDragging = false; handleSwipe(e.screenX); } };
    const handleMouseLeave = () => { isDragging = false; };

    node.addEventListener('touchstart', handleTouchStart);
    node.addEventListener('touchend', handleTouchEnd);

    // Mouse events only on mobile-width screens (for emulator testing)
    const isMobileWidth = window.innerWidth <= 900;
    if (isMobileWidth) {
      node.addEventListener('mousedown', handleMouseDown);
      node.addEventListener('mouseup', handleMouseUp);
      node.addEventListener('mouseleave', handleMouseLeave);
    }

    planCarouselCleanupRef.current = () => {
      node.removeEventListener('touchstart', handleTouchStart);
      node.removeEventListener('touchend', handleTouchEnd);
      node.removeEventListener('mousedown', handleMouseDown);
      node.removeEventListener('mouseup', handleMouseUp);
      node.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [giftPlans.length]);

  // Album assignment popup state
  const [showAlbumPopup, setShowAlbumPopup] = useState(false);
  const [pendingImages, setPendingImages] = useState([]);
  const [albumName, setAlbumName] = useState('');
  const toNameInputRef = useRef(null);

  // Track "All photos" album for automatic image grouping
  const [allPhotosAlbumId, setAllPhotosAlbumId] = useState(null);
  const allPhotosBackendIdRef = useRef(null); // Backend album_id (using ref for immediate access)
  const ALL_PHOTOS_ALBUM_NAME = 'All photos';
  const [expandedAlbums, setExpandedAlbums] = useState({}); // Track which albums are expanded { albumId: true/false }
  const [fetchedAlbumPhotos, setFetchedAlbumPhotos] = useState({}); // { [uploadId]: { loading: bool, photos: [] } }

  // Toast/Snackbar state
  const [snackbar, setSnackbar] = useState({ isVisible: false, message: '', type: 'error' });

  // QR code flip state
  const [showQrCode, setShowQrCode] = useState(false);
  const [showFilesQrCode, setShowFilesQrCode] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shortShareUrl, setShortShareUrl] = useState('');
  const shortShareUrlRef = useRef('');

  // Build the raw share URL path (without origin)
  const buildSharePath = useCallback(() => {
    const params = new URLSearchParams();
    params.set('test', 'upload');
    if (giftUserUid) params.set('giftUid', giftUserUid);
    const token = localStorage.getItem('token');
    if (token) params.set('token', token);
    const access = localStorage.getItem('uploader_token');
    if (access) params.set('access', access);
    params.set('share', 'true');
    if (contributorName.trim()) params.set('contributorName', contributorName.trim());
    if (formData.firstname.trim()) params.set('recipientName', formData.firstname.trim());
    return `gift?${params.toString()}`;
  }, [giftUserUid, formData.firstname, contributorName]);

  // Get a shortened share URL, caching the result
  const getShortenedShareUrl = useCallback(async () => {
    // Return cached short URL if available
    if (shortShareUrlRef.current) return shortShareUrlRef.current;
    const path = buildSharePath();
    const token = localStorage.getItem('token') || '';
    try {
      const res = await createShortLink({ token, path });
      if (res?.data?.link) {
        shortShareUrlRef.current = res.data.link;
        setShortShareUrl(res.data.link);
        return res.data.link;
      }
    } catch (err) {
      console.error('Short link creation failed, using full URL', err);
    }
    // Fallback to full URL if API fails
    return `${window.location.origin}/${path}`;
  }, [buildSharePath]);

  // Track if user already uploaded from mobile (to show "Already uploaded" link on desktop)
  const [mobileUploadCompleted, setMobileUploadCompleted] = useState(false);

  // Mobile flow push messages state
  const [mobileFlowMessages, setMobileFlowMessages] = useState([]);

  // Track recently processed push messages to filter duplicates
  const processedPushMessages = useRef(new Set());

  // Track file upload batch counter for unique push identifiers
  const fileUploadBatchCounter = useRef(0);

  // Polling fallback for mobile upload completion (when SSE misses events)
  const mobileCompletionPollRef = useRef(null);
  // Polling fallback for payweb push (when SSE misses the event)
  const paywebPollRef = useRef(null);
  // Polling fallback for paymentDone push on mobile (when SSE misses the event)
  const paymentDonePollRef = useRef(null);

  // Track if mobile-desktop connection is active (received push from mobile)
  const [isMobileDesktopConnected, setIsMobileDesktopConnected] = useState(false);

  // Mobile payment popup state
  const [showMobilePaymentPopup, setShowMobilePaymentPopup] = useState(false);

  // Mobile upload complete popup state
  const [showMobileUploadCompletePopup, setShowMobileUploadCompletePopup] = useState(false);

  // Waiting for web payment screen (mobile upload mode)
  const [showWaitingForPayment, setShowWaitingForPayment] = useState(false);

  // Cloud service connection state (using CloudUploadModal like main app)
  const [cloudModalOpen, setCloudModalOpen] = useState(false);
  const [selectedCloudService, setSelectedCloudService] = useState(null);

  // Active tab state for Step 4 (Upload, Layout, Content)
  const [activeTab, setActiveTab] = useState('upload');

  // Projector view state
  const [showProjectorView, setShowProjectorView] = useState(() => urlParams.testParam === 'projector');

  // Success section state (after Send Gift)
  const [showSuccessSection, setShowSuccessSection] = useState(() => urlParams.testParam === 'success' || urlParams.statusPage);
  const [successNote, setSuccessNote] = useState('');
  const [setpasswordUrl, setSetpasswordUrl] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [paymentValidationErrors, setPaymentValidationErrors] = useState({ cardholderName: false, deliveryDate: false });
  const [sendingGift, setSendingGift] = useState(false);

  // API-fetched gift status data (populated from getGiftStatus)
  const [giftStatusData, setGiftStatusData] = useState(null);
  const [cachedEmailHtml, setCachedEmailHtml] = useState(null);

  // Gift status dashboard state
  const [giftDeliveryStatus, setGiftDeliveryStatus] = useState('pending'); // 'pending' | 'delivered' | 'opened'
  const [emailSentStatus, setEmailSentStatus] = useState('sent'); // 'sent' | 'resending' | 'resent'
  const [smsSentStatus, setSmsSentStatus] = useState('idle'); // 'idle' | 'sending' | 'sent'
  const [smsPhoneNumber, setSmsPhoneNumber] = useState('');
  const [smsError, setSmsError] = useState('');
  const [smsCountryIdx, setSmsCountryIdx] = useState(0);

  const countryCodeOptions = [
    { code: '+1', flag: '\u{1F1FA}\u{1F1F8}', placeholder: '555 123 4567' },
    { code: '+1', flag: '\u{1F1E8}\u{1F1E6}', placeholder: '555 123 4567' },
    { code: '+7', flag: '\u{1F1F7}\u{1F1FA}', placeholder: '912 345 6789' },
    { code: '+20', flag: '\u{1F1EA}\u{1F1EC}', placeholder: '100 123 4567' },
    { code: '+27', flag: '\u{1F1FF}\u{1F1E6}', placeholder: '71 123 4567' },
    { code: '+30', flag: '\u{1F1EC}\u{1F1F7}', placeholder: '691 234 5678' },
    { code: '+31', flag: '\u{1F1F3}\u{1F1F1}', placeholder: '6 1234 5678' },
    { code: '+32', flag: '\u{1F1E7}\u{1F1EA}', placeholder: '470 12 34 56' },
    { code: '+33', flag: '\u{1F1EB}\u{1F1F7}', placeholder: '6 12 34 56 78' },
    { code: '+34', flag: '\u{1F1EA}\u{1F1F8}', placeholder: '612 34 56 78' },
    { code: '+36', flag: '\u{1F1ED}\u{1F1FA}', placeholder: '20 123 4567' },
    { code: '+39', flag: '\u{1F1EE}\u{1F1F9}', placeholder: '312 345 6789' },
    { code: '+40', flag: '\u{1F1F7}\u{1F1F4}', placeholder: '712 345 678' },
    { code: '+41', flag: '\u{1F1E8}\u{1F1ED}', placeholder: '78 123 45 67' },
    { code: '+43', flag: '\u{1F1E6}\u{1F1F9}', placeholder: '664 123 4567' },
    { code: '+44', flag: '\u{1F1EC}\u{1F1E7}', placeholder: '7911 123456' },
    { code: '+45', flag: '\u{1F1E9}\u{1F1F0}', placeholder: '20 12 34 56' },
    { code: '+46', flag: '\u{1F1F8}\u{1F1EA}', placeholder: '70 123 45 67' },
    { code: '+47', flag: '\u{1F1F3}\u{1F1F4}', placeholder: '412 34 567' },
    { code: '+48', flag: '\u{1F1F5}\u{1F1F1}', placeholder: '512 345 678' },
    { code: '+49', flag: '\u{1F1E9}\u{1F1EA}', placeholder: '151 1234 5678' },
    { code: '+51', flag: '\u{1F1F5}\u{1F1EA}', placeholder: '912 345 678' },
    { code: '+52', flag: '\u{1F1F2}\u{1F1FD}', placeholder: '55 1234 5678' },
    { code: '+53', flag: '\u{1F1E8}\u{1F1FA}', placeholder: '5 123 4567' },
    { code: '+54', flag: '\u{1F1E6}\u{1F1F7}', placeholder: '11 1234 5678' },
    { code: '+55', flag: '\u{1F1E7}\u{1F1F7}', placeholder: '11 91234 5678' },
    { code: '+56', flag: '\u{1F1E8}\u{1F1F1}', placeholder: '9 1234 5678' },
    { code: '+57', flag: '\u{1F1E8}\u{1F1F4}', placeholder: '301 234 5678' },
    { code: '+58', flag: '\u{1F1FB}\u{1F1EA}', placeholder: '412 123 4567' },
    { code: '+60', flag: '\u{1F1F2}\u{1F1FE}', placeholder: '12 345 6789' },
    { code: '+61', flag: '\u{1F1E6}\u{1F1FA}', placeholder: '412 345 678' },
    { code: '+62', flag: '\u{1F1EE}\u{1F1E9}', placeholder: '812 345 6789' },
    { code: '+63', flag: '\u{1F1F5}\u{1F1ED}', placeholder: '912 345 6789' },
    { code: '+64', flag: '\u{1F1F3}\u{1F1FF}', placeholder: '21 123 4567' },
    { code: '+65', flag: '\u{1F1F8}\u{1F1EC}', placeholder: '9123 4567' },
    { code: '+66', flag: '\u{1F1F9}\u{1F1ED}', placeholder: '81 234 5678' },
    { code: '+81', flag: '\u{1F1EF}\u{1F1F5}', placeholder: '90 1234 5678' },
    { code: '+82', flag: '\u{1F1F0}\u{1F1F7}', placeholder: '10 1234 5678' },
    { code: '+84', flag: '\u{1F1FB}\u{1F1F3}', placeholder: '91 234 56 78' },
    { code: '+86', flag: '\u{1F1E8}\u{1F1F3}', placeholder: '131 2345 6789' },
    { code: '+90', flag: '\u{1F1F9}\u{1F1F7}', placeholder: '501 234 5678' },
    { code: '+91', flag: '\u{1F1EE}\u{1F1F3}', placeholder: '98765 43210' },
    { code: '+92', flag: '\u{1F1F5}\u{1F1F0}', placeholder: '301 234 5678' },
    { code: '+93', flag: '\u{1F1E6}\u{1F1EB}', placeholder: '70 123 4567' },
    { code: '+94', flag: '\u{1F1F1}\u{1F1F0}', placeholder: '71 234 5678' },
    { code: '+95', flag: '\u{1F1F2}\u{1F1F2}', placeholder: '9 123 4567' },
    { code: '+212', flag: '\u{1F1F2}\u{1F1E6}', placeholder: '612 345 678' },
    { code: '+213', flag: '\u{1F1E9}\u{1F1FF}', placeholder: '551 23 45 67' },
    { code: '+216', flag: '\u{1F1F9}\u{1F1F3}', placeholder: '20 123 456' },
    { code: '+234', flag: '\u{1F1F3}\u{1F1EC}', placeholder: '802 123 4567' },
    { code: '+254', flag: '\u{1F1F0}\u{1F1EA}', placeholder: '712 345 678' },
    { code: '+255', flag: '\u{1F1F9}\u{1F1FF}', placeholder: '621 234 567' },
    { code: '+256', flag: '\u{1F1FA}\u{1F1EC}', placeholder: '712 345 678' },
    { code: '+260', flag: '\u{1F1FF}\u{1F1F2}', placeholder: '95 512 3456' },
    { code: '+263', flag: '\u{1F1FF}\u{1F1FC}', placeholder: '71 234 5678' },
    { code: '+351', flag: '\u{1F1F5}\u{1F1F9}', placeholder: '912 345 678' },
    { code: '+353', flag: '\u{1F1EE}\u{1F1EA}', placeholder: '85 123 4567' },
    { code: '+354', flag: '\u{1F1EE}\u{1F1F8}', placeholder: '611 1234' },
    { code: '+358', flag: '\u{1F1EB}\u{1F1EE}', placeholder: '41 234 5678' },
    { code: '+370', flag: '\u{1F1F1}\u{1F1F9}', placeholder: '612 34 567' },
    { code: '+371', flag: '\u{1F1F1}\u{1F1FB}', placeholder: '2123 4567' },
    { code: '+372', flag: '\u{1F1EA}\u{1F1EA}', placeholder: '5123 4567' },
    { code: '+380', flag: '\u{1F1FA}\u{1F1E6}', placeholder: '50 123 4567' },
    { code: '+381', flag: '\u{1F1F7}\u{1F1F8}', placeholder: '60 123 4567' },
    { code: '+420', flag: '\u{1F1E8}\u{1F1FF}', placeholder: '601 123 456' },
    { code: '+421', flag: '\u{1F1F8}\u{1F1F0}', placeholder: '912 123 456' },
    { code: '+852', flag: '\u{1F1ED}\u{1F1F0}', placeholder: '5123 4567' },
    { code: '+853', flag: '\u{1F1F2}\u{1F1F4}', placeholder: '6612 3456' },
    { code: '+880', flag: '\u{1F1E7}\u{1F1E9}', placeholder: '1812 345678' },
    { code: '+886', flag: '\u{1F1F9}\u{1F1FC}', placeholder: '912 345 678' },
    { code: '+960', flag: '\u{1F1F2}\u{1F1FB}', placeholder: '791 2345' },
    { code: '+961', flag: '\u{1F1F1}\u{1F1E7}', placeholder: '71 123 456' },
    { code: '+962', flag: '\u{1F1EF}\u{1F1F4}', placeholder: '7 9012 3456' },
    { code: '+963', flag: '\u{1F1F8}\u{1F1FE}', placeholder: '944 567 890' },
    { code: '+964', flag: '\u{1F1EE}\u{1F1F6}', placeholder: '791 234 5678' },
    { code: '+965', flag: '\u{1F1F0}\u{1F1FC}', placeholder: '500 12345' },
    { code: '+966', flag: '\u{1F1F8}\u{1F1E6}', placeholder: '51 234 5678' },
    { code: '+971', flag: '\u{1F1E6}\u{1F1EA}', placeholder: '50 123 4567' },
    { code: '+972', flag: '\u{1F1EE}\u{1F1F1}', placeholder: '50 123 4567' },
    { code: '+973', flag: '\u{1F1E7}\u{1F1ED}', placeholder: '3600 1234' },
    { code: '+974', flag: '\u{1F1F6}\u{1F1E6}', placeholder: '3312 3456' },
    { code: '+977', flag: '\u{1F1F3}\u{1F1F5}', placeholder: '984 123 4567' },
    { code: '+993', flag: '\u{1F1F9}\u{1F1F2}', placeholder: '66 123456' },
    { code: '+994', flag: '\u{1F1E6}\u{1F1FF}', placeholder: '40 123 45 67' },
    { code: '+995', flag: '\u{1F1EC}\u{1F1EA}', placeholder: '555 12 34 56' },
    { code: '+998', flag: '\u{1F1FA}\u{1F1FF}', placeholder: '91 123 45 67' },
  ];
  const currentCountry = countryCodeOptions[smsCountryIdx] || countryCodeOptions[0];
  const smsCountryCode = currentCountry.code;

  // Layout tab state - content order items
  const [layoutItems, setLayoutItems] = useState([
    { id: 'categories', title: 'Categories', order: 1 },
    { id: 'playlists', title: 'Playlists', order: 2 },
    { id: 'videos', title: 'Videos', order: 3 },
    { id: 'photos', title: 'Photo Albums', order: 4 }
  ]);
  const [newLayoutOrder, setNewLayoutOrder] = useState(undefined);
  const [layoutOrderLoading, setLayoutOrderLoading] = useState(false);

  // useSWR for layout reorder API (same pattern as ContentGroupButton.jsx)
  // Safety: conditional key (null when no items) prevents automatic fetching
  const { mutate: updateLayoutOrder } = useSWR(
    newLayoutOrder?.items ? [endpoints.reOrderMyContentType, newLayoutOrder.items] : null,
    {
      fetcher: (url, newItems) => dataFetcher(url, { items: newItems }),
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      onSuccess: ({ success }) => {
        if (success) {
          console.log('[GIFT_FLOW] Layout order updated successfully');
        } else {
          console.error('[GIFT_FLOW] Failed to update layout order');
        }
        setLayoutOrderLoading(false);
        setNewLayoutOrder(undefined); // Reset after successful update
      },
      onError: (err) => {
        logAppEvent({
          userName: `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
          functionAffected: 'Reorder Content Types',
          description: 'Gift flow content type reorder failed',
          severity: 'medium',
          errorMessage: err?.response?.data?.message || err?.message || 'Content type reorder failed',
          responseStatus: err?.response?.status || null,
          responseBody: err?.response?.data?.message || null,
          requestUrl: endpoints.reOrderMyContentType,
          requestMethod: 'POST',
          component: 'GiftFlow',
          userAction: 'Reorder content types called',
        });
        console.error('[GIFT_FLOW] Error updating layout order:', err);
        setLayoutOrderLoading(false);
        setNewLayoutOrder(undefined); // Reset on error too
      },
    }
  );

  // Trigger layout order update when newLayoutOrder changes
  useEffect(() => {
    if (newLayoutOrder?.items) {
      setLayoutOrderLoading(true);
      updateLayoutOrder();
    }
  }, [newLayoutOrder, updateLayoutOrder]);

  // Content tab state
  const [contentTabType, setContentTabType] = useState('videos'); // 'videos' or 'photos'
  const [selectedContentItems, setSelectedContentItems] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editVisibility, setEditVisibility] = useState('2');
  const [editLoading, setEditLoading] = useState(false);

  // Bulk edit state - individual items with their edits
  const [bulkEditItems, setBulkEditItems] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});

  // Create category state
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  // Real content data from API (replacing dummy data)
  const [userVideos, setUserVideos] = useState([]);
  const [userAlbums, setUserAlbums] = useState([]);
  const [userCategories, setUserCategories] = useState([]);
  const [userPlaylists, setUserPlaylists] = useState([]);
  const [defaultBackgroundImage, setDefaultBackgroundImage] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentFetched, setContentFetched] = useState(false);
  const [layoutFetched, setLayoutFetched] = useState(false);
  const [projectorDataFetched, setProjectorDataFetched] = useState(false);

  // Fetch user's videos from API (same logic as YourVideos.jsx)
  const fetchUserVideos = useCallback(async () => {
    if (!getToken()) {
      console.log('[GIFT_FLOW] No token, skipping video fetch');
      return;
    }
    try {
      console.log('[GIFT_FLOW] Fetching user videos...');
      const response = await apiAxios.post(`${baseUrl}/getMyVideosList`);
      console.log('[GIFT_FLOW] API Response:', response.data);
      console.log('[GIFT_FLOW] First video raw data:', response.data?.data?.[0]);
      if (response.data?.success && response.data?.data) {
        const videos = response.data.data.map((video) => {
          // Handle thumbnail same as main app (YourVideos.jsx line 3290-3292)
          const videoThumbnail = Array.isArray(video.thumbnail)
            ? video.thumbnail[0] || image04
            : video.thumbnail || video.thumbnails || image04;

          console.log('[GIFT_FLOW] Video thumbnail:', video.title, videoThumbnail);

          return {
            id: video.content_id || video.video_id || video.id,
            video_id: video.video_id || video.content_id || video.id,
            content_id: video.content_id || video.id,
            draft_id: video.draft_id || null,
            title: video.title || 'Untitled',
            description: video.description || video.video_description || '',
            thumbnail: videoThumbnail,
            duration: video.duration || video.video_duration || '',
            date: video.publish_date || video.created || video.created_at || '',
            category: video.category_name || video.category || 'All Videos',
            category_id: video.category_id || null,
            visibility: video.visibility || '2',
            status: video.status || 1,
          };
        });
        setUserVideos(videos);
        console.log('[GIFT_FLOW] Fetched', videos.length, 'videos');
      }
    } catch (err) {
      console.error('[GIFT_FLOW] Error fetching videos:', err);
    }
  }, []);

  // Fetch user's albums from API
  const fetchUserAlbums = useCallback(async () => {
    if (!getToken()) {
      console.log('[GIFT_FLOW] No token, skipping album fetch');
      return;
    }
    try {
      console.log('[GIFT_FLOW] Fetching user albums...');
      const response = await apiAxios.post(`${baseUrl}/getMyAlbum`);
      console.log('[GIFT_FLOW] Albums API Response:', response.data);
      if (response.data?.success && response.data?.data) {
        const albums = response.data.data.map((album) => {
          // Try multiple fields for photo count: photo_count, total_photos, photos array length
          const photoCount = album.photo_count || album.total_photos || (Array.isArray(album.photos) ? album.photos.length : 0);
          console.log('[GIFT_FLOW] Album:', album.title, 'photo_count:', album.photo_count, 'total_photos:', album.total_photos, 'photos:', album.photos?.length, 'using:', photoCount);
          return {
            id: album.id || album.album_id,
            title: album.title || album.album_title || 'Untitled Album',
            thumbnail: album.imagekit_icon || album.icon || album.thumbnail || image01,
            photoCount,
            date: album.created_at || album.upload_date || '',
          };
        });
        setUserAlbums(albums);
        console.log('[GIFT_FLOW] Fetched', albums.length, 'albums');
      }
    } catch (err) {
      console.error('[GIFT_FLOW] Error fetching albums:', err);
    }
  }, []);

  // Open video preview for a completed upload using its registered content_id
  const handleUploadVideoPreview = async (contentId) => {
    setUploadPreviewOpen(true);
    setUploadPreviewLoading(true);
    setUploadPreviewVideo(null);
    try {
      const response = await getVideosDetail({ id: contentId });
      if (response?.data?.success && response?.data?.data) {
        const details = response.data.data;
        const thumb = Array.isArray(details.thumbnail) ? details.thumbnail[0] : details.thumbnail || '';
        setUploadPreviewVideo({
          title: details.title || '',
          thumbnail: thumb,
          videoUrl: details.video_raw_file || '',
        });
      }
    } catch (err) {
      console.error('[GIFT_FLOW] Error fetching video preview:', err);
    } finally {
      setUploadPreviewLoading(false);
    }
  };

  // Open video preview for unregistered uploads using local blob URL or S3 URL
  const handleLocalVideoPreview = (file) => {
    setUploadPreviewOpen(true);
    setUploadPreviewLoading(false);
    setUploadPreviewVideo({
      title: file.name,
      thumbnail: file.posterUrl || '',
      videoUrl: file.s3Url || file.previewUrl,
    });
  };

  // Open album preview for a completed upload using its registered album_id
  const handleUploadAlbumPreview = async (albumId) => {
    setUploadAlbumOpen(true);
    setUploadAlbumLoading(true);
    setUploadAlbumDetail({ title: '', photos: [] });
    try {
      const response = await apiAxios.post(`${baseUrl}/getMyAlbumDetail`, { album_id: albumId });
      if (response?.data?.success && response?.data?.data) {
        const { data } = response.data;
        const photos = data[0]?.photos || data?.photos || [];
        setUploadAlbumDetail({ title: data[0]?.title || 'Album', photos });
      }
    } catch (err) {
      console.error('[GIFT_FLOW] Error fetching album preview:', err);
    } finally {
      setUploadAlbumLoading(false);
    }
  };

  // Fetch photos for a share/registered album to show in the expand section
  const fetchAlbumPhotosForUpload = useCallback(async (uploadId, albumId) => {
    setFetchedAlbumPhotos((prev) => ({ ...prev, [uploadId]: { loading: true, photos: [] } }));
    try {
      const response = await apiAxios.post(`${baseUrl}/getMyAlbumDetail`, { album_id: albumId });
      if (response?.data?.success && response?.data?.data) {
        const { data } = response.data;
        const photos = data[0]?.photos || data?.photos || [];
        setFetchedAlbumPhotos((prev) => ({ ...prev, [uploadId]: { loading: false, photos } }));
      } else {
        setFetchedAlbumPhotos((prev) => ({ ...prev, [uploadId]: { loading: false, photos: [] } }));
      }
    } catch (err) {
      console.error('[GIFT_FLOW] Error fetching album photos for expand:', err);
      setFetchedAlbumPhotos((prev) => ({ ...prev, [uploadId]: { loading: false, photos: [] } }));
    }
  }, []);

  // Delete a registered upload from step 5 and step 3
  const handleUploadDelete = async () => {
    if (!uploadDeletePending) return;
    setUploadDeleting(true);
    try {
      const { uploadId, type, id } = uploadDeletePending;
      if (type === 'photo') {
        const { albumFileId } = uploadDeletePending;
        await apiAxios.post(`${baseUrl}/${endpoints.deleteMyPhoto}`, { photo_id: id });
        // Remove photo from album in selectedFiles and compute remaining count
        let newPhotoCount = 0;
        setSelectedFiles((prev) => {
          const updated = prev.map((f) => {
            if (f.id === albumFileId && f.type === 'album' && f.photos) {
              const remaining = f.photos.filter((p) => String(p.photo_id) !== String(id));
              newPhotoCount = remaining.length;
              return { ...f, photos: remaining, photoCount: remaining.length };
            }
            return f;
          });
          if (newPhotoCount === 0) {
            return updated.filter((f) => f.id !== albumFileId);
          }
          return updated;
        });
        // Remove from fetchedAlbumPhotos cache too
        setFetchedAlbumPhotos((prev) => {
          const entry = prev[uploadId];
          if (!entry) return prev;
          const remaining = (entry.photos || []).filter((sp) => String(sp.id) !== String(id));
          return { ...prev, [uploadId]: { ...entry, photos: remaining } };
        });
        // Update or remove the upload entry
        if (newPhotoCount === 0) {
          setUploads((prev) => prev.filter((u) => u.id !== uploadId && u.linkedAlbumId !== albumFileId));
        } else {
          setUploads((prev) => prev.map((u) => {
            if (u.id === uploadId || u.linkedAlbumId === albumFileId) {
              return { ...u, fileName: `All photos (${newPhotoCount})` };
            }
            return u;
          }));
        }
      } else if (type === 'video') {
        await apiAxios.post(`${baseUrl}/deleteVideo`, { video_id: id });
        // Remove from uploads list (step 5)
        setUploads((prev) => prev.filter((u) => u.id !== uploadId));
        // Remove from selectedFiles (step 3)
        setSelectedFiles((prev) => prev.filter((f) => f.id !== uploadId));
      } else {
        await apiAxios.post(`${baseUrl}/deleteMyAlbum`, { album_id: id });
        // Remove from uploads list (step 5)
        setUploads((prev) => prev.filter((u) => u.id !== uploadId));
        // Remove from selectedFiles (step 3)
        setSelectedFiles((prev) => prev.filter((f) => f.id !== uploadId));
      }
      setUploadDeletePending(null);
    } catch (err) {
      console.error('[GIFT_FLOW] Error deleting upload:', err);
    } finally {
      setUploadDeleting(false);
    }
  };

  // Soft delete — move item to Restore tab instead of calling API
  const handleSoftDelete = useCallback((itemData) => {
    setUploads((prev) => prev.filter((u) => u.id !== itemData.uploadId));
    setSelectedFiles((prev) => prev.filter((f) => f.id !== itemData.uploadId));
    deletedFileNamesRef.current.add(itemData.fileName);
    setDeletedItems((prev) => [...prev, itemData]);
    // Notify mobile to also soft-delete (move to its restore tab)
    if (sendMobilePushMessageRef.current) {
      sendMobilePushMessageRef.current('softDeleteUpload', [itemData.fileName]);
    }
  }, []);

  // Soft delete a photo from within an album
  const handleSoftDeletePhoto = useCallback((itemData) => {
    const { uploadId, albumFileId, registeredId } = itemData;
    setSelectedFiles((prev) => {
      const updated = prev.map((f) => {
        if (f.id === albumFileId && f.type === 'album' && f.photos) {
          const remaining = f.photos.filter((p) => String(p.photo_id || p.id) !== String(registeredId));
          return { ...f, photos: remaining, photoCount: remaining.length };
        }
        return f;
      });
      const albumEntry = updated.find((f) => f.id === albumFileId);
      if (albumEntry && albumEntry.photos && albumEntry.photos.length === 0) {
        return updated.filter((f) => f.id !== albumFileId);
      }
      return updated;
    });
    setUploads((prev) => {
      const remainingCount = selectedFiles.find((f) => f.id === albumFileId)?.photos
        ?.filter((p) => String(p.photo_id || p.id) !== String(registeredId)).length || 0;
      if (remainingCount === 0) {
        return prev.filter((u) => u.id !== uploadId && u.linkedAlbumId !== albumFileId);
      }
      return prev.map((u) => {
        if (u.id === uploadId || u.linkedAlbumId === albumFileId) {
          return { ...u, fileName: `All photos (${remainingCount})` };
        }
        return u;
      });
    });
    setFetchedAlbumPhotos((prev) => {
      const entry = prev[uploadId];
      if (!entry) return prev;
      const remaining = (entry.photos || []).filter((sp) => String(sp.id) !== String(registeredId));
      return { ...prev, [uploadId]: { ...entry, photos: remaining } };
    });
    deletedFileNamesRef.current.add(itemData.fileName);
    setDeletedItems((prev) => [...prev, itemData]);
    // Notify mobile to also soft-delete this photo (move to its restore tab)
    if (sendMobilePushMessageRef.current) {
      const uid = itemData.photoData?.mobileUid || itemData.registeredId || itemData.photoData?.id;
      sendMobilePushMessageRef.current('softDeleteAlbumPhoto', [uid ? `${itemData.fileName}|${uid}` : itemData.fileName]);
    }
  }, [selectedFiles]);

  // Restore — move item back from Restore tab
  const handleRestore = useCallback((item) => {
    setDeletedItems((prev) => prev.filter((d) => !(d.uploadId === item.uploadId && d.type === item.type && d.fileName === item.fileName)));
    deletedFileNamesRef.current.delete(item.fileName);
    if (item.type === 'photo') {
      if (item.photoData) {
        setSelectedFiles((prev) => prev.map((f) => {
          if (f.id === item.albumFileId && f.type === 'album') {
            return { ...f, photos: [...(f.photos || []), item.photoData], photoCount: (f.photoCount || 0) + 1 };
          }
          return f;
        }));
        setFetchedAlbumPhotos((prev) => {
          const entry = prev[item.uploadId];
          if (!entry) return prev;
          return { ...prev, [item.uploadId]: { ...entry, photos: [...(entry.photos || []), item.photoData] } };
        });
        // Update upload fileName count
        setUploads((prev) => prev.map((u) => {
          if (u.id === item.uploadId || u.linkedAlbumId === item.albumFileId) {
            const currentCount = parseInt((u.fileName.match(/\((\d+)\)/) || [])[1] || '0', 10);
            return { ...u, fileName: `All photos (${currentCount + 1})` };
          }
          return u;
        }));
      }
      // Notify mobile to also restore this photo
      if (sendMobilePushMessageRef.current) {
        const uid = item.photoData?.mobileUid || item.registeredId || item.photoData?.id;
        sendMobilePushMessageRef.current('restoreAlbumPhoto', [uid ? `${item.fileName}|${uid}` : item.fileName]);
      }
    } else {
      if (item.uploadData) {
        setUploads((prev) => [...prev, item.uploadData]);
      }
      if (item.selectedFileData) {
        setSelectedFiles((prev) => [...prev, item.selectedFileData]);
      }
      // Notify mobile to also restore this upload
      if (sendMobilePushMessageRef.current) {
        sendMobilePushMessageRef.current('restoreUpload', [item.fileName]);
      }
    }
  }, []);

  // Fire actual API deletes for all registered items in deletedItems
  const flushDeletedItems = useCallback(async () => {
    if (deletedItems.length === 0) return;
    const deletePromises = deletedItems
      .filter((item) => item.source === 'registered')
      .map((item) => {
        if (item.type === 'video') {
          return apiAxios.post(`${baseUrl}/deleteVideo`, { video_id: item.registeredId }).catch((err) => {
            console.error('[GIFT_FLOW] Failed to delete video:', item.registeredId, err);
          });
        }
        if (item.type === 'photo') {
          return apiAxios.post(`${baseUrl}/${endpoints.deleteMyPhoto}`, { photo_id: item.registeredId }).catch((err) => {
            console.error('[GIFT_FLOW] Failed to delete photo:', item.registeredId, err);
          });
        }
        return apiAxios.post(`${baseUrl}/deleteMyAlbum`, { album_id: item.registeredId }).catch((err) => {
          console.error('[GIFT_FLOW] Failed to delete album:', item.registeredId, err);
        });
      });
    await Promise.all(deletePromises);

    setDeletedItems([]);
  }, [deletedItems]);

  // If restore tab becomes empty, switch back to 'all'
  useEffect(() => {
    if (contentFilter === 'restore' && deletedItems.length === 0) {
      setContentFilter('all');
    }
  }, [deletedItems.length, contentFilter]);

  // When leaving step 3 or 5, flush any pending soft deletes
  useEffect(() => {
    if (currentStep !== 3 && currentStep !== 5 && deletedItems.length > 0) {
      flushDeletedItems();
    }
  }, [currentStep]);

  // Fetch user's layout from API (same as content-layout/index.jsx)
  const fetchUserLayout = useCallback(async () => {
    if (!getToken()) {
      console.log('[GIFT_FLOW] No token, skipping layout fetch');
      return;
    }
    try {
      console.log('[GIFT_FLOW] Fetching user layout...');
      const response = await apiAxios.post(`${baseUrl}/getContentType`);
      console.log('[GIFT_FLOW] Layout API Response:', response.data);
      if (response.data?.success && response.data?.data) {
        // Map the content types to layout items format
        // API returns: { id, content_type, order_number }
        const layoutData = response.data.data.map((item) => ({
          id: item.id,
          title: item.content_type,
          order: parseInt(item.order_number, 10) || 0,
        }));
        // Sort by order_number
        layoutData.sort((a, b) => a.order - b.order);
        setLayoutItems(layoutData);
        console.log('[GIFT_FLOW] Fetched layout:', layoutData);
      }
    } catch (err) {
      console.error('[GIFT_FLOW] Error fetching layout:', err);
    }
  }, []);

  // Fetch user's categories from API (for projector view and edit modal)
  const fetchUserCategories = useCallback(async () => {
    if (!getToken()) {
      console.log('[GIFT_FLOW] No token, skipping categories fetch');
      return;
    }
    try {
      console.log('[GIFT_FLOW] Fetching user categories...');
      const response = await apiAxios.post(`${baseUrl}/getMyCategory`);
      console.log('[GIFT_FLOW] Categories API Response:', response.data);
      if (response.data?.success && response.data?.data) {
        const categories = response.data.data.map((cat) => {
          // Categories use: thumbnails array (video thumbnails) OR imagekit_icon/icon as fallback
          // Same logic as content-layout-details/index.jsx line 358 & 387
          const firstThumbnail = Array.isArray(cat.thumbnails) && cat.thumbnails.length > 0
            ? cat.thumbnails[0]
            : null;
          const categoryImage = firstThumbnail || cat.imagekit_icon || cat.icon || null;
          console.log('[GIFT_FLOW] Category:', cat.title, 'thumbnails:', cat.thumbnails, 'imagekit_icon:', cat.imagekit_icon, 'icon:', cat.icon, 'using:', categoryImage);
          return {
            id: cat.id,
            title: cat.title,
            icon: categoryImage,
            videoCount: cat.video_count || 0,
            order: parseInt(cat.order_number, 10) || 0,
          };
        });
        // Sort categories: "All Videos" at top, then by order_number
        categories.sort((a, b) => {
          const aIsAllVideos = a.title?.toLowerCase() === 'all videos';
          const bIsAllVideos = b.title?.toLowerCase() === 'all videos';
          if (aIsAllVideos && !bIsAllVideos) return -1;
          if (!aIsAllVideos && bIsAllVideos) return 1;
          return a.order - b.order;
        });
        setUserCategories(categories);
        console.log('[GIFT_FLOW] Fetched', categories.length, 'categories');
      }
    } catch (err) {
      console.error('[GIFT_FLOW] Error fetching categories:', err);
    }
  }, []);

  // Fetch user's playlists from API (for projector view)
  const fetchUserPlaylists = useCallback(async () => {
    if (!getToken()) {
      console.log('[GIFT_FLOW] No token, skipping playlists fetch');
      return;
    }
    try {
      console.log('[GIFT_FLOW] Fetching user playlists...');
      const response = await apiAxios.post(`${baseUrl}/getMyPlaylist`);
      console.log('[GIFT_FLOW] Playlists API Response:', response.data);
      if (response.data?.success && response.data?.data) {
        const playlists = response.data.data.map((pl) => {
          // Playlists use: thumbnails array (video thumbnails) OR imagekit_icon/icon as fallback
          const firstThumbnail = Array.isArray(pl.thumbnails) && pl.thumbnails.length > 0
            ? pl.thumbnails[0]
            : null;
          const playlistImage = firstThumbnail || pl.imagekit_icon || pl.icon || null;
          console.log('[GIFT_FLOW] Playlist:', pl.title, 'thumbnails:', pl.thumbnails, 'imagekit_icon:', pl.imagekit_icon, 'icon:', pl.icon, 'using:', playlistImage);
          return {
            id: pl.id,
            title: pl.title,
            icon: playlistImage,
            videoCount: pl.video_count || 0,
          };
        });
        setUserPlaylists(playlists);
        console.log('[GIFT_FLOW] Fetched', playlists.length, 'playlists');
      }
    } catch (err) {
      console.error('[GIFT_FLOW] Error fetching playlists:', err);
    }
  }, []);

  // Fetch background images (for categories/playlists without icons)
  const fetchBackgroundImages = useCallback(async () => {
    if (!getToken()) {
      console.log('[GIFT_FLOW] No token, skipping background images fetch');
      return;
    }
    try {
      console.log('[GIFT_FLOW] Fetching background images...');
      const response = await apiAxios.post(`${baseUrl}/getBackgroundImages`);
      console.log('[GIFT_FLOW] Background Images API Response:', response.data);
      if (response.data?.success && response.data?.data) {
        // Get the default background image (is_default === 1)
        const defaultImg = response.data.data.find((item) => parseInt(item.is_default, 10) === 1);
        if (defaultImg) {
          setDefaultBackgroundImage(defaultImg.imagekit_image || defaultImg.image);
          console.log('[GIFT_FLOW] Default background image set:', defaultImg.imagekit_image || defaultImg.image);
        }
      }
    } catch (err) {
      console.error('[GIFT_FLOW] Error fetching background images:', err);
    }
  }, []);

  // Reset content filter to 'all' when entering step 5
  useEffect(() => {
    if (currentStep === 5) {
      setContentFilter('all');
    }
  }, [currentStep]);

  // Fetch content when on step 5 (upload progress) and content tab is active
  useEffect(() => {
    if (currentStep === 5 && activeTab === 'content' && !contentFetched && getToken()) {
      setContentLoading(true);
      Promise.all([fetchUserVideos(), fetchUserAlbums(), fetchUserCategories(), fetchUserPlaylists()])
        .finally(() => {
          setContentLoading(false);
          setContentFetched(true);
        });
    }
  }, [currentStep, activeTab, contentFetched, fetchUserVideos, fetchUserAlbums, fetchUserCategories, fetchUserPlaylists]);

  // Fetch layout when on step 5 and layout tab is active
  useEffect(() => {
    if (currentStep === 5 && activeTab === 'layout' && !layoutFetched && getToken()) {
      fetchUserLayout().finally(() => {
        setLayoutFetched(true);
      });
    }
  }, [currentStep, activeTab, layoutFetched, fetchUserLayout]);

  // Fetch all projector view data when projector view is opened
  useEffect(() => {
    if (showProjectorView && !projectorDataFetched && getToken()) {
      console.log('[GIFT_FLOW] Fetching all projector view data...');
      Promise.all([
        fetchUserCategories(),
        fetchUserPlaylists(),
        fetchUserVideos(),
        fetchUserAlbums(),
        fetchBackgroundImages(),
        fetchUserLayout(),
      ]).finally(() => {
        setProjectorDataFetched(true);
        console.log('[GIFT_FLOW] Projector view data fetched');
      });
    }
  }, [showProjectorView, projectorDataFetched, fetchUserCategories, fetchUserPlaylists, fetchUserVideos, fetchUserAlbums, fetchBackgroundImages, fetchUserLayout]);

  // Refresh content after upload completes
  const refreshContent = useCallback(() => {
    setContentFetched(false);
    if (getToken()) {
      setContentLoading(true);
      Promise.all([fetchUserVideos(), fetchUserAlbums()])
        .finally(() => {
          setContentLoading(false);
          setContentFetched(true);
        });
    }
  }, [fetchUserVideos, fetchUserAlbums]);

  // Content tab handlers
  const toggleContentItemSelection = (itemId) => {
    setSelectedContentItems((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      }
      return [...prev, itemId];
    });
  };

  const selectAllContentItems = () => {
    const currentItems = contentTabType === 'videos' ? userVideos : userAlbums;
    if (selectedContentItems.length === currentItems.length) {
      setSelectedContentItems([]);
    } else {
      setSelectedContentItems(currentItems.map((item) => item.id));
    }
  };

  const handleEditContentItem = async (item) => {
    // Clear any previous bulk edit state
    setBulkEditItems([]);
    setExpandedItems({});

    // Show modal with loading state
    setShowEditModal(true);
    setEditLoading(true);

    // Check if this is an album (photos tab) or video
    const isAlbum = contentTabType === 'photos';

    try {
      if (isAlbum) {
        // Fetch album details
        const albumId = item.album_id || item.id;
        console.log('[GIFT_FLOW] Fetching album details for id:', albumId);
        const response = await apiAxios.post(`${baseUrl}/${endpoints.getMyAlbumDetail}`, {
          album_id: albumId,
          token: getToken(),
        });

        if (response?.data?.success && response?.data?.data) {
          const albumDetails = response.data.data;
          console.log('[GIFT_FLOW] Album details from API:', albumDetails);

          // Store the full album data for saving
          setEditingItem({
            ...item,
            ...albumDetails,
            album_id: albumId,
            isAlbum: true,
          });

          // Initialize form state with API response values
          setEditTitle(albumDetails.title || '');
          setEditDescription(albumDetails.description || '');
          setEditVisibility(albumDetails.visibility || '2');
        } else {
          // Fallback to item data if API fails
          console.log('[GIFT_FLOW] Album API failed, using item data');
          setEditingItem({ ...item, album_id: item.album_id || item.id, isAlbum: true });
          setEditTitle(item.title || '');
          setEditDescription(item.description || '');
          setEditVisibility(item.visibility || '2');
        }
      } else {
        // Fetch full video details from API (same as VideoEditModal)
        const videoId = item.video_id || item.content_id || item.id;
        console.log('[GIFT_FLOW] Fetching video details for id:', videoId);
        const response = await getVideosDetail({ id: videoId });

        if (response?.data?.success && response?.data?.data) {
          const videoDetails = response.data.data;
          console.log('[GIFT_FLOW] Video details from API:', videoDetails);

          // Store the full video data for saving
          setEditingItem({
            ...item,
            ...videoDetails,
            video_id: videoId,
            isAlbum: false,
          });

          // Initialize form state with API response values
          setEditTitle(videoDetails.title || '');
          setEditDescription(videoDetails.description || '');
          setEditVisibility(videoDetails.visibility || '2');

          // Set category
          if (videoDetails.category_id) {
            setEditCategory(videoDetails.category_id);
          } else {
            const allVideosCategory = userCategories.find((cat) => cat.title?.toLowerCase() === 'all videos');
            setEditCategory(allVideosCategory?.id || '');
          }
        } else {
          // Fallback to item data if API fails
          console.log('[GIFT_FLOW] API failed, using item data');
          setEditingItem({ ...item, isAlbum: false });
          setEditTitle(item.title || '');
          setEditDescription(item.description || '');
          setEditVisibility(item.visibility || '2');
          if (item.category_id) {
            setEditCategory(item.category_id);
          } else {
            const allVideosCategory = userCategories.find((cat) => cat.title?.toLowerCase() === 'all videos');
            setEditCategory(allVideosCategory?.id || '');
          }
        }
      }
    } catch (error) {
      console.error('[GIFT_FLOW] Error fetching details:', error);
      // Fallback to item data
      setEditingItem({ ...item, isAlbum });
      setEditTitle(item.title || '');
      setEditDescription(item.description || '');
      setEditVisibility(item.visibility || '2');
      if (!isAlbum && item.category_id) {
        setEditCategory(item.category_id);
      } else if (!isAlbum) {
        const allVideosCategory = userCategories.find((cat) => cat.title?.toLowerCase() === 'all videos');
        setEditCategory(allVideosCategory?.id || '');
      }
    } finally {
      setEditLoading(false);
    }
  };

  // Create new category
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;

    setCreatingCategory(true);
    try {
      const response = await apiAxios.post(`${baseUrl}/addEditCategory`, {
        token: getToken(),
        title: newCategoryName.trim(),
      });

      if (response?.data?.success) {
        // Refresh categories list
        await fetchUserCategories();
        // Find the newly created category and select it
        const newCatResponse = await apiAxios.post(`${baseUrl}/getMyCategory`);
        if (newCatResponse.data?.success && newCatResponse.data?.data) {
          const newCat = newCatResponse.data.data.find(
            (cat) => cat.title?.toLowerCase() === newCategoryName.trim().toLowerCase()
          );
          if (newCat) {
            setEditCategory(newCat.id);
          }
        }
        setNewCategoryName('');
        setShowCreateCategory(false);
        setSnackbar({
          isVisible: true,
          message: 'Category created successfully',
          type: 'success',
        });
      } else {
        setSnackbar({
          isVisible: true,
          message: response?.data?.message || 'Failed to create category',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('[GIFT_FLOW] Error creating category:', error);
      setSnackbar({
        isVisible: true,
        message: error?.response?.data?.message || 'Failed to create category',
        type: 'error',
      });
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleDeleteContentItems = () => {
    if (selectedContentItems.length === 0) return;
    setShowDeleteConfirm(true);
  };

  const confirmDeleteContentItems = async () => {
    setDeleteLoading(true);

    // Get full item objects from IDs (selectedContentItems contains IDs, not objects)
    const items = contentTabType === 'videos' ? userVideos : userAlbums;
    const selectedItems = selectedContentItems
      .map((id) => items.find((item) => item.id === id))
      .filter(Boolean);

    try {
      let response;

      if (contentTabType === 'photos') {
        // Albums/Photos use different endpoint: /deleteMyAlbum with album_id array
        const albumIds = selectedItems.map((item) => String(item.id));
        console.log('[GIFT_FLOW] Delete albums payload:', { album_id: albumIds });

        response = await apiAxios.post(`${baseUrl}/${endpoints.deleteMyAlbum}`, {
          album_id: albumIds,
          token: getToken(),
        });
      } else {
        // Videos use /deleteDraftContent with draft_id and content_id arrays
        const selectedDraftIds = selectedItems
          .filter((item) => item.draft_id)
          .map((item) => item.draft_id);
        const selectedContentIds = selectedItems
          .filter((item) => item.content_id && !item.draft_id)
          .map((item) => item.content_id);

        console.log('[GIFT_FLOW] Delete videos payload:', {
          draft_id: selectedDraftIds,
          content_id: selectedContentIds,
        });

        response = await apiAxios.post(`${baseUrl}/deleteDraftContent`, {
          token: getToken(),
          draft_id: selectedDraftIds,
          content_id: selectedContentIds,
        });
      }

      if (response?.data?.message === true || response?.data?.success) {
        // Success - clear selection and refresh content
        setSelectedContentItems([]);
        setShowDeleteConfirm(false);
        setSnackbar({
          isVisible: true,
          message: `Successfully deleted ${selectedContentItems.length} item${selectedContentItems.length > 1 ? 's' : ''}`,
          type: 'success',
        });
        // Refresh content lists
        fetchUserVideos();
        fetchUserAlbums();
      } else {
        // API returned an error message
        setSnackbar({
          isVisible: true,
          message: response?.data?.message || 'Failed to delete items',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('[GIFT_FLOW] Error deleting content:', error);
      setSnackbar({
        isVisible: true,
        message: error?.response?.data?.message || 'Failed to delete items',
        type: 'error',
      });
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  // Initialize bulk edit items when opening modal with multiple selections
  const initializeBulkEditItems = useCallback((itemIds) => {
    const items = contentTabType === 'videos' ? userVideos : userAlbums;
    const editItems = itemIds.map((itemId) => {
      const item = items.find((i) => i.id === itemId);
      if (!item) return null;
      // Find default category (All Videos) if no category set
      const allVideosCategory = userCategories.find((cat) => cat.title?.toLowerCase() === 'all videos');
      // Handle thumbnails array - use item thumbnails or create array from single thumbnail
      const thumbnailsArray = Array.isArray(item.thumbnails)
        ? item.thumbnails
        : (item.thumbnail ? [item.thumbnail] : [image04]);
      return {
        id: item.id,
        content_id: item.content_id || item.id,
        draft_id: item.draft_id || null,
        title: item.title || '',
        description: item.description || '',
        category_id: item.category_id || allVideosCategory?.id || '',
        subcategory_id: item.subcategory_id || '',
        visibility: item.visibility || '2',
        thumbnail: item.thumbnail || image04,
        thumbnails: thumbnailsArray,
        selectedThumbnail: 0,
        duration: item.duration || '',
        selectedPlaylists: item.playlists || [],
        selectedGroups: item.groups || [],
      };
    }).filter(Boolean);
    setBulkEditItems(editItems);
    // Reset expanded items when initializing
    setExpandedItems({});
  }, [contentTabType, userVideos, userAlbums, userCategories]);

  // Update a single item in bulk edit
  const updateBulkEditItem = useCallback((itemId, field, value) => {
    setBulkEditItems((prev) => prev.map((item) => {
      if (item.id === itemId) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  }, []);

  // Remove item from bulk edit
  const removeBulkEditItem = useCallback((itemId) => {
    setBulkEditItems((prev) => prev.filter((item) => item.id !== itemId));
    setSelectedContentItems((prev) => prev.filter((id) => id !== itemId));
  }, []);

  // Toggle expanded row in bulk edit
  const toggleExpandedItem = useCallback((itemId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  }, []);

  // Save single item edit
  const saveEditContentItem = async () => {
    if (!editingItem) return;

    setEditLoading(true);

    try {
      let response;

      // Check if editing an album or video
      if (editingItem.isAlbum || contentTabType === 'photos') {
        // Album edit - use addEditAlbum endpoint
        const albumId = editingItem.album_id || editingItem.id;

        console.log('[GIFT_FLOW] Album edit API call details:', {
          album_id: albumId,
          title: editTitle,
          description: editDescription,
          visibility: editVisibility,
        });

        response = await apiAxios.post(`${baseUrl}/${endpoints.addEditAlbum}`, {
          album_id: albumId,
          title: editTitle || '',
          description: editDescription || '',
          visibility: editVisibility || '2',
          group_id: editVisibility === '3' && editingItem.group_id ? editingItem.group_id : '',
          token: getToken(),
        });

        if (response?.data?.success || response?.data?.album_id) {
          setSnackbar({
            isVisible: true,
            message: 'Album updated successfully',
            type: 'success',
          });
          setShowEditModal(false);
          setEditingItem(null);
          setSelectedContentItems([]);
          // Refresh content list
          fetchUserAlbums();
        } else {
          setSnackbar({
            isVisible: true,
            message: response?.data?.message || 'Failed to update album',
            type: 'error',
          });
        }
      } else {
        // Video edit - use addVideoContent endpoint
        const formData = new FormData();
        const videoId = editingItem.video_id || editingItem.content_id || editingItem.id;

        // Required fields - same as VideoEditModal
        formData.append('video_id', videoId);
        formData.append('draft_id', editingItem.draft_id || '');
        formData.append('title', editTitle || '');
        formData.append('category_id', editCategory || '');

        // Handle description - use 'delete' if empty (same as VideoEditModal)
        if (editDescription && editDescription.trim()) {
          formData.append('description', editDescription);
        } else {
          formData.append('description', 'delete');
        }

        formData.append('visibility', editVisibility || '2');
        formData.append('status', 1);

        // Add subcategory if available
        if (editingItem.subcategory_id) {
          formData.append('subcategory_id', editingItem.subcategory_id);
        }

        // Add playlists if available (comma-separated)
        const playlists = editingItem.playlists || editingItem.playlist_id;
        if (playlists) {
          const playlistStr = Array.isArray(playlists) ? playlists.join(',') : playlists;
          if (playlistStr) {
            formData.append('playlist_id', playlistStr);
          }
        }

        // Add group_id if visibility is Group (comma-separated)
        if (editVisibility === '3' && editingItem.groups) {
          const groupStr = Array.isArray(editingItem.groups) ? editingItem.groups.join(',') : editingItem.groups;
          if (groupStr) {
            formData.append('group_id', groupStr);
          }
        }

        // Handle thumbnail_list - send comma-separated URLs
        const thumbnails = editingItem.thumbnails || editingItem.thumbnail;
        if (thumbnails) {
          const thumbnailList = Array.isArray(thumbnails) ? thumbnails.join(',') : thumbnails;
          if (thumbnailList) {
            formData.append('thumbnail_list', thumbnailList);
          }
        }

        // Handle selected thumbnail
        if (Array.isArray(editingItem.thumbnails) && editingItem.thumbnails.length > 0) {
          formData.append('thumbnails', editingItem.thumbnails[0]);
        } else if (editingItem.thumbnail) {
          formData.append('thumbnails', editingItem.thumbnail);
        }

        console.log('[GIFT_FLOW] Video edit API call details:', {
          video_id: videoId,
          draft_id: editingItem.draft_id,
          title: editTitle,
          category_id: editCategory,
          visibility: editVisibility,
        });

        response = await apiAxios.post(`${baseUrl}${endpoints.addVideoContent}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response?.data?.success) {
          setSnackbar({
            isVisible: true,
            message: 'Video updated successfully',
            type: 'success',
          });
          setShowEditModal(false);
          setEditingItem(null);
          setSelectedContentItems([]);
          // Refresh content list
          fetchUserVideos();
        } else {
          setSnackbar({
            isVisible: true,
            message: response?.data?.message || 'Failed to update video',
            type: 'error',
          });
        }
      }
    } catch (error) {
      console.error('[GIFT_FLOW] Error updating content:', error);
      setSnackbar({
        isVisible: true,
        message: error?.response?.data?.message || 'Failed to update content',
        type: 'error',
      });
    } finally {
      setEditLoading(false);
    }
  };

  // Save bulk edit items
  const saveBulkEditContentItems = async () => {
    if (bulkEditItems.length === 0) return;

    setEditLoading(true);

    // Check if editing albums or videos
    const isAlbumEdit = contentTabType === 'photos';

    try {
      if (isAlbumEdit) {
        // Albums - call addEditAlbum for each album sequentially
        console.log('[GIFT_FLOW] Bulk album edit - updating', bulkEditItems.length, 'albums');

        // Process albums sequentially using reduce
        const results = await bulkEditItems.reduce(async (accPromise, item) => {
          const acc = await accPromise;
          try {
            const albumId = item.album_id || item.id;
            const visibilityStr = Array.isArray(item.visibility)
              ? String(item.visibility[0] || '2')
              : String(item.visibility || '2');

            const response = await apiAxios.post(`${baseUrl}/${endpoints.addEditAlbum}`, {
              album_id: albumId,
              title: String(item.title || ''),
              description: String(item.description || ''),
              visibility: visibilityStr,
              group_id: visibilityStr === '3' && item.group_id ? item.group_id : '',
              token: getToken(),
            });

            if (response?.data?.success || response?.data?.album_id) {
              return { ...acc, successCount: acc.successCount + 1 };
            }
            return { ...acc, failCount: acc.failCount + 1 };
          } catch (err) {
            console.error('[GIFT_FLOW] Error updating album:', err);
            return { ...acc, failCount: acc.failCount + 1 };
          }
        }, Promise.resolve({ successCount: 0, failCount: 0 }));

        const { successCount, failCount } = results;

        if (successCount > 0) {
          setSnackbar({
            isVisible: true,
            message: failCount > 0
              ? `Updated ${successCount} albums, ${failCount} failed`
              : `Successfully updated ${successCount} albums`,
            type: failCount > 0 ? 'warning' : 'success',
          });
          setShowEditModal(false);
          setEditingItem(null);
          setSelectedContentItems([]);
          setBulkEditItems([]);
          setExpandedItems({});
          fetchUserAlbums();
        } else {
          setSnackbar({
            isVisible: true,
            message: 'Failed to update albums',
            type: 'error',
          });
        }
      } else {
        // Videos - use bulk edit endpoint
        // Build data array for bulk edit API - same structure as MultipleVideoEditModal
        // All values MUST be strings to avoid PHP "Array to string conversion" error
        const data = bulkEditItems.map((item) => {
          // Get thumbnail as string - handle array or string
          let thumbnailStr = '';
          if (Array.isArray(item.thumbnails) && item.thumbnails.length > 0) {
            const selected = item.thumbnails[item.selectedThumbnail || 0];
            thumbnailStr = Array.isArray(selected) ? selected[0] || '' : selected || '';
          } else if (item.thumbnail) {
            thumbnailStr = Array.isArray(item.thumbnail) ? item.thumbnail[0] || '' : item.thumbnail;
          }

          // Get category_id as string
          const categoryStr = Array.isArray(item.category_id)
            ? String(item.category_id[0] || '')
            : String(item.category_id || '');

          // Get visibility as string
          const visibilityStr = Array.isArray(item.visibility)
            ? String(item.visibility[0] || '2')
            : String(item.visibility || '2');

          const videoData = {
            draft_id: String(item.draft_id || ''),
            description: String(item.description || ''),
            thumbnail: String(thumbnailStr),
            playlist_ids: Array.isArray(item.selectedPlaylists) && item.selectedPlaylists.length > 0
              ? item.selectedPlaylists.join(',')
              : '',
            group_ids: Array.isArray(item.selectedGroups) && item.selectedGroups.length > 0
              ? item.selectedGroups.join(',')
              : '',
          };

          // Add title if present
          if (item.title) {
            videoData.title = String(item.title);
          }

          // Add category_id if present
          if (categoryStr) {
            videoData.category_id = categoryStr;
          }

          // Add visibility if present
          if (visibilityStr) {
            videoData.visibility = visibilityStr;
          }

          return videoData;
        });

        const payload = {
          token: getToken(),
          data,
        };

        console.log('[GIFT_FLOW] Bulk video edit payload:', payload);

        const response = await apiAxios.post(`${baseUrl}${endpoints.bulkEdit}`, payload);

        if (response?.data?.success) {
          setSnackbar({
            isVisible: true,
            message: `Successfully updated ${bulkEditItems.length} videos`,
            type: 'success',
          });
          setShowEditModal(false);
          setEditingItem(null);
          setSelectedContentItems([]);
          // Reset bulk edit state
          setBulkEditItems([]);
          setExpandedItems({});
          // Refresh content list
          fetchUserVideos();
        } else {
          setSnackbar({
            isVisible: true,
            message: response?.data?.message || 'Failed to update videos',
            type: 'error',
          });
        }
      }
    } catch (error) {
      console.error('[GIFT_FLOW] Error bulk updating content:', error);
      setSnackbar({
        isVisible: true,
        message: error?.response?.data?.message || 'Failed to update items',
        type: 'error',
      });
    } finally {
      setEditLoading(false);
    }
  };

  // Handle file selection for video/photos
  const handleFileSelect = useCallback((event, fileType) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const validExtensions = fileType === 'video' ? VIDEO_EXTENSIONS : IMAGE_EXTENSIONS;
    const validFiles = files.filter((file) => {
      const ext = `.${file.name.split('.').pop().toLowerCase()}`;
      return validExtensions.includes(ext);
    });

    if (validFiles.length === 0) {
      setError(`Please select valid ${fileType} files`);
      return;
    }

    // Calculate current totals
    const currentTotalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);

    // Calculate new file totals
    const newFilesSize = validFiles.reduce((sum, f) => sum + f.size, 0);
    const totalSizeAfterAdd = currentTotalSize + newFilesSize;

    // Validate total size limit
    if (totalSizeAfterAdd > MAX_TOTAL_SIZE_BYTES) {
      setSnackbar({
        isVisible: true,
        message: 'Upload limit exceeded! You can upload upto 10GB at a time.',
        type: 'error'
      });
      // Reset input
      if (event.target) {
        const inputElement = event.target;
        setTimeout(() => { inputElement.value = ''; }, 0);
      }
      return;
    }

    const newFiles = validFiles.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      name: file.name,
      size: file.size,
      type: fileType,
      source: 'local',
      previewUrl: URL.createObjectURL(file),
    }));

    // Add to state IMMEDIATELY (synchronous — critical for mobile browser compatibility)
    setSelectedFiles((prev) => [...prev, ...newFiles]);
    setShowAddMore(false);
    setError('');

    // Generate video poster thumbnails in background (iOS Safari won't auto-render <video> frames)
    const videoEntries = newFiles.filter((f) => f.type === 'video');
    if (videoEntries.length > 0) {
      (async () => {
        const posterMap = {};
        await Promise.all(videoEntries.map(async (f) => {
          const poster = await captureVideoFrame(f.file);
          if (poster) {
            posterMap[f.id] = poster;
          } else {
            console.warn('[THUMB_CAPTURE] No poster generated for:', f.name, '- step 3 will show placeholder');
          }
        }));
        console.log('[THUMB_CAPTURE] Poster results:', Object.keys(posterMap).length, '/', videoEntries.length, 'succeeded');
        if (Object.keys(posterMap).length > 0) {
          setSelectedFiles((prev) => prev.map((f) => (posterMap[f.id] ? { ...f, posterUrl: posterMap[f.id] } : f)));
        }
      })();
    }

    // Reset input
    if (event.target) {
      const inputElement = event.target;
      setTimeout(() => { inputElement.value = ''; }, 0);
    }

    // Send push to desktop in background (fire-and-forget with micro thumbnails)
    // If desktop misses this (fileComplete overwrites it), the fileComplete handler
    // will create entries as a fallback.
    if (isMobileUploadMode && giftUserUid) {
      fileUploadBatchCounter.current += 1;
      const batchId = fileUploadBatchCounter.current;
      const fileNames = newFiles.map((f) => `${f.name}|${f.id}`);

      // Generate micro thumbnails in background, then send push
      (async () => {
        try {
          const thumbMap = {};
          await Promise.all(
            newFiles.map(async (f) => {
              const ext = `.${f.name.split('.').pop().toLowerCase()}`;
              if (IMAGE_EXTENSIONS.includes(ext) && f.file) {
                const thumb = await generateMicroThumbnail(f.file);
                if (thumb) thumbMap[f.name] = thumb;
              }
            })
          );
          const metadata = Object.keys(thumbMap).length > 0 ? { thumbs: thumbMap } : null;
          console.log('[FILE_SELECT] Sending push:', fileNames, 'Batch:', batchId, 'thumbs:', Object.keys(thumbMap).length);
          sendMobilePushMessageRef.current(`fileUpload${batchId}`, fileNames, null, null, metadata);
        } catch (e) {
          console.error('[FILE_SELECT] Push error:', e);
          sendMobilePushMessageRef.current(`fileUpload${batchId}`, fileNames);
        }
      })();
    }
  }, [selectedFiles, currentStep, isMobileUploadMode, giftUserUid]);

  // Handle combined file selection (videos + photos together)
  const handleCombinedFileSelect = useCallback((event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Separate videos and images
    const videoFiles = [];
    const imageFiles = [];

    files.forEach((file) => {
      const ext = `.${file.name.split('.').pop().toLowerCase()}`;
      if (VIDEO_EXTENSIONS.includes(ext)) {
        videoFiles.push(file);
      } else if (IMAGE_EXTENSIONS.includes(ext)) {
        imageFiles.push(file);
      }
    });

    if (videoFiles.length === 0 && imageFiles.length === 0) {
      setError('Please select valid video or image files');
      return;
    }

    // Calculate current totals
    const currentTotalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);

    // Calculate new file totals
    const allValidFiles = [...videoFiles, ...imageFiles];
    const newFilesSize = allValidFiles.reduce((sum, f) => sum + f.size, 0);
    const totalSizeAfterAdd = currentTotalSize + newFilesSize;

    // Validate total size limit
    if (totalSizeAfterAdd > MAX_TOTAL_SIZE_BYTES) {
      setSnackbar({
        isVisible: true,
        message: 'Upload limit exceeded! You can upload upto 10GB at a time.',
        type: 'error'
      });
      if (event.target) {
        const inputElement = event.target;
        setTimeout(() => { inputElement.value = ''; }, 0);
      }
      return;
    }

    // Create video entries with IDs (hoisted so push can reference them)
    const newVideoFiles = videoFiles.length > 0 ? videoFiles.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      name: file.name,
      size: file.size,
      type: 'video',
      source: 'local',
      previewUrl: URL.createObjectURL(file),
    })) : [];

    // Create album entry with IDs (hoisted so push can reference them)
    let albumItem = null;
    if (imageFiles.length > 0) {
      const totalSize = imageFiles.reduce((sum, file) => sum + file.size, 0);
      const albumId = `album-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      albumItem = {
        id: albumId,
        name: ALL_PHOTOS_ALBUM_NAME, // Same name - batches merge in backend
        type: 'album',
        source: 'local',
        size: totalSize,
        photoCount: imageFiles.length,
        photos: imageFiles.map((file) => ({
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          file,
          name: file.name,
          size: file.size,
          previewUrl: URL.createObjectURL(file),
        })),
      };
    }

    // Clear re-uploaded filenames from deletedFileNamesRef so registration doesn't skip them
    newVideoFiles.forEach((f) => deletedFileNamesRef.current.delete(f.name));
    if (albumItem) {
      albumItem.photos.forEach((p) => deletedFileNamesRef.current.delete(p.name));
      deletedFileNamesRef.current.delete(albumItem.name);
    }

    // Add to state IMMEDIATELY (synchronous — critical for mobile browser compatibility)
    if (newVideoFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...newVideoFiles]);
    }
    if (albumItem) {
      setSelectedFiles((prev) => [...prev, albumItem]);
      if (!allPhotosAlbumId) {
        setAllPhotosAlbumId(albumItem.id);
      }
    }
    setShowAddMore(false);
    setError('');

    // Generate video poster thumbnails in background (iOS Safari won't auto-render <video> frames)
    if (newVideoFiles.length > 0) {
      (async () => {
        const posterMap = {};
        await Promise.all(newVideoFiles.map(async (f) => {
          const poster = await captureVideoFrame(f.file);
          if (poster) {
            posterMap[f.id] = poster;
          } else {
            console.warn('[THUMB_CAPTURE] No poster generated for:', f.name, '- step 3 will show placeholder');
          }
        }));
        console.log('[THUMB_CAPTURE] Poster results:', Object.keys(posterMap).length, '/', newVideoFiles.length, 'succeeded');
        if (Object.keys(posterMap).length > 0) {
          setSelectedFiles((prev) => prev.map((f) => (posterMap[f.id] ? { ...f, posterUrl: posterMap[f.id] } : f)));
        }
      })();
    }

    // Reset input
    if (event.target) {
      const inputElement = event.target;
      setTimeout(() => { inputElement.value = ''; }, 0);
    }

    // Send push to desktop in background (fire-and-forget with micro thumbnails)
    // If desktop misses this (fileComplete overwrites it), the fileComplete handler
    // will create entries as a fallback.
    if (isMobileUploadMode && giftUserUid) {
      fileUploadBatchCounter.current += 1;
      const batchId = fileUploadBatchCounter.current;

      const fileNames = [
        ...newVideoFiles.map((f) => `${f.name}|${f.id}`),
        ...(albumItem ? albumItem.photos.map((p) => `${p.name}|${p.id}`) : []),
      ];

      // Generate micro thumbnails in background, then send push
      (async () => {
        try {
          const thumbMap = {};
          if (albumItem) {
            await Promise.all(
              albumItem.photos.map(async (photo) => {
                if (photo.file) {
                  const thumb = await generateMicroThumbnail(photo.file);
                  if (thumb) thumbMap[photo.name] = thumb;
                }
              })
            );
          }
          const metadata = Object.keys(thumbMap).length > 0 ? { thumbs: thumbMap } : null;
          console.log('[COMBINED_FILE_SELECT] Sending push:', fileNames, 'Batch:', batchId, 'thumbs:', Object.keys(thumbMap).length);
          sendMobilePushMessageRef.current(`fileUpload${batchId}`, fileNames, null, null, metadata);
        } catch (e) {
          console.error('[COMBINED_FILE_SELECT] Push error:', e);
          sendMobilePushMessageRef.current(`fileUpload${batchId}`, fileNames);
        }
      })();
    }
  }, [selectedFiles, isMobileUploadMode, giftUserUid]);

  // Handle album assignment confirmation
  const handleAlbumAssign = () => {
    if (!albumName.trim()) {
      setSnackbar({
        isVisible: true,
        message: 'Please enter an album name',
        type: 'error'
      });
      return;
    }

    // Create ONE album item containing all photos
    const totalSize = pendingImages.reduce((sum, file) => sum + file.size, 0);
    const albumItem = {
      id: `album-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: albumName.trim(),
      type: 'album',
      source: 'local',
      size: totalSize,
      photoCount: pendingImages.length,
      photos: pendingImages.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        name: file.name,
        size: file.size,
      })),
    };

    setSelectedFiles((prev) => [...prev, albumItem]);
    setShowAlbumPopup(false);
    setPendingImages([]);
    setAlbumName('');
  };

  // Handle skip album assignment - add photos individually
  const handleSkipAlbum = () => {
    const newImageFiles = pendingImages.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      name: file.name,
      size: file.size,
      type: 'photo',
      source: 'local',
    }));

    setSelectedFiles((prev) => [...prev, ...newImageFiles]);
    setShowAlbumPopup(false);
    setPendingImages([]);
    setAlbumName('');
  };

  // Handle cloud service connection (like main app UploadDropdown)
  const handleCloudConnect = (service) => {
    // Save current state before OAuth redirect (state will be lost on redirect)
    sessionStorage.setItem('giftFlowState', JSON.stringify({
      currentStep,
      selectedChoice,
      formData,
      giftUserUid,
      selectedFiles: selectedFiles.map((f) => ({
        id: f.id,
        name: f.name,
        size: f.size,
        type: f.type,
        source: f.source,
        // Preserve cloud metadata so cloud uploads can be retried after OAuth redirect
        ...(f.cloudFile ? { cloudFile: f.cloudFile } : {}),
        ...(f.photos ? {
          photoCount: f.photoCount,
          photos: f.photos.map((p) => ({
            id: p.id,
            name: p.name,
            size: p.size,
            cloudFile: p.cloudFile,
            cloudSource: p.cloudSource,
          })),
        } : {}),
      })),
      // Persist upload statuses so completed uploads aren't re-triggered after OAuth redirect
      // Use `uploads` state (from closure) instead of uploadsRef.current to avoid stale ref
      // (uploadsRef is synced via useEffect which runs after render — can lag behind)
      uploads: uploads.map((u) => ({
        id: u.id,
        fileName: u.fileName,
        fileSize: u.fileSize,
        status: u.status,
        progress: u.progress,
      })),
      queuedFileIds: Array.from(queuedFileIdsRef.current),
    }));

    // Set gift flow marker so CloudUpload page knows to redirect back to /gift
    sessionStorage.setItem('giftFlowCloudConnect', service);
    sessionStorage.setItem('oauthReturnUrl', '/gift');

    setSelectedCloudService(service);
    setCloudModalOpen(true);
  };

  // Handle closing cloud modal
  const handleCloudModalClose = () => {
    setCloudModalOpen(false);
    setSelectedCloudService(null);
  };

  // Check if returning from OAuth and restore state
  useEffect(() => {
    const autoOpenService = sessionStorage.getItem('autoOpenCloudModal');
    const savedState = sessionStorage.getItem('giftFlowState');

    if (autoOpenService && savedState) {
      console.log('🔄 Restoring gift flow state after OAuth');

      try {
        const state = JSON.parse(savedState);

        // Restore all state
        setCurrentStep(state.currentStep);
        setSelectedChoice(state.selectedChoice);
        setFormData(state.formData);
        setGiftUserUid(state.giftUserUid);
        if (state.selectedFiles && state.selectedFiles.length > 0) {
          setSelectedFiles(state.selectedFiles);
        }

        // Restore upload statuses so completed uploads aren't re-triggered.
        // For non-terminal uploads (UPLOADING/PENDING/PAUSED):
        //   - Cloud uploads (have cloudFile data in selectedFiles): drop from uploads & queuedFileIds
        //     so the auto-upload effect will re-trigger them automatically
        //   - Local uploads: mark as FAILED (File objects can't survive page reload)
        const cloudFileIds = new Set(
          (state.selectedFiles || [])
            .filter((f) => f.cloudFile || (f.photos && f.photos.some((p) => p.cloudFile)))
            .map((f) => f.id)
        );

        if (state.uploads && state.uploads.length > 0) {
          const restoredUploads = [];
          const retryIds = new Set(); // IDs to remove from queuedFileIds so auto-upload retries them
          state.uploads.forEach((u) => {
            const isTerminal = u.status === UPLOAD_STATUS.COMPLETED
              || u.status === UPLOAD_STATUS.S3_COMPLETE
              || u.status === UPLOAD_STATUS.FAILED
              || u.status === UPLOAD_STATUS.CANCELLED;
            if (isTerminal) {
              // Completed/failed uploads: restore as-is
              restoredUploads.push({
                id: u.id,
                fileName: u.fileName,
                fileSize: u.fileSize,
                status: u.status,
                progress: u.progress,
                bytesUploaded: 0,
                speed: 0,
                timeRemaining: 0,
                startTime: null,
                lastUpdateTime: null,
                lastBytesUploaded: 0,
                isPaused: false,
                secureUploadId: null,
              });
            } else if (cloudFileIds.has(u.id)) {
              // Non-terminal cloud upload: DON'T add to uploads, remove from queuedFileIds
              // so auto-upload will create a fresh upload entry and retry from scratch
              retryIds.add(u.id);
            } else {
              // Non-terminal local upload: can't retry (File objects lost) — mark FAILED
              restoredUploads.push({
                id: u.id,
                fileName: u.fileName,
                fileSize: u.fileSize,
                status: UPLOAD_STATUS.FAILED,
                progress: u.progress,
                bytesUploaded: 0,
                speed: 0,
                timeRemaining: 0,
                startTime: null,
                lastUpdateTime: null,
                lastBytesUploaded: 0,
                isPaused: false,
                secureUploadId: null,
              });
            }
          });
          if (restoredUploads.length > 0) {
            setUploads(restoredUploads);
          }

          // Add only non-retried IDs to queuedFileIds
          if (state.queuedFileIds && state.queuedFileIds.length > 0) {
            state.queuedFileIds.forEach((id) => {
              if (!retryIds.has(id)) {
                queuedFileIdsRef.current.add(id);
              }
            });
          }
        } else if (state.queuedFileIds && state.queuedFileIds.length > 0) {
          // No uploads to restore — still restore queuedFileIds for safety
          state.queuedFileIds.forEach((id) => queuedFileIdsRef.current.add(id));
        }

        // Small delay to ensure state is set, then open modal
        setTimeout(() => {
          console.log('Opening cloud modal for:', autoOpenService);
          setSelectedCloudService(autoOpenService);
          setCloudModalOpen(true);
        }, 100);

        // Clean up
        sessionStorage.removeItem('autoOpenCloudModal');
        sessionStorage.removeItem('giftFlowState');
      } catch (error) {
        console.error('Error restoring gift flow state:', error);
        sessionStorage.removeItem('autoOpenCloudModal');
        sessionStorage.removeItem('giftFlowState');
      }
    }
  }, []);

  // Extract and set tokens from URL params if present
  // IMPORTANT: Store access (uploader token) BEFORE token, so getUploaderToken() finds it cached
  useEffect(() => {
    if (urlParams.access) {
      localStorage.setItem('uploader_token', urlParams.access);
      // Parse and store expiry from JWT so isTokenExpired() returns false
      try {
        const payload = JSON.parse(atob(urlParams.access.split('.')[1]));
        if (payload.exp) {
          localStorage.setItem('uploader_token_expiry', (payload.exp * 1000).toString());
        }
      } catch (e) { /* ignore parse errors */ }
      console.log('[GIFT_FLOW] Uploader token extracted from URL and stored in localStorage');
    }
    if (urlParams.token) {
      localStorage.setItem('token', urlParams.token);
      console.log('[GIFT_FLOW] Token extracted from URL and stored in localStorage');
      // Only pre-fetch uploader token if not already provided via access param
      if (!urlParams.access) {
        getUploaderToken().catch((err) => console.warn('[GIFT_FLOW] Failed to pre-fetch uploader token:', err));
      }
    }
    // If both token and access are present, payment is already done (web user paid)
    if (urlParams.token && urlParams.access) {
      paymentDoneRef.current = true;
      console.log('[GIFT_FLOW] Both token and access present — payment already done, skipping payment');
    }
  }, [urlParams.token, urlParams.access]);

  // Check gift status on load - skip payment if already completed
  useEffect(() => {
    if (!giftUserUid) return;
    const checkGiftPaymentStatus = async () => {
      try {
        const response = await getGiftStatus(giftUserUid);
        if (response?.data?.data?.payment_complete === '1') {
          console.log('[GIFT_FLOW] Payment already completed, skipping to upload step');
          setCurrentStep(3);
        }
      } catch (err) {
        console.error('[GIFT_FLOW] Error checking gift status:', err);
      }
    };
    checkGiftPaymentStatus();
  }, [giftUserUid]);

  // Fetch gift status data when success section is shown — populates entire success page
  useEffect(() => {
    if (!showSuccessSection || !giftUserUid) return;
    const fetchGiftStatusData = async () => {
      try {
        const response = await getGiftStatus(giftUserUid);
        const data = response?.data?.data;
        if (data) {
          // Store full API response for rendering
          setGiftStatusData(data);

          // Update delivery date from API if not already set locally
          if (data.delivery_date && !deliveryDate) {
            setDeliveryDate(data.delivery_date);
          }

          // Determine gift received status from `status` field
          // scheduled/delivered = sent (pending), opened = opened
          const statusVal = (data.status || '').toLowerCase();
          if (statusVal === 'opened') {
            setGiftDeliveryStatus('opened');
          } else {
            // 'scheduled', 'delivered', or any other value = sent/pending
            setGiftDeliveryStatus('pending');
          }
        }
      } catch (err) {
        console.error('[GIFT_FLOW] Error fetching gift status data:', err);
      }
    };
    fetchGiftStatusData();

    // Fetch cloud/uploader access token using the user token
    const fetchCloudToken = async () => {
      const userToken = localStorage.getItem('token') || urlParams.token;
      if (!userToken) return;
      try {
        const response = await axios.post(
          `${cloudBaseUrl}/auth/get-token/`,
          { token: userToken },
          { headers: { 'Content-Type': 'application/json' } },
        );
        if (response?.data?.access) {
          localStorage.setItem('uploader_token', response.data.access);
          console.log('[GIFT_FLOW] Cloud access token saved');
        }
      } catch (err) {
        console.error('[GIFT_FLOW] Error fetching cloud token:', err);
      }
    };
    fetchCloudToken();

    // Pre-fetch email HTML for copy buttons (needed synchronously on iOS Safari)
    const fetchEmailHtml = async () => {
      const userToken = localStorage.getItem('token') || urlParams.token;
      if (!userToken) return;
      try {
        const response = await previewGiftEmail({ uid: giftUserUid, token: userToken });
        const html = typeof response?.data === 'string' ? response.data : (response?.data?.data || response?.data?.html || '');
        if (html) setCachedEmailHtml(html);
      } catch (err) {
        console.warn('[GIFT_FLOW] Failed to pre-fetch email HTML:', err);
      }
    };
    fetchEmailHtml();

    // Update URL to include status, giftUid, and token so page works on refresh
    const token = localStorage.getItem('token') || urlParams.token || '';
    const newUrl = `${window.location.pathname}?status=true&giftUid=${giftUserUid}${token ? `&token=${token}` : ''}`;
    window.history.replaceState(null, '', newUrl);
  }, [showSuccessSection, giftUserUid]);

  // Auto-resize payment note textarea - DISABLED: Using fixed height with scroll instead
  // useEffect(() => {
  //   if (paymentNoteRef.current && formData.note) {
  //     const textarea = paymentNoteRef.current;
  //     textarea.style.height = 'auto';
  //     textarea.style.height = `${textarea.scrollHeight}px`;
  //   }
  // }, [formData.note, currentStep, selectedPlan]);

  // Helper function to send push messages via SSE
  // mobileUpload: true = message FROM mobile (affects desktop only)
  // mobileUpload: false = message FROM desktop (affects mobile only)
  const sendMobilePushMessage = useCallback(async (message, files = [], token = null, uploaderToken = null, metadata = null) => {
    if (!giftUserUid) {
      console.warn('[MOBILE_FLOW] Cannot send push message: giftUserUid not available');
      return;
    }

    // Share/contributor flow: skip all push messages during upload
    // (shareComplete is sent separately via updateGiftStatus after registration)
    if (urlParams.share) {
      console.log('[SHARE_FLOW] Skipping push message in contributor flow:', message);
      return;
    }

    try {
      // Set mobileUpload based on origin:
      // - true if from mobile device (message should affect desktop)
      // - false if from desktop (message should affect mobile)
      const isFromMobile = isMobileUploadMode;

      // Format push data to match the expected structure
      // If "Pay from Web" was clicked, include payweb:true so it survives being overwritten by fileComplete etc.
      const paywebFlag = paywebRequestedRef.current && isFromMobile ? ',payweb:true' : '';
      // Encode metadata as base64 JSON — base64 chars (A-Za-z0-9+/=) are safe for the pseudo-JSON parser
      const metadataField = metadata ? `,meta:'${btoa(JSON.stringify(metadata))}'` : '';
      const pushData = `{mobileUpload:${isFromMobile},push:'${message}',files:${JSON.stringify(files)}${token ? `,token:'${token}'` : ''}${uploaderToken ? `,access:'${uploaderToken}'` : ''}${paywebFlag}${metadataField}}`;

      syncLog(isFromMobile ? 'MOBILE' : 'DESKTOP', `SENDING: ${message}`, { files: files?.length || 0 });

      const apiResponse = await updateGiftStatus({
        uuid: giftUserUid,
        push: pushData,
        token: token || undefined,
        uploaderToken: uploaderToken || undefined,
        mobileUpload: isFromMobile
      });

      syncLog(isFromMobile ? 'MOBILE' : 'DESKTOP', `SENT OK: ${message}`, { status: apiResponse?.status });
      return true;
    } catch (error) {
      console.error('[DEBUG_PAYWEB] updateGiftStatus API FAILED:', error?.response?.status, error?.response?.data, error?.message);
      console.error('[MOBILE_FLOW] Error sending push message:', error);
      return false;
    }
  }, [giftUserUid, isMobileUploadMode]);

  // Keep ref in sync so callbacks defined before sendMobilePushMessage can use it
  sendMobilePushMessageRef.current = sendMobilePushMessage;

  // Build metadata payload for desktop to register mobile-uploaded files.
  // Called when mobile sends allUploadsComplete so desktop has everything
  // it needs to register files even if mobile tab is closed.
  const buildMobileUploadMetadata = useCallback(async () => {
    const videos = [];
    const albums = [];

    // Collect video metadata from uploadManagerSecure + videoMetadataRef
    const s3CompleteUploads = uploadManagerSecure.getS3CompleteUploads();
    const videoPromises = s3CompleteUploads.map(async (upload) => {
      const metadata = videoMetadataRef.current.get(upload.draftId);
      if (metadata) {
        // Compress main thumbnail to tiny size for push (~3-8KB instead of ~50-100KB)
        const tinyThumb = metadata.thumbnailBase64
          ? await compressThumbnailForPush(metadata.thumbnailBase64)
          : null;

        videos.push({
          draftId: upload.draftId,
          fileName: upload.fileName,
          s3Key: upload.s3Key,
          videoTitle: metadata.videoTitle,
          videoHeight: metadata.thumbnailData?.videoHeight || 0,
          videoWidth: metadata.thumbnailData?.videoWidth || 0,
          fileSize: metadata.file?.size || 0,
          thumbnail: tinyThumb,
        });
      }
    });
    await Promise.all(videoPromises);

    // Also include completed uploads whose secure manager status moved past s3_complete
    const completedPromises = [];
    uploadsRef.current.forEach((u) => {
      if (u.status === UPLOAD_STATUS.COMPLETED && u.secureUploadId && !videos.find((v) => v.draftId === u.secureUploadId)) {
        const metadata = videoMetadataRef.current.get(u.secureUploadId);
        const managerUpload = uploadManagerSecure.getUpload(u.secureUploadId);
        if (metadata && managerUpload) {
          completedPromises.push((async () => {
            const tinyThumb = metadata.thumbnailBase64
              ? await compressThumbnailForPush(metadata.thumbnailBase64)
              : null;

            videos.push({
              draftId: u.secureUploadId,
              fileName: u.fileName,
              s3Key: managerUpload.s3Key,
              videoTitle: metadata.videoTitle,
              videoHeight: metadata.thumbnailData?.videoHeight || 0,
              videoWidth: metadata.thumbnailData?.videoWidth || 0,
              fileSize: metadata.file?.size || 0,
              thumbnail: tinyThumb,
            });
          })());
        }
      }
    });
    await Promise.all(completedPromises);

    // Collect photo album metadata from photoMetadataRef
    photoMetadataRef.current.forEach((albumData, albumUploadId) => {
      if (albumData.photos && albumData.photos.length > 0) {
        albums.push({
          albumUploadId,
          albumName: albumData.albumName,
          photos: albumData.photos,
        });
      }
    });

    return { videos, albums };
  }, [compressThumbnailForPush]);

  // Extract mobile upload metadata from push data and store in mobileUploadMetadataRef
  const extractAndStoreMobileMetadata = useCallback((parsedPush, rawPushStr) => {
    let metaBase64 = null;

    // Try parsed push first (SSE path)
    if (parsedPush?.meta) {
      metaBase64 = parsedPush.meta;
    }

    // Fallback: extract from raw push string (polling path)
    if (!metaBase64 && rawPushStr) {
      const metaMatch = rawPushStr.match(/meta:'([A-Za-z0-9+/=]+)'/);
      if (metaMatch) {
        [, metaBase64] = metaMatch;
      }
    }

    if (!metaBase64) return;

    try {
      const metadata = JSON.parse(atob(metaBase64));
      console.log('[MOBILE_FLOW] Extracted mobile upload metadata:', {
        videoCount: metadata.videos?.length || 0,
        albumCount: metadata.albums?.length || 0,
      });
      mobileUploadMetadataRef.current = metadata;
    } catch (err) {
      console.error('[MOBILE_FLOW] Failed to parse mobile upload metadata:', err);
    }
  }, []);

  // Recovery: create mobile preview entries from metadata when fileUpload/fileComplete
  // pushes were overwritten before SSE could deliver them (common for instant uploads).
  const createMissingMobileEntries = useCallback(() => {
    const meta = mobileUploadMetadataRef.current;
    if (!meta) return;

    setSelectedFiles((prev) => {
      const hasMobile = prev.some((f) => f.source === 'mobile-preview');
      if (hasMobile) return prev; // entries already exist

      const newEntries = [];

      // Create video entries
      if (meta.videos?.length > 0) {
        const s3BaseUrl = generateS3Url();
        meta.videos.forEach((v) => {
          newEntries.push({
            id: `preview-recovery-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
            file: null,
            name: v.fileName,
            mobileUid: v.draftId,
            size: v.fileSize || 0,
            type: 'video',
            source: 'mobile-preview',
            isPreview: true,
            previewUrl: v.s3Key ? `${s3BaseUrl}${v.s3Key}` : null,
            posterUrl: v.thumbnail || null,
          });
        });
      }

      // Create album entries
      if (meta.albums?.length > 0) {
        meta.albums.forEach((a) => {
          const albumId = `preview-album-recovery-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
          newEntries.push({
            id: albumId,
            file: null,
            name: a.albumName || 'All photos',
            size: 0,
            type: 'album',
            source: 'mobile-preview',
            isPreview: true,
            photoCount: a.photos?.length || 0,
            photos: (a.photos || []).map((p, i) => ({
              id: `recovery-photo-${Date.now()}-${i}-${Math.random().toString(36).substring(2, 11)}`,
              file: null,
              name: p.name || `photo-${i + 1}`,
              size: p.size || 0,
              previewUrl: null,
            })),
          });
        });
      }

      if (newEntries.length > 0) {
        console.log('[MOBILE_FLOW] allUploadsComplete recovery: creating', newEntries.length, 'missing entries from metadata');
        return [...prev, ...newEntries];
      }
      return prev;
    });

    // Also create upload entries so step 3 shows them
    setUploads((prev) => {
      const hasMobileUploads = prev.some((u) => u.isMobileUpload);
      if (hasMobileUploads) return prev;

      const newUploads = [];

      if (meta.videos?.length > 0) {
        meta.videos.forEach((v) => {
          newUploads.push({
            id: `mobile-recovery-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
            fileName: v.fileName,
            mobileUid: v.draftId,
            fileSize: v.fileSize || 0,
            status: UPLOAD_STATUS.COMPLETED,
            progress: 100,
            bytesUploaded: 0,
            speed: 0,
            timeRemaining: 0,
            startTime: Date.now(),
            lastUpdateTime: null,
            lastBytesUploaded: 0,
            isPaused: false,
            secureUploadId: null,
            isMobileUpload: true,
          });
        });
      }

      if (meta.albums?.length > 0) {
        meta.albums.forEach((a) => {
          const albumUploadName = `All photos (${a.photos?.length || 0})`;
          newUploads.push({
            id: `mobile-album-recovery-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
            fileName: albumUploadName,
            mobileUid: `album-recovery-${Date.now()}`,
            fileSize: 0,
            status: UPLOAD_STATUS.COMPLETED,
            progress: 100,
            bytesUploaded: 0,
            speed: 0,
            timeRemaining: 0,
            startTime: Date.now(),
            lastUpdateTime: null,
            lastBytesUploaded: 0,
            isPaused: false,
            secureUploadId: null,
            isMobileUpload: true,
          });
        });
      }

      if (newUploads.length > 0) {
        console.log('[MOBILE_FLOW] allUploadsComplete recovery: creating', newUploads.length, 'missing upload entries');
        setIsUploading(true);
        setShowQrCode(false);
        setShowFilesQrCode(false);
        setShowShareModal(false);
        return [...newUploads, ...prev];
      }
      return prev;
    });
  }, []);

  // Pre-fetch short link when success section or share modal opens (so QR codes and inputs show it)
  useEffect(() => {
    if (showSuccessSection || showShareModal) {
      getShortenedShareUrl();
    }
  }, [showSuccessSection, showShareModal, getShortenedShareUrl]);

  // SSE: Listen to real-time gift upload status updates when uid is available
  // Share/contributor flow does not need push — skip SSE entirely
  useEffect(() => {
    if (urlParams.share) {
      console.log('[SSE] Share/contributor flow: skipping SSE connection (no push needed)');
      return;
    }

    if (!giftUserUid) {
      console.log('[SSE] giftUserUid not available yet, skipping SSE connection');
      return;
    }

    console.log('[SSE] Connecting with giftUserUid:', giftUserUid);

    // Fetch initial gift status
    const fetchInitialStatus = async () => {
      try {
        const response = await getGiftStatus(giftUserUid);
        console.log('Gift status (initial):', response.data);
      } catch (err) {
        console.error('Error fetching gift status:', err);
      }
    };
    fetchInitialStatus();

    // Track whether the first SSE message is the initial replay of stored state.
    // When SSE connects, the server sends the current gift status (including old push data
    // from previous upload sessions). Without this flag, stale file lists from past uploads
    // would appear as if they were just uploaded by a new visitor.
    // On SSE reconnection (within the same subscription), the flag is already false,
    // so reconnect replays go through the normal dedup path (processedPushMessages).
    let isInitialSSEMessage = true;

    // Subscribe to real-time updates via SSE
    const unsubscribe = subscribeToGiftUploadStatus(giftUserUid, {
      onOpen: () => {
        syncLog('DESKTOP', 'SSE CONNECTED');
      },
      onMessage: (data) => {
        // Handle parsed push data from mobile flow
        if (data.parsedPush) {
          // Use root-level mobileUpload if available, otherwise fall back to parsedPush
          const mobileUpload = typeof data.mobileUpload === 'boolean' ? data.mobileUpload : data.parsedPush.mobileUpload;
          const { push, files } = data.parsedPush;

          // Calculate delay between server timestamp and now
          const serverTime = data.timestamp ? new Date(`${data.timestamp.replace(' ', 'T')}Z`).getTime() : null;
          const localTime = Date.now();
          const delayMs = serverTime ? localTime - serverTime : null;
          syncLog('DESKTOP', `SSE RECEIVED: ${push}`, { from: mobileUpload ? 'mobile' : 'desktop', files: files?.length || 0, serverTs: data.timestamp, delayMs });

          // Create unique message ID to detect duplicates
          const filesKey = files && Array.isArray(files) ? files.sort().join(',') : '';
          const messageId = `${push}_${mobileUpload}_${filesKey}_${data.timestamp || ''}`;

          // Check if we've already processed this message recently
          if (processedPushMessages.current.has(messageId)) {
            syncLog('DESKTOP', `SKIPPED (dedup): ${push}`);
            return;
          }

          // Mark message as processed
          processedPushMessages.current.add(messageId);

          // Clean up old message IDs (keep only last 50)
          if (processedPushMessages.current.size > 50) {
            const items = Array.from(processedPushMessages.current);
            processedPushMessages.current = new Set(items.slice(-50));
          }

          // Handle the initial SSE message — it may be a replay of stored state OR a fresh message.
          // When SSE connects, the server sends the current gift status (including old push data
          // from previous upload sessions).
          if (isInitialSSEMessage) {
            isInitialSSEMessage = false;

            // In share mode, a new contributor is visiting — always skip the initial
            // SSE replay as it contains data from previous contributors' sessions
            if (urlParams.share) {
              console.log('[SSE] Share mode: skipping initial push replay from previous session:', push, files);
              return;
            }

            // Check if the message is recent (within 30 seconds) by examining timestamp
            const messageTimestamp = data.timestamp ? new Date(data.timestamp).getTime() : 0;
            const now = Date.now();
            const MESSAGE_FRESHNESS_THRESHOLD = 30000; // 30 seconds
            const isStaleMessage = messageTimestamp > 0 && (now - messageTimestamp) > MESSAGE_FRESHNESS_THRESHOLD;

            // Also check if this is a fileUpload or fileComplete message from mobile - these should never be skipped
            const isMobileUploadMessage = mobileUpload === true && (
              push?.startsWith('fileUpload')
              || push === 'fileComplete'
              || push === 'allUploadsComplete'
              || push === 'fileProgress'
            );

            if (isStaleMessage && !isMobileUploadMessage) {
              console.log('[SSE] Skipping initial push replay (stale stored state):', push, files, 'age:', now - messageTimestamp, 'ms');
              return;
            }

            console.log('[SSE] Processing initial message as fresh:', push, files, 'age:', messageTimestamp > 0 ? `${now - messageTimestamp}ms` : 'no timestamp', 'isMobileUpload:', isMobileUploadMessage);
          }

          console.log('[MOBILE_FLOW] Received push data:', {
            from: mobileUpload ? 'mobile' : 'desktop',
            mobileUpload,
            push,
            files,
            messageId
          });

          // Store the push message in state
          setMobileFlowMessages((prev) => [...prev, {
            timestamp: new Date().toISOString(),
            mobileUpload,
            message: push,
            files
          }]);

          // Handle push messages based on origin and current device
          // mobileUpload: true = FROM mobile, affects DESKTOP only
          // mobileUpload: false = FROM desktop, affects MOBILE only

          if (push) {
            console.log('[DEBUG_PAYWEB] Routing push message:', {
              push,
              mobileUpload,
              isMobileUploadMode,
              willRouteToDesktop: mobileUpload === true && !isMobileUploadMode,
              willRouteToMobile: mobileUpload === false && isMobileUploadMode,
              isPayweb: push === 'payweb',
            });
            // Messages FROM mobile (mobileUpload: true) - Only DESKTOP reacts
            if (mobileUpload === true && !isMobileUploadMode) {
              // Handle 'fileUpload*' push (fileUpload1, fileUpload2, etc.) - Display file names from mobile as preview
              if (push?.startsWith('fileUpload') && files && Array.isArray(files) && files.length > 0) {
                syncLog('DESKTOP', `PROCESSING fileUpload - adding ${files.length} files to UI`);

                // Reset mobileUploadCompleted so desktop properly tracks new upload cycle
                setMobileUploadCompleted(false);

                // Clear previous fileComplete/allUploadsComplete dedup entries
                // so the new cycle's completion messages are not filtered out
                const entriesToRemove = [];
                processedPushMessages.current.forEach((msgId) => {
                  if (msgId.startsWith('fileComplete_') || msgId.startsWith('allUploadsComplete_')) {
                    entriesToRemove.push(msgId);
                  }
                });
                entriesToRemove.forEach((msgId) => processedPushMessages.current.delete(msgId));

                // Extract thumbnails from meta field if available
                let thumbMap = {};
                const metaBase64 = data.parsedPush?.meta;
                if (metaBase64) {
                  try {
                    const metaData = JSON.parse(atob(metaBase64));
                    thumbMap = metaData.thumbs || {};
                  } catch (e) { /* ignore */ }
                }
                // Fallback: extract from raw push string (polling path)
                if (Object.keys(thumbMap).length === 0 && data.push) {
                  const metaMatch = typeof data.push === 'string' && data.push.match(/meta:'([A-Za-z0-9+/=]+)'/);
                  if (metaMatch) {
                    try {
                      const metaData = JSON.parse(atob(metaMatch[1]));
                      thumbMap = metaData.thumbs || {};
                    } catch (e) { /* ignore */ }
                  }
                }

                // Parse files: each entry may be "name|uid" (new format) or just "name" (legacy)
                const parsedFiles = files.map((entry) => {
                  const pipeIdx = entry.lastIndexOf('|');
                  if (pipeIdx > 0) {
                    return { name: entry.substring(0, pipeIdx), uid: entry.substring(pipeIdx + 1) };
                  }
                  return { name: entry, uid: `legacy-${Date.now()}-${Math.random().toString(36).substring(2, 9)}` };
                });

                // Separate video and image files by extension
                const videoFiles = [];
                const imageFiles = [];
                parsedFiles.forEach((pf) => {
                  const ext = `.${pf.name.split('.').pop().toLowerCase()}`;
                  if (VIDEO_EXTENSIONS.includes(ext)) {
                    videoFiles.push(pf);
                  } else if (IMAGE_EXTENSIONS.includes(ext)) {
                    imageFiles.push(pf);
                  } else {
                    imageFiles.push(pf);
                  }
                });

                // Dedup by uid (not name) — prevents SSE replay duplicates while allowing same-name re-uploads
                const existingMobileUids = new Set(
                  selectedFiles.filter((f) => f.source === 'mobile-preview').map((f) => f.mobileUid).filter(Boolean)
                );
                // Also collect uids from album photos
                selectedFiles.filter((f) => f.source === 'mobile-preview' && f.type === 'album').forEach((album) => {
                  (album.photos || []).forEach((p) => { if (p.mobileUid) existingMobileUids.add(p.mobileUid); });
                });

                const newVideoFiles = videoFiles.filter((pf) => !existingMobileUids.has(pf.uid));
                const newImageFiles = imageFiles.filter((pf) => !existingMobileUids.has(pf.uid));

                // Skip entirely if all files are duplicates (SSE replay)
                if (newVideoFiles.length === 0 && newImageFiles.length === 0) {
                  console.log('[MOBILE_FLOW] All files already present (by uid), skipping duplicate fileUpload push');
                  return;
                }

                // Add video files as individual preview entries
                const previewFiles = newVideoFiles.map((pf) => ({
                  id: `preview-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
                  file: null,
                  name: pf.name,
                  mobileUid: pf.uid,
                  size: 0,
                  type: 'video',
                  source: 'mobile-preview',
                  isPreview: true,
                  previewUrl: thumbMap[pf.name] || null,
                  posterUrl: thumbMap[pf.name] || null,
                }));

                // Group image files into an album preview entry per push batch
                const albumId = `preview-album-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
                if (newImageFiles.length > 0) {
                  previewFiles.push({
                    id: albumId,
                    file: null,
                    name: 'All photos',
                    size: 0,
                    type: 'album',
                    source: 'mobile-preview',
                    isPreview: true,
                    photoCount: newImageFiles.length,
                    photos: newImageFiles.map((pf) => ({
                      id: `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
                      file: null,
                      name: pf.name,
                      mobileUid: pf.uid,
                      size: 0,
                      previewUrl: thumbMap[pf.name] || null,
                    })),
                  });
                }

                setSelectedFiles((prev) => [...prev, ...previewFiles]);

                // Dedup upload entries by uid
                const existingUploadUids = new Set(
                  uploadsRef.current.filter((u) => u.isMobileUpload).map((u) => u.mobileUid).filter(Boolean)
                );

                // Create upload progress entries — one per video, one per album
                const mobileUploadEntries = newVideoFiles
                  .filter((pf) => !existingUploadUids.has(pf.uid))
                  .map((pf) => ({
                    id: `mobile-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
                    fileName: pf.name,
                    mobileUid: pf.uid,
                    fileSize: 0,
                    status: UPLOAD_STATUS.UPLOADING,
                    progress: 0,
                    bytesUploaded: 0,
                    speed: 0,
                    timeRemaining: 0,
                    startTime: Date.now(),
                    lastUpdateTime: null,
                    lastBytesUploaded: 0,
                    isPaused: false,
                    secureUploadId: null,
                    isMobileUpload: true,
                  }));

                const albumUploadName = `All photos (${newImageFiles.length})`;
                if (newImageFiles.length > 0) {
                  const albumUploadId = `mobile-album-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
                  mobileUploadEntries.push({
                    id: albumUploadId,
                    fileName: albumUploadName,
                    mobileUid: `album-${albumId}`,
                    linkedAlbumId: albumId,
                    fileSize: 0,
                    status: UPLOAD_STATUS.UPLOADING,
                    progress: 0,
                    bytesUploaded: 0,
                    speed: 0,
                    timeRemaining: 0,
                    startTime: Date.now(),
                    lastUpdateTime: null,
                    lastBytesUploaded: 0,
                    isPaused: false,
                    secureUploadId: null,
                    isMobileUpload: true,
                  });
                }

                setUploads((prev) => [...mobileUploadEntries, ...prev]);
                setIsUploading(true);

                // Close QR code modal if open since mobile is now connected
                setShowQrCode(false);
                setShowFilesQrCode(false);
                setShowShareModal(false);

                const newFileCount = newVideoFiles.length + newImageFiles.length;
                syncLog('DESKTOP', `UI UPDATED - added ${newFileCount} files`, { videos: mobileUploadEntries.filter((e) => !e.fileName.startsWith('All photos')).length, albums: mobileUploadEntries.filter((e) => e.fileName.startsWith('All photos')).length });

                if (newFileCount > 0) {
                  setSnackbar({
                    isVisible: true,
                    message: `Received ${newFileCount} file(s) from mobile`,
                    type: 'success'
                  });
                }

                // Start polling fallback for payweb push (SSE may miss it during reconnects)
                if (!paywebPollRef.current && currentStepRef.current !== 4) {
                  let paywebPollCount = 0;
                  paywebPollRef.current = setInterval(async () => {
                    paywebPollCount += 1;
                    console.log('[DEBUG_PAYWEB] Payweb poll tick #', paywebPollCount, 'currentStep:', currentStepRef.current);
                    if (paywebPollCount > 60) { // Stop after 5 minutes (60 * 5s)
                      clearInterval(paywebPollRef.current);
                      paywebPollRef.current = null;
                      return;
                    }
                    // If already navigated to step 4, stop polling
                    if (currentStepRef.current === 4) {
                      console.log('[DEBUG_PAYWEB] Already on step 4, stopping payweb poll');
                      clearInterval(paywebPollRef.current);
                      paywebPollRef.current = null;
                      return;
                    }
                    try {
                      const statusRes = await getGiftStatus(giftUserUid);
                      const statusData = statusRes?.data?.data || statusRes?.data || {};
                      const pushStr = statusData.push || '';
                      console.log('[DEBUG_PAYWEB] Poll response pushStr:', pushStr, 'hasPayweb:', pushStr.includes?.('payweb'), 'hasMobileTrue:', pushStr.includes?.('mobileUpload:true'));
                      if (pushStr.includes && pushStr.includes('payweb') && pushStr.includes('mobileUpload:true') && !paymentDoneRef.current) {
                        console.log('[DEBUG_PAYWEB] ✅ Poll detected payweb from mobile!');
                        setIsMobileDesktopConnected(true);
                        setSnackbar({
                          isVisible: true,
                          message: 'Complete payment for the Mobile upload to be completed',
                          type: 'default'
                        });
                        if (currentStepRef.current !== 4) {
                          animateToStep(4);
                        }
                        // Start uploading desktop's own files in background (if any)
                        setTimeout(() => {
                          if (handleStartUploadRef.current) {
                            console.log('[MOBILE_FLOW] Starting desktop uploads in background after payweb (poll)');
                            handleStartUploadRef.current();
                          }
                        }, 500);
                        clearInterval(paywebPollRef.current);
                        paywebPollRef.current = null;
                      }
                    } catch (err) {
                      console.warn('[MOBILE_FLOW] Payweb poll fallback error:', err);
                    }
                  }, 5000);
                }
              }

              // Handle 'fileComplete' push - Mark individual mobile upload as completed on desktop
              // Also CREATES entries if they don't exist yet (handles race where fileComplete
              // overwrites fileUpload on server before SSE delivers it — common for small files)
              // With cumulative tracking, files[] contains ALL completed files - process each one
              if (push === 'fileComplete' && files && files.length > 0) {
                syncLog('DESKTOP', `PROCESSING fileComplete: ${files.length} files in cumulative list`);

                // Extract S3 URL and thumbnails from meta (only applies to most recent file in message)
                let s3Url = null;
                let fcThumbMap = {};
                const fcMeta = data.parsedPush?.meta;
                if (fcMeta) {
                  try {
                    const metaData = JSON.parse(atob(fcMeta));
                    s3Url = metaData.s3Url;
                    fcThumbMap = metaData.thumbs || {};
                  } catch (e) { /* ignore */ }
                }
                if (!s3Url && data.push) {
                  const metaMatch = typeof data.push === 'string' && data.push.match(/meta:'([A-Za-z0-9+/=]+)'/);
                  if (metaMatch) {
                    try {
                      const metaData = JSON.parse(atob(metaMatch[1]));
                      s3Url = metaData.s3Url;
                      if (!Object.keys(fcThumbMap).length) fcThumbMap = metaData.thumbs || {};
                    } catch (e) { /* ignore */ }
                  }
                }

                // Process each file in the cumulative list
                const newlyCompletedFiles = [];
                files.forEach((fileEntry, fileIndex) => {
                  // Decode name|uid format (backward compatible with legacy plain names)
                  const pipeIdx = fileEntry.lastIndexOf('|');
                  const completedFileName = pipeIdx > 0 ? fileEntry.substring(0, pipeIdx) : fileEntry;
                  const completedFileUid = pipeIdx > 0 ? fileEntry.substring(pipeIdx + 1) : null;

                  // Try to mark existing entry as complete; track whether we found a match
                  // Album names can be "All photos" or "All photos (N)" - match either format
                  const isAlbumName = completedFileName === 'All photos' || completedFileName.startsWith('All photos (');
                  let foundMatch = false;
                  setUploads((prev) => {
                    let matched = false;
                    const updated = prev.map((u) => {
                      if (matched || !u.isMobileUpload) return u;
                      const uidMatch = completedFileUid && u.mobileUid && u.mobileUid === completedFileUid;
                      const nameMatch = !completedFileUid && u.fileName === completedFileName;
                      // Match any album format: "All photos" matches "All photos (N)" and vice versa
                      const albumMatch = isAlbumName && (u.fileName === 'All photos' || u.fileName.startsWith('All photos ('));
                      if (uidMatch || nameMatch || albumMatch) {
                        matched = true;
                        // Only update if not already completed (handles SSE reconnect replays)
                        if (u.status !== UPLOAD_STATUS.COMPLETED) {
                          syncLog('DESKTOP', `fileComplete MATCHED: ${u.fileName} -> COMPLETED`);
                          return { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100 };
                        }
                        // Already completed - still count as matched to prevent duplicate creation
                        return u;
                      }
                      return u;
                    });
                    foundMatch = matched;
                    return updated;
                  });

                  // If no existing entry matched, fileUpload push was missed (race condition).
                  // Create the entries now so the file/album appears on desktop.
                  if (!foundMatch) {
                    const isAlbum = completedFileName === 'All photos' || completedFileName.startsWith('All photos (');
                    const ext = isAlbum ? '' : `.${completedFileName.split('.').pop().toLowerCase()}`;
                    const isImage = !isAlbum && IMAGE_EXTENSIONS.includes(ext);

                    // Check if album already exists in selectedFiles to prevent duplicates
                    const albumAlreadyExists = isAlbum && selectedFilesRef.current.some(
                      (f) => f.type === 'album' && (f.name === 'All photos' || f.name.startsWith('All photos'))
                    );

                    if (isAlbum && albumAlreadyExists) {
                      syncLog('DESKTOP', `fileComplete[${fileIndex}] SKIP album - already exists`);
                    } else if (isAlbum) {
                      // Extract photo count from "All photos (N)"
                      const countMatch = completedFileName.match(/All photos \((\d+)\)/);
                      const photoCount = countMatch ? parseInt(countMatch[1], 10) : 1;
                      const albumId = `preview-album-fc-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

                      // Create album selectedFiles entry with placeholder photos
                      setSelectedFiles((prev) => [...prev, {
                        id: albumId,
                        file: null,
                        name: 'All photos',
                        size: 0,
                        type: 'album',
                        source: 'mobile-preview',
                        isPreview: true,
                        photoCount,
                        photos: Array.from({ length: photoCount }, (_, i) => ({
                          id: `fc-photo-${Date.now()}-${i}-${Math.random().toString(36).substring(2, 11)}`,
                          file: null,
                          name: `photo-${i + 1}`,
                          size: 0,
                          previewUrl: null,
                        })),
                      }]);

                      // Create upload entry with UPLOADING status first, then mark COMPLETED
                      // This ensures the UI shows "uploading" state briefly before completing
                      const albumUploadId = `mobile-album-fc-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
                      setUploads((prev) => [{
                        id: albumUploadId,
                        fileName: completedFileName,
                        mobileUid: completedFileUid || `album-${albumId}`,
                        linkedAlbumId: albumId,
                        fileSize: 0,
                        status: UPLOAD_STATUS.UPLOADING,
                        progress: 0,
                        bytesUploaded: 0,
                        speed: 0,
                        timeRemaining: 0,
                        startTime: Date.now(),
                        lastUpdateTime: null,
                        lastBytesUploaded: 0,
                        isPaused: false,
                        secureUploadId: null,
                        isMobileUpload: true,
                      }, ...prev]);
                      // Mark as completed after a brief delay to show the uploading state
                      setTimeout(() => {
                        setUploads((prev) => prev.map((u) => (u.id === albumUploadId
                          ? { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100 }
                          : u)));
                      }, 500);
                      newlyCompletedFiles.push(completedFileName);
                    } else if (!isImage) {
                      // Only create entries for VIDEOS - images will be grouped in album via allUploadsComplete
                      // This prevents individual photo entries from appearing in the upload list
                      const previewId = `preview-fc-${Date.now()}-${fileIndex}-${Math.random().toString(36).substring(2, 11)}`;
                      // S3 URL only applies to the last file in the message (most recently completed)
                      const thisFileS3Url = (fileIndex === files.length - 1) ? s3Url : null;
                      // Use mobile thumbnail from meta if available; fall back to S3 URL for previewUrl only
                      const fcThumb = fcThumbMap[completedFileName] || null;
                      setSelectedFiles((prev) => [...prev, {
                        id: previewId,
                        file: null,
                        name: completedFileName,
                        mobileUid: completedFileUid,
                        size: 0,
                        type: 'video',
                        source: 'mobile-preview',
                        isPreview: true,
                        previewUrl: thisFileS3Url,
                        posterUrl: fcThumb,
                      }]);

                      // Create upload entry with UPLOADING status first, then mark COMPLETED
                      // This ensures the UI shows "uploading" state briefly before completing
                      const videoUploadId = `mobile-fc-${Date.now()}-${fileIndex}-${Math.random().toString(36).substring(2, 11)}`;
                      setUploads((prev) => [{
                        id: videoUploadId,
                        fileName: completedFileName,
                        mobileUid: completedFileUid,
                        fileSize: 0,
                        status: UPLOAD_STATUS.UPLOADING,
                        progress: 0,
                        bytesUploaded: 0,
                        speed: 0,
                        timeRemaining: 0,
                        startTime: Date.now(),
                        lastUpdateTime: null,
                        lastBytesUploaded: 0,
                        isPaused: false,
                        secureUploadId: null,
                        isMobileUpload: true,
                      }, ...prev]);
                      // Mark as completed after a brief delay to show the uploading state
                      setTimeout(() => {
                        setUploads((prev) => prev.map((u) => (u.id === videoUploadId
                          ? { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100 }
                          : u)));
                      }, 500);
                      newlyCompletedFiles.push(completedFileName);
                    }
                    // Skip individual image entries - they'll be grouped in album
                  }
                });

                // Close QR modal and show notification if any new files were processed
                if (newlyCompletedFiles.length > 0) {
                  setShowQrCode(false);
                  setShowFilesQrCode(false);
                  setShowShareModal(false);
                  setIsUploading(true);

                  const msg = newlyCompletedFiles.length === 1
                    ? `Received completed file from mobile: ${newlyCompletedFiles[0]}`
                    : `Received ${newlyCompletedFiles.length} completed files from mobile`;
                  setSnackbar({
                    isVisible: true,
                    message: msg,
                    type: 'success'
                  });
                }

                // Update video thumbnail with S3 URL (applies to last file in cumulative list)
                if (s3Url && files.length > 0) {
                  const lastFile = files[files.length - 1];
                  const lastPipeIdx = lastFile.lastIndexOf('|');
                  const lastFileName = lastPipeIdx > 0 ? lastFile.substring(0, lastPipeIdx) : lastFile;
                  const lastFileUid = lastPipeIdx > 0 ? lastFile.substring(lastPipeIdx + 1) : null;
                  setSelectedFiles((prev) => prev.map((f) => {
                    if (f.source === 'mobile-preview' && f.type === 'video' && !f.previewUrl) {
                      const uidMatch = lastFileUid && f.mobileUid === lastFileUid;
                      const nameMatch = !lastFileUid && f.name === lastFileName;
                      if (uidMatch || nameMatch) return { ...f, previewUrl: s3Url };
                    }
                    return f;
                  }));
                }
              }

              // Handle 'fileProgress' push - Update mobile upload progress on desktop
              if (push === 'fileProgress') {
                let progressMap = {};
                const fpMeta = data.parsedPush?.meta;
                if (fpMeta) {
                  try {
                    const metaData = JSON.parse(atob(fpMeta));
                    progressMap = metaData.progress || {};
                  } catch (e) { /* ignore */ }
                }
                if (!progressMap || Object.keys(progressMap).length === 0) {
                  // Fallback: extract from raw push string
                  if (data.push) {
                    const metaMatch = typeof data.push === 'string' && data.push.match(/meta:'([A-Za-z0-9+/=]+)'/);
                    if (metaMatch) {
                      try {
                        const metaData = JSON.parse(atob(metaMatch[1]));
                        progressMap = metaData.progress || {};
                      } catch (e) { /* ignore */ }
                    }
                  }
                }
                if (Object.keys(progressMap).length > 0) {
                  console.log('[MOBILE_FLOW] Progress update from mobile:', progressMap);

                  // Parse progress keys: may be "name|uid" (new) or plain "name" (legacy)
                  // Build a uid→progress map and a name→progress map for matching
                  const uidProgressMap = {};
                  const nameProgressMap = {};
                  let imgSum = 0;
                  let imgCount = 0;
                  Object.entries(progressMap).forEach(([key, prog]) => {
                    const pipeIdx = key.lastIndexOf('|');
                    const fileName = pipeIdx > 0 ? key.substring(0, pipeIdx) : key;
                    const uid = pipeIdx > 0 ? key.substring(pipeIdx + 1) : null;
                    if (uid) uidProgressMap[uid] = prog;
                    nameProgressMap[fileName] = prog;
                    const ext = `.${fileName.split('.').pop().toLowerCase()}`;
                    if (IMAGE_EXTENSIONS.includes(ext)) {
                      imgSum += prog;
                      imgCount += 1;
                    }
                  });
                  const albumAvg = imgCount > 0 ? Math.round(imgSum / imgCount) : null;

                  setUploads((prev) => prev.map((u) => {
                    if (!u.isMobileUpload || u.status === UPLOAD_STATUS.COMPLETED) return u;
                    // Match by mobileUid first (precise), then fall back to fileName
                    const uidProg = u.mobileUid && uidProgressMap[u.mobileUid];
                    if (uidProg !== undefined) {
                      return uidProg > u.progress ? { ...u, progress: uidProg } : u;
                    }
                    if (nameProgressMap[u.fileName] !== undefined) {
                      const realProg = nameProgressMap[u.fileName];
                      return realProg > u.progress ? { ...u, progress: realProg } : u;
                    }
                    // Album: average of all image file progress (never go backwards)
                    if (u.fileName.startsWith('All photos') && albumAvg !== null) {
                      return albumAvg > u.progress ? { ...u, progress: albumAvg } : u;
                    }
                    return u;
                  }));
                }
              }

              // Handle 'allUploadsComplete' push - All mobile files have finished uploading
              if (push === 'allUploadsComplete') {
                syncLog('DESKTOP', 'PROCESSING allUploadsComplete - marking all mobile uploads done');

                // Safety net: mark any remaining mobile uploads as completed
                setUploads((prev) => prev.map((u) => {
                  if (u.isMobileUpload && u.status !== UPLOAD_STATUS.COMPLETED) {
                    return { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100 };
                  }
                  return u;
                }));

                // Only stop uploading if desktop has no active uploads of its own
                const hasOwnActiveUploads = uploadsRef.current.some(
                  (u) => !u.isMobileUpload && (u.status === UPLOAD_STATUS.UPLOADING || u.status === UPLOAD_STATUS.PENDING || u.status === UPLOAD_STATUS.PAUSED)
                );
                if (!hasOwnActiveUploads) {
                  setIsUploading(false);
                }
                setMobileUploadCompleted(true);
                setContentFetched(false);

                // Extract and store mobile upload metadata for desktop-side registration
                extractAndStoreMobileMetadata(data.parsedPush, data.push);

                // Create missing preview entries if fileUpload was overwritten (instant upload race)
                createMissingMobileEntries();
                // Flip QR card back — fileUpload push was overwritten so it never closed QR
                setShowQrCode(false);
                setShowFilesQrCode(false);
                setShowShareModal(false);

                // Update video preview thumbnails now that uploads are complete.
                // Use the mobile-sent thumbnail (video.thumbnail) as posterUrl so the web
                // displays the exact same thumbnail that gets registered and shown in View My Projector.
                if (mobileUploadMetadataRef.current?.videos?.length > 0) {
                  const s3BaseUrl = generateS3Url();
                  setSelectedFiles((prev) => prev.map((f) => {
                    if (f.source === 'mobile-preview' && f.type === 'video') {
                      const videoMeta = mobileUploadMetadataRef.current.videos.find((v) => v.fileName === f.name);
                      if (videoMeta) {
                        const updates = {};
                        if (!f.previewUrl && videoMeta.s3Key) {
                          updates.previewUrl = `${s3BaseUrl}${videoMeta.s3Key}`;
                        }
                        // Always set posterUrl from mobile thumbnail so display matches registration
                        if (videoMeta.thumbnail) {
                          updates.posterUrl = videoMeta.thumbnail;
                        }
                        if (Object.keys(updates).length > 0) {
                          return { ...f, ...updates };
                        }
                      }
                    }
                    return f;
                  }));
                }

                // If payment is already done, register mobile uploads now (desktop handles all registration)
                if (paymentDoneRef.current && mobileUploadMetadataRef.current && registerS3CompleteUploadsRef.current) {
                  registerS3CompleteUploadsRef.current();
                }
                // NOTE: Don't stop the poll here — mobile may re-upload more files
              }

              // shareComplete handler removed — contributor flow no longer sends push messages

              // "cancelUpload" - Mobile canceled uploads, remove from desktop UI
              if (push === 'cancelUpload' && files && files.length > 0) {
                console.log('[MOBILE_FLOW] Mobile canceled uploads:', files);
                const cancelNames = new Set();
                const cancelUids = new Set();
                files.forEach((entry) => {
                  const pIdx = entry.lastIndexOf('|');
                  const fn = pIdx > 0 ? entry.substring(0, pIdx) : entry;
                  const uid = pIdx > 0 ? entry.substring(pIdx + 1) : null;
                  cancelNames.add(fn);
                  if (uid) cancelUids.add(uid);
                  deletedFileNamesRef.current.add(fn);
                });
                // Remove from uploads list
                setUploads((prev) => prev.filter((u) => {
                  if (u.mobileUid && cancelUids.has(u.mobileUid)) return false;
                  if (u.isMobileUpload && cancelNames.has(u.fileName)) return false;
                  return true;
                }));
                // Remove from selected files
                setSelectedFiles((prev) => prev.filter((f) => {
                  if (f.mobileUid && cancelUids.has(f.mobileUid)) return false;
                  if (f.source === 'mobile-preview' && cancelNames.has(f.name)) return false;
                  return true;
                }));
                // Remove from mobile metadata to prevent registration
                if (mobileUploadMetadataRef.current) {
                  if (mobileUploadMetadataRef.current.videos) {
                    mobileUploadMetadataRef.current.videos = mobileUploadMetadataRef.current.videos.filter((v) => !cancelNames.has(v.fileName));
                  }
                  if (mobileUploadMetadataRef.current.albums) {
                    mobileUploadMetadataRef.current.albums = mobileUploadMetadataRef.current.albums.map((a) => ({
                      ...a,
                      photos: a.photos ? a.photos.filter((p) => !cancelNames.has(p.name)) : [],
                    })).filter((a) => a.photos && a.photos.length > 0);
                  }
                }
              }

              // "deleteUpload" - Mobile deleted a completed upload, remove from desktop too
              if (push === 'deleteUpload' && files && files.length > 0) {
                console.log('[MOBILE_FLOW] Mobile requested delete for files:', files);
                // Decode name|uid format — collect both names and uids for matching
                const deleteNames = new Set();
                const deleteUids = new Set();
                files.forEach((entry) => {
                  const pIdx = entry.lastIndexOf('|');
                  const fn = pIdx > 0 ? entry.substring(0, pIdx) : entry;
                  const uid = pIdx > 0 ? entry.substring(pIdx + 1) : null;
                  deleteNames.add(fn);
                  if (uid) deleteUids.add(uid);
                  deletedFileNamesRef.current.add(fn);
                });
                setUploads((prev) => prev.filter((u) => {
                  if (u.mobileUid && deleteUids.has(u.mobileUid)) return false;
                  return !deleteNames.has(u.fileName);
                }));
                setSelectedFiles((prev) => prev.filter((f) => {
                  if (f.mobileUid && deleteUids.has(f.mobileUid)) return false;
                  return !deleteNames.has(f.name);
                }));
                // Also remove from mobile metadata to prevent registration
                if (mobileUploadMetadataRef.current) {
                  if (mobileUploadMetadataRef.current.videos) {
                    mobileUploadMetadataRef.current.videos = mobileUploadMetadataRef.current.videos.filter((v) => !deleteNames.has(v.fileName));
                  }
                  if (mobileUploadMetadataRef.current.albums) {
                    mobileUploadMetadataRef.current.albums = mobileUploadMetadataRef.current.albums.map((a) => ({
                      ...a,
                      photos: a.photos ? a.photos.filter((p) => !deleteNames.has(p.name)) : [],
                    })).filter((a) => a.photos && a.photos.length > 0);
                  }
                }
              }

              // "deleteAlbumPhoto" - Other side deleted an individual photo from an album
              if (push === 'deleteAlbumPhoto' && files && files.length > 0) {
                console.log('[MOBILE_FLOW] Other side deleted album photo(s):', files);
                // Decode name|uid format
                const deleteNames = new Set();
                const deleteUids = new Set();
                files.forEach((entry) => {
                  const pIdx = entry.lastIndexOf('|');
                  const fn = pIdx > 0 ? entry.substring(0, pIdx) : entry;
                  const uid = pIdx > 0 ? entry.substring(pIdx + 1) : null;
                  deleteNames.add(fn);
                  if (uid) deleteUids.add(uid);
                  deletedFileNamesRef.current.add(fn);
                });
                // Match by unique ID only (not name) to avoid clearing same-name photos in other albums
                const photoMatchById = (p) => {
                  if (p.mobileUid && deleteUids.has(p.mobileUid)) return true;
                  if (p.id && deleteUids.has(p.id)) return true;
                  return false;
                };
                // Remove photo from album in selectedFiles
                setSelectedFiles((prev) => {
                  const updated = prev.map((f) => {
                    if (f.type === 'album' && f.photos) {
                      const remaining = f.photos.filter((p) => !photoMatchById(p));
                      if (remaining.length !== f.photos.length) {
                        return { ...f, photos: remaining, photoCount: remaining.length };
                      }
                    }
                    return f;
                  });
                  return updated.filter((f) => !(f.type === 'album' && f.photos && f.photos.length === 0));
                });
                // Update upload entry fileName count or remove if empty
                setUploads((prev) => {
                  const updated = prev.map((u) => {
                    if (u.fileName && (u.fileName === ALL_PHOTOS_ALBUM_NAME || u.fileName.startsWith('All photos ('))) {
                      const linkedAlbum = selectedFiles.find((f) => f.type === 'album' && (f.id === u.id || f.id === u.linkedAlbumId));
                      if (linkedAlbum && linkedAlbum.photos) {
                        const remaining = linkedAlbum.photos.filter((p) => !photoMatchById(p));
                        if (remaining.length === 0) return null;
                        return { ...u, fileName: `All photos (${remaining.length})` };
                      }
                    }
                    return u;
                  });
                  return updated.filter(Boolean);
                });
                // Remove from mobileUploadMetadataRef
                if (mobileUploadMetadataRef.current && mobileUploadMetadataRef.current.albums) {
                  mobileUploadMetadataRef.current.albums = mobileUploadMetadataRef.current.albums.map((a) => ({
                    ...a,
                    photos: a.photos ? a.photos.filter((p) => !deleteNames.has(p.name)) : [],
                  })).filter((a) => a.photos && a.photos.length > 0);
                }
                // Remove from photoMetadataRef (desktop albums)
                photoMetadataRef.current.forEach((albumData, key) => {
                  if (albumData.photos) {
                    const filtered = albumData.photos.filter((p) => !deleteNames.has(p.name));
                    if (filtered.length === 0) {
                      photoMetadataRef.current.delete(key);
                    } else {
                      photoMetadataRef.current.set(key, { ...albumData, photos: filtered });
                    }
                  }
                });
              }

              // "paymentDone" - Mobile completed payment, desktop receives tokens to start uploading
              if (push === 'paymentDone') {
                console.log('[MOBILE_FLOW] Payment completed on mobile, desktop receiving tokens');

                const token = data.parsedPush?.token || null;
                const accessToken = data.parsedPush?.access || null;
                console.log('[MOBILE_FLOW] Extracted token from mobile:', token ? `${token.substring(0, 20)}...` : 'null');
                console.log('[MOBILE_FLOW] Extracted access from mobile:', accessToken ? `${accessToken.substring(0, 20)}...` : 'null');

                if (token) {
                  localStorage.setItem('token', token);
                  console.log('[MOBILE_FLOW] Token stored in localStorage from mobile payment');
                } else {
                  console.error('[MOBILE_FLOW] No token received from mobile paymentDone!');
                }

                if (accessToken) {
                  localStorage.setItem('uploader_token', accessToken);
                  try {
                    const payload = JSON.parse(atob(accessToken.split('.')[1]));
                    if (payload.exp) {
                      localStorage.setItem('uploader_token_expiry', (payload.exp * 1000).toString());
                    }
                  } catch (e) { console.warn('[MOBILE_FLOW] Failed to parse uploader token expiry:', e); }
                  console.log('[MOBILE_FLOW] Uploader token stored in localStorage from mobile payment');
                } else {
                  console.warn('[MOBILE_FLOW] No uploader token from mobile, falling back to getUploaderToken()');
                  getUploaderToken().catch((err) => console.warn('[MOBILE_FLOW] Failed to fetch uploader token:', err));
                }

                // Go directly to upload progress (skip payment step)
                setShowPayment(false);
                setPaymentData(null);
                animateToStep(5);

                // Start uploading desktop's own files after animation
                setTimeout(() => {
                  if (handleStartUploadRef.current) {
                    console.log('[MOBILE_FLOW] Starting desktop upload after mobile payment');
                    handleStartUploadRef.current();
                  }
                }, 500);

                setSnackbar({
                  isVisible: true,
                  message: 'Payment complete! Starting upload...',
                  type: 'success'
                });

                // Start polling fallback for mobile upload completion
                // SSE may miss rapid successive events after paymentDone
                // NOTE: Don't stop this poll on allUploadsComplete — mobile may re-upload more files
                if (mobileCompletionPollRef.current) clearInterval(mobileCompletionPollRef.current);
                let pollCount = 0;
                let prePollLastSeenPush = '';
                mobileCompletionPollRef.current = setInterval(async () => {
                  pollCount += 1;
                  if (pollCount > 120) { // Stop after 10 minutes (120 * 5s)
                    clearInterval(mobileCompletionPollRef.current);
                    mobileCompletionPollRef.current = null;
                    return;
                  }
                  try {
                    const statusRes = await getGiftStatus(giftUserUid);
                    const statusData = statusRes?.data?.data || statusRes?.data || {};
                    const pushStr = statusData.push || '';

                    // Skip if push hasn't changed
                    if (pushStr === prePollLastSeenPush) return;
                    prePollLastSeenPush = pushStr;

                    if (!pushStr.includes || !pushStr.includes('mobileUpload:true')) return;
                    console.log('[MOBILE_FLOW] Poll fallback - status push:', pushStr);

                    // Check if push contains allUploadsComplete from mobile
                    if (pushStr.includes && pushStr.includes('allUploadsComplete') && pushStr.includes('mobileUpload:true')) {
                      console.log('[MOBILE_FLOW] Poll detected mobile allUploadsComplete');

                      // Mark all remaining mobile uploads as completed
                      setUploads((prev) => prev.map((u) => {
                        if (u.isMobileUpload && u.status !== UPLOAD_STATUS.COMPLETED) {
                          return { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100 };
                        }
                        return u;
                      }));

                      // Only stop uploading if desktop has no active uploads
                      const hasOwnActive = uploadsRef.current.some(
                        (u) => !u.isMobileUpload && (u.status === UPLOAD_STATUS.UPLOADING || u.status === UPLOAD_STATUS.PENDING || u.status === UPLOAD_STATUS.PAUSED)
                      );
                      if (!hasOwnActive) {
                        setIsUploading(false);
                      }
                      setMobileUploadCompleted(true);
                      setContentFetched(false);

                      // Extract and store mobile upload metadata for desktop-side registration
                      extractAndStoreMobileMetadata(null, pushStr);

                      // Create missing preview entries if fileUpload was overwritten (instant upload race)
                      createMissingMobileEntries();
                      // Flip QR card back — fileUpload push was overwritten so it never closed QR
                      setShowQrCode(false);
                      setShowFilesQrCode(false);
                      setShowShareModal(false);

                      // Update video preview thumbnails — use mobile thumbnail for consistent display
                      if (mobileUploadMetadataRef.current?.videos?.length > 0) {
                        const s3BaseUrl = generateS3Url();
                        setSelectedFiles((prev) => prev.map((f) => {
                          if (f.source === 'mobile-preview' && f.type === 'video') {
                            const videoMeta = mobileUploadMetadataRef.current.videos.find((v) => v.fileName === f.name);
                            if (videoMeta) {
                              const updates = {};
                              if (!f.previewUrl && videoMeta.s3Key) {
                                updates.previewUrl = `${s3BaseUrl}${videoMeta.s3Key}`;
                              }
                              if (videoMeta.thumbnail) {
                                updates.posterUrl = videoMeta.thumbnail;
                              }
                              if (Object.keys(updates).length > 0) {
                                return { ...f, ...updates };
                              }
                            }
                          }
                          return f;
                        }));
                      }

                      // If payment is already done, register mobile uploads now (desktop handles all registration)
                      if (paymentDoneRef.current && mobileUploadMetadataRef.current && registerS3CompleteUploadsRef.current) {
                        registerS3CompleteUploadsRef.current();
                      }

                      clearInterval(mobileCompletionPollRef.current);
                      mobileCompletionPollRef.current = null;
                    }

                    // Check for individual fileComplete from mobile
                    if (pushStr.includes && pushStr.includes('fileComplete') && pushStr.includes('mobileUpload:true')) {
                      // Try to extract file name from the push string — may be "name|uid" or just "name"
                      const filesMatch = pushStr.match(/files:\["?([^"\]]+)"?\]/);
                      if (filesMatch && filesMatch[1]) {
                        const rawEntry = filesMatch[1];
                        const pIdx = rawEntry.lastIndexOf('|');
                        const completedFileName = pIdx > 0 ? rawEntry.substring(0, pIdx) : rawEntry;
                        const completedUid = pIdx > 0 ? rawEntry.substring(pIdx + 1) : null;
                        console.log('[MOBILE_FLOW] Poll detected mobile fileComplete:', completedFileName, 'uid:', completedUid);
                        setUploads((prev) => {
                          let matched = false;
                          return prev.map((u) => {
                            if (matched || !u.isMobileUpload || u.status === UPLOAD_STATUS.COMPLETED) return u;
                            const uidMatch = completedUid && u.mobileUid && u.mobileUid === completedUid;
                            const nameMatch = !completedUid && u.fileName === completedFileName;
                            if (uidMatch || nameMatch) {
                              matched = true;
                              return { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100 };
                            }
                            return u;
                          });
                        });
                      }
                    }
                  } catch (err) {
                    console.warn('[MOBILE_FLOW] Poll fallback error:', err);
                  }
                }, 5000);
              }

              // "payweb" - Mobile requests desktop to go to payment page
              // Check both push === 'payweb' (direct) and parsedPush.payweb === true (piggy-backed on fileComplete etc.)
              const hasPaywebFlag = push === 'payweb' || data.parsedPush?.payweb === true;
              console.log('[DEBUG_PAYWEB] Checking payweb condition:', { push, pushEqualsPayweb: push === 'payweb', parsedPaywebFlag: data.parsedPush?.payweb, hasPaywebFlag, currentStep: currentStepRef.current, paymentDone: paymentDoneRef.current });
              if (hasPaywebFlag && currentStepRef.current !== 4 && !paymentDoneRef.current) {
                console.log('[DEBUG_PAYWEB] ✅ PAYWEB handler entered! currentStep:', currentStepRef.current);
                setIsMobileDesktopConnected(true); // Mark mobile-desktop connection as active
                setSnackbar({
                  isVisible: true,
                  message: 'Complete payment for the Mobile upload to be completed',
                  type: 'default'
                });
                console.log('[DEBUG_PAYWEB] Calling animateToStep(4)');
                animateToStep(4);
                // Start uploading desktop's own files in background (if any)
                setTimeout(() => {
                  if (handleStartUploadRef.current) {
                    console.log('[MOBILE_FLOW] Starting desktop uploads in background after payweb');
                    handleStartUploadRef.current();
                  }
                }, 500);
                // Clear payweb polling since SSE delivered the event
                if (paywebPollRef.current) {
                  clearInterval(paywebPollRef.current);
                  paywebPollRef.current = null;
                }
              }
            }

            // Messages FROM desktop (mobileUpload: false) - Only MOBILE reacts
            if (mobileUpload === false && isMobileUploadMode) {
              console.log('[MOBILE_FLOW] Processing message from desktop on mobile');

              // "cancelUpload" - Desktop requests mobile to cancel file uploads
              if (push === 'cancelUpload' && files && files.length > 0) {
                console.log('[MOBILE_FLOW] Desktop requested cancel for files:', files);

                const cancelledIds = [];
                const cancelledNames = [];

                // Process all files in the cancel batch — decode name|uid format
                files.forEach((cancelEntry) => {
                  const pipeIdx = cancelEntry.lastIndexOf('|');
                  const cancelFileName = pipeIdx > 0 ? cancelEntry.substring(0, pipeIdx) : cancelEntry;
                  const cancelUid = pipeIdx > 0 ? cancelEntry.substring(pipeIdx + 1) : null;

                  const matchingUpload = uploadsRef.current.find(
                    (u) => {
                      if (u.status === UPLOAD_STATUS.COMPLETED || u.status === UPLOAD_STATUS.CANCELLED || u.status === UPLOAD_STATUS.S3_COMPLETE) return false;
                      // Match by uid first (precise), then fall back to fileName
                      if (cancelUid && u.id === cancelUid) return true;
                      return !cancelUid && u.fileName === cancelFileName;
                    }
                  );
                  if (matchingUpload) {
                    if (matchingUpload.secureUploadId) {
                      uploadManagerSecure.cancelUpload(matchingUpload.secureUploadId);
                    }
                    cancelledIds.push(matchingUpload.id);
                    cancelledNames.push(cancelFileName);
                  }
                });

                if (cancelledIds.length > 0) {
                  setUploads((prev) => prev.map((u) => (cancelledIds.includes(u.id)
                    ? { ...u, status: UPLOAD_STATUS.CANCELLED }
                    : u)));
                  setTimeout(() => {
                    setUploads((prev) => prev.filter((u) => !cancelledIds.includes(u.id)));
                    setSelectedFiles((prev) => prev.filter((f) => !(f.isPreview && cancelledNames.includes(f.name))));
                  }, 1000);
                }
              }

              // "deleteUpload" - Desktop deleted a completed upload, remove from mobile too
              if (push === 'deleteUpload' && files && files.length > 0) {
                console.log('[MOBILE_FLOW] Desktop requested delete for files:', files);
                files.forEach((fn) => deletedFileNamesRef.current.add(fn));
                setUploads((prev) => prev.filter((u) => !files.includes(u.fileName)));
                setSelectedFiles((prev) => prev.filter((f) => !files.includes(f.name)));
              }

              // "deleteAlbumPhoto" - Desktop deleted an individual photo from an album
              if (push === 'deleteAlbumPhoto' && files && files.length > 0) {
                console.log('[MOBILE_FLOW] Desktop deleted album photo(s):', files);
                // Decode name|uid format
                const delNames = new Set();
                const delUids = new Set();
                files.forEach((entry) => {
                  const pIdx = entry.lastIndexOf('|');
                  const fn = pIdx > 0 ? entry.substring(0, pIdx) : entry;
                  const uid = pIdx > 0 ? entry.substring(pIdx + 1) : null;
                  delNames.add(fn);
                  if (uid) delUids.add(uid);
                  deletedFileNamesRef.current.add(fn);
                });
                // Match by unique ID only (not name) to avoid clearing same-name photos in other albums
                const photoMatchId = (p) => {
                  if (p.mobileUid && delUids.has(p.mobileUid)) return true;
                  if (p.id && delUids.has(p.id)) return true;
                  return false;
                };
                // Remove photo from album in selectedFiles
                setSelectedFiles((prev) => {
                  const updated = prev.map((f) => {
                    if (f.type === 'album' && f.photos) {
                      const remaining = f.photos.filter((p) => !photoMatchId(p));
                      if (remaining.length !== f.photos.length) {
                        return { ...f, photos: remaining, photoCount: remaining.length };
                      }
                    }
                    return f;
                  });
                  return updated.filter((f) => !(f.type === 'album' && f.photos && f.photos.length === 0));
                });
                // Update upload entry count or remove if empty
                setUploads((prev) => {
                  const updated = prev.map((u) => {
                    if (u.fileName && (u.fileName === ALL_PHOTOS_ALBUM_NAME || u.fileName.startsWith('All photos ('))) {
                      const linkedAlbum = selectedFiles.find((sf) => sf.type === 'album' && (sf.id === u.id || sf.id === u.linkedAlbumId));
                      if (linkedAlbum && linkedAlbum.photos) {
                        const remaining = linkedAlbum.photos.filter((p) => !photoMatchId(p));
                        if (remaining.length === 0) return null;
                        return { ...u, fileName: `All photos (${remaining.length})` };
                      }
                    }
                    return u;
                  });
                  return updated.filter(Boolean);
                });
                // Remove from photoMetadataRef
                photoMetadataRef.current.forEach((albumData, key) => {
                  if (albumData.photos) {
                    const filtered = albumData.photos.filter((p) => !delNames.has(p.name));
                    if (filtered.length === 0) {
                      photoMetadataRef.current.delete(key);
                    } else {
                      photoMetadataRef.current.set(key, { ...albumData, photos: filtered });
                    }
                  }
                });
              }

              // "softDeleteUpload" - Desktop soft-deleted an upload, move to mobile restore tab
              if (push === 'softDeleteUpload' && files && files.length > 0) {
                console.log('[MOBILE_FLOW] Desktop soft-deleted uploads:', files);
                const softDeleteNames = new Set(files);
                files.forEach((fn) => deletedFileNamesRef.current.add(fn));

                // Build deletedItems entries from current state before removing
                const newDeletedItems = [];
                uploadsRef.current.forEach((u) => {
                  if (softDeleteNames.has(u.fileName)) {
                    const matchedFile = selectedFilesRef.current.find((f) => f.id === u.id || f.name === u.fileName);
                    newDeletedItems.push({
                      uploadId: u.id,
                      type: (u.type === 'album' || u.fileName === ALL_PHOTOS_ALBUM_NAME || u.fileName.startsWith('All photos (')) ? 'album' : 'video',
                      fileName: u.fileName,
                      thumbnail: matchedFile?.posterUrl || matchedFile?.thumbnail || (matchedFile?.type === 'album' && matchedFile?.photos?.[0]?.previewUrl) || (matchedFile?.type === 'album' && matchedFile?.photos?.[0]?.imagekit_url) || u.thumbnail || null,
                      registeredId: u.registeredId || matchedFile?.registeredId || null,
                      source: u.registeredId ? 'registered' : 'unregistered',
                      uploadData: { ...u },
                      selectedFileData: matchedFile ? { ...matchedFile } : null,
                    });
                  }
                });

                setUploads((prev) => prev.filter((u) => !softDeleteNames.has(u.fileName)));
                setSelectedFiles((prev) => prev.filter((f) => !softDeleteNames.has(f.name)));
                if (newDeletedItems.length > 0) {
                  setDeletedItems((prev) => [...prev, ...newDeletedItems]);
                }
              }

              // "softDeleteAlbumPhoto" - Desktop soft-deleted a photo, move to mobile restore tab
              if (push === 'softDeleteAlbumPhoto' && files && files.length > 0) {
                console.log('[MOBILE_FLOW] Desktop soft-deleted album photo(s):', files);
                const sdNames = new Set();
                const sdUids = new Set();
                files.forEach((entry) => {
                  const pIdx = entry.lastIndexOf('|');
                  const fn = pIdx > 0 ? entry.substring(0, pIdx) : entry;
                  const uid = pIdx > 0 ? entry.substring(pIdx + 1) : null;
                  sdNames.add(fn);
                  if (uid) sdUids.add(String(uid));
                  deletedFileNamesRef.current.add(fn);
                });
                const sdPhotoMatch = (p) => {
                  if (p.mobileUid && sdUids.has(String(p.mobileUid))) return true;
                  if (p.id && sdUids.has(String(p.id))) return true;
                  if (p.photo_id && sdUids.has(String(p.photo_id))) return true;
                  return false;
                };

                // Build deletedItems from matched photos before removing
                const newPhotoDeletedItems = [];
                selectedFilesRef.current.forEach((f) => {
                  if (f.type === 'album' && f.photos) {
                    f.photos.forEach((p) => {
                      if (sdPhotoMatch(p)) {
                        const uploadEntry = uploadsRef.current.find((u) => u.id === f.id || u.linkedAlbumId === f.id);
                        newPhotoDeletedItems.push({
                          uploadId: uploadEntry?.id || f.id,
                          type: 'photo',
                          fileName: p.name || p.fileName,
                          thumbnail: p.previewUrl || p.imagekit_url || null,
                          registeredId: p.photo_id || p.id,
                          source: p.photo_id ? 'registered' : 'unregistered',
                          albumFileId: f.id,
                          photoData: { ...p },
                        });
                      }
                    });
                  }
                });

                // Remove photos from albums
                setSelectedFiles((prev) => {
                  const updated = prev.map((f) => {
                    if (f.type === 'album' && f.photos) {
                      const remaining = f.photos.filter((p) => !sdPhotoMatch(p));
                      if (remaining.length !== f.photos.length) {
                        return { ...f, photos: remaining, photoCount: remaining.length };
                      }
                    }
                    return f;
                  });
                  return updated.filter((f) => !(f.type === 'album' && f.photos && f.photos.length === 0));
                });
                // Update upload entry count or remove if empty
                setUploads((prev) => {
                  const updated = prev.map((u) => {
                    if (u.fileName && (u.fileName === ALL_PHOTOS_ALBUM_NAME || u.fileName.startsWith('All photos ('))) {
                      const linkedAlbum = selectedFilesRef.current.find((sf) => sf.type === 'album' && (sf.id === u.id || sf.id === u.linkedAlbumId));
                      if (linkedAlbum && linkedAlbum.photos) {
                        const remaining = linkedAlbum.photos.filter((p) => !sdPhotoMatch(p));
                        if (remaining.length === 0) return null;
                        return { ...u, fileName: `All photos (${remaining.length})` };
                      }
                    }
                    return u;
                  });
                  return updated.filter(Boolean);
                });
                // Remove from photoMetadataRef
                photoMetadataRef.current.forEach((albumData, key) => {
                  if (albumData.photos) {
                    const filtered = albumData.photos.filter((p) => !sdNames.has(p.name));
                    if (filtered.length === 0) {
                      photoMetadataRef.current.delete(key);
                    } else {
                      photoMetadataRef.current.set(key, { ...albumData, photos: filtered });
                    }
                  }
                });
                if (newPhotoDeletedItems.length > 0) {
                  setDeletedItems((prev) => [...prev, ...newPhotoDeletedItems]);
                }
              }

              // "restoreUpload" - Desktop restored a soft-deleted upload, restore on mobile too
              if (push === 'restoreUpload' && files && files.length > 0) {
                console.log('[MOBILE_FLOW] Desktop restored uploads:', files);
                const restoreNames = new Set(files);
                files.forEach((fn) => deletedFileNamesRef.current.delete(fn));

                // Find matching items in deletedItems and move back to uploads/selectedFiles
                setDeletedItems((prev) => {
                  const toRestore = prev.filter((d) => restoreNames.has(d.fileName) && d.type !== 'photo');
                  const remaining = prev.filter((d) => !(restoreNames.has(d.fileName) && d.type !== 'photo'));
                  toRestore.forEach((item) => {
                    if (item.uploadData) {
                      setUploads((prevUploads) => [...prevUploads, item.uploadData]);
                    }
                    if (item.selectedFileData) {
                      setSelectedFiles((prevFiles) => [...prevFiles, item.selectedFileData]);
                    }
                  });
                  return remaining;
                });
              }

              // "restoreAlbumPhoto" - Desktop restored a soft-deleted photo, restore on mobile too
              if (push === 'restoreAlbumPhoto' && files && files.length > 0) {
                console.log('[MOBILE_FLOW] Desktop restored album photo(s):', files);
                const rNames = new Set();
                const rUids = new Set();
                files.forEach((entry) => {
                  const pIdx = entry.lastIndexOf('|');
                  const fn = pIdx > 0 ? entry.substring(0, pIdx) : entry;
                  const uid = pIdx > 0 ? entry.substring(pIdx + 1) : null;
                  rNames.add(fn);
                  if (uid) rUids.add(String(uid));
                  deletedFileNamesRef.current.delete(fn);
                });
                const rPhotoMatch = (p) => {
                  if (p.mobileUid && rUids.has(String(p.mobileUid))) return true;
                  if (p.id && rUids.has(String(p.id))) return true;
                  if (p.photo_id && rUids.has(String(p.photo_id))) return true;
                  return false;
                };

                setDeletedItems((prev) => {
                  const toRestore = prev.filter((d) => d.type === 'photo' && d.photoData && rPhotoMatch(d.photoData));
                  const remaining = prev.filter((d) => !(d.type === 'photo' && d.photoData && rPhotoMatch(d.photoData)));
                  toRestore.forEach((item) => {
                    if (item.photoData) {
                      // Add photo back to its album
                      setSelectedFiles((prevFiles) => prevFiles.map((f) => {
                        if (f.id === item.albumFileId && f.type === 'album') {
                          return { ...f, photos: [...(f.photos || []), item.photoData], photoCount: (f.photoCount || 0) + 1 };
                        }
                        return f;
                      }));
                      // Update upload fileName count
                      setUploads((prevUploads) => prevUploads.map((u) => {
                        if (u.id === item.uploadId || u.linkedAlbumId === item.albumFileId) {
                          const currentCount = parseInt((u.fileName.match(/\((\d+)\)/) || [])[1] || '0', 10);
                          return { ...u, fileName: `All photos (${currentCount + 1})` };
                        }
                        return u;
                      }));
                    }
                  });
                  return remaining;
                });
              }

              // "paymentDone" - Desktop notifies mobile that payment is complete
              if (push === 'paymentDone') {
                console.log('[MOBILE_FLOW] Payment completed on desktop');
                console.log('[MOBILE_FLOW] Full SSE data:', data);

                // Extract token from parsed push data
                const token = data.parsedPush?.token || null;
                console.log('[MOBILE_FLOW] Extracted token:', token ? `${token.substring(0, 20)}...` : 'null');

                if (token) {
                  localStorage.setItem('token', token);
                  console.log('[MOBILE_FLOW] Token stored in localStorage');
                } else {
                  console.error('[MOBILE_FLOW] No token received in paymentDone message!');
                }

                // Mobile receives payment confirmation
                console.log('[MOBILE_FLOW] Setting showWaitingForPayment to false');
                setShowWaitingForPayment(false);

                // Mark payment as done so the upload manager subscription auto-registers S3-complete uploads
                paymentDoneRef.current = true;
                // Stop piggy-backing payweb flag on future pushes
                paywebRequestedRef.current = false;

                // Clear paymentDone poll since SSE delivered the event
                if (paymentDonePollRef.current) {
                  clearInterval(paymentDonePollRef.current);
                  paymentDonePollRef.current = null;
                }

                // Register any S3-complete uploads immediately (others will auto-register
                // via the subscription effect as they reach s3_complete)
                if (registerS3CompleteUploadsRef.current) {
                  console.log('[MOBILE_FLOW] Registering any S3-complete uploads now');
                  registerS3CompleteUploadsRef.current();
                }

                // Notify desktop about completed uploads
                buildMobileUploadMetadata().then((meta) => {
                  sendMobilePushMessage('allUploadsComplete', [], null, null, meta);
                });

                setSnackbar({
                  isVisible: true,
                  message: 'Payment complete! Registering videos...',
                  type: 'success'
                });
              }
            }

            // Handle generic messages (can be processed by both)
            if (push === 'hello') {
              console.log('[MOBILE_FLOW] Device connected:', mobileUpload ? 'mobile' : 'desktop');
            }
          }
        }
      },
      onError: (error) => {
        console.error('SSE: Connection error:', error);
      }
    });

    // Cleanup: close SSE connection, polling, and push throttle timer on unmount or when uid changes
    return () => {
      unsubscribe();
      if (mobileCompletionPollRef.current) {
        clearInterval(mobileCompletionPollRef.current);
        mobileCompletionPollRef.current = null;
      }
      if (paywebPollRef.current) {
        clearInterval(paywebPollRef.current);
        paywebPollRef.current = null;
      }
      if (paymentDonePollRef.current) {
        clearInterval(paymentDonePollRef.current);
        paymentDonePollRef.current = null;
      }
    };
  }, [giftUserUid, isMobileUploadMode, animateToStep, setSnackbar, setShowWaitingForPayment, setMobileFlowMessages]);

  // Handle files selected from cloud browsers (like main app)
  const handleCloudFilesSelected = (files, source) => {
    const videoFiles = [];
    const photoFiles = [];

    files.forEach((file) => {
      // Determine file type: Dropbox uses fileType ('video'/'image'), Google Drive uses mimeType
      const isVideo = file.fileType === 'video' || file.mimeType?.startsWith('video/');
      if (isVideo) {
        videoFiles.push(file);
      } else {
        photoFiles.push(file);
      }
    });

    const newEntries = [];

    // Add videos as individual entries (same as local video flow)
    videoFiles.forEach((file) => {
      newEntries.push({
        id: `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
        file: null,
        name: file.name,
        size: file.size || 0,
        type: 'video',
        source,
        cloudFile: file,
      });
    });

    // Group photos into an album entry (same as local photo flow)
    if (photoFiles.length > 0) {
      const totalSize = photoFiles.reduce((sum, f) => sum + (f.size || 0), 0);
      newEntries.push({
        id: `album-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: ALL_PHOTOS_ALBUM_NAME,
        type: 'album',
        source,
        size: totalSize,
        photoCount: photoFiles.length,
        photos: photoFiles.map((file) => ({
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          file: null, // Cloud photos — will be downloaded during upload
          name: file.name,
          size: file.size || 0,
          cloudFile: file,
          cloudSource: source,
        })),
      });
    }

    setSelectedFiles((prev) => [...prev, ...newEntries]);
    setShowAddMore(false);

    // Ensure selectedChoice is set to 'you-help-build' for proper flow
    if (!selectedChoice) {
      setSelectedChoice('you-help-build');
    }

    setSnackbar({
      isVisible: true,
      message: `${files.length} file(s) selected from ${source === 'dropbox' ? 'Dropbox' : 'Google Drive'}`,
      type: 'error' // Using 'error' for black background
    });
  };

  // Remove selected file
  const handleRemoveFile = (fileId) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  // Download file from cloud service (Dropbox or Google Drive)
  const downloadCloudFile = async (fileData, onProgress) => {
    const { cloudFile, source, id } = fileData;

    // Get access token from localStorage
    const cloudServices = localStorage.getItem('cloudConnectedServices');
    const cloudAccessToken = cloudServices ? JSON.parse(cloudServices)[source]?.accessToken : null;

    if (!cloudAccessToken) {
      throw new Error(`${source === 'dropbox' ? 'Dropbox' : 'Google Drive'} not connected`);
    }

    let tempUrl = null;
    let headers = {};

    if (source === 'dropbox') {
      // Get Dropbox temporary link
      const dropboxHeaders = {
        Authorization: `Bearer ${cloudAccessToken}`,
        'Content-Type': 'application/json',
      };

      // Add namespace header for shared folder access
      if (cloudFile.namespaceId) {
        dropboxHeaders['Dropbox-API-Path-Root'] = JSON.stringify({
          '.tag': 'namespace_id',
          namespace_id: cloudFile.namespaceId,
        });
      }

      const response = await axios.post(
        'https://api.dropboxapi.com/2/files/get_temporary_link',
        { path: cloudFile.id },
        { headers: dropboxHeaders },
      );
      tempUrl = response.data.link;
    } else if (source === 'googledrive') {
      // Google Drive direct download link
      tempUrl = `https://www.googleapis.com/drive/v3/files/${cloudFile.id}?alt=media`;
      headers = {
        Authorization: `Bearer ${cloudAccessToken}`,
      };
    }

    // Download file with progress tracking
    const downloadResponse = await fetch(tempUrl, { headers });

    if (!downloadResponse.ok) {
      throw new Error(`Failed to download ${cloudFile.name}`);
    }

    const contentLength = downloadResponse.headers.get('content-length');
    const totalBytes = contentLength ? parseInt(contentLength, 10) : (cloudFile.size || 0);

    // Read response body with progress tracking
    const reader = downloadResponse.body.getReader();
    const chunks = [];
    let receivedBytes = 0;
    let lastProgressUpdate = 0;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();

      if (done) break;

      chunks.push(value);
      receivedBytes += value.length;

      // Throttle progress updates to every 100ms
      const now = Date.now();
      if (now - lastProgressUpdate > 100) {
        lastProgressUpdate = now;
        if (totalBytes > 0) {
          const downloadProgress = Math.round((receivedBytes / totalBytes) * 50); // Use 50% for download
          onProgress(id, downloadProgress, receivedBytes, totalBytes * 2); // Double size for download+upload
        }
      }
    }

    // Combine chunks into blob
    const blob = new Blob(chunks);

    // Determine MIME type: Google Drive has mimeType, Dropbox uses fileType + extension
    let mimeType = cloudFile.mimeType || blob.type || '';
    if (!mimeType || mimeType === 'application/octet-stream') {
      const ext = (cloudFile.name || '').split('.').pop().toLowerCase();
      const mimeMap = {
        mp4: 'video/mp4',
        mov: 'video/quicktime',
        avi: 'video/x-msvideo',
        mkv: 'video/x-matroska',
        webm: 'video/webm',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        webp: 'image/webp',
        heic: 'image/heic',
      };
      mimeType = mimeMap[ext] || 'application/octet-stream';
    }

    // Create a File object from blob
    const fileObject = new File([blob], cloudFile.name, { type: mimeType });

    // Return both file and tempUrl so caller can use tempUrl for preview
    return { file: fileObject, tempUrl };
  };

  // Upload a single file to S3
  const uploadFileToS3 = useCallback(async (fileData, onProgress) => {
    let { file } = fileData;
    const { id, cloudFile, source, type, name: albumName, photos } = fileData;

    try {
      // Handle album upload - upload each photo to S3 via gift upload session (like videos, 1 part per photo)
      // Album creation happens after payment via createAndAttachAlbum API
      if (type === 'album' && photos && photos.length > 0) {
        const totalSize = photos.reduce((sum, p) => sum + p.size, 0);
        let totalUploaded = 0;
        const photoS3Data = []; // Collect S3 paths for post-payment createAndAttachAlbum

        console.log('[GIFT_FLOW] Uploading', photos.length, 'photos to S3 individually');

        // Upload each photo to S3 via gift upload session (1 part per photo since photos are small)
        for (let i = 0; i < photos.length; i += 1) {
          const photo = photos[i];
          try {
            // If cloud photo (no local file), download it first
            let photoFile = photo.file;
            if (!photoFile && photo.cloudFile) {
              console.log('[GIFT_FLOW] Downloading cloud photo:', photo.name);
              const downloadResult = await downloadCloudFile({ cloudFile: photo.cloudFile, source: photo.cloudSource || source, id }, () => {});
              photoFile = downloadResult.file;
              // Store tempUrl in photo object for preview
              if (downloadResult.tempUrl) {
                setSelectedFiles((prev) => prev.map((f) => {
                  if (f.id === id && f.type === 'album' && f.photos) {
                    return {
                      ...f,
                      photos: f.photos.map((p) => (p.id === photo.id ? { ...p, previewUrl: downloadResult.tempUrl } : p)),
                    };
                  }
                  return f;
                }));
                // Also update the album modal if it's open showing this album
                setStep3AlbumModal((prev) => {
                  if (prev && prev.upload?.id === id) {
                    return {
                      ...prev,
                      photos: prev.photos?.map((p) => (p.id === photo.id ? { ...p, previewUrl: downloadResult.tempUrl } : p)),
                    };
                  }
                  return prev;
                });
              }
            }
            if (!photoFile) {
              console.error('[GIFT_FLOW] No file for photo:', photo.name);
              throw new Error('No file available for photo');
            }

            // Step 1: Create gift upload session for this photo
            const session = await createGiftUploadSession(giftUserUid, {
              file_name: photo.name,
              file_size: photoFile.size || photo.size,
              content_type: photoFile.type || 'image/jpeg',
            });

            const presignedUrl = session.presigned_urls?.[0]?.presigned_url;
            if (!presignedUrl) {
              console.error('[GIFT_FLOW] No presigned URL for photo:', photo.name);
              logAppEvent({
                userName: 'Gift User',
                functionAffected: 'Gift Upload',
                description: `Gift album photo session returned no presigned URL — photo: "${photo.name}", album: "${albumName}", photoIndex: ${i + 1}/${photos.length}, giftUuid: ${giftUserUid}`,
                severity: 'high',
                errorMessage: 'No presigned URL returned from session',
                component: 'giftFlowUpload',
                userAction: `uploading album photo: ${photo.name}`,
              });
            } else {
              // Capture current totalUploaded for progress callback (avoids no-loop-func)
              const uploadedSoFar = totalUploaded;

              // Step 2: Upload the photo file (1 part)
              const partResult = await uploadPartWithPresignedUrl(
                presignedUrl,
                photoFile,
                1,
                (loaded, total) => {
                  const photoProgress = loaded / total;
                  const overallUploaded = uploadedSoFar + (photo.size * photoProgress);
                  const progressPercent = Math.round((overallUploaded / totalSize) * 95);
                  onProgress(id, progressPercent, overallUploaded, totalSize);
                }
              );

              // Step 3: Complete the upload session
              const completeResponse = await completeGiftUploadSession(session.id, giftUserUid, [partResult]);

              const s3Key = completeResponse?.s3_key || completeResponse?.file_key || session.s3_key || session.file_key;

              photoS3Data.push({
                path: s3Key,
                size: String(photo.size),
                name: photo.name,
              });

              totalUploaded += photo.size;
              const progressPercent = Math.round((totalUploaded / totalSize) * 95);
              onProgress(id, progressPercent, totalUploaded, totalSize);
              console.log('[GIFT_FLOW] Photo uploaded to S3:', photo.name, 'key:', s3Key);
            }
          } catch (photoErr) {
            console.error('[GIFT_FLOW] Photo S3 upload error:', photo.name, photoErr);
            logAppEvent({
              userName: 'Gift User',
              functionAffected: 'Gift Upload',
              description: `Gift album photo upload failed — photo: "${photo.name}", album: "${albumName}", photoIndex: ${i + 1}/${photos.length}, giftUuid: ${giftUserUid}`,
              severity: 'high',
              errorMessage: photoErr?.message || 'Photo upload failed',
              errorStack: photoErr?.stack || null,
              component: 'giftFlowUpload',
              userAction: `uploading album photo: ${photo.name}`,
            });
          }
        }

        // Store photo metadata for post-payment createAndAttachAlbum call
        photoMetadataRef.current.set(id, {
          albumName,
          photos: photoS3Data,
        });

        onProgress(id, 100, totalSize, totalSize);
        console.log('[GIFT_FLOW] Album S3 upload complete:', albumName, 'photos:', photoS3Data.length);
        return { success: true, photoCount: photoS3Data.length };
      }

      // Handle individual photo (type='photo') - upload to S3 and store for createAndAttachAlbum
      if (type === 'photo' && file) {
        const photoS3Data = [];
        try {
          const session = await createGiftUploadSession(giftUserUid, {
            file_name: file.name,
            file_size: file.size,
            content_type: file.type || 'image/jpeg',
          });
          const presignedUrl = session.presigned_urls?.[0]?.presigned_url;
          if (!presignedUrl) {
            logAppEvent({
              userName: 'Gift User',
              functionAffected: 'Gift Upload',
              description: `Gift single photo session returned no presigned URL — photo: "${file.name}", giftUuid: ${giftUserUid}`,
              severity: 'high',
              errorMessage: 'No presigned URL returned from session',
              component: 'giftFlowUpload',
              userAction: `uploading single photo: ${file.name}`,
            });
          }
          if (presignedUrl) {
            const partResult = await uploadPartWithPresignedUrl(
              presignedUrl, file, 1,
              (loaded, total) => onProgress(id, Math.round((loaded / total) * 95), loaded, total)
            );
            const completeResponse = await completeGiftUploadSession(session.id, giftUserUid, [partResult]);
            const s3Key = completeResponse?.s3_key || completeResponse?.file_key || session.s3_key || session.file_key;
            if (s3Key) {
              photoS3Data.push({ path: s3Key, size: String(file.size), name: file.name });
            }
          }
        } catch (photoErr) {
          console.error('[GIFT_FLOW] Single photo upload error:', file.name, photoErr);
          logAppEvent({
            userName: 'Gift User',
            functionAffected: 'Gift Upload',
            description: `Gift single photo upload failed — photo: "${file.name}", giftUuid: ${giftUserUid}`,
            severity: 'high',
            errorMessage: photoErr?.message || 'Single photo upload failed',
            errorStack: photoErr?.stack || null,
            component: 'giftFlowUpload',
            userAction: `uploading single photo: ${file.name}`,
          });
        }
        photoMetadataRef.current.set(id, { albumName: albumName || file.name, photos: photoS3Data });
        onProgress(id, 100, file.size, file.size);
        return { success: true, photoCount: photoS3Data.length };
      }

      // If it's a cloud file (has cloudFile but no file), download it first
      if (cloudFile && !file) {
        const downloadResult = await downloadCloudFile(fileData, onProgress);
        file = downloadResult.file;
        // Store tempUrl for video preview
        if (downloadResult.tempUrl) {
          setSelectedFiles((prev) => prev.map((f) => (f.id === id ? { ...f, previewUrl: downloadResult.tempUrl } : f)));
        }
      }

      // For videos, generate thumbnails first (same as main app)
      let thumbnailData = null;
      let thumbnailBase64 = null;
      let thumbnailsBinary = [];
      const videoTitle = file.name.replace(/\.[^/.]+$/, ''); // Remove extension

      if (type === 'video') {
        console.log('[GIFT_FLOW] Generating thumbnails for video:', file.name);

        try {
          thumbnailData = await generateThumbnailsFromVideo(file);
          console.log('[THUMB_DEBUG] generateThumbnailsFromVideo result:', {
            fileName: file.name,
            frameCount: thumbnailData?.thumbnails?.length || 0,
            firstFrameType: thumbnailData?.thumbnails?.[0] ? thumbnailData.thumbnails[0].substring(0, 30) : 'none',
            videoHeight: thumbnailData?.videoHeight,
            videoWidth: thumbnailData?.videoWidth,
            videoDuration: thumbnailData?.videoDuration,
          });
        } catch (thumbGenErr) {
          console.warn('[THUMB_DEBUG] generateThumbnailsFromVideo FAILED:', thumbGenErr);
          thumbnailData = { thumbnails: [image04], videoHeight: 0, videoWidth: 0, videoDuration: 0 };
        }

        // If generation succeeded, update posterUrl in selectedFiles so step 3/5
        // display matches the thumbnail sent to backend for registration
        const firstThumb = thumbnailData?.thumbnails?.[0];
        if (firstThumb && firstThumb !== image04 && firstThumb.startsWith('data:')) {
          setSelectedFiles((prev) => prev.map((f) => (f.name === file.name && f.type === 'video' ? { ...f, posterUrl: firstThumb } : f)));
        }

        // If generateThumbnailsFromVideo failed (returned fallback), try to use the
        // posterUrl from captureVideoFrame (which may have succeeded for the same video)
        const usedFallback = thumbnailData?.thumbnails?.length > 0 && thumbnailData.thumbnails[0] === image04;
        if (usedFallback) {
          const matchedSelected = selectedFilesRef.current.find((f) => f.name === file.name && f.posterUrl);
          if (matchedSelected?.posterUrl && matchedSelected.posterUrl.startsWith('data:')) {
            console.log('[THUMB_DEBUG] generateThumbnailsFromVideo used fallback, substituting posterUrl from captureVideoFrame');
            thumbnailData = { ...thumbnailData, thumbnails: [matchedSelected.posterUrl] };
          } else {
            // Both captureVideoFrame and generateThumbnailsFromVideo failed — try backend API
            console.warn('[THUMB_DEBUG] Both client methods failed for:', file.name, '— trying backend API fallback');
            try {
              const videoFilePiece = new FormData();
              videoFilePiece.append('video_file', file.slice(0, 5 * 1024 * 1024)); // First 5MB
              const backendResp = await fetch('https://conf.eq7.in/generate-thumbnail/', {
                method: 'POST',
                body: videoFilePiece,
              });
              if (backendResp.ok) {
                const backendData = await backendResp.json();
                if (backendData?.thumbnail) {
                  const backendThumbUrl = `data:image/png;base64,${backendData.thumbnail}`;
                  console.log('[THUMB_DEBUG] Backend API fallback SUCCESS for:', file.name);
                  thumbnailData = { ...thumbnailData, thumbnails: [backendThumbUrl] };
                  setSelectedFiles((prev) => prev.map((f) => (f.name === file.name && f.type === 'video' ? { ...f, posterUrl: backendThumbUrl } : f)));
                } else {
                  console.warn('[THUMB_DEBUG] Backend API returned no thumbnail for:', file.name, '— using projector logo');
                  setSelectedFiles((prev) => prev.map((f) => (f.name === file.name && f.type === 'video' ? { ...f, posterUrl: image04 } : f)));
                }
              } else {
                console.error('[THUMB_DEBUG] Backend API failed with status:', backendResp.status, 'for:', file.name);
                setSelectedFiles((prev) => prev.map((f) => (f.name === file.name && f.type === 'video' ? { ...f, posterUrl: image04 } : f)));
              }
            } catch (backendErr) {
              console.error('[THUMB_DEBUG] Backend API fallback error for:', file.name, backendErr);
              setSelectedFiles((prev) => prev.map((f) => (f.name === file.name && f.type === 'video' ? { ...f, posterUrl: image04 } : f)));
            }
          }
        }

        try {
          const { thumbnails = [], videoHeight = 0, videoWidth = 0 } = thumbnailData || {};

          if (thumbnails.length > 0) {
            // Get main thumbnail (first one) - same as main app
            const selectedThumbnailSrc = thumbnails[0];

            if (selectedThumbnailSrc === image04) {
              // Fallback image - convert to base64
              const response = await fetch(image04);
              const thumbnailBlob = await response.blob();
              thumbnailBase64 = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.readAsDataURL(thumbnailBlob);
              });
            } else if (selectedThumbnailSrc && selectedThumbnailSrc.startsWith('data:')) {
              thumbnailBase64 = selectedThumbnailSrc;
            } else if (selectedThumbnailSrc && selectedThumbnailSrc.startsWith('blob:')) {
              thumbnailBase64 = await convertBlobUrlToBase64(selectedThumbnailSrc);
            }

            // Convert all thumbnails to binary (same as main app)
            thumbnailsBinary = await Promise.all(
              thumbnails.map(async (thumbnail) => {
                try {
                  if (thumbnail === image04) {
                    const response = await fetch(image04);
                    const thumbnailBlob = await response.blob();
                    const base64 = await new Promise((resolve) => {
                      const reader = new FileReader();
                      reader.onload = () => resolve(reader.result);
                      reader.readAsDataURL(thumbnailBlob);
                    });
                    return base64ToBinary(base64);
                  }
                  if (!thumbnail) return null;
                  let base64 = thumbnail;
                  if (thumbnail.startsWith('blob:')) {
                    base64 = await convertBlobUrlToBase64(thumbnail);
                  }
                  return base64ToBinary(base64);
                } catch (error) {
                  console.error('[GIFT_FLOW] Error converting thumbnail:', error);
                  return null;
                }
              })
            );

            // Filter out nulls
            thumbnailsBinary = thumbnailsBinary.filter((t) => t !== null);
          }

          console.log('[GIFT_FLOW] Thumbnails generated:', thumbnails.length, 'Binary:', thumbnailsBinary.length);
        } catch (thumbErr) {
          console.error('[GIFT_FLOW] Thumbnail generation failed:', thumbErr);
          // Use fallback
          thumbnailData = { thumbnails: [image04], videoHeight: 0, videoWidth: 0, videoDuration: 0 };
        }
      }

      // Use secure upload system (same as main app)
      const draftId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      console.log('[GIFT_FLOW] Starting secure upload for:', file.name, 'draftId:', draftId);

      // Store video metadata for post-payment registration
      console.log('[THUMB_DEBUG] Storing metadata for draftId:', draftId, {
        fileName: file.name,
        videoTitle,
        hasThumbnailBase64: !!thumbnailBase64,
        thumbnailBase64Type: thumbnailBase64 ? thumbnailBase64.substring(0, 30) : 'null',
        thumbnailsBinaryCount: thumbnailsBinary.length,
        thumbnailDataFrames: thumbnailData?.thumbnails?.length || 0,
        videoHeight: thumbnailData?.videoHeight,
        videoWidth: thumbnailData?.videoWidth,
      });
      videoMetadataRef.current.set(draftId, {
        videoTitle,
        thumbnailBase64,
        thumbnailsBinary,
        thumbnailData,
        file,
      });

      // Store secureUploadId in upload state so pause/resume/cancel can reference it
      // Don't force UPLOADING status — secure manager subscription will set the correct status
      // (PENDING if queued, UPLOADING if active — matching main app behavior)
      setUploads((prev) => prev.map((u) => (u.id === id
        ? { ...u, secureUploadId: draftId }
        : u)));

      // Wrap in a Promise so uploadFileToS3 can await it
      const result = await new Promise((resolveUpload) => {
        uploadLargeFile({
          userId: giftUserUid || 'anonymous',
          file,
          draftId,
          thumbnails: thumbnailsBinary,
          suppressToast: true,
          giftUuid: giftUserUid, // Gift flow: use gift APIs, S3 upload only (no registration)
          onProgress: (progress, totalUploaded, totalBytes) => {
            // Bridge secure manager progress to gift flow progress format
            let progressPercent = Math.round(progress * 0.9); // 0-90% for upload
            if (cloudFile && !fileData.file) {
              progressPercent = 50 + Math.round(progress * 0.4); // 50-90% for cloud files
            }
            onProgress(id, progressPercent, totalUploaded, totalBytes);
          },
          // Gift flow: onSuccess is NOT called by upload manager (skipped for gift uploads).
          // Registration (registerDocument + addVideo) happens after payment in registerGiftVideos().
          onSuccess: null,
          onError: (error) => {
            console.error('[GIFT_FLOW] Secure upload error:', error);
            logAppEvent({
              userName: 'Gift User',
              functionAffected: 'Gift Upload',
              description: `Gift video secure upload error — file: "${file.name}", giftUuid: ${giftUserUid}`,
              severity: 'high',
              errorMessage: error?.message || 'Secure upload failed',
              errorStack: error?.stack || null,
              component: 'giftFlowUpload',
              userAction: `uploading gift video: ${file.name}`,
            });
            resolveUpload({ success: false, error: error?.message || 'Upload failed' });
          },
          updateUploadPercentages: null, // Gift flow has its own progress UI
        });

        // For gift flow, the upload manager marks status as S3_COMPLETE (not COMPLETED)
        // and does not call onSuccess. Wait for S3_COMPLETE before resolving.
        let pollCount = 0;
        const checkInterval = setInterval(() => {
          const taskStatus = uploadManagerSecure.getUploadStatus(draftId);
          pollCount += 1;
          if (taskStatus === 's3_complete' || taskStatus === 'completed') {
            clearInterval(checkInterval);
            resolveUpload({ success: true, draftId });
          } else if (taskStatus === 'failed' || taskStatus === 'cancelled') {
            clearInterval(checkInterval);
            resolveUpload({ success: false, error: 'Upload failed or cancelled' });
          } else if (taskStatus === null && pollCount > 10) {
            // Task removed from manager (auto-hide) — check local uploads state
            const localUpload = uploadsRef.current.find((u) => u.secureUploadId === draftId);
            if (localUpload && localUpload.status === UPLOAD_STATUS.COMPLETED) {
              clearInterval(checkInterval);
              resolveUpload({ success: true, draftId });
            } else if (!localUpload || pollCount > 60) {
              // Task gone from both manager and local state, or polling too long (30s)
              clearInterval(checkInterval);
              resolveUpload({ success: true, draftId }); // Assume success if it was removed
            }
          }
        }, 500);
      });

      return result;
    } catch (err) {
      console.error('[GIFT_FLOW] Upload error:', err);
      logAppEvent({
        userName: 'Gift User',
        functionAffected: 'Gift Upload',
        description: `Gift upload failed — file: "${file?.name || fileData?.name || 'unknown'}", type: ${type || 'unknown'}, giftUuid: ${giftUserUid}`,
        severity: 'high',
        errorMessage: err?.message || 'Gift upload failed',
        errorStack: err?.stack || null,
        component: 'giftFlowUpload',
        userAction: `uploading gift file: ${file?.name || fileData?.name || 'unknown'}`,
      });
      return { success: false, error: err.message };
    }
  }, [giftUserUid]);

  // Pause upload handler
  const handlePauseUpload = useCallback((uploadId) => {
    console.log('[GIFT_FLOW] Pausing upload:', uploadId);
    const upload = uploadsRef.current.find((u) => u.id === uploadId);
    if (upload?.secureUploadId) {
      uploadManagerSecure.pauseUpload(upload.secureUploadId);
    }
    setUploads((prev) => prev.map((u) => (u.id === uploadId && u.status === UPLOAD_STATUS.UPLOADING
      ? { ...u, status: UPLOAD_STATUS.PAUSED, isPaused: true }
      : u)));
  }, []);

  // Resume upload handler
  const handleResumeUpload = useCallback((uploadId) => {
    console.log('[GIFT_FLOW] Resuming upload:', uploadId);
    const upload = uploadsRef.current.find((u) => u.id === uploadId);
    if (!upload) {
      console.error('[GIFT_FLOW] Cannot resume: upload not found');
      return;
    }

    if (upload.status !== UPLOAD_STATUS.PAUSED && upload.status !== UPLOAD_STATUS.NETWORK_ERROR) {
      console.error('[GIFT_FLOW] Cannot resume: upload is not paused. Status:', upload.status);
      return;
    }

    if (networkStatus === 'offline') {
      console.error('[GIFT_FLOW] Cannot resume: No network connection');
      return;
    }

    // Delegate to secure manager
    if (upload.secureUploadId) {
      uploadManagerSecure.resumeUpload(upload.secureUploadId);
    }

    setIsUploading(true);
    setUploads((prev) => prev.map((u) => (u.id === uploadId
      ? { ...u, status: UPLOAD_STATUS.UPLOADING, isPaused: false }
      : u)));
  }, [networkStatus]);

  // Cancel upload handler
  const handleCancelUpload = useCallback(async (uploadId) => {
    console.log('[GIFT_FLOW] Cancelling upload:', uploadId);
    const upload = uploadsRef.current.find((u) => u.id === uploadId);
    if (!upload) return;

    // For mobile uploads: batch cancel push to mobile and remove matching preview file
    if (upload.isMobileUpload) {
      console.log('[GIFT_FLOW] Cancelling mobile upload:', upload.fileName, 'mobileUid:', upload.mobileUid);

      // Batch cancel file names (name|uid format) — send one push with all cancels after a short delay
      const cancelEntry = upload.mobileUid ? `${upload.fileName}|${upload.mobileUid}` : upload.fileName;
      pendingCancelsRef.current.push(cancelEntry);
      if (cancelBatchTimerRef.current) {
        clearTimeout(cancelBatchTimerRef.current);
      }
      cancelBatchTimerRef.current = setTimeout(async () => {
        const filesToCancel = [...pendingCancelsRef.current];
        pendingCancelsRef.current = [];
        cancelBatchTimerRef.current = null;
        try {
          await sendMobilePushMessage('cancelUpload', filesToCancel);
        } catch (err) {
          console.error('[GIFT_FLOW] Failed to send cancelUpload push:', err);
        }
      }, 300);

      // Mark as cancelled
      setUploads((prev) => prev.map((u) => (u.id === uploadId
        ? { ...u, status: UPLOAD_STATUS.CANCELLED }
        : u)));

      // Remove from uploads and also remove matching preview file from selectedFiles by mobileUid (or name fallback)
      const cancelUid = upload.mobileUid;
      setTimeout(() => {
        setUploads((prev) => prev.filter((u) => u.id !== uploadId));
        setSelectedFiles((prev) => prev.filter((f) => {
          if (!f.isPreview) return true;
          if (cancelUid && f.mobileUid) return f.mobileUid !== cancelUid;
          return f.name !== upload.fileName;
        }));
      }, 1000);
      return;
    }

    // Delegate to secure manager for desktop uploads
    if (upload.secureUploadId) {
      uploadManagerSecure.cancelUpload(upload.secureUploadId);
    }

    // MOBILE canceling its own uploads: notify desktop so canceled files are removed from web UI
    if (isMobileUploadMode && giftUserUid) {
      // Remove from completedFilesRef so canceled file isn't included in subsequent fileComplete messages
      completedFilesRef.current = completedFilesRef.current.filter((entry) => {
        const pipeIdx = entry.lastIndexOf('|');
        const entryName = pipeIdx > 0 ? entry.substring(0, pipeIdx) : entry;
        const entryUid = pipeIdx > 0 ? entry.substring(pipeIdx + 1) : null;
        if (upload.secureUploadId && entryUid === upload.secureUploadId) return false;
        if (entryName === upload.fileName) return false;
        return true;
      });

      // Send cancelUpload push to desktop (batch similar to isMobileUpload case above)
      const cancelEntry = upload.secureUploadId ? `${upload.fileName}|${upload.secureUploadId}` : upload.fileName;
      pendingCancelsRef.current.push(cancelEntry);

      if (cancelBatchTimerRef.current) {
        clearTimeout(cancelBatchTimerRef.current);
      }
      cancelBatchTimerRef.current = setTimeout(async () => {
        const filesToCancel = [...pendingCancelsRef.current];
        pendingCancelsRef.current = [];
        cancelBatchTimerRef.current = null;
        try {
          await sendMobilePushMessage('cancelUpload', filesToCancel);
          console.log('[MOBILE_FLOW] Sent cancelUpload to desktop for:', filesToCancel);
        } catch (err) {
          console.error('[GIFT_FLOW] Failed to send cancelUpload push:', err);
        }
      }, 300);
    }

    // Mark as cancelled
    setUploads((prev) => prev.map((u) => (u.id === uploadId
      ? { ...u, status: UPLOAD_STATUS.CANCELLED }
      : u)));

    // Remove from progress list and selected files after a brief delay so user sees "Cancelled" status
    setTimeout(() => {
      setUploads((prev) => prev.filter((u) => u.id !== uploadId));
      setSelectedFiles((prev) => prev.filter((f) => f.id !== uploadId));
    }, 1000);

    // Remove from queued files
    queuedFileIdsRef.current.delete(uploadId);
  }, [sendMobilePushMessage, isMobileUploadMode, giftUserUid]);

  // Start uploading all selected files (or just navigate if already uploading from step 3)
  const handleStartUpload = useCallback(async () => {
    // Filter out files that have already been queued/uploaded and preview files from mobile
    const filesToUpload = selectedFiles.filter((f) => !queuedFileIdsRef.current.has(f.id) && !f.isPreview);

    if (selectedFiles.length === 0) {
      setError('Please select at least one file to upload');
      return;
    }

    // If all files are already queued (auto-uploaded from step 3), just navigate
    // Start uploads in background immediately, show payment page
    // Uploads use gift UUID (no token needed) — registration happens after payment
    if (selectedChoice === 'you-help-build' && !isMobileUploadMode) {
      try {
        const statusRes = await getGiftStatus(giftUserUid);
        if (statusRes?.data?.data?.payment_complete === '1') {
          console.log('[GIFT_FLOW] Payment already complete, skipping to upload progress');
          animateToStep(5);
        } else {
          // Show payment page — uploads start in background below
          animateToStep(4);
        }
      } catch (err) {
        console.warn('[GIFT_FLOW] Failed to check payment status, showing payment page');
        animateToStep(4);
      }
    } else {
      // Mobile QR users or other flows — go to upload progress
      animateToStep(5);
    }

    setError('');

    // If all files already queued from auto-upload on step 3, skip re-initialization
    if (filesToUpload.length === 0) return;

    setIsUploading(true);

    // Initialize upload progress only for new files
    console.log('[GIFT_FLOW] Initializing uploads for', filesToUpload.length, 'files');
    const newUploads = filesToUpload.map((f) => {
      // Store File object in ref (File objects don't serialize well in state)
      console.log('[GIFT_FLOW] Storing fileData in ref:', {
        id: f.id,
        name: f.name,
        type: f.type,
        hasFile: !!f.file,
        fileSize: f.file?.size || f.size
      });
      fileDataRef.current.set(f.id, f);

      return {
        id: f.id,
        fileName: f.name,
        fileSize: f.size,
        status: UPLOAD_STATUS.PENDING,
        progress: 0,
        bytesUploaded: 0,
        speed: 0,
        timeRemaining: 0,
        startTime: null,
        lastUpdateTime: null,
        lastBytesUploaded: 0,
        isPaused: false,
        secureUploadId: null, // Set when secure upload starts
      };
    });

    // Preserve existing uploads and add new ones at the top
    // Keep all existing uploads (some may still be uploading from auto-upload on step 3)
    setUploads((prev) => [...newUploads, ...prev]);

    console.log('[GIFT_FLOW] After initialization:');
    console.log('[GIFT_FLOW] - fileDataRef size:', fileDataRef.current.size);
    console.log('[GIFT_FLOW] - fileDataRef keys:', Array.from(fileDataRef.current.keys()));

    // Mark these files as queued to prevent duplicate uploads
    filesToUpload.forEach((f) => queuedFileIdsRef.current.add(f.id));

    // Separate videos and albums (they use different upload paths)
    const videoFiles = filesToUpload.filter((f) => f.type === 'video');
    const albumFiles = filesToUpload.filter((f) => f.type !== 'video');

    const allPromises = [];

    // Videos: queue with concurrency limit — only upload when status changes to UPLOADING
    if (videoFiles.length > 0) {
      const videoPromise = new Promise((resolveAllVideos) => {
        const videoQueue = [...videoFiles];
        const activeVideos = new Set();
        const videoTracker = { completed: 0 };

        const onVideoProgress = (id, progress, bytesUploaded, totalBytes) => {
          setUploads((prev) => prev.map((u) => {
            if (u.id !== id) return u;
            const safeProgress = Math.max(progress, u.progress || 0);
            const safeBytesUploaded = Math.max(bytesUploaded, u.bytesUploaded || 0);
            const now = Date.now();
            const timeDiff = (now - (u.lastUpdateTime || now)) / 1000;
            const bytesDiff = safeBytesUploaded - (u.lastBytesUploaded || 0);
            const speed = timeDiff > 0 && bytesDiff > 0 ? bytesDiff / timeDiff : (u.speed || 0);
            const remaining = totalBytes - safeBytesUploaded;
            const timeRemaining = speed > 0 ? remaining / speed : 0;
            return { ...u, progress: safeProgress, bytesUploaded: safeBytesUploaded, speed, timeRemaining, lastUpdateTime: now, lastBytesUploaded: safeBytesUploaded };
          }));
        };

        // eslint-disable-next-line no-inner-declarations
        function processVideoQueue(completedFileData, completedResult) {
          if (completedFileData) {
            setUploads((prev) => prev.map((u) => {
              if (u.id !== completedFileData.id) return u;
              if (u.status === UPLOAD_STATUS.COMPLETED && !completedResult.success) return u;
              return {
                ...u,
                status: completedResult.success ? UPLOAD_STATUS.COMPLETED : UPLOAD_STATUS.FAILED,
                progress: completedResult.success ? 100 : u.progress,
                error: completedResult.error,
              };
            }));

            if (isMobileUploadMode && giftUserUid && completedResult.success) {
              // Include S3 URL so desktop can show video thumbnail immediately
              let fileMeta = null;
              if (completedResult.draftId) {
                const mgr = uploadManagerSecure.getUpload(completedResult.draftId);
                if (mgr?.s3Key) {
                  fileMeta = { s3Url: `${generateS3Url()}${mgr.s3Key}` };
                }
              }
              // Add to cumulative list and send ALL completed files (prevents message overwrite loss)
              const fileEntry = `${completedFileData.name}|${completedFileData.id}`;
              if (!completedFilesRef.current.includes(fileEntry)) {
                completedFilesRef.current.push(fileEntry);
              }
              sendMobilePushMessage('fileComplete', [...completedFilesRef.current], null, null, fileMeta);
            }

            activeVideos.delete(completedFileData.id);
            videoTracker.completed += 1;
            if (videoTracker.completed === videoFiles.length) {
              resolveAllVideos();
            }
          }

          while (activeVideos.size < MAX_CONCURRENT_UPLOADS && videoQueue.length > 0) {
            const fileData = videoQueue.shift();
            activeVideos.add(fileData.id);

            setUploads((prev) => prev.map((u) => (u.id === fileData.id
              ? { ...u, status: UPLOAD_STATUS.UPLOADING, startTime: Date.now(), lastUpdateTime: Date.now() }
              : u)));

            uploadFileToS3(fileData, onVideoProgress)
              .then((result) => processVideoQueue(fileData, result));
          }
        }

        processVideoQueue();
      });
      allPromises.push(videoPromise);
    }

    // Albums/photos: queue with concurrency limit (max 3) since they bypass secure manager
    if (albumFiles.length > 0) {
      const albumPromise = new Promise((resolveAll) => {
        const queue = [...albumFiles];
        const activeAlbums = new Set();
        const tracker = { completed: 0 };

        // Single function handles both starting uploads and picking next from queue
        // eslint-disable-next-line no-inner-declarations
        function processAlbumQueue(completedFileData, completedResult) {
          // Handle completion of a finished upload (if called with args)
          if (completedFileData) {
            setUploads((prev) => prev.map((u) => {
              if (u.id !== completedFileData.id) return u;
              if (u.status === UPLOAD_STATUS.COMPLETED && !completedResult.success) return u;
              return {
                ...u,
                status: completedResult.success ? UPLOAD_STATUS.COMPLETED : UPLOAD_STATUS.FAILED,
                progress: completedResult.success ? 100 : u.progress,
                error: completedResult.error,
              };
            }));

            // Notify desktop that this file has completed uploading (cumulative)
            // Skip individual album entries - we'll send "All photos (N)" when all photos are done
            const isAlbumEntry = completedFileData.type === 'album' || completedFileData.id?.startsWith('album-');
            if (isMobileUploadMode && giftUserUid && completedResult.success && !isAlbumEntry) {
              const fileEntry = `${completedFileData.name}|${completedFileData.id}`;
              if (!completedFilesRef.current.includes(fileEntry)) {
                completedFilesRef.current.push(fileEntry);
              }
              sendMobilePushMessage('fileComplete', [...completedFilesRef.current]);
            }

            activeAlbums.delete(completedFileData.id);
            tracker.completed += 1;

            if (tracker.completed === albumFiles.length) {
              // Notify desktop that the entire album is complete (cumulative)
              // Count actual photos uploaded, not album wrapper entries
              const photoCount = albumFiles.reduce((count, f) => {
                if (f.type === 'album' && f.photos) return count + f.photos.length;
                return count + 1;
              }, 0);
              if (isMobileUploadMode && giftUserUid) {
                const albumEntry = `All photos (${photoCount})|album-complete-${Date.now()}`;
                if (!completedFilesRef.current.some((e) => e.startsWith('All photos ('))) {
                  completedFilesRef.current.push(albumEntry);
                }
                sendMobilePushMessage('fileComplete', [...completedFilesRef.current]);
              }
              resolveAll();
              return;
            }
          }

          // Start uploads up to concurrency limit
          while (activeAlbums.size < MAX_CONCURRENT_UPLOADS && queue.length > 0) {
            const fileData = queue.shift();
            activeAlbums.add(fileData.id);

            setUploads((prev) => prev.map((u) => (u.id === fileData.id
              ? { ...u, status: UPLOAD_STATUS.UPLOADING, startTime: Date.now(), lastUpdateTime: Date.now() }
              : u)));

            uploadFileToS3(fileData, (id, progress, bytesUploaded, totalBytes) => {
              setUploads((prev) => prev.map((u) => {
                if (u.id !== id) return u;
                const safeProgress = Math.max(progress, u.progress || 0);
                const safeBytesUploaded = Math.max(bytesUploaded, u.bytesUploaded || 0);
                const now = Date.now();
                const timeDiff = (now - (u.lastUpdateTime || now)) / 1000;
                const bytesDiff = safeBytesUploaded - (u.lastBytesUploaded || 0);
                const speed = timeDiff > 0 && bytesDiff > 0 ? bytesDiff / timeDiff : (u.speed || 0);
                const remaining = totalBytes - safeBytesUploaded;
                const timeRemaining = speed > 0 ? remaining / speed : 0;
                return { ...u, progress: safeProgress, bytesUploaded: safeBytesUploaded, speed, timeRemaining, lastUpdateTime: now, lastBytesUploaded: safeBytesUploaded };
              }));
            }).then((result) => processAlbumQueue(fileData, result));
          }
        }

        processAlbumQueue();
      });
      allPromises.push(albumPromise);
    }

    // Wait for all uploads to finish
    await Promise.allSettled(allPromises);

    // Let React process the setUploads state updates before checking ref
    setTimeout(() => {
      const hasActiveOrPausedUploads = uploadsRef.current.some(
        (u) => u.status === UPLOAD_STATUS.UPLOADING || u.status === UPLOAD_STATUS.PAUSED || u.status === UPLOAD_STATUS.PENDING
      );
      if (!hasActiveOrPausedUploads) {
        setIsUploading(false);
        // Notify desktop that ALL uploads are complete
        if (isMobileUploadMode && giftUserUid) {
          buildMobileUploadMetadata().then((meta) => {
            sendMobilePushMessage('allUploadsComplete', [], null, null, meta);
          });
        }
      }
      setContentFetched(false); // Reset so content tab will refresh
      // If payment is already done and photo albums are ready, register them now
      // Skip on mobile — desktop handles registration via metadata push
      if (paymentDoneRef.current && !isMobileUploadMode && photoMetadataRef.current.size > 0 && registerS3CompleteUploadsRef.current) {
        registerS3CompleteUploadsRef.current();
      }
    }, 200);
  }, [selectedFiles, uploadFileToS3, isMobileUploadMode, giftUserUid, sendMobilePushMessage]);

  // Handle uploading newly added files on step 5
  const handleUploadNewFiles = useCallback((newFiles) => {
    if (newFiles.length === 0) return;

    // Filter out files that are already queued to prevent duplicates and preview files from mobile
    const filesToUpload = newFiles.filter((f) => !queuedFileIdsRef.current.has(f.id) && !f.isPreview);
    if (filesToUpload.length === 0) return;

    // Mark files as queued
    filesToUpload.forEach((f) => queuedFileIdsRef.current.add(f.id));

    // Create upload entries
    console.log('[GIFT_FLOW] handleUploadNewFiles: Initializing', filesToUpload.length, 'new files');
    const newUploads = filesToUpload.map((f) => {
      // Store File object in ref for resume support
      console.log('[GIFT_FLOW] Storing new fileData in ref:', { id: f.id, name: f.name });
      fileDataRef.current.set(f.id, f);

      return {
        id: f.id,
        fileName: f.name,
        fileSize: f.size,
        status: UPLOAD_STATUS.PENDING,
        progress: 0,
        bytesUploaded: 0,
        speed: 0,
        timeRemaining: 0,
        startTime: null,
        lastUpdateTime: null,
        lastBytesUploaded: 0,
        isPaused: false,
        secureUploadId: null,
      };
    });

    setUploads((prev) => [...newUploads, ...prev]);
    setIsUploading(true);

    // Separate videos and albums (they use different upload paths)
    const videoFiles = filesToUpload.filter((f) => f.type === 'video');
    const albumFiles = filesToUpload.filter((f) => f.type !== 'video');

    // Helper for progress callback
    const onProgressCallback = (id, progress, bytesUploaded, totalBytes) => {
      setUploads((prev) => prev.map((u) => {
        if (u.id !== id) return u;
        const safeProgress = Math.max(progress, u.progress || 0);
        const safeBytesUploaded = Math.max(bytesUploaded, u.bytesUploaded || 0);
        const now = Date.now();
        const timeDiff = (now - (u.lastUpdateTime || now)) / 1000;
        const bytesDiff = safeBytesUploaded - (u.lastBytesUploaded || 0);
        const speed = timeDiff > 0 && bytesDiff > 0 ? bytesDiff / timeDiff : (u.speed || 0);
        const remaining = totalBytes - safeBytesUploaded;
        const timeRemaining = speed > 0 ? remaining / speed : 0;
        return { ...u, progress: safeProgress, bytesUploaded: safeBytesUploaded, speed, timeRemaining, lastUpdateTime: now, lastBytesUploaded: safeBytesUploaded };
      }));
    };

    const updateFinalStatus = (fileData, result) => {
      setUploads((prev) => prev.map((u) => {
        if (u.id !== fileData.id) return u;
        // Don't overwrite COMPLETED status with FAILED (can happen if polling resolves after subscriber)
        if (u.status === UPLOAD_STATUS.COMPLETED && !result.success) return u;
        return {
          ...u,
          status: result.success ? UPLOAD_STATUS.COMPLETED : UPLOAD_STATUS.FAILED,
          progress: result.success ? 100 : u.progress,
          error: result.error,
        };
      }));

      // Notify desktop that this file has completed uploading (cumulative)
      // Skip album entries - we send "All photos (N)" when all photos are done
      const isAlbumEntry = fileData.type === 'album' || fileData.id?.startsWith('album-');
      if (isMobileUploadMode && giftUserUid && result.success && !isAlbumEntry) {
        let fileMeta = null;
        if (result.draftId) {
          const mgr = uploadManagerSecure.getUpload(result.draftId);
          if (mgr?.s3Key) {
            fileMeta = { s3Url: `${generateS3Url()}${mgr.s3Key}` };
          }
        }
        // Add to cumulative list and send ALL completed files
        const fileEntry = `${fileData.name}|${fileData.id}`;
        if (!completedFilesRef.current.includes(fileEntry)) {
          completedFilesRef.current.push(fileEntry);
        }
        sendMobilePushMessage('fileComplete', [...completedFilesRef.current], null, null, fileMeta);
      }
    };

    const checkAllDone = () => {
      setTimeout(() => {
        const stillUploading = uploadsRef.current.some((u) => u.status === UPLOAD_STATUS.UPLOADING || u.status === UPLOAD_STATUS.PENDING);
        if (!stillUploading) {
          setIsUploading(false);
          setContentFetched(false);
          // Notify desktop that ALL uploads are complete
          if (isMobileUploadMode && giftUserUid) {
            buildMobileUploadMetadata().then((meta) => {
              sendMobilePushMessage('allUploadsComplete', [], null, null, meta);
            });
          }
        }
        // If payment is already done and photo albums are ready, register them now
        // Skip on mobile — desktop handles registration via metadata push
        if (paymentDoneRef.current && !isMobileUploadMode && photoMetadataRef.current.size > 0 && registerS3CompleteUploadsRef.current) {
          registerS3CompleteUploadsRef.current();
        }
      }, 200);
    };

    // Videos: queue with concurrency limit — only start uploading when status changes to UPLOADING
    if (videoFiles.length > 0) {
      const videoQueue = [...videoFiles];
      const activeVideos = new Set();

      // eslint-disable-next-line no-inner-declarations
      function processVideoQueue(completedFileData, completedResult) {
        if (completedFileData) {
          updateFinalStatus(completedFileData, completedResult);
          activeVideos.delete(completedFileData.id);
          checkAllDone();
        }

        while (activeVideos.size < MAX_CONCURRENT_UPLOADS && videoQueue.length > 0) {
          const fileData = videoQueue.shift();
          activeVideos.add(fileData.id);

          setUploads((prev) => prev.map((u) => (u.id === fileData.id
            ? { ...u, status: UPLOAD_STATUS.UPLOADING, startTime: Date.now(), lastUpdateTime: Date.now() }
            : u)));

          uploadFileToS3(fileData, onProgressCallback)
            .then((result) => processVideoQueue(fileData, result));
        }
      }

      processVideoQueue();
    }

    // Albums/photos: queue with concurrency limit (max 3) since they bypass secure manager
    if (albumFiles.length > 0) {
      const queue = [...albumFiles];
      const activeAlbums = new Set();
      let albumCompleted = 0;

      // Single function handles both starting uploads and picking next from queue
      // eslint-disable-next-line no-inner-declarations
      function processAlbumQueue(completedFileData, completedResult) {
        if (completedFileData) {
          updateFinalStatus(completedFileData, completedResult);
          activeAlbums.delete(completedFileData.id);
          albumCompleted += 1;

          // Notify desktop when entire album is done (cumulative)
          // Count actual photos uploaded, not album wrapper entries
          if (albumCompleted === albumFiles.length && isMobileUploadMode && giftUserUid) {
            const photoCount = albumFiles.reduce((count, f) => {
              if (f.type === 'album' && f.photos) return count + f.photos.length;
              return count + 1;
            }, 0);
            const albumEntry = `All photos (${photoCount})|album-complete-${Date.now()}`;
            if (!completedFilesRef.current.some((e) => e.startsWith('All photos ('))) {
              completedFilesRef.current.push(albumEntry);
            }
            sendMobilePushMessage('fileComplete', [...completedFilesRef.current]);
          }

          checkAllDone();
        }

        while (activeAlbums.size < MAX_CONCURRENT_UPLOADS && queue.length > 0) {
          const fileData = queue.shift();
          activeAlbums.add(fileData.id);

          setUploads((prev) => prev.map((u) => (u.id === fileData.id
            ? { ...u, status: UPLOAD_STATUS.UPLOADING, startTime: Date.now(), lastUpdateTime: Date.now() }
            : u)));

          uploadFileToS3(fileData, onProgressCallback)
            .then((result) => processAlbumQueue(fileData, result));
        }
      }

      processAlbumQueue();
    }
  }, [uploadFileToS3, isMobileUploadMode, giftUserUid, sendMobilePushMessage]);

  // Update ref when handleStartUpload changes (for SSE handler access)
  useEffect(() => {
    handleStartUploadRef.current = handleStartUpload;
  }, [handleStartUpload]);

  // Track previous file count for auto-upload detection
  const prevFileCountRef = useRef(selectedFiles.length);

  // Auto-upload new files when on step 3 (file selection) or step 5 (upload progress)
  useEffect(() => {
    if (currentStep !== 3 && currentStep !== 5) return;

    // Get items (albums or files) that haven't been queued yet
    // Treat albums (like "All photos") as single items, not individual photos
    const newItems = selectedFiles.filter((item) => !queuedFileIdsRef.current.has(item.id) && !item.isPreview);

    if (newItems.length > 0) {
      console.log('[GIFT_FLOW] Auto-upload detected', newItems.length, 'new items on step', currentStep);
      setIsUploading(true);
      handleUploadNewFiles(newItems);
    }
  }, [selectedFiles, currentStep, handleUploadNewFiles]);

  const clearAllFlowData = () => {
    setSelectedFiles([]);
    setUploads([]);
    setFormData({ to: '', from: '', email: '', note: '', occasions: [], otherReason: '' });
    setShowPayment(false);
    setPaymentData(null);
    setSelectedPlan(null);
    setPaymentSectionExpanded(false);
    setStep1SubStep(1);
    setPromoFieldTouched(false);
    setPromoInputVisible(false);
    setPromoCodeNew('');
    setPromoResponse(null);
    setPromoActive(false);
    setDiscountedPrice('');
    setCalculatedDiscount('');
  };

  const handleBackClick = () => {
    if (showSuccessSection) {
      // Success page — go back to gift home without alert
      window.location.href = '/gift';
      return;
    }
    if (currentStep === 0) {
      navigate(-1);
    } else if (currentStep === 1) {
      // Handle sub-step navigation for Step 1
      if (step1SubStep === 2) {
        // Go back from short note — show sender inline fields if normal flow
        if (!urlParams.share && !isMobileUploadMode && localStorage.getItem('senderName')) {
          setShowSenderInline(true);
          setStep1SubStep(1);
        } else {
          setStep1SubStep(1);
        }
      } else if (showSenderInline) {
        // Go back from sender inline fields to recipient fields
        setShowSenderInline(false);
      } else {
        // Go back to intro
        animateToStep(0);
      }
    } else if (currentStep === 3) {
      // Mobile upload mode: don't navigate to step 1 (notes flow) — just go back in browser history
      if (isMobileUploadMode) {
        window.history.back();
        return;
      }
      if (uploads.length > 0) {
        // eslint-disable-next-line no-alert
        if (window.confirm('All your uploaded files and progress will be lost. Are you sure you want to go back?')) {
          clearAllFlowData();
          setStep1SubStep(2);
          animateToStep(1);
        }
      } else {
        // No uploads yet — go back to note section (step 1 substep 2)
        setStep1SubStep(2);
        animateToStep(1);
      }
    } else if (currentStep === 4) {
      setShowPayment(false);
      setPaymentData(null);
      setSelectedPlan(null);
      setPaymentSectionExpanded(false);
      animateToStep(3);
    } else if (currentStep === 5) {
      if (uploads.length > 0) {
        // eslint-disable-next-line no-alert
        if (window.confirm('All your uploaded files and progress will be lost. Are you sure you want to go back?')) {
          animateToStep(3);
        }
      } else {
        animateToStep(3);
      }
    } else if (currentStep === 2) {
      // From step 2 (choice), go back to note/occasion section (step1SubStep 2)
      setStep1SubStep(2);
      animateToStep(1);
    } else {
      animateToStep(currentStep - 1);
    }
  };

  // Keep a ref to handleBackClick so the popstate listener always uses the latest version
  const handleBackClickRef = useRef(handleBackClick);
  useEffect(() => { handleBackClickRef.current = handleBackClick; });

  // Whether user has uploads worth protecting (files selected or uploading)
  const hasUploadsToProtect = uploads.length > 0;

  // Warn before refresh/tab-close only when uploads have started — skip on success page or upload complete popup
  useEffect(() => {
    if (!hasUploadsToProtect || showSuccessSection || showMobileUploadCompletePopup) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUploadsToProtect, showSuccessSection, showMobileUploadCompletePopup]);

  // Push browser history entry on step/substep change so browser back button works like app back
  useEffect(() => {
    window.history.pushState({ giftStep: currentStep, subStep: step1SubStep }, '');
  }, [currentStep, step1SubStep]);

  // Handle browser back button — behave like app BACK button
  useEffect(() => {
    const onPopState = () => {
      if (currentStep === 0) {
        // On intro step, allow normal browser back
        return;
      }
      // Mobile upload mode: allow normal browser back (go to previous page, not step 1)
      if (isMobileUploadMode) {
        return;
      }
      // Push a new entry so the next browser-back also stays in the gift flow
      window.history.pushState(null, '');
      handleBackClickRef.current();
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [currentStep, isMobileUploadMode]);

  const handleChoiceClick = (choice) => {
    setSelectedChoice(choice);

    if (choice === 'you-help-build') {
      // "You Help Build" goes to upload section (Step 3) first
      animateToStep(3);
    } else {
      // "They Build" goes directly to payment (Step 4)
      animateToStep(4);
    }
  };

  // Handle form input changes
  // Auto-capitalize first letter of each word for name fields
  const capitalizeWords = (str) => str.replace(/\b\w/g, (char) => char.toUpperCase());

  const handleInputChange = (field, value) => {
    const processedValue = field === 'firstname' ? capitalizeWords(value) : value;
    setFormData((prev) => ({ ...prev, [field]: processedValue }));
    if (error) setError('');
  };

  // Generate personalized note based on occasion (will be shown on last screen)
  const generateNoteForOccasion = (occasion, recipientName) => {
    const noteTemplates = {
      'Wedding': `${recipientName}, as you begin this beautiful chapter together, I wanted to gift you something to hold onto forever. From the first dance to the last toast, this gift will help you preserve every magical moment. Cheers to your happily ever after!`,
      'New Baby': `Welcome to parenthood, ${recipientName}! Those tiny fingers, first smiles, and midnight giggles go by so fast. This gift will help you freeze time and treasure every little milestone. Can't wait to watch your family grow!`,
      'Anniversary': `${recipientName}, another year of love in the books! From where it all began to where you are now, your journey deserves to be celebrated. I'm gifting you a way to keep building your beautiful story together!`,
      'Celebration of Life': `${recipientName}, some people leave footprints on our hearts that last forever. This gift will help you keep those precious memories close, turning moments into lasting tributes that honor a life well lived.`,
      'Vacation Recap': `${recipientName}, adventures are best when they're remembered! From breathtaking views to spontaneous detours, every trip tells a story. This gift lets you relive those incredible moments whenever wanderlust calls!`,
      'Preserving Memories': `${recipientName}, memories are the threads that weave our lives together. Whether it's old photos, family videos, or moments yet to come, this gift will help you keep your story alive for generations.`,
      'Birthday Party': `Happy Birthday, ${recipientName}! Another trip around the sun calls for a celebration worth remembering. This gift will help you capture every moment - from the candles to the cake, the laughter to the surprises!`,
      'Family Storytelling': `${recipientName}, every family has stories that deserve to be told and retold. The traditions, the inside jokes, the moments that made you who you are. This gift will help keep your legacy alive!`,
    };

    return noteTemplates[occasion] || `Dear ${recipientName}, I hope you enjoy this special gift! May it help you preserve and cherish your most precious memories.`;
  };

  // Generate merged note from multiple selected occasions
  const generateMergedNote = (occasions, recipientName) => {
    if (occasions.length === 0) return '';
    if (occasions.length === 1) {
      return generateNoteForOccasion(occasions[0], recipientName);
    }

    // For multiple occasions, merge notes with line breaks
    const notes = occasions.map((occasion) => generateNoteForOccasion(occasion, recipientName));
    return notes.join('\n\n');
  };

  // Max character limit for note field - removed for multiple occasions support
  // const NOTE_MAX_LENGTH = 330;

  // Handle occasion selection - toggle occasions in array and generate merged note
  const handleOccasionClick = (occasion) => {
    setNoteAnimating(true);

    setFormData((prev) => {
      const currentOccasions = prev.occasions || [];
      let newOccasions;

      // Toggle occasion in array
      if (currentOccasions.includes(occasion)) {
        // Remove if already selected
        newOccasions = currentOccasions.filter((occ) => occ !== occasion);
      } else {
        // Add if not selected
        newOccasions = [...currentOccasions, occasion];
      }

      // For "Other Milestone" or multiple occasions, clear note and let user type
      if ((occasion === 'Other Milestone' && newOccasions.includes(occasion)) || newOccasions.length > 1) {
        return {
          ...prev,
          occasions: newOccasions,
          note: ''
        };
      }

      // Generate note only for single occasion selection (excluding "Other Milestone")
      const occasionsForNote = newOccasions.filter((occ) => occ !== 'Other Milestone');
      const fullName = [prev.firstname, prev.lastname].filter(Boolean).join(' ') || 'friend';
      const generatedNote = generateMergedNote(occasionsForNote, fullName);

      return {
        ...prev,
        occasions: newOccasions,
        note: generatedNote
      };
    });

    // Reset animation state after animation completes and focus textarea if Other Milestone
    setTimeout(() => {
      setNoteAnimating(false);
      if (occasion === 'Other Milestone' && noteTextareaRef.current) {
        const textarea = noteTextareaRef.current;
        textarea.focus();
        textarea.selectionStart = textarea.value.length;
        textarea.selectionEnd = textarea.value.length;
      }
    }, 600);
  };

  // Handle occasion carousel navigation
  // const occasions = ['New Baby', 'Anniversary', 'Wedding', 'Birthday', 'Graduation', 'Holiday'];
  const occasions = ['Wedding', 'New Baby', 'Anniversary', 'Celebration of Life', 'Vacation Recap', 'Preserving Memories', 'Birthday Party', 'Family Storytelling', 'Other Milestone'];
  const VISIBLE_OCCASIONS = 3;
  const totalPages = Math.ceil(occasions.length / VISIBLE_OCCASIONS);

  const handleOccasionScrollLeft = () => {
    setOccasionScrollIndex((prev) => Math.max(0, prev - VISIBLE_OCCASIONS));
  };

  const handleOccasionScrollRight = () => {
    setOccasionScrollIndex((prev) => {
      const nextIndex = prev + VISIBLE_OCCASIONS;
      return nextIndex < occasions.length ? nextIndex : prev;
    });
  };

  // Check if we can scroll left or right
  const canScrollLeft = occasionScrollIndex > 0;
  const canScrollRight = occasionScrollIndex + VISIBLE_OCCASIONS < occasions.length;

  // Save gift user (Step 1 - first page with name/email)
  const handleSaveGiftUser = async () => {
    if (!formData.firstname || !formData.email || !cloudflareToken) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const [firstname, ...lastnameParts] = formData.firstname.trim().split(' ');
      const lastname = lastnameParts.join(' ');

      const payload = {
        firstname: firstname || formData.firstname,
        lastname: lastname || formData.lastname,
        email: formData.email,
        recaptchaResponse: cloudflareToken,
        v2: true,
        ...(urlParams.referral ? { referral: urlParams.referral } : {})
      };

      const response = await saveGiftUser(payload);

      if (response.data?.success) {
        setGiftUserUid(response.data.uid);
        allPhotosBackendIdRef.current = null; // Reset for new user

        // Check if email already exists in the system (existing user)
        try {
          const emailCheckResult = await dataFetcher(endpoints.signinEmail, { email: formData.email });
          if (emailCheckResult?.success) {
            localStorage.setItem('existingUser', 'true');
          } else {
            localStorage.removeItem('existingUser');
          }
        } catch (emailCheckErr) {
          console.warn('[GIFT_FLOW] Email existence check failed:', emailCheckErr);
          localStorage.removeItem('existingUser');
        }

        // Normal flow: show sender fields inline before moving to note section
        if (!urlParams.share && !isMobileUploadMode) {
          setShowSenderInline(true);
        } else {
          setStep1SubStep(2); // Move to note selection page
        }
      } else {
        setError(response.data?.message || 'Failed to save user details');
        // Reset Turnstile on any backend error
        setCloudflareToken('');
        setCloudflareVerifying(true);
        if (window.turnstile && turnstileWidgetId.current) {
          window.turnstile.reset(turnstileWidgetId.current);
        }
      }
    } catch (err) {
      console.error('Error saving gift user:', err);
      const errorMessage = err.response?.data?.message || 'An error occurred. Please try again.';
      setError(errorMessage);
      // Reset Turnstile on any backend error so user can retry
      setCloudflareToken('');
      setCloudflareVerifying(true);
      if (window.turnstile && turnstileWidgetId.current) {
        window.turnstile.reset(turnstileWidgetId.current);
      }
    } finally {
      setLoading(false);
    }
  };

  // Update gift user with note (Step 1 - second page with note selection)
  const handleUpdateGiftUser = () => {
    if (!giftUserUid) {
      setError('Please select or enter a note');
      return;
    }
    // Single occasion — note is auto-generated, require it
    if (formData.occasions && formData.occasions.length === 1 && !formData.occasions.includes('Other Milestone') && !formData.note) {
      setError('Please select or enter a note');
      return;
    }
    // Other Milestone — user must type a note
    if (formData.occasions && formData.occasions.includes('Other Milestone') && !formData.note) {
      setError('Please enter a note');
      return;
    }

    // Normal flow: navigate to step 3, show sender popup overlay if needed
    if (!urlParams.share) {
      const savedSenderName = localStorage.getItem('senderName');
      const savedSenderEmail = localStorage.getItem('senderEmail');
      if (savedSenderName && savedSenderEmail) {
        // Sender info saved — skip popup, pre-fill cardholder email
        handleInputChange('cardholderEmail', savedSenderEmail.trim());
      } else {
        setShowSenderPopup(true);
      }
      setSelectedChoice('you-help-build');
      animateToStep(3);
    } else {
      // Share flow: skip sender popup, go directly to upload
      setSelectedChoice('you-help-build');
      animateToStep(3);
    }

    // Only call API now if single occasion (note is ready), otherwise call after payment
    if (formData.note) {
      updateGiftUser({
        uid: giftUserUid,
        note: formData.note
      }).catch((err) => {
        console.error('Error updating gift user:', err);
        logAppEvent({
          userName: 'Gift User',
          functionAffected: 'Gift Update User',
          description: `updateGiftUser failed (occasion selection) — payload: { uid: "${giftUserUid}", note: "${formData.note || ''}" }`,
          severity: 'high',
          errorMessage: err?.response?.data?.message || err?.message || 'Update gift user failed',
          errorStack: err?.stack || null,
          responseStatus: err?.response?.status || null,
          component: 'giftFlowUpdateUser',
          userAction: 'updating gift user note after occasion selection',
        });
      });
    }
  };

  const handleLoginClick = () => {
    // When on Step 1, handle sub-step navigation
    if (currentStep === 1) {
      if (step1SubStep === 1) {
        // First page: save gift user with name/email
        handleSaveGiftUser();
      } else {
        // Second page: update gift user with note
        handleUpdateGiftUser();
      }
    } else if (currentStep === 3) {
      animateToStep(4);
    } else {
      animateToStep(currentStep + 1);
    }
  };

  // Fetch gift plans (Step 4)
  useEffect(() => {
    if (currentStep === 4 && giftPlans.length === 0) {
      const fetchGiftPlans = async () => {
        setLoading(true);
        try {
          const response = await getAllGiftPlans();
          if (response.data?.success && response.data?.data) {
            setGiftPlans(response.data.data);
          }
        } catch (err) {
          console.error('Error fetching gift plans:', err);
          setError('Failed to load plans');
        } finally {
          setLoading(false);
        }
      };
      fetchGiftPlans();
    }
  }, [currentStep, giftPlans.length]);

  // Auto-create payment intent when plans are loaded (Step 4) - DISABLED
  // Now payment intent is created only when user clicks "Choose" button on a plan
  // useEffect(() => {
  //   if (currentStep === 4 && giftPlans.length > 0 && !paymentData && giftUserUid) {
  //     const autoCreatePaymentIntent = async () => {
  //       try {
  //         const firstPlan = giftPlans[0];
  //         const intentResponse = await createPaymentIntent({
  //           uid: giftUserUid,
  //           plan_id: firstPlan.id,
  //           url: window.location.origin
  //         });

  //         if (intentResponse.data?.success && intentResponse.data?.client_secret) {
  //           const apiAmount = intentResponse.data.amount ? (intentResponse.data.amount / 100).toFixed(2) : '0.00';
  //           setPaymentData({
  //             clientSecret: intentResponse.data.client_secret,
  //             planId: firstPlan.id,
  //             planAmount: apiAmount,
  //             planName: 'Projector Gift'
  //           });
  //           setShowPayment(true);
  //         }
  //       } catch (err) {
  //         console.error('Error auto-creating payment intent:', err);
  //       }
  //     };
  //     autoCreatePaymentIntent();
  //   }
  // }, [currentStep, giftPlans, paymentData, giftUserUid]);

  // Auto-focus cardholder name input when payment step loads
  useEffect(() => {
    if (currentStep === 4 && cardholderNameRef.current) {
      cardholderNameRef.current.focus();
    }
  }, [currentStep]);

  // Auto-focus To: name input when step 1 substep 1 loads
  useEffect(() => {
    if (currentStep === 1 && step1SubStep === 1 && toNameInputRef.current) {
      toNameInputRef.current.focus();
    }
  }, [currentStep, step1SubStep]);

  // Handle plan selection from carousel - Creates payment intent when user chooses a plan
  const handleChoosePlan = async (plan) => {
    if (!giftUserUid) {
      setError('User ID not found. Please start over.');
      return;
    }

    // Don't show loading state - works in background
    setError('');

    try {
      // Immediately set selected plan to hide carousel
      setSelectedPlan(plan);

      // Re-validate promo code with new plan if one is active
      if (promoCodeNew?.length >= 6) {
        setPromoLoading(true);
        if (promoCancelToken.current) {
          promoCancelToken.current.cancel('Plan changed, re-validating promo.');
        }
        promoCancelToken.current = axios.CancelToken.source();
        try {
          const promoRes = await axios({
            url: `${baseUrl}/getPromoAmount`,
            method: 'POST',
            data: {
              uid: giftUserUid,
              promo_code: promoCodeNew,
              plan_id: plan.id,
            },
            headers: { 'Content-Type': 'application/json' },
            cancelToken: promoCancelToken.current.token,
          });
          setPromoResponse(promoRes.data);
          const amountOff = promoRes.data?.data?.amount;
          const priceInDollar = amountOff / 100;
          const planAmount = plan.amount ? parseFloat(plan.amount) : 0;
          setCalculatedDiscount(priceInDollar.toFixed(2));
          setDiscountedPrice((planAmount - priceInDollar).toFixed(2));
          setPromoErrMsg('');
        } catch (err) {
          if (!axios.isCancel(err)) {
            setPromoErrMsg(err?.response?.data?.message || 'Something went wrong');
            setPromoResponse(null);
            setDiscountedPrice('');
            setCalculatedDiscount('');
          }
        } finally {
          setPromoLoading(false);
        }
      }

      // Create payment intent for the chosen plan
      const intentResponse = await createPaymentIntent({
        uid: giftUserUid,
        plan_id: plan.id,
        url: window.location.origin,
        // test: true,
        ...(promoCodeNew?.length >= 6 ? { promo: promoCodeNew } : {}),
      });

      if (intentResponse.data?.success && intentResponse.data?.client_secret) {
        // Use the amount from the API response (in cents, convert to dollars for display)
        const apiAmount = intentResponse.data.amount ? (intentResponse.data.amount / 100).toFixed(2) : '0.00';

        // Set payment data and show payment form immediately
        setPaymentData({
          clientSecret: intentResponse.data.client_secret,
          planId: plan.id,
          planAmount: apiAmount,
          planName: 'Projector Gift'
        });
        setShowPayment(true);
        setPaymentSectionExpanded(true); // Show payment form immediately after intent is created
      } else {
        setError(intentResponse.data?.message || 'Failed to create payment intent');
        // Reset selected plan on error so user can try again
        setSelectedPlan(null);
      }
    } catch (err) {
      console.error('Error creating payment intent:', err);
      setError(err.response?.data?.message || 'An error occurred during payment');
      // Reset selected plan on error so user can try again
      setSelectedPlan(null);
    }
  };

  // Auto-select removed - user picks a plan from the right side grid

  // Proceed to payment after filling account info
  const handleProceedToPayment = () => {
    // Validate account info
    const isEmailValid = formData.cardholderEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.cardholderEmail);

    if (!formData.cardholderName || !isEmailValid) {
      setError('Please fill in your name and a valid email address');
      return;
    }

    // Show payment section (this hides account info automatically)
    setPaymentSectionExpanded(true);
  };

  // Promo code helpers
  const calculateDiscountedPrice = (amountOff) => {
    const priceInDollar = amountOff / 100;
    const planAmount = selectedPlan?.amount ? parseFloat(selectedPlan.amount) : 0;
    const discounted = planAmount - priceInDollar;
    setCalculatedDiscount(priceInDollar.toFixed(2));
    setDiscountedPrice(discounted.toFixed(2));
  };

  const handlePromoCodeChange = (e) => {
    const newPromoCode = e.target.value?.toUpperCase();
    setPromoCodeNew(newPromoCode);

    if (newPromoCode?.length >= 6) {
      setPromoActive(true);
      setPromoLoading(true);
    }
    if (newPromoCode?.length > 0 && newPromoCode?.length < 6) {
      setPromoErrMsg('');
      setPromoResponse(null);
      setDiscountedPrice('');
      setCalculatedDiscount('');
      setPromoActive(false);
    }
    if (newPromoCode?.length === 0) {
      setPromoErrMsg('');
      setPromoResponse(null);
      setDiscountedPrice('');
      setCalculatedDiscount('');
      setPromoActive(false);
      // Recreate payment intent without promo to revert to full price
      if (selectedPlan?.id && giftUserUid) {
        createPaymentIntent({
          uid: giftUserUid,
          plan_id: selectedPlan.id,
          url: window.location.origin,
        }).then((intentResponse) => {
          if (intentResponse.data?.success && intentResponse.data?.client_secret) {
            const apiAmount = intentResponse.data.amount ? (intentResponse.data.amount / 100).toFixed(2) : '0.00';
            setPaymentData({
              clientSecret: intentResponse.data.client_secret,
              planId: selectedPlan.id,
              planAmount: apiAmount,
              planName: 'Projector Gift',
            });
          }
        }).catch((err) => {
          console.error('[GIFT_FLOW] Failed to recreate payment intent without promo:', err);
        });
      }
    }

    if (promoDebounceRef.current) {
      clearTimeout(promoDebounceRef.current);
    }
    if (promoCancelToken.current) {
      promoCancelToken.current.cancel('Operation canceled due to new request.');
    }
    promoCancelToken.current = axios.CancelToken.source();

    if (newPromoCode?.length >= 6) {
      promoDebounceRef.current = setTimeout(async () => {
        try {
          const response = await axios({
            url: `${baseUrl}/getPromoAmount`,
            method: 'POST',
            data: {
              uid: giftUserUid,
              promo_code: newPromoCode,
              plan_id: selectedPlan?.id,
            },
            headers: { 'Content-Type': 'application/json' },
            cancelToken: promoCancelToken.current.token,
          });
          setPromoResponse(response.data);
          calculateDiscountedPrice(response.data?.data?.amount);
          setPromoErrMsg('');

          // Recreate payment intent with promo so Stripe charges the discounted amount
          if (selectedPlan?.id && giftUserUid) {
            try {
              const intentResponse = await createPaymentIntent({
                uid: giftUserUid,
                plan_id: selectedPlan.id,
                url: window.location.origin,
                promo: newPromoCode,
              });
              if (intentResponse.data?.success && intentResponse.data?.client_secret) {
                const apiAmount = intentResponse.data.amount ? (intentResponse.data.amount / 100).toFixed(2) : '0.00';
                setPaymentData({
                  clientSecret: intentResponse.data.client_secret,
                  planId: selectedPlan.id,
                  planAmount: apiAmount,
                  planName: 'Projector Gift',
                });
              }
            } catch (intentErr) {
              console.error('[GIFT_FLOW] Failed to recreate payment intent with promo:', intentErr);
            }
          }
        } catch (err) {
          if (!axios.isCancel(err)) {
            setPromoErrMsg(err?.response?.data?.message || 'Something went wrong');
            setPromoResponse(null);
            setDiscountedPrice('');
            setCalculatedDiscount('');
          }
        } finally {
          setPromoLoading(false);
        }
      }, 500);
    }
  };

  // Handle plan selection and payment (legacy function - kept for backwards compatibility)
  const handlePlanPurchase = async (planId) => {
    if (!giftUserUid) {
      setError('User ID not found. Please start over.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Find the selected plan details
      const selectedPlanData = giftPlans.find((plan) => plan.id === planId);

      // Create payment intent
      const intentResponse = await createPaymentIntent({
        uid: giftUserUid,
        plan_id: planId,
        url: window.location.origin,
        test: true,
        ...(promoCodeNew?.length >= 6 ? { promo: promoCodeNew } : {}),
      });

      if (intentResponse.data?.success && intentResponse.data?.client_secret) {
        // Use the amount from the API response (in cents, convert to dollars for display)
        const apiAmount = intentResponse.data.amount ? (intentResponse.data.amount / 100).toFixed(2) : '0.00';

        // Set payment data and show inline payment
        setPaymentData({
          clientSecret: intentResponse.data.client_secret,
          planId,
          planAmount: apiAmount,
          planName: 'Projector Gift'
        });
        setShowPayment(true);
      } else {
        setError(intentResponse.data?.message || 'Failed to create payment intent');
      }
    } catch (err) {
      console.error('Error creating payment intent:', err);
      setError(err.response?.data?.message || 'An error occurred during payment');
    } finally {
      setLoading(false);
    }
  };

  // Register S3-complete uploads after payment (registerDocument + addVideo)
  const registerS3CompleteUploads = useCallback(async () => {
    if (isRegisteringRef.current) return; // Prevent concurrent calls
    isRegisteringRef.current = true;
    setIsRegistering(true);

    try {
      const s3CompleteUploads = uploadManagerSecure.getS3CompleteUploads();
      const hasPhotoAlbums = photoMetadataRef.current.size > 0;
      const hasMobileMetadata = mobileUploadMetadataRef.current !== null
        && ((mobileUploadMetadataRef.current.videos?.length || 0) > 0 || (mobileUploadMetadataRef.current.albums?.length || 0) > 0);
      if (s3CompleteUploads.length === 0 && !hasPhotoAlbums && !hasMobileMetadata) {
        console.log('[GIFT_FLOW] No S3-complete uploads, photo albums, or mobile metadata to register yet');
        return;
      }

      console.log('[GIFT_FLOW] Registering', s3CompleteUploads.length, 'S3-complete uploads after payment');

      await Promise.all(s3CompleteUploads.map(async (upload) => {
        try {
          // Skip if file was deleted by user — mark completed to prevent infinite retry
          if (deletedFileNamesRef.current.has(upload.fileName)) {
            console.log('[GIFT_FLOW] Upload was deleted, skipping registration:', upload.fileName);
            uploadManagerSecure.markUploadCompleted(upload.id);
            return;
          }
          const metadata = videoMetadataRef.current.get(upload.draftId);
          if (!metadata) {
            console.warn('[GIFT_FLOW] No metadata found for draftId:', upload.draftId);
            console.log('[THUMB_DEBUG] Registration - All stored draftIds:', [...videoMetadataRef.current.keys()]);
            // Mark completed to prevent infinite retry loop
            uploadManagerSecure.markUploadCompleted(upload.id);
            return;
          }

          const { videoTitle, thumbnailBase64: thumbBase64, thumbnailsBinary: thumbsBinary, thumbnailData: thumbData, file } = metadata;
          console.log('[THUMB_DEBUG] Registration - metadata for:', upload.fileName, {
            draftId: upload.draftId,
            hasThumbnailBase64: !!thumbBase64,
            thumbnailBase64Preview: thumbBase64 ? thumbBase64.substring(0, 50) : 'null',
            thumbnailsBinaryCount: thumbsBinary?.length || 0,
            thumbnailsBinarySizes: thumbsBinary?.map((b) => b?.size || 0) || [],
            thumbnailDataFrames: thumbData?.thumbnails?.length || 0,
            videoHeight: thumbData?.videoHeight,
            videoWidth: thumbData?.videoWidth,
          });

          // Route individual image files to createAndAttachAlbum instead of addVideo
          const uploadFileExt = `.${upload.fileName.split('.').pop().toLowerCase()}`;
          if (IMAGE_EXTENSIONS.includes(uploadFileExt)) {
            photoMetadataRef.current.set(`direct-${upload.draftId}`, {
              albumName: 'All photos',
              photos: [{ path: upload.s3Key, size: String(file?.size || 0), name: upload.fileName }],
            });
            uploadManagerSecure.markUploadCompleted(upload.id);
            videoMetadataRef.current.delete(upload.draftId);
            return;
          }

          // Step 1: Register document
          console.log('[GIFT_FLOW] Registering document for:', upload.fileName);
          const documentResponse = await registerDocument({
            name: upload.fileName,
            url: upload.s3Key,
            thumbnail: upload.s3Key,
            thumbnails: [],
            uploaded_at: new Date().toISOString(),
          });

          const documentId = documentResponse.id;
          console.log('[GIFT_FLOW] Document registered:', documentId);

          // Step 2: Call /V6/addVideo API
          const savedContNameForVideo = urlParams.share ? localStorage.getItem('contributorsName') : localStorage.getItem('senderName');
          const videoCategoryName = savedContNameForVideo
            ? `${savedContNameForVideo.trim()}'s videos`
            : 'All Videos';
          const formData = new FormData();
          formData.append('draft_id', upload.draftId);
          formData.append('title', videoTitle);
          formData.append('category_id', '');
          formData.append('category_name', videoCategoryName);
          formData.append('file_name', upload.fileName);
          formData.append('file_key', upload.s3Key);
          formData.append('document_id', documentId || '');
          formData.append('bucket_folder_path', upload.s3Key);

          if (thumbBase64) {
            const thumbSource = thumbBase64.startsWith('data:image/png') ? 'backend-api-fallback' : thumbBase64 === image04 ? 'projector-logo-fallback' : 'client-generated';
            const thumbBinary = base64ToBinary(thumbBase64);
            console.log('[THUMB_DEBUG] Registration - thumbnail source:', thumbSource, '| binary size:', thumbBinary?.size || 0, '| file:', upload.fileName);
            formData.append('thumbnail', thumbBinary);
          } else {
            console.warn('[THUMB_DEBUG] Registration - NO main thumbnail for:', upload.fileName, '— sending empty');
          }

          const thumbs = upload.thumbnails || thumbsBinary;
          console.log('[THUMB_DEBUG] Registration - thumbnails[] source:', upload.thumbnails ? 'upload.thumbnails' : 'thumbsBinary', 'count:', thumbs?.length || 0);
          thumbs.forEach((thumbnail) => {
            if (thumbnail) {
              try {
                const binaryThumb = typeof thumbnail === 'string'
                  ? base64ToBinary(thumbnail)
                  : thumbnail;
                formData.append('thumbnails[]', binaryThumb);
              } catch (thumbErr) {
                console.error('[GIFT_FLOW] Thumbnail conversion failed:', thumbErr);
              }
            }
          });

          formData.append('description', '');
          formData.append('video_size', changeSize(file.size));
          formData.append('group_ids', JSON.stringify([]));
          formData.append('visibility', '1');
          formData.append('subcategory_id', '');
          formData.append('playlist_ids', '');
          formData.append('videoHeight', thumbData?.videoHeight || '');
          formData.append('videoWidth', thumbData?.videoWidth || '');
          formData.append('videoOreintation', (thumbData?.videoHeight > thumbData?.videoWidth) ? '2' : '1');
          formData.append('s3_status', 'completed');

          // Log addVideo payload
          const payloadLog = {};
          formData.forEach((value, key) => { payloadLog[key] = value instanceof Blob ? `Blob(${value.size} bytes)` : value; });
          console.log('[GIFT_FLOW] addVideo payload:', payloadLog);

          const isExistingUser = localStorage.getItem('existingUser') === 'true';

          let addVideoResponse;

          if (isExistingUser) {
            // Existing user: store action for later execution
            console.log('[GIFT_FLOW] Existing user — storing addVideo as pending gift action');
            const actionPayload = {};
            formData.forEach((value, key) => {
              if (!(value instanceof Blob)) {
                actionPayload[key] = value;
              }
            });
            const actionResponse = await addGiftAction({
              gift_uuid: giftUserUid,
              user_id: localStorage.getItem('giftUserId') || null,
              action: {
                action_id: `vid_${upload.draftId}`,
                action_type: 'addVideo',
                endpoint: '/V6/addVideo',
                method: 'POST',
                payload: actionPayload,
              },
            });
            console.log('[GIFT_FLOW] Gift action stored successfully:', actionResponse?.data);
            addVideoResponse = { data: { success: true, draft_id: upload.draftId } };
          } else {
            // New user: call addVideo directly
            addVideoResponse = await apiAxios.post(`${baseUrl}/V6/addVideo`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });

            console.log('[GIFT_FLOW] Video registered successfully:', upload.fileName);
            console.log('[THUMB_DEBUG] Registration - addVideo response:', {
              fileName: upload.fileName,
              success: addVideoResponse?.data?.success,
              thumbnail: addVideoResponse?.data?.thumbnail || addVideoResponse?.data?.data?.thumbnail || 'not in response',
              draftId: addVideoResponse?.data?.draft_id,
            });

            // Step 3: Link draft to document
            if (documentId && addVideoResponse?.data?.draft_id) {
              try {
                await updateDraftLink({
                  document_id: documentId,
                  draft_id: addVideoResponse.data.draft_id,
                });
                console.log('[GIFT_FLOW] Draft linked to document successfully');
              } catch (linkErr) {
                console.error('[GIFT_FLOW] Failed to link draft:', linkErr);
              }
            }
          }

          // Mark as fully completed in upload manager
          uploadManagerSecure.markUploadCompleted(upload.id);

          // Store registered content_id so Preview button can appear in upload listing
          const registeredContentId = addVideoResponse?.data?.content_id
            || addVideoResponse?.data?.video_id
            || addVideoResponse?.data?.id
            || null;

          // Update local uploads state
          setUploads((prev) => prev.map((u) => (u.secureUploadId === upload.draftId
            ? { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100, registeredContentId }
            : u)));

          // Clean up stored metadata
          videoMetadataRef.current.delete(upload.draftId);
        } catch (err) {
          console.error('[GIFT_FLOW] Failed to register upload:', upload.fileName, err);
          logAppEvent({
            userName: 'Gift User',
            functionAffected: 'Gift Registration',
            description: `Gift video registration failed — file: "${upload.fileName}", draftId: ${upload.draftId}, giftUuid: ${giftUserUid}`,
            severity: 'high',
            errorMessage: err?.response?.data?.message || err?.message || 'Video registration failed',
            errorStack: err?.stack || null,
            responseStatus: err?.response?.status || null,
            component: 'giftFlowRegistration',
            userAction: `registering gift video: ${upload.fileName}`,
          });
          // Mark as completed even on error to prevent infinite retry loop
          // (the subscription keeps calling registerS3CompleteUploads while s3_complete uploads exist)
          uploadManagerSecure.markUploadCompleted(upload.id);
          setUploads((prev) => prev.map((u) => (u.secureUploadId === upload.draftId
            ? { ...u, status: UPLOAD_STATUS.FAILED, progress: 100 }
            : u)));
          videoMetadataRef.current.delete(upload.draftId);
        }
      }));

      // Register photo albums via createAndAttachAlbum API (photos uploaded to S3, need album creation after payment)
      if (photoMetadataRef.current.size > 0) {
        const photoEntries = Array.from(photoMetadataRef.current.entries());
        console.log('[GIFT_FLOW] Registering', photoEntries.length, 'photo album(s) via createAndAttachAlbum');

        // Fetch existing album list to check if album already exists
        let existingAlbumId = null;
        const isContributorFlow = !!urlParams.share;
        const savedContName = isContributorFlow ? localStorage.getItem('contributorsName') : localStorage.getItem('senderName');
        const contFullName = savedContName ? savedContName.trim() : null;
        try {
          const albumListResponse = await apiAxios.post(`${baseUrl}/getAlbumList`, {});
          const existingAlbums = albumListResponse.data?.data || [];
          if (existingAlbums.length > 0) {
            if (contFullName) {
              // Contributor flow: match album whose title starts with contributor's first name
              const contMatch = existingAlbums.find((a) => a.title && a.title.startsWith(contFullName));
              if (contMatch) {
                existingAlbumId = contMatch.album_id || contMatch.id;
                console.log('[GIFT_FLOW] Contributor flow: found matching album for', contFullName, '- album_id:', existingAlbumId);
              } else {
                console.log('[GIFT_FLOW] Contributor flow: no album matching', contFullName, '- will create new album');
              }
            } else {
              // Normal flow: use first existing album
              existingAlbumId = existingAlbums[0].album_id || existingAlbums[0].id;
              console.log('[GIFT_FLOW] Found existing album, will use album_id:', existingAlbumId);
            }
          } else {
            console.log('[GIFT_FLOW] No existing albums, will create new album with title and icon');
          }
        } catch (listErr) {
          console.warn('[GIFT_FLOW] Failed to fetch album list, will create new album:', listErr);
        }

        // Merge all photo batches into a single album to avoid creating separate albums per batch
        // Include photos from: selectedFiles albums, individual photos, AND mobile-uploaded photos
        const visiblePhotoNames = new Set();
        selectedFilesRef.current.filter((f) => f.type === 'album').forEach((album) => {
          (album.photos || []).forEach((p) => { if (p.name) visiblePhotoNames.add(p.name); });
        });
        // Also include individual photo files (type='photo' from skip-album flow)
        selectedFilesRef.current.filter((f) => f.type === 'photo').forEach((photo) => {
          if (photo.name) visiblePhotoNames.add(photo.name);
        });
        // Also include mobile-preview albums (files uploaded from mobile)
        selectedFilesRef.current.filter((f) => f.type === 'album' && f.source === 'mobile-preview').forEach((album) => {
          (album.photos || []).forEach((p) => { if (p.name) visiblePhotoNames.add(p.name); });
        });
        // Include photos from mobileUploadMetadataRef (in case selectedFiles wasn't updated)
        if (mobileUploadMetadataRef.current?.albums) {
          mobileUploadMetadataRef.current.albums.forEach((album) => {
            (album.photos || []).forEach((p) => { if (p.name) visiblePhotoNames.add(p.name); });
          });
        }

        const allPhotos = [];
        const allAlbumUploadIds = [];
        photoEntries.forEach(([albumUploadId, albumData]) => {
          if (albumData.photos && albumData.photos.length > 0) {
            const filteredPhotos = albumData.photos.filter((p) => visiblePhotoNames.has(p.name));
            if (filteredPhotos.length > 0) {
              allPhotos.push(...filteredPhotos);
              allAlbumUploadIds.push(albumUploadId);
            }
          }
        });

        if (allPhotos.length > 0) {
          try {
            let payload;
            if (existingAlbumId) {
              payload = {
                album_id: existingAlbumId,
                token: localStorage.getItem('token') || '',
                photos: allPhotos,
              };
            } else {
              // Use contributor/sender name for album title (e.g., "Layn's Album")
              const savedContNameForTitle = urlParams.share ? localStorage.getItem('contributorsName') : localStorage.getItem('senderName');
              const albumTitle = savedContNameForTitle
                ? `${savedContNameForTitle.trim()}'s Album`
                : 'All photos';
              payload = {
                title: albumTitle,
                description: '',
                visibility: '2',
                group_id: '',
                token: localStorage.getItem('token') || '',
                icon: allPhotos[0]?.path || '',
                photos: allPhotos,
              };
            }

            console.log('[GIFT_FLOW] Calling createAndAttachAlbum:', {
              albumId: existingAlbumId || 'new',
              photoCount: payload.photos.length,
              batchCount: allAlbumUploadIds.length,
            });

            const isExistingUserAlbum = localStorage.getItem('existingUser') === 'true';
            let albumCreateResponse;

            if (isExistingUserAlbum) {
              // Existing user: store action for later execution
              console.log('[GIFT_FLOW] Existing user — storing createAndAttachAlbum as pending gift action');
              const { token, ...actionPayload } = payload;
              const actionResponse = await addGiftAction({
                gift_uuid: giftUserUid,
                user_id: localStorage.getItem('giftUserId') || null,
                action: {
                  action_id: `album_${allAlbumUploadIds[0] || Date.now()}`,
                  action_type: 'createAndAttachAlbum',
                  endpoint: '/createAndAttachAlbum',
                  method: 'POST',
                  payload: actionPayload,
                },
              });
              console.log('[GIFT_FLOW] Album gift action stored successfully:', actionResponse?.data);
              albumCreateResponse = { data: { success: true } };
            } else {
              albumCreateResponse = await apiAxios.post(`${baseUrl}/createAndAttachAlbum`, payload);
              console.log('[GIFT_FLOW] Album created and attached successfully');
            }

            // Mark all batches as registered to prevent duplicate calls via mobileUploadMetadataRef path
            allAlbumUploadIds.forEach((id) => registeredMobileIdsRef.current.add(id));

            const registeredAlbumId = existingAlbumId
              || albumCreateResponse?.data?.album_id
              || albumCreateResponse?.data?.id
              || null;

            // Fetch album detail to get server photo_ids before marking complete
            let albumPhotosWithIds = [];
            if (registeredAlbumId) {
              try {
                const detailRes = await apiAxios.post(`${baseUrl}/${endpoints.getMyAlbumDetail}`, { album_id: registeredAlbumId });
                if (detailRes?.data?.success && detailRes?.data?.data) {
                  albumPhotosWithIds = detailRes.data.data[0]?.photos || detailRes.data.data?.photos || [];
                }
              } catch (detailErr) {
                console.error('[GIFT_FLOW] Failed to fetch album detail after create:', detailErr);
              }
              // Store in fetchedAlbumPhotos for each album upload entry
              allAlbumUploadIds.forEach((uid) => {
                setFetchedAlbumPhotos((prev) => ({ ...prev, [uid]: { loading: false, photos: albumPhotosWithIds } }));
              });

              // Attach server photo_id to local photos in selectedFiles by matching order
              if (albumPhotosWithIds.length > 0) {
                const sorted = [...albumPhotosWithIds].sort((a, b) => Number(a.order_number) - Number(b.order_number));
                const nameToPhotoId = {};
                allPhotos.forEach((lp, idx) => {
                  if (sorted[idx]) nameToPhotoId[lp.name] = sorted[idx].id;
                });
                setSelectedFiles((prev) => prev.map((f) => {
                  if (f.type !== 'album' || !f.photos) return f;
                  const hasUpdate = f.photos.some((p) => nameToPhotoId[p.name]);
                  if (!hasUpdate) return f;
                  return { ...f, photos: f.photos.map((p) => (nameToPhotoId[p.name] ? { ...p, photo_id: nameToPhotoId[p.name] } : p)) };
                }));
              }
            }

            // Update local uploads state for all album entries
            setUploads((prev) => prev.map((u) => {
              if (allAlbumUploadIds.includes(u.id)) {
                return { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100, registeredAlbumId };
              }
              return u;
            }));

            // Clean up stored metadata
            allAlbumUploadIds.forEach((id) => photoMetadataRef.current.delete(id));
          } catch (albumErr) {
            console.error('[GIFT_FLOW] Failed to create album:', albumErr);
            logAppEvent({
              userName: 'Gift User',
              functionAffected: 'Gift Registration',
              description: `Gift album registration failed — photoCount: ${allPhotos.length}, batchCount: ${allAlbumUploadIds.length}, giftUuid: ${giftUserUid}`,
              severity: 'high',
              errorMessage: albumErr?.response?.data?.message || albumErr?.message || 'Album registration failed',
              errorStack: albumErr?.stack || null,
              responseStatus: albumErr?.response?.status || null,
              component: 'giftFlowRegistration',
              userAction: 'registering gift photo album',
            });
            // Clean up metadata even on error to prevent infinite retry loop
            allAlbumUploadIds.forEach((id) => photoMetadataRef.current.delete(id));
          }
        }
      }

      // Register mobile-uploaded files using metadata received via push message.
      // This handles the case where mobile uploaded files and closed the tab,
      // so desktop registers them after payment.
      if (mobileUploadMetadataRef.current) {
        // Grab metadata but DON'T clear yet - we'll put back failed items for retry
        const mobileMeta = mobileUploadMetadataRef.current;
        mobileUploadMetadataRef.current = null;

        console.log('[GIFT_FLOW] Mobile metadata to process:', {
          videos: mobileMeta.videos?.map((v) => ({ fileName: v.fileName, draftId: v.draftId })) || [],
          albums: mobileMeta.albums?.map((a) => ({ albumName: a.albumName, albumUploadId: a.albumUploadId, photoCount: a.photos?.length || 0 })) || [],
        });

        // Track which videos failed registration for retry
        const failedVideos = [];

        // Register mobile-uploaded videos
        if (mobileMeta.videos && mobileMeta.videos.length > 0) {
          console.log('[GIFT_FLOW] Registering', mobileMeta.videos.length, 'mobile-uploaded video(s)');

          await Promise.all(mobileMeta.videos.map(async (video) => {
            // Skip if file was deleted by user
            if (deletedFileNamesRef.current.has(video.fileName)) {
              console.log('[GIFT_FLOW] Mobile video was deleted, skipping registration:', video.fileName);
              return;
            }
            // Skip if already registered (prevents duplicate API calls from multiple triggers)
            if (registeredMobileIdsRef.current.has(video.draftId)) {
              console.log('[GIFT_FLOW] Mobile video already registered, skipping:', video.fileName);
              return;
            }
            try {
              // Step 1: Register document
              console.log('[GIFT_FLOW] Registering mobile document for:', video.fileName);
              const documentResponse = await registerDocument({
                name: video.fileName,
                url: video.s3Key,
                thumbnail: video.s3Key,
                thumbnails: [],
                uploaded_at: new Date().toISOString(),
              });

              const documentId = documentResponse.id;
              console.log('[GIFT_FLOW] Mobile document registered:', documentId);

              // Step 2: Call /V6/addVideo API
              const savedMobileContNameForVideo = urlParams.share ? localStorage.getItem('contributorsName') : localStorage.getItem('senderName');
              const mobileCategoryName = savedMobileContNameForVideo
                ? `${savedMobileContNameForVideo.trim().split(' ')[0]}'s videos`
                : 'All Videos';
              const formData = new FormData();
              formData.append('draft_id', video.draftId);
              formData.append('title', video.videoTitle);
              formData.append('category_id', '');
              formData.append('category_name', mobileCategoryName);
              formData.append('file_name', video.fileName);
              formData.append('file_key', video.s3Key);
              formData.append('document_id', documentId || '');
              formData.append('bucket_folder_path', video.s3Key);

              // Use compressed thumbnail from mobile push metadata if available
              if (video.thumbnail) {
                try {
                  formData.append('thumbnail', base64ToBinary(video.thumbnail));
                  formData.append('thumbnails[]', base64ToBinary(video.thumbnail));
                } catch (thumbErr) {
                  console.error('[GIFT_FLOW] Mobile thumbnail conversion failed:', thumbErr);
                }
              }

              formData.append('description', '');
              formData.append('video_size', changeSize(video.fileSize));
              formData.append('group_ids', JSON.stringify([]));
              formData.append('visibility', '1');
              formData.append('subcategory_id', '');
              formData.append('playlist_ids', '');
              formData.append('videoHeight', video.videoHeight || '');
              formData.append('videoWidth', video.videoWidth || '');
              formData.append('videoOreintation', (video.videoHeight > video.videoWidth) ? '2' : '1');
              formData.append('s3_status', 'completed');

              // Log mobile addVideo payload
              const mobilePayloadLog = {};
              formData.forEach((value, key) => { mobilePayloadLog[key] = value instanceof Blob ? `Blob(${value.size} bytes)` : value; });
              console.log('[GIFT_FLOW] Mobile addVideo payload:', mobilePayloadLog);

              const isExistingUser = localStorage.getItem('existingUser') === 'true';

              let addVideoResponse;

              if (isExistingUser) {
                // Existing user: store action for later execution
                console.log('[GIFT_FLOW] Existing user — storing mobile addVideo as pending gift action');
                const actionPayload = {};
                formData.forEach((value, key) => {
                  if (!(value instanceof Blob)) {
                    actionPayload[key] = value;
                  }
                });
                const actionResponse = await addGiftAction({
                  gift_uuid: giftUserUid,
                  user_id: localStorage.getItem('giftUserId') || null,
                  action: {
                    action_id: `vid_mobile_${video.draftId}`,
                    action_type: 'addVideo',
                    endpoint: '/V6/addVideo',
                    method: 'POST',
                    payload: actionPayload,
                  },
                });
                console.log('[GIFT_FLOW] Mobile gift action stored successfully:', actionResponse?.data);
                addVideoResponse = { data: { success: true, draft_id: video.draftId } };
              } else {
                addVideoResponse = await apiAxios.post(`${baseUrl}/V6/addVideo`, formData, {
                  headers: { 'Content-Type': 'multipart/form-data' },
                });

                // Check if video already registered
                if (addVideoResponse?.data?.success === false && addVideoResponse?.data?.message?.includes('already exists')) {
                  console.log('[GIFT_FLOW] Mobile video already exists, skipping:', video.fileName);
                  registeredMobileIdsRef.current.add(video.draftId);
                  return;
                }

                console.log('[GIFT_FLOW] Mobile video registered successfully:', video.fileName);

                // Step 3: Link draft to document
                if (documentId && addVideoResponse?.data?.draft_id) {
                  try {
                    await updateDraftLink({
                      document_id: documentId,
                      draft_id: addVideoResponse.data.draft_id,
                    });
                    console.log('[GIFT_FLOW] Mobile draft linked to document successfully');
                  } catch (linkErr) {
                    console.error('[GIFT_FLOW] Failed to link mobile draft:', linkErr);
                  }
                }
              }

              // Mark as registered to prevent duplicate calls
              registeredMobileIdsRef.current.add(video.draftId);

              // Mark corresponding mobile upload entry as completed in UI and store content ID
              const mobileRegisteredContentId = addVideoResponse?.data?.content_id
                || addVideoResponse?.data?.video_id
                || addVideoResponse?.data?.id
                || null;
              setUploads((prev) => prev.map((u) => {
                if (u.isMobileUpload && u.fileName === video.fileName) {
                  if (u.status !== UPLOAD_STATUS.COMPLETED) {
                    return { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100, registeredContentId: mobileRegisteredContentId };
                  }
                  // Already completed — just attach the content ID so preview/delete buttons appear
                  if (mobileRegisteredContentId && !u.registeredContentId) {
                    return { ...u, registeredContentId: mobileRegisteredContentId };
                  }
                }
                return u;
              }));
            } catch (err) {
              console.error('[GIFT_FLOW] Failed to register mobile video:', video.fileName, err);
              logAppEvent({
                userName: 'Gift User',
                functionAffected: 'Gift Registration',
                description: `Gift mobile video registration failed — file: "${video.fileName}", draftId: ${video.draftId}, giftUuid: ${giftUserUid}`,
                severity: 'high',
                errorMessage: err?.response?.data?.message || err?.message || 'Mobile video registration failed',
                errorStack: err?.stack || null,
                responseStatus: err?.response?.status || null,
                component: 'giftFlowRegistration',
                userAction: `registering mobile gift video: ${video.fileName}`,
              });
              // Check if it's an "already exists" error (HTTP error response) — don't retry
              const errMsg = err?.response?.data?.message || err?.response?.data?.error || err?.message || '';
              if (errMsg.toLowerCase().includes('already exist') || errMsg.toLowerCase().includes('duplicate')) {
                console.log('[GIFT_FLOW] Mobile video already exists (error response), marking as done:', video.fileName);
                registeredMobileIdsRef.current.add(video.draftId);
              } else {
                // Track retry count to prevent infinite loop
                const retryCount = (video.retryCount || 0) + 1;
                if (retryCount < 3) {
                  failedVideos.push({ ...video, retryCount });
                } else {
                  console.error('[GIFT_FLOW] Mobile video registration failed after 3 retries, giving up:', video.fileName);
                  registeredMobileIdsRef.current.add(video.draftId);
                }
              }
            }
          }));
        }

        // Track which albums failed registration for retry
        const failedAlbums = [];

        // Register mobile-uploaded photo albums
        if (mobileMeta.albums && mobileMeta.albums.length > 0) {
          console.log('[GIFT_FLOW] Registering', mobileMeta.albums.length, 'mobile-uploaded album(s)');

          // Fetch existing album list (reuse any album created above for desktop photos)
          let mobileExistingAlbumId = null;
          const isMobileContributorFlow = !!urlParams.share;
          const savedMobileContNameForMatch = isMobileContributorFlow ? localStorage.getItem('contributorsName') : localStorage.getItem('senderName');
          const mobileContFirstName = savedMobileContNameForMatch ? savedMobileContNameForMatch.trim().split(' ')[0] : null;
          try {
            const albumListResponse = await apiAxios.post(`${baseUrl}/getAlbumList`, {});
            const existingAlbums = albumListResponse.data?.data || [];
            if (existingAlbums.length > 0) {
              if (mobileContFirstName) {
                // Contributor flow: match album whose title starts with contributor's first name
                const contMatch = existingAlbums.find((a) => a.title && a.title.startsWith(mobileContFirstName));
                if (contMatch) {
                  mobileExistingAlbumId = contMatch.album_id || contMatch.id;
                  console.log('[GIFT_FLOW] Mobile contributor flow: found matching album for', mobileContFirstName, '- album_id:', mobileExistingAlbumId);
                } else {
                  console.log('[GIFT_FLOW] Mobile contributor flow: no album matching', mobileContFirstName, '- will create new album');
                }
              } else {
                // Normal flow: use first existing album
                mobileExistingAlbumId = existingAlbums[0].album_id || existingAlbums[0].id;
                console.log('[GIFT_FLOW] Found existing album for mobile photos, album_id:', mobileExistingAlbumId);
              }
            }
          } catch (listErr) {
            console.warn('[GIFT_FLOW] Failed to fetch album list for mobile albums:', listErr);
          }

          // Merge all mobile album batches into a single album call to avoid creating separate albums per batch
          // Include photos from: selectedFiles albums, individual photos, AND mobile metadata itself
          const visibleMobilePhotoNames = new Set();
          selectedFilesRef.current.filter((f) => f.type === 'album').forEach((album) => {
            (album.photos || []).forEach((p) => { if (p.name) visibleMobilePhotoNames.add(p.name); });
          });
          // Also include individual photo files (type='photo' from skip-album flow)
          selectedFilesRef.current.filter((f) => f.type === 'photo').forEach((photo) => {
            if (photo.name) visibleMobilePhotoNames.add(photo.name);
          });
          // IMPORTANT: Also include photos from mobile metadata itself - this ensures mobile photos
          // are registered even if fileUpload push was missed and they weren't added to selectedFiles
          mobileMeta.albums.forEach((album) => {
            (album.photos || []).forEach((p) => { if (p.name) visibleMobilePhotoNames.add(p.name); });
          });

          const allMobilePhotos = [];
          const allMobileAlbumIds = [];
          // Use contributor/sender name for album title (e.g., "Layn's Album")
          const savedMobileContName = urlParams.share ? localStorage.getItem('contributorsName') : localStorage.getItem('senderName');
          let mobileAlbumName = savedMobileContName
            ? `${savedMobileContName.split(' ')[0]}'s Album`
            : 'All photos';
          mobileMeta.albums.forEach((album) => {
            if (registeredMobileIdsRef.current.has(album.albumUploadId)) {
              console.log('[GIFT_FLOW] Mobile album already registered, skipping:', album.albumName);
              return;
            }
            if (album.photos && album.photos.length > 0) {
              const filteredPhotos = album.photos.filter((p) => visibleMobilePhotoNames.has(p.name));
              if (filteredPhotos.length > 0) {
                allMobilePhotos.push(...filteredPhotos);
                allMobileAlbumIds.push(album.albumUploadId);
                if (!savedMobileContName) {
                  mobileAlbumName = album.albumName || mobileAlbumName;
                }
              }
            }
          });

          if (allMobilePhotos.length > 0) {
            try {
              let payload;
              if (mobileExistingAlbumId) {
                payload = {
                  album_id: mobileExistingAlbumId,
                  token: localStorage.getItem('token') || '',
                  photos: allMobilePhotos,
                };
              } else {
                payload = {
                  title: mobileAlbumName,
                  description: '',
                  visibility: '2',
                  group_id: '',
                  token: localStorage.getItem('token') || '',
                  icon: allMobilePhotos[0]?.path || '',
                  photos: allMobilePhotos,
                };
              }

              console.log('[GIFT_FLOW] Calling createAndAttachAlbum for mobile album:', {
                albumId: mobileExistingAlbumId || 'new',
                photoCount: payload.photos.length,
                batchCount: allMobileAlbumIds.length,
              });

              const isExistingUserMobileAlbum = localStorage.getItem('existingUser') === 'true';
              let mobileAlbumResponse;

              if (isExistingUserMobileAlbum) {
                // Existing user: store action for later execution
                console.log('[GIFT_FLOW] Existing user — storing mobile createAndAttachAlbum as pending gift action');
                const { token, ...mobileActionPayload } = payload;
                const actionResponse = await addGiftAction({
                  gift_uuid: giftUserUid,
                  user_id: localStorage.getItem('giftUserId') || null,
                  action: {
                    action_id: `album_mobile_${allMobileAlbumIds[0] || Date.now()}`,
                    action_type: 'createAndAttachAlbum',
                    endpoint: '/createAndAttachAlbum',
                    method: 'POST',
                    payload: mobileActionPayload,
                  },
                });
                console.log('[GIFT_FLOW] Mobile album gift action stored successfully:', actionResponse?.data);
                mobileAlbumResponse = { data: { success: true } };
              } else {
                mobileAlbumResponse = await apiAxios.post(`${baseUrl}/createAndAttachAlbum`, payload);
              }

              // Mark all batches as registered
              allMobileAlbumIds.forEach((id) => registeredMobileIdsRef.current.add(id));
              console.log('[GIFT_FLOW] Mobile album created and attached successfully:', mobileAlbumName);

              const mobileRegisteredAlbumId = mobileExistingAlbumId
                || mobileAlbumResponse?.data?.album_id
                || mobileAlbumResponse?.data?.id
                || null;

              // Fetch album detail to get server photo_ids before marking complete
              if (mobileRegisteredAlbumId) {
                try {
                  const detailRes = await apiAxios.post(`${baseUrl}/${endpoints.getMyAlbumDetail}`, { album_id: mobileRegisteredAlbumId });
                  if (detailRes?.data?.success && detailRes?.data?.data) {
                    const mobileAlbumPhotos = detailRes.data.data[0]?.photos || detailRes.data.data?.photos || [];
                    allMobileAlbumIds.forEach((uid) => {
                      setFetchedAlbumPhotos((prev) => ({ ...prev, [uid]: { loading: false, photos: mobileAlbumPhotos } }));
                    });
                    // Attach server photo_id to local photos in selectedFiles by matching order
                    if (mobileAlbumPhotos.length > 0) {
                      const sorted = [...mobileAlbumPhotos].sort((a, b) => Number(a.order_number) - Number(b.order_number));
                      const nameToPhotoId = {};
                      allMobilePhotos.forEach((lp, idx) => {
                        if (sorted[idx]) nameToPhotoId[lp.name] = sorted[idx].id;
                      });
                      setSelectedFiles((prev) => prev.map((f) => {
                        if (f.type !== 'album' || !f.photos) return f;
                        const hasUpdate = f.photos.some((p) => nameToPhotoId[p.name]);
                        if (!hasUpdate) return f;
                        return { ...f, photos: f.photos.map((p) => (nameToPhotoId[p.name] ? { ...p, photo_id: nameToPhotoId[p.name] } : p)) };
                      }));
                    }
                  }
                } catch (detailErr) {
                  console.error('[GIFT_FLOW] Failed to fetch mobile album detail after create:', detailErr);
                }
              }

              // Update all mobile album upload entries with the registeredAlbumId
              setUploads((prev) => prev.map((u) => {
                const isMatch = u.isMobileUpload && (
                  allMobileAlbumIds.includes(u.id)
                  || u.fileName === mobileAlbumName
                  || u.fileName.startsWith(`${mobileAlbumName} (`)
                  || u.fileName === ALL_PHOTOS_ALBUM_NAME
                  || u.fileName.startsWith('All photos (')
                );
                if (isMatch) {
                  if (u.status !== UPLOAD_STATUS.COMPLETED) {
                    return { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100, registeredAlbumId: mobileRegisteredAlbumId };
                  }
                  if (mobileRegisteredAlbumId && !u.registeredAlbumId) {
                    return { ...u, registeredAlbumId: mobileRegisteredAlbumId };
                  }
                }
                return u;
              }));
            } catch (albumErr) {
              console.error('[GIFT_FLOW] Failed to create mobile album:', albumErr);
              logAppEvent({
                userName: 'Gift User',
                functionAffected: 'Gift Registration',
                description: `Gift mobile album registration failed — photoCount: ${allMobilePhotos.length}, batchCount: ${allMobileAlbumIds.length}, giftUuid: ${giftUserUid}`,
                severity: 'high',
                errorMessage: albumErr?.response?.data?.message || albumErr?.message || 'Mobile album registration failed',
                errorStack: albumErr?.stack || null,
                responseStatus: albumErr?.response?.status || null,
                component: 'giftFlowRegistration',
                userAction: 'registering mobile gift photo album',
              });
              // Track failed albums for retry with retry count limit
              mobileMeta.albums.forEach((album) => {
                if (!registeredMobileIdsRef.current.has(album.albumUploadId)) {
                  const retryCount = (album.retryCount || 0) + 1;
                  if (retryCount < 3) {
                    failedAlbums.push({ ...album, retryCount });
                  } else {
                    console.error('[GIFT_FLOW] Mobile album registration failed after 3 retries, giving up:', album.albumName);
                    registeredMobileIdsRef.current.add(album.albumUploadId);
                  }
                }
              });
            }
          }
        }

        // If any videos or albums failed, put them back for retry on next call
        if (failedVideos.length > 0 || failedAlbums.length > 0) {
          console.log('[GIFT_FLOW] Some mobile uploads failed, putting back for retry:', {
            failedVideos: failedVideos.length,
            failedAlbums: failedAlbums.length,
          });
          mobileUploadMetadataRef.current = {
            videos: failedVideos,
            albums: failedAlbums,
          };
        }
      }

      // After all registrations, update gift user with contributor/sender upload details
      try {
        const isContributor = !!urlParams.share;
        const personName = isContributor
          ? (localStorage.getItem('contributorsName') || '').trim()
          : (localStorage.getItem('senderName') || '').trim();
        const displayName = personName.split(' ')[0] || 'unknown';

        // Compute counts and sizes from selectedFiles
        const currentFiles = selectedFilesRef.current || [];
        const videoItems = currentFiles.filter((f) => f.type === 'video');
        const albumItems = currentFiles.filter((f) => f.type === 'album');
        const photoItems = currentFiles.filter((f) => f.type === 'photo');
        const totalVideos = videoItems.length;
        const totalAlbums = albumItems.length + (photoItems.length > 0 ? 1 : 0);
        const totalPhotos = albumItems.reduce((sum, a) => sum + (a.photos?.length || a.photoCount || 0), 0) + photoItems.length;
        const videosSize = videoItems.reduce((sum, f) => sum + (f.size || 0), 0);
        const photosSize = albumItems.reduce((sum, f) => sum + (f.size || 0), 0)
          + photoItems.reduce((sum, f) => sum + (f.size || 0), 0);

        if (giftUserUid && (totalVideos > 0 || totalPhotos > 0)) {
          const contributorDetails = {
            [displayName]: {
              videos: totalVideos,
              albums: totalAlbums,
              photos: totalPhotos,
              videos_size: videosSize,
              photos_size: photosSize,
            },
          };

          console.log('[GIFT_FLOW] Updating gift user with contributor_details:', contributorDetails);
          await updateGiftUser({
            uid: giftUserUid,
            note: '',
            contributor_details: contributorDetails,
          });
          console.log('[GIFT_FLOW] Gift user updated with contributor_details successfully');
        }
      } catch (detailsErr) {
        console.error('[GIFT_FLOW] Failed to update gift user with contributor_details:', detailsErr);
        logAppEvent({
          userName: 'Gift User',
          functionAffected: 'Gift Update User',
          description: `updateGiftUser failed (contributor details) — uid: "${giftUserUid}"`,
          severity: 'high',
          errorMessage: detailsErr?.response?.data?.message || detailsErr?.message || 'Update gift user with contributor details failed',
          errorStack: detailsErr?.stack || null,
          responseStatus: detailsErr?.response?.status || null,
          component: 'giftFlowUpdateUser',
          userAction: 'updating gift user with contributor details',
        });
      }

      setContentFetched(false); // Refresh content tab

      // Share contributor: shareComplete push removed — no push needed in contributor flow
    } finally {
      // Re-check if more uploads completed while we were registering
      const remainingS3 = uploadManagerSecure.getS3CompleteUploads();
      const remainingPhotos = photoMetadataRef.current.size > 0;
      const remainingMobileMeta = mobileUploadMetadataRef.current !== null;
      const hasRemaining = (remainingS3.length > 0 || remainingPhotos || remainingMobileMeta) && paymentDoneRef.current;
      if (hasRemaining) {
        // Don't reset isRegistering — keep "Almost ready" visible to avoid flicker
        isRegisteringRef.current = false;
        setTimeout(() => {
          if (registerS3CompleteUploadsRef.current) {
            registerS3CompleteUploadsRef.current();
          }
        }, 300);
      } else {
        isRegisteringRef.current = false;
        setIsRegistering(false);
      }
    }
  }, [giftUserUid]);

  // Keep ref in sync for subscription effect access
  useEffect(() => {
    registerS3CompleteUploadsRef.current = registerS3CompleteUploads;
  }, [registerS3CompleteUploads]);

  // Handle payment success
  const handlePaymentSuccess = async (responseData) => {
    paymentDoneRef.current = true;

    // Store user ID from registerGiftUser response for existing user gift actions
    const giftUserId = responseData?.data?.userDetails?.[0]?.id;
    if (giftUserId) {
      localStorage.setItem('giftUserId', giftUserId);
    }

    // Store setpassword_url from registerGiftUser response
    const passwordUrl = responseData?.data?.setpassword_url || responseData?.setpassword_url || '';
    if (passwordUrl) {
      setSetpasswordUrl(passwordUrl);
    }
    paywebRequestedRef.current = false;

    // For multiple occasions, note is typed on payment page — send it now
    if (giftUserUid && formData.note) {
      updateGiftUser({
        uid: giftUserUid,
        note: formData.note
      }).catch((err) => {
        console.error('Error updating gift user note after payment:', err);
        logAppEvent({
          userName: 'Gift User',
          functionAffected: 'Gift Update User',
          description: `updateGiftUser failed (after payment) — payload: { uid: "${giftUserUid}", note: "${formData.note || ''}" }`,
          severity: 'high',
          errorMessage: err?.response?.data?.message || err?.message || 'Update gift user note failed',
          errorStack: err?.stack || null,
          responseStatus: err?.response?.status || null,
          component: 'giftFlowUpdateUser',
          userAction: 'updating gift user note after payment',
        });
      });
    }

    // Send push message with token to the other device
    // Only needed when mobile pays (to notify desktop) — not when desktop pays,
    // since desktop handles all registration itself and mobile doesn't need notification.
    const token = localStorage.getItem('token');
    if (isMobileUploadMode && giftUserUid) {
      console.log('[MOBILE_FLOW] Mobile payment done, sending token to desktop');
      await sendMobilePushMessage('paymentDone', [], token);
      console.log('[MOBILE_FLOW] Sent "paymentDone" push to desktop');
    }

    // Format delivery date for display
    const formattedDeliveryDate = deliveryDate
      ? `${(new Date(`${deliveryDate}T00:00:00`).getMonth() + 1).toString().padStart(2, '0')}/${new Date(`${deliveryDate}T00:00:00`).getDate().toString().padStart(2, '0')}/${new Date(`${deliveryDate}T00:00:00`).getFullYear()}`
      : '';

    // Show success message
    // const deliveryMsg = formattedDeliveryDate
    //   ? `Purchase successful! Your gift will be on its way on ${formattedDeliveryDate}.`
    //   : 'Purchase successful!';
    setSnackbar({
      isVisible: true,
      message: 'Your Purchase was Successful',
      type: 'success'
    });

    // If "You Help Build" was selected, uploads are already running in background
    // Navigate to progress view and register S3-complete uploads
    if (selectedChoice === 'you-help-build') {
      setShowPayment(false);
      setPaymentData(null);
      animateToStep(5); // Go to upload progress view

      // Send gift email without waiting for it to complete
      sendGiftEmail({ uuid: giftUserUid }).catch((error) => {
        console.error('Error sending gift email:', error);
        logAppEvent({
          userName: 'Gift User',
          functionAffected: 'Gift Email',
          description: `sendGiftEmail failed (you-help-build flow) — giftUuid: ${giftUserUid}`,
          severity: 'high',
          errorMessage: error?.response?.data?.message || error?.message || 'Send gift email failed',
          errorStack: error?.stack || null,
          responseStatus: error?.response?.status || null,
          component: 'giftFlowEmail',
          userAction: 'sending gift email after payment',
        });
      });

      // If user already uploaded from mobile, mark mobile entries as complete
      if (mobileUploadCompleted) {
        setUploads((prev) => prev.map((u) => {
          if (u.isMobileUpload && u.status !== UPLOAD_STATUS.COMPLETED) {
            return { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100 };
          }
          return u;
        }));
      }

      // Start persistent poll for all mobile push types (SSE is unreliable)
      // Handles: fileUpload* (new files), fileComplete, allUploadsComplete, re-uploads
      if (isMobileDesktopConnected) {
        if (mobileCompletionPollRef.current) clearInterval(mobileCompletionPollRef.current);
        let lastSeenPush = '';
        mobileCompletionPollRef.current = setInterval(async () => {
          // Stop if navigated away from step 5
          if (currentStepRef.current !== 5) {
            clearInterval(mobileCompletionPollRef.current);
            mobileCompletionPollRef.current = null;
            return;
          }
          try {
            const statusRes = await getGiftStatus(giftUserUid);
            const statusData = statusRes?.data?.data || statusRes?.data || {};
            const pushStr = statusData.push || '';

            // Skip if push hasn't changed
            if (pushStr === lastSeenPush) return;
            lastSeenPush = pushStr;

            if (!pushStr.includes || !pushStr.includes('mobileUpload:true')) return;
            console.log('[MOBILE_FLOW] Post-payment poll - new push:', pushStr);

            // Handle allUploadsComplete
            if (pushStr.includes && pushStr.includes('allUploadsComplete') && pushStr.includes('mobileUpload:true')) {
              console.log('[MOBILE_FLOW] Poll detected mobile allUploadsComplete');

              // Mark all remaining mobile uploads as completed
              setUploads((prev) => prev.map((u) => {
                if (u.isMobileUpload && u.status !== UPLOAD_STATUS.COMPLETED) {
                  return { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100 };
                }
                return u;
              }));

              const hasOwnActive = uploadsRef.current.some(
                (u) => !u.isMobileUpload && (u.status === UPLOAD_STATUS.UPLOADING || u.status === UPLOAD_STATUS.PENDING || u.status === UPLOAD_STATUS.PAUSED)
              );
              if (!hasOwnActive) {
                setIsUploading(false);
              }
              setMobileUploadCompleted(true);
              setContentFetched(false);

              // Extract and store mobile upload metadata for desktop-side registration
              extractAndStoreMobileMetadata(null, pushStr);

              // Create missing preview entries if fileUpload was overwritten (instant upload race)
              createMissingMobileEntries();
              // Flip QR card back — fileUpload push was overwritten so it never closed QR
              setShowQrCode(false);
              setShowFilesQrCode(false);
              setShowShareModal(false);

              // Update video preview thumbnails — use mobile thumbnail for consistent display
              if (mobileUploadMetadataRef.current?.videos?.length > 0) {
                const s3BaseUrl = generateS3Url();
                setSelectedFiles((prev) => prev.map((f) => {
                  if (f.source === 'mobile-preview' && f.type === 'video') {
                    const videoMeta = mobileUploadMetadataRef.current.videos.find((v) => v.fileName === f.name);
                    if (videoMeta) {
                      const updates = {};
                      if (!f.previewUrl && videoMeta.s3Key) {
                        updates.previewUrl = `${s3BaseUrl}${videoMeta.s3Key}`;
                      }
                      if (videoMeta.thumbnail) {
                        updates.posterUrl = videoMeta.thumbnail;
                      }
                      if (Object.keys(updates).length > 0) {
                        return { ...f, ...updates };
                      }
                    }
                  }
                  return f;
                }));
              }

              // Payment is already done at this point (post-payment poll), register mobile uploads
              if (mobileUploadMetadataRef.current && registerS3CompleteUploadsRef.current) {
                registerS3CompleteUploadsRef.current();
              }
            }

            // Handle individual fileComplete — decode name|uid format
            if (pushStr.includes && pushStr.includes('fileComplete') && pushStr.includes('mobileUpload:true')) {
              const filesMatch = pushStr.match(/files:\["?([^"\]]+)"?\]/);
              if (filesMatch && filesMatch[1]) {
                const rawEntry = filesMatch[1];
                const pIdx = rawEntry.lastIndexOf('|');
                const completedFileName = pIdx > 0 ? rawEntry.substring(0, pIdx) : rawEntry;
                const completedUid = pIdx > 0 ? rawEntry.substring(pIdx + 1) : null;
                console.log('[MOBILE_FLOW] Poll detected mobile fileComplete:', completedFileName, 'uid:', completedUid);
                setUploads((prev) => {
                  let matched = false;
                  return prev.map((u) => {
                    if (matched || !u.isMobileUpload || u.status === UPLOAD_STATUS.COMPLETED) return u;
                    const uidMatch = completedUid && u.mobileUid && u.mobileUid === completedUid;
                    const nameMatch = !completedUid && u.fileName === completedFileName;
                    if (uidMatch || nameMatch) {
                      matched = true;
                      return { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100 };
                    }
                    return u;
                  });
                });
              }
            }
          } catch (err) {
            console.warn('[MOBILE_FLOW] Post-payment poll error:', err);
          }
        }, 3000);
      }

      // Register all S3-complete uploads now that we have a token
      registerS3CompleteUploads();

      return;
    }

    // For "They Build", show success modal
    const expressData = {
      plan_name: paymentData?.planName || 'Projector Gift',
      is_trial: false,
      plan_duration: '730',
      datetime: new Date().toISOString(),
    };

    // Send gift email without waiting for it to complete
    sendGiftEmail({ uuid: giftUserUid }).catch((error) => {
      console.error('Error sending gift email:', error);
      logAppEvent({
        userName: 'Gift User',
        functionAffected: 'Gift Email',
        description: `sendGiftEmail failed (they-build flow) — giftUuid: ${giftUserUid}`,
        severity: 'high',
        errorMessage: error?.response?.data?.message || error?.message || 'Send gift email failed',
        errorStack: error?.stack || null,
        responseStatus: error?.response?.status || null,
        component: 'giftFlowEmail',
        userAction: 'sending gift email after payment',
      });
    });

    setGiftExpressData(expressData);
    setShowSuccessModal(true);
  };

  // Handle payment error
  const handlePaymentError = (errorMsg) => {
    console.error('Payment error:', errorMsg);

    // Show error message to user
    setSnackbar({
      isVisible: true,
      message: `Payment failed: ${errorMsg || 'Please try again'}`,
      type: 'error'
    });
    // Force dismiss on iOS where MUI autoHideDuration can be unreliable
    setTimeout(() => setSnackbar((prev) => ({ ...prev, isVisible: false })), 4000);
  };

  const resetTurnstile = () => {
    if (window.turnstile && turnstileRef.current) {
      // Remove existing widget
      if (turnstileWidgetId.current) {
        try {
          window.turnstile.remove(turnstileWidgetId.current);
        } catch (e) {
          console.log('Error removing Turnstile widget:', e);
        }
        turnstileWidgetId.current = null;
      }
      // Clear the container
      turnstileRef.current.innerHTML = '';
      setCloudflareToken('');
      setCloudflareVerifying(true);
    }
  };

  const renderTurnstile = () => {
    if (window.turnstile && turnstileRef.current) {
      try {
        // Check if already rendered to avoid duplicate widgets
        if (!turnstileRef.current.hasChildNodes()) {
          setCloudflareVerifying(true);
          turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
            sitekey: CLOUDFLARE_TURNSTILE_SITE_KEY,
            callback: (token) => {
              console.log('Turnstile token received:', token);
              setCloudflareToken(token);
              setCloudflareVerifying(false);
            },
            'expired-callback': () => {
              console.log('Turnstile token expired, refreshing...');
              setCloudflareToken('');
              setCloudflareVerifying(true);
              // Reset and re-render to get a new token
              if (window.turnstile && turnstileWidgetId.current) {
                window.turnstile.reset(turnstileWidgetId.current);
              }
            },
            'error-callback': () => {
              console.error('Turnstile error occurred');
              setCloudflareVerifying(false);
            },
            theme: 'light',
          });
        }
      } catch (error) {
        console.error('Error rendering Turnstile:', error);
        setCloudflareVerifying(false);
      }
    }
  };

  useEffect(() => {
    const loadScriptByURL = (id, url, callback) => {
      const isScriptExist = document.getElementById(id);
      if (!isScriptExist) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        script.onload = function () {
          if (callback) callback();
        };
        document.body.appendChild(script);
      }
      if (isScriptExist && callback) callback();
    };

    // Load Cloudflare Turnstile script
    loadScriptByURL(
      'turnstile-script',
      'https://challenges.cloudflare.com/turnstile/v0/api.js',
      () => {
        console.log('Turnstile script loaded');
        // Attempt to render after script loads
        setTimeout(() => {
          renderTurnstile();
        }, 100);
      }
    );
  }, []);

  useEffect(() => {
    // Render Turnstile when on Step 1 substep 1 and showing the "To" (recipient) form
    if (currentStep === 1 && step1SubStep === 1 && !showSenderInline) {
      // Reset and re-render Turnstile
      resetTurnstile();
      const timer = setTimeout(() => {
        renderTurnstile();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentStep, step1SubStep, showSenderInline]);

  // Handle viewport resize - reset Turnstile when crossing mobile/desktop breakpoint
  useEffect(() => {
    const MOBILE_BREAKPOINT = 768;
    let lastViewportType = window.innerWidth < MOBILE_BREAKPOINT ? 'mobile' : 'desktop';

    const handleResize = () => {
      const currentViewportType = window.innerWidth < MOBILE_BREAKPOINT ? 'mobile' : 'desktop';

      // Only reset if we crossed the breakpoint
      if (currentViewportType !== lastViewportType) {
        console.log('Viewport changed from', lastViewportType, 'to', currentViewportType);
        lastViewportType = currentViewportType;

        // Reset and re-render Turnstile
        resetTurnstile();
        setTimeout(() => {
          renderTurnstile();
        }, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track grid animation completion for Step 1 sub-step 2
  useEffect(() => {
    if (currentStep === 1 && step1SubStep === 2) {
      setGridAnimationComplete(false);
      // Last button (index 8) delay: 2.4s + animation duration: 1.6s = 4.0s
      const timer = setTimeout(() => {
        setGridAnimationComplete(true);
      }, 2900);
      return () => clearTimeout(timer);
    }
  }, [currentStep, step1SubStep]);

  // Allow Enter key to trigger Continue on Step 1 sub-step 2 (occasion selection)
  useEffect(() => {
    if (currentStep !== 1 || step1SubStep !== 2 || !gridAnimationComplete) return undefined;
    const handleEnter = (e) => {
      if (e.key === 'Enter' && formData.occasions && formData.occasions.length > 0 && !(formData.occasions.includes('Other Milestone') && !formData.note)) {
        // Don't trigger when typing in the "Other Milestone" textarea
        if (e.target.tagName === 'TEXTAREA') return;
        handleLoginClick();
      }
    };
    document.addEventListener('keydown', handleEnter);
    return () => document.removeEventListener('keydown', handleEnter);
  }, [currentStep, step1SubStep, gridAnimationComplete, formData.occasions, formData.note]);

  // Auto-focus note textarea when it appears and place cursor at end
  useEffect(() => {
    if (gridAnimationComplete && noteTextareaRef.current) {
      const textarea = noteTextareaRef.current;
      textarea.focus();
      textarea.selectionStart = textarea.value.length;
      textarea.selectionEnd = textarea.value.length;
    }
  }, [gridAnimationComplete]);

  // Success Section - Full Page View
  if (showSuccessSection) {
    // Compute delivery date helpers
    const isFutureDelivery = (() => {
      if (!deliveryDate) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const delivery = new Date(deliveryDate);
      delivery.setHours(0, 0, 0, 0);
      return delivery > today;
    })();

    const formattedDeliveryDate = deliveryDate
      ? `${(new Date(`${deliveryDate}T00:00:00`).getMonth() + 1).toString().padStart(2, '0')}/${new Date(`${deliveryDate}T00:00:00`).getDate().toString().padStart(2, '0')}/${new Date(`${deliveryDate}T00:00:00`).getFullYear()}`
      : null;

    // Use API data with local state fallback
    const recipientEmail = giftStatusData?.email || formData.email || formData.cardholderEmail || '';
    const recipientName = giftStatusData ? `${giftStatusData.firstname || ''} ${giftStatusData.lastname || ''}`.trim() : (formData.firstname ? formData.firstname.trim() : '');
    const senderName = giftStatusData?.from_name || formData.cardholderName || '';
    const senderEmail = giftStatusData?.from_email || formData.cardholderEmail || '';
    const displayDeliveryDate = formattedDeliveryDate || (giftStatusData?.delivery_date ? `${(new Date(`${giftStatusData.delivery_date}T00:00:00`).getMonth() + 1).toString().padStart(2, '0')}/${new Date(`${giftStatusData.delivery_date}T00:00:00`).getDate().toString().padStart(2, '0')}/${new Date(`${giftStatusData.delivery_date}T00:00:00`).getFullYear()}` : '');
    const planName = giftStatusData?.plan_details?.product_name || selectedPlan?.name || '';
    const planAmount = giftStatusData?.plan_details ? `$${(parseFloat(giftStatusData.plan_details.amount) / 100).toFixed(2)}` : (selectedPlan?.amount ? `$${parseFloat(selectedPlan.amount).toFixed(2)}` : '');
    const planDescription = giftStatusData?.plan_details?.price_description || '';
    const apiDeliveryStatus = giftStatusData?.delivery_status;

    // Resend email handler
    const handleResendEmail = async () => {
      if (emailSentStatus === 'resending') return;
      setEmailSentStatus('resending');
      try {
        await sendGiftEmail({ uuid: giftUserUid });
        setEmailSentStatus('resent');
        setSnackbar({ isVisible: true, message: 'Gift email resent successfully!', type: 'success' });
        // Reset back to sent after 3s
        setTimeout(() => setEmailSentStatus('sent'), 3000);
      } catch (err) {
        console.error('[GIFT_FLOW] Resend email error:', err);
        setEmailSentStatus('sent');
        setSnackbar({ isVisible: true, message: 'Failed to resend email. Try again.', type: 'error' });
      }
    };

    // Send via SMS handler
    const handleSendSms = async () => {
      if (smsSentStatus === 'sending') return;
      setSmsError('');
      // Basic phone validation
      const cleaned = smsPhoneNumber.replace(/[^\d+]/g, '');
      if (cleaned.length < 10) {
        setSmsError('Please enter a valid phone number');
        return;
      }
      setSmsSentStatus('sending');
      try {
        const fullNumber = cleaned.startsWith('+') ? cleaned : `${smsCountryCode}${cleaned}`;
        const userToken = localStorage.getItem('token') || urlParams.token || '';
        await sendGiftLinkToPhone({ mobile: fullNumber, uid: giftUserUid, token: userToken });
        setSnackbar({ isVisible: true, message: 'Gift link sent via SMS!', type: 'success' });
        setSmsPhoneNumber('');
        setSmsSentStatus('idle');
      } catch (err) {
        console.error('[GIFT_FLOW] SMS send error:', err);
        setSmsSentStatus('idle');
        setSmsError('Failed to open SMS. Please copy the link manually.');
      }
    };

    return (
      <div className={styles.giftPage}>
        <a href="https://www.projectorstream.com/" className={styles.successLogoLink}>
          <Logo className={styles.logo} isLarge isLight />
        </a>
        <button
          className={styles.sendAnotherGiftButton}
          onClick={() => { window.location.href = '/gift'; }}
        >
          Send Another Gift
        </button>
        <div className={styles.successPageContainer}>
          {/* Common centered heading */}
          <div className={styles.successHeader}>
            <h2 className={styles.successTitle}>
              Delivery Instructions for Their Projector
              {/* <span className={styles.successTitleWord}>Their Projector</span>
              {' '}
              <br className={styles.mobileLineBreak} />
              {isFutureDelivery ? (
                <>
                  <span className={styles.successTitleWord}>Will Be</span>
                  {' '}
                  <span style={{ color: '#5aa5ef' }}>Sent</span>
                  {' '}
                  <span className={styles.successTitleWord}>{`on ${formattedDeliveryDate}`}</span>
                </>
              ) : (
                <>
                  <span className={styles.successTitleWord}>is ready to be</span>
                  {' '}
                  <span style={{ color: '#5aa5ef' }}>Sent</span>
                </>
              )} */}
            </h2>
            {recipientEmail && (
              <p className={styles.successSubtitle}>
                {`to ${recipientEmail}`}
              </p>
            )}
            <p className={styles.successSpamNote}>
              {isFutureDelivery
                ? `Please check your spam folder on ${formattedDeliveryDate} for your receipt and Projector link`
                : 'Please check your spam folder for your receipt and Projector link'}
            </p>
            <div className={styles.successSparkles}>
              <svg className={styles.successSparkle} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="#5aa5ef" />
              </svg>
              <svg className={styles.successSparkle2} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="#3d8bd4" />
              </svg>
              <svg className={styles.successSparkle3} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="#5aa5ef" />
              </svg>
            </div>
          </div>

          <div className={styles.successDashboard}>
            {/* LEFT COLUMN - Existing success content */}
              <div className={styles.successSection}>
                {/* If you wish to copy gift link text - commented out but preserving space */}
                {/* <div style={{ margin: '12px 0 12px 0', visibility: 'hidden' }}>
                  <p style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>
                    &nbsp;
                  </p>
                </div> */}
                {/*
                <p style={{ color: '#fff', fontFamily: "'Montserrat', sans-serif", fontSize: '1rem', fontWeight: 600, margin: '12px 0 12px 0', textAlign: 'center' }}>
                  {'If you wish to copy the gift link to the person, '}
                  <span
                    role="button"
                    tabIndex={0}
                    style={{ textDecoration: 'underline', color: '#5aa5ef', cursor: 'pointer', fontWeight: 700, display: 'block' }}
                    onClick={() => {
                      if (setpasswordUrl) {
                        navigator.clipboard.writeText(setpasswordUrl).then(() => {
                          setSnackbar({ isVisible: true, message: 'Signup link copied to clipboard!', type: 'success' });
                        }).catch(() => {
                          const textArea = document.createElement('textarea');
                          textArea.value = setpasswordUrl;
                          textArea.style.position = 'fixed';
                          textArea.style.left = '-9999px';
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                          setSnackbar({ isVisible: true, message: 'Signup link copied to clipboard!', type: 'success' });
                        });
                      }
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.click(); }}
                  >
                    Copy Projector Signup link here.
                  </span>
                </p>
                */}
                {/* Send Gift Options - Single container */}
                <div className={styles.successSendContainer}>
                  <div className={styles.successSendSection}>
                    {/* QR Code Section */}
                    {!isMobileUploadMode && (
                      <>
                        <p className={styles.successSendText}>Send QR Code For Others To Add Media</p>
                        <div className={styles.successQrContainer}>
                          <div className={styles.successQrWrapper}>
                            <QRCodeComponent
                              url={shortShareUrl || `${window.location.origin}/${buildSharePath()}`}
                              size={150}
                            />
                          </div>
                          <button
                            className={styles.successQrDownloadButton}
                            onClick={() => {
                              const canvas = document.querySelector(`.${styles.successQrWrapper} canvas`);
                              if (canvas) {
                                const link = document.createElement('a');
                                link.download = 'gift-qr-code.png';
                                link.href = canvas.toDataURL('image/png');
                                link.click();
                              }
                            }}
                          >
                            <span style={{ fontSize: '0.65rem', fontWeight: '600', letterSpacing: '0.02em' }}>Download</span>
                          </button>
                        </div>
                      </>
                    )}

                    {/* Send URL Section */}
                    <p className={styles.successSendText} style={{ marginTop: isMobileUploadMode ? 0 : '8px' }}>Share Link to Add Media</p>
                    <div className={styles.successUrlInputWrapper}>
                      <input
                        type="text"
                        className={styles.successUrlInput}
                        value={shortShareUrl || `${window.location.origin}/${buildSharePath()}`}
                        disabled
                        readOnly
                      />
                      <button
                        className={styles.successUrlCopyButton}
                        onClick={async () => {
                          const url = await getShortenedShareUrl();
                          navigator.clipboard.writeText(url);
                          setSnackbar({
                            isVisible: true,
                            message: 'Link copied to clipboard!',
                            type: 'success'
                          });
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="9" y="9" width="13" height="13" rx="2" stroke="white" strokeWidth="2" />
                          <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="white" strokeWidth="2" />
                        </svg>
                      </button>
                    </div>
                    <p style={{ color: '#aaa', fontFamily: "'Montserrat', sans-serif", fontSize: '0.8rem', fontWeight: 500, margin: '8px 0 0 0', textAlign: 'center' }}>Copy and save this link to add more content to Projector</p>
                  </div>
                </div>
              </div>

            {/* RIGHT COLUMN - Gift Status Dashboard */}
            <div className={styles.successDashboardRight}>
              <div className={styles.statusPanel}>
                <h3 className={styles.statusPanelTitle}>Delivery Instructions</h3>

                {/* Plan Info */}
                <div className={styles.statusCard}>
                  <div className={styles.statusCardHeader}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="#5aa5ef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className={styles.statusCardLabel}>Projector Plan</span>
                  </div>
                  <div className={styles.statusCardValue} style={{ color: '#5aa5ef', fontSize: '1rem' }}>
                    {planAmount || '$99.99'}
                    {planDescription && (
                      <span style={{ marginLeft: '6px', opacity: 0.7 }}>
                        (
                        {planDescription}
                        )
                      </span>
                    )}
                  </div>
                </div>

                {/* Gift Received Status */}
                <div className={styles.statusCard}>
                  <div className={styles.statusCardHeader}>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 12V22H4V12" stroke="#5aa5ef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M22 7H2V12H22V7Z" stroke="#5aa5ef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M12 22V7" stroke="#5aa5ef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M12 7H7.5C6.83696 7 6.20107 6.73661 5.73223 6.26777C5.26339 5.79893 5 5.16304 5 4.5C5 3.83696 5.26339 3.20107 5.73223 2.73223C6.20107 2.26339 6.83696 2 7.5 2C11 2 12 7 12 7Z" stroke="#5aa5ef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M12 7H16.5C17.163 7 17.7989 6.73661 18.2678 6.26777C18.7366 5.79893 19 5.16304 19 4.5C19 3.83696 18.7366 3.20107 18.2678 2.73223C17.7989 2.26339 17.163 2 16.5 2C13 2 12 7 12 7Z" stroke="#5aa5ef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className={styles.statusCardLabel}>Delivery Status</span>
                  </div>
                  <div className={styles.statusProgressRow}>
                    <div className={`${styles.statusStep} ${styles.statusStepDone}`}>
                      <div className={styles.statusStepDot} />
                      <span>Gift Ready</span>
                    </div>
                    <div className={`${styles.statusStepLine} ${giftDeliveryStatus === 'opened' ? styles.statusStepLineDone : ''}`} />
                    <div className={`${styles.statusStep} ${giftDeliveryStatus === 'opened' ? styles.statusStepDone : ''}`}>
                      <div className={styles.statusStepDot} />
                      <span>Opened</span>
                    </div>
                  </div>

                  {/* Send Yourself */}
                  <div style={{ width: '100%', height: '1px', background: 'rgba(255,255,255,0.1)', margin: '8px 0' }} />
                  <div className={styles.statusCardHeader} style={{ flex: 1 }}>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="2" y="4" width="20" height="16" rx="2" stroke="#5aa5ef" strokeWidth="2" />
                      <path d="M22 7L13.03 12.7C12.4 13.07 11.6 13.07 10.97 12.7L2 7" stroke="#5aa5ef" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <span className={styles.statusCardLabel}>Copy and Send By Email</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
                      <span
                        role="button"
                        tabIndex={0}
                        title="Copy gift signup link"
                        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}
                        onClick={async () => {
                          if (!cachedEmailHtml) {
                            setSnackbar({ isVisible: true, message: 'Email content is loading, please try again', type: 'error' });
                            return;
                          }
                          const plainText = cachedEmailHtml
                            .replace(/<head[\s\S]*?<\/head>/gi, '')
                            .replace(/<style[\s\S]*?<\/style>/gi, '')
                            .replace(/<a\s[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, '$2 ($1)')
                            .replace(/<[^>]*>/g, '')
                            .replace(/\s{2,}/g, '\n')
                            .trim();
                          // Mobile: open share intent, then overwrite clipboard with rich HTML after
                          if (window.innerWidth <= 1024 && navigator.share) {
                            try {
                              await navigator.share({
                                title: 'Projector Gift',
                                text: plainText,
                              });
                              // Share resolved (user picked Copy or an app) — overwrite clipboard with rich HTML
                              try {
                                if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
                                  await navigator.clipboard.write([
                                    new ClipboardItem({
                                      'text/html': new Blob([cachedEmailHtml], { type: 'text/html' }),
                                      'text/plain': new Blob([plainText], { type: 'text/plain' }),
                                    }),
                                  ]);
                                } else {
                                  const el = document.createElement('div');
                                  el.innerHTML = cachedEmailHtml;
                                  el.style.position = 'fixed';
                                  el.style.left = '-9999px';
                                  el.contentEditable = 'true';
                                  document.body.appendChild(el);
                                  const range = document.createRange();
                                  range.selectNodeContents(el);
                                  const sel = window.getSelection();
                                  sel.removeAllRanges();
                                  sel.addRange(range);
                                  document.execCommand('copy');
                                  document.body.removeChild(el);
                                }
                              } catch (copyErr) {
                                // Silent fail — share already happened
                              }
                              setSnackbar({ isVisible: true, message: 'Email content copied! Paste it in your Mail to send.', type: 'success' });
                              return;
                            } catch (err) {
                              if (err.name === 'AbortError') return;
                              // Fall through to desktop copy if share fails
                            }
                          }
                          // Helper: open mailto after copy (email client opens with pre-filled details)
                          // const openMailto = () => {
                          //   const subject = encodeURIComponent('You Have Received Projector Memories!');
                          //   const body = encodeURIComponent(plainText);
                          //   const to = encodeURIComponent(recipientEmail || '');
                          //   // Try mailto first, fall back to Gmail web if no mail app opens
                          //   const beforeTime = Date.now();
                          //   window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
                          //   setTimeout(() => {
                          //     // If page didn't lose focus, mailto likely failed (no app opened)
                          //     if (Date.now() - beforeTime < 1500 && document.hasFocus()) {
                          //       window.open(`https://mail.google.com/mail/?view=cm&to=${to}&su=${subject}&body=${body}`, '_blank');
                          //     }
                          //   }, 1000);
                          // };

                          // Desktop / fallback: ClipboardItem with both HTML and plain text
                          if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
                            navigator.clipboard.write([
                              new ClipboardItem({
                                'text/html': new Blob([cachedEmailHtml], { type: 'text/html' }),
                                'text/plain': new Blob([plainText], { type: 'text/plain' }),
                              }),
                            ])
                              .then(() => {
                                setSnackbar({ isVisible: true, message: 'Email content copied! Paste it in your Mail to send.', type: 'success' });
                                // openMailto();
                              })
                              .catch(() => {
                                // iOS Safari fallback: contentEditable div for rich HTML
                                const el = document.createElement('div');
                                el.innerHTML = cachedEmailHtml;
                                el.style.position = 'fixed';
                                el.style.left = '-9999px';
                                el.contentEditable = 'true';
                                document.body.appendChild(el);
                                const range = document.createRange();
                                range.selectNodeContents(el);
                                const sel = window.getSelection();
                                sel.removeAllRanges();
                                sel.addRange(range);
                                document.execCommand('copy');
                                document.body.removeChild(el);
                                setSnackbar({ isVisible: true, message: 'Email content copied! Paste it in your Mail to send.', type: 'success' });
                                // openMailto();
                              });
                          } else {
                            // No ClipboardItem: contentEditable div fallback
                            const el = document.createElement('div');
                            el.innerHTML = cachedEmailHtml;
                            el.style.position = 'fixed';
                            el.style.left = '-9999px';
                            el.contentEditable = 'true';
                            document.body.appendChild(el);
                            const range = document.createRange();
                            range.selectNodeContents(el);
                            const sel = window.getSelection();
                            sel.removeAllRanges();
                            sel.addRange(range);
                            document.execCommand('copy');
                            document.body.removeChild(el);
                            setSnackbar({ isVisible: true, message: 'Email content copied! Paste it in your Mail to send.', type: 'success' });
                            // openMailto();
                          }
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.click(); }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="9" y="9" width="13" height="13" rx="2" stroke="#5aa5ef" strokeWidth="2" />
                          <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="#5aa5ef" strokeWidth="2" />
                        </svg>
                      </span>
                    </div>
                  </div>

                  {/* Send via SMS - icon only, no functionality yet */}
                  <div style={{ width: '100%', height: '1px', background: 'rgba(255,255,255,0.1)', margin: '8px 0' }} />
                  <div className={styles.statusCardHeader} style={{ flex: 1 }}>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="5" y="2" width="14" height="20" rx="2" stroke="#5aa5ef" strokeWidth="2" />
                      <path d="M12 18H12.01" stroke="#5aa5ef" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <span className={styles.statusCardLabel}>Copy and Send By SMS/Text</span>
                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
                      <span
                        role="button"
                        tabIndex={0}
                        title="Copy gift content for SMS"
                        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                        onClick={async () => {
                          if (!cachedEmailHtml) {
                            setSnackbar({ isVisible: true, message: 'Content is loading, please try again', type: 'error' });
                            return;
                          }
                          const plainText = cachedEmailHtml
                            .replace(/<head[\s\S]*?<\/head>/gi, '')
                            .replace(/<style[\s\S]*?<\/style>/gi, '')
                            .replace(/<a\s[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, '$2 ($1)')
                            .replace(/<[^>]*>/g, '')
                            .replace(/\s{2,}/g, '\n')
                            .trim();
                          // Mobile: open share intent, then overwrite clipboard with rich HTML after
                          if (window.innerWidth <= 1024 && navigator.share) {
                            try {
                              await navigator.share({
                                title: 'Projector Gift',
                                text: plainText,
                              });
                              // Share resolved — overwrite clipboard with rich HTML
                              try {
                                if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
                                  await navigator.clipboard.write([
                                    new ClipboardItem({
                                      'text/html': new Blob([cachedEmailHtml], { type: 'text/html' }),
                                      'text/plain': new Blob([plainText], { type: 'text/plain' }),
                                    }),
                                  ]);
                                } else {
                                  const el = document.createElement('div');
                                  el.innerHTML = cachedEmailHtml;
                                  el.style.position = 'fixed';
                                  el.style.left = '-9999px';
                                  el.contentEditable = 'true';
                                  document.body.appendChild(el);
                                  const range = document.createRange();
                                  range.selectNodeContents(el);
                                  const sel = window.getSelection();
                                  sel.removeAllRanges();
                                  sel.addRange(range);
                                  document.execCommand('copy');
                                  document.body.removeChild(el);
                                }
                              } catch (copyErr) {
                                // Silent fail — share already happened
                              }
                              setSnackbar({ isVisible: true, message: 'Content copied! Paste it in your SMS to send.', type: 'success' });
                              return;
                            } catch (err) {
                              if (err.name === 'AbortError') return;
                              // Fall through to desktop copy if share fails
                            }
                          }
                          // Desktop / fallback: ClipboardItem with both HTML and plain text
                          if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
                            navigator.clipboard.write([
                              new ClipboardItem({
                                'text/html': new Blob([cachedEmailHtml], { type: 'text/html' }),
                                'text/plain': new Blob([plainText], { type: 'text/plain' }),
                              }),
                            ])
                              .then(() => setSnackbar({ isVisible: true, message: 'Content copied! Paste it in your SMS to send.', type: 'success' }))
                              .catch(() => {
                                const el = document.createElement('div');
                                el.innerHTML = cachedEmailHtml;
                                el.style.position = 'fixed';
                                el.style.left = '-9999px';
                                el.contentEditable = 'true';
                                document.body.appendChild(el);
                                const range = document.createRange();
                                range.selectNodeContents(el);
                                const sel = window.getSelection();
                                sel.removeAllRanges();
                                sel.addRange(range);
                                document.execCommand('copy');
                                document.body.removeChild(el);
                                setSnackbar({ isVisible: true, message: 'Content copied! Paste it in your SMS to send.', type: 'success' });
                              });
                          } else {
                            const el = document.createElement('div');
                            el.innerHTML = cachedEmailHtml;
                            el.style.position = 'fixed';
                            el.style.left = '-9999px';
                            el.contentEditable = 'true';
                            document.body.appendChild(el);
                            const range = document.createRange();
                            range.selectNodeContents(el);
                            const sel = window.getSelection();
                            sel.removeAllRanges();
                            sel.addRange(range);
                            document.execCommand('copy');
                            document.body.removeChild(el);
                            setSnackbar({ isVisible: true, message: 'Content copied! Paste it in your SMS to send.', type: 'success' });
                          }
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.click(); }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="9" y="9" width="13" height="13" rx="2" stroke="#5aa5ef" strokeWidth="2" />
                          <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="#5aa5ef" strokeWidth="2" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Sender Info */}
                <div className={styles.statusCard}>
                  <div className={styles.statusCardHeader}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 2L11 13" stroke="#5aa5ef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="#5aa5ef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className={styles.statusCardLabel}>From</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 'auto', alignItems: 'flex-end' }}>
                    <div className={styles.statusCardValue}>
                      {senderName || 'N/A'}
                    </div>
                    <div className={styles.statusCardValue} style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                      {senderEmail || ''}
                    </div>
                  </div>
                </div>

                {/* Delivery Date - commented out
                <div className={styles.statusCard}>
                  <div className={styles.statusCardHeader}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="18" height="18" rx="2" stroke="#5aa5ef" strokeWidth="2" />
                      <path d="M16 2V6" stroke="#5aa5ef" strokeWidth="2" strokeLinecap="round" />
                      <path d="M8 2V6" stroke="#5aa5ef" strokeWidth="2" strokeLinecap="round" />
                      <path d="M3 10H21" stroke="#5aa5ef" strokeWidth="2" />
                    </svg>
                    <span className={styles.statusCardLabel}>Delivery</span>
                  </div>
                  <div className={styles.statusCardValue}>
                    {displayDeliveryDate}
                  </div>
                  <div className={`${styles.statusBadge} ${styles[`statusBadge_${giftDeliveryStatus}`]}`}>
                    {apiDeliveryStatus == null ? 'Sent' : apiDeliveryStatus.charAt(0).toUpperCase() + apiDeliveryStatus.slice(1)}
                  </div>
                </div>
                */}

              </div>
            </div>
          </div>
        </div>

        {/* Snackbar for success section */}
        <Snackbar
          isVisible={snackbar.isVisible}
          message={snackbar.message}
          type={snackbar.type}
          onClose={() => setSnackbar((prev) => ({ ...prev, isVisible: false }))}
          autoHideDuration={5000}
        />
      </div>
    );
  }

  return (
    <div className={`${styles.giftPage} ${currentStep === 0 ? styles.giftPageIntro : ''}`}>

      {/* Contributor name popup - shown when share=true */}
      {showContributorPopup && (
        <div className={styles.contributorOverlay}>
          <div className={styles.contributorCard}>
            <div className={styles.contributorIconWrap}>
              <div className={styles.contributorIconRing} />
              <svg className={styles.contributorIconSvg} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0 2c-3.33 0-10 1.67-10 5v2h20v-2c0-3.33-6.67-5-10-5z" fill="#5aa5ef" />
              </svg>
            </div>
            <h2 className={styles.contributorTitle}>{urlParams.recipientName ? `Welcome! Add to ${urlParams.recipientName}'s Projector` : 'Welcome, Contributor!'}</h2>
            <p className={styles.contributorDescription}>
              Enter your name and email so the gift recipient knows who Added Media.
            </p>
            <div className={styles.contributorField}>
              <label className={styles.contributorLabel}>Your Name *</label>
              <input
                type="text"
                className={styles.contributorInput}
                value={contributorName}
                onChange={(e) => setContributorName(capitalizeWords(e.target.value))}
                onBlur={() => setContNameTouched(true)}
                placeholder="Enter your full name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && contFullNameValid && !(contributorEmail && !contEmailValid)) {
                    localStorage.setItem('contributorsName', contributorName.trim());
                    setShowContributorPopup(false);
                  }
                }}
              />
              {contNameTouched && contNameValidReady && contributorName && contNameNoSpaces.length < 4 && (
                <p className={styles.contributorError}>Please enter a name with at least 4 characters</p>
              )}
              {contNameTouched && contNameValidReady && contributorName && contNameNoSpaces.length >= 4 && !contHasSpace && (
                <p className={styles.contributorError}>Last name is required</p>
              )}
            </div>
            <div className={styles.contributorField}>
              <label className={styles.contributorLabel}>Your Email</label>
              <input
                type="email"
                className={styles.contributorInput}
                value={contributorEmail}
                onChange={(e) => setContributorEmail(e.target.value)}
                onBlur={() => setContEmailTouched(true)}
                placeholder="Enter your email (optional)"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && contFullNameValid && !(contributorEmail && !contEmailValid)) {
                    localStorage.setItem('contributorsName', contributorName.trim());
                    setShowContributorPopup(false);
                  }
                }}
              />
              {contEmailTouched && contEmailValidReady && contributorEmail && !contEmailValid && (
                <p className={styles.contributorError}>Please enter a valid email address</p>
              )}
            </div>
            <button
              type="button"
              className={styles.contributorContinueBtn}
              disabled={!contFullNameValid || (contributorEmail && !contEmailValid)}
              onClick={() => {
                localStorage.setItem('contributorsName', contributorName.trim());
                setShowContributorPopup(false);
                // Scroll to top after popup closes (mobile keyboard may shift viewport)
                setTimeout(() => window.scrollTo(0, 0), 100);
              }}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Sender name/email popup - shown in normal /gift flow after occasion selection */}
      {showSenderPopup && (
        <div className={styles.contributorOverlay}>
          <div className={styles.contributorCard}>
            <div className={styles.contributorIconWrap}>
              <div className={styles.contributorIconRing} />
              <svg className={styles.contributorIconSvg} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0 2c-3.33 0-10 1.67-10 5v2h20v-2c0-3.33-6.67-5-10-5z" fill="#5aa5ef" />
              </svg>
            </div>
            <h2 className={styles.contributorTitle}>Who Is This Gift From?</h2>
            <p className={styles.contributorDescription}>
              Enter your name and email so the recipient knows who sent this gift.
            </p>
            <div className={styles.contributorField}>
              <label className={styles.contributorLabel}>Your Name *</label>
              <input
                type="text"
                className={styles.contributorInput}
                value={senderName}
                onChange={(e) => setSenderName(capitalizeWords(e.target.value))}
                onBlur={() => setSenderNameTouched(true)}
                placeholder="Enter your full name"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && senderFullNameValid && senderEmailValid) {
                    localStorage.setItem('senderName', senderName.trim());
                    localStorage.setItem('senderEmail', senderEmail.trim());
                    handleInputChange('cardholderEmail', senderEmail.trim());
                    setShowSenderPopup(false);
                  }
                }}
              />
              {senderNameTouched && senderNameValidReady && senderName && senderNameNoSpaces.length < 4 && (
                <p className={styles.contributorError}>Please enter a name with at least 4 characters</p>
              )}
              {senderNameTouched && senderNameValidReady && senderName && senderNameNoSpaces.length >= 4 && !senderHasSpace && (
                <p className={styles.contributorError}>Last name is required</p>
              )}
            </div>
            <div className={styles.contributorField}>
              <label className={styles.contributorLabel}>Your Email *</label>
              <input
                type="email"
                className={styles.contributorInput}
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                onBlur={() => setSenderEmailTouched(true)}
                placeholder="Enter your email"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && senderFullNameValid && senderEmailValid) {
                    localStorage.setItem('senderName', senderName.trim());
                    localStorage.setItem('senderEmail', senderEmail.trim());
                    handleInputChange('cardholderEmail', senderEmail.trim());
                    setShowSenderPopup(false);
                  }
                }}
              />
              {senderEmailTouched && senderEmailValidReady && senderEmail && !senderEmailValid && (
                <p className={styles.contributorError}>Please enter a valid email address</p>
              )}
              {senderEmailTouched && senderEmailValidReady && !senderEmail && (
                <p className={styles.contributorError}>Email is required</p>
              )}
            </div>
            <button
              type="button"
              className={styles.contributorContinueBtn}
              disabled={!senderFullNameValid || !senderEmailValid}
              onClick={() => {
                localStorage.setItem('senderName', senderName.trim());
                localStorage.setItem('senderEmail', senderEmail.trim());
                handleInputChange('cardholderEmail', senderEmail.trim());
                setShowSenderPopup(false);
              }}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Upload item video preview modal */}
      {uploadPreviewOpen && (
        <div role="button" tabIndex={0} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }} onClick={() => setUploadPreviewOpen(false)} onKeyDown={(e) => { if (e.key === 'Escape') setUploadPreviewOpen(false); }}>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div role="dialog" aria-modal="true" style={{ background: '#1a1d28', borderRadius: '16px', overflow: 'hidden', width: '100%', maxWidth: '600px', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 8px 32px rgba(0,0,0,0.7)', position: 'relative' }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <button type="button" style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10, background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', fontSize: '1.4rem', width: '34px', height: '34px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setUploadPreviewOpen(false)}>&times;</button>
            <div style={{ width: '100%', aspectRatio: '16/9', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              {uploadPreviewLoading ? (
                <svg className="loading-circle" viewBox="0 0 50 50" style={{ width: '40px', height: '40px' }}><circle className="circle" cx="25" cy="25" r="20" fill="none" strokeWidth="4" /></svg>
              ) : uploadPreviewVideo?.videoUrl ? (
                <video src={uploadPreviewVideo.videoUrl} controls autoPlay muted style={{ width: '100%', height: '100%', objectFit: 'contain' }}>
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#888', gap: '8px' }}>
                  <img src={uploadPreviewVideo?.thumbnail || image04} alt={uploadPreviewVideo?.title} style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', top: 0, left: 0, opacity: 0.5 }} />
                  <p style={{ position: 'relative', zIndex: 1 }}>Video preview not available</p>
                </div>
              )}
            </div>
            {uploadPreviewVideo?.title && (
              <div style={{ padding: '12px 16px', color: '#fff', fontWeight: 600, fontSize: '0.9rem', fontFamily: "'Montserrat', sans-serif", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                {uploadPreviewVideo.title}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload item album preview modal */}
      {uploadAlbumOpen && (
        <div role="button" tabIndex={0} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }} onClick={() => setUploadAlbumOpen(false)} onKeyDown={(e) => { if (e.key === 'Escape') setUploadAlbumOpen(false); }}>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div role="dialog" aria-modal="true" style={{ background: '#1a1d28', borderRadius: '16px', overflow: 'hidden', width: '100%', maxWidth: '720px', maxHeight: '85vh', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 8px 32px rgba(0,0,0,0.7)', position: 'relative', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <button type="button" style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10, background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', fontSize: '1.4rem', width: '34px', height: '34px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setUploadAlbumOpen(false)}>&times;</button>
            {uploadAlbumDetail?.title && (
              <div style={{ padding: '16px 50px 16px 20px', color: '#fff', fontWeight: 600, fontSize: '1.1rem', fontFamily: "'Montserrat', sans-serif", borderBottom: '1px solid rgba(255,255,255,0.08)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {uploadAlbumDetail.title}
              </div>
            )}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px', minHeight: '200px' }}>
              {uploadAlbumLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
                  <svg className="loading-circle" viewBox="0 0 50 50" style={{ width: '40px', height: '40px' }}><circle className="circle" cx="25" cy="25" r="20" fill="none" strokeWidth="4" /></svg>
                </div>
              ) : uploadAlbumDetail?.photos?.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  {uploadAlbumDetail.photos.map((photo, index) => (
                    <div key={photo.id || index} style={{ aspectRatio: '1', borderRadius: '8px', overflow: 'hidden', background: 'rgba(255,255,255,0.05)' }}>
                      <img src={photo.imagekit_url || photo.photo_file || image01} alt={photo.title || `Photo ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.src = image01; }} />
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px', color: '#888' }}>
                  <p>No photos in this album</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel upload confirm dialog (only for cancel operations) */}
      {step3DeletePending && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ background: '#1a1d28', borderRadius: '16px', padding: '28px 24px', width: '100%', maxWidth: '380px', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 8px 32px rgba(0,0,0,0.7)' }}>
            <h3 style={{ margin: '0 0 10px', color: '#fff', fontFamily: "'Montserrat', sans-serif", fontSize: '1rem', fontWeight: 700 }}>
              Cancel Upload?
            </h3>
            <p style={{ margin: '0 0 20px', color: '#aaa', fontFamily: "'Montserrat', sans-serif", fontSize: '0.85rem' }}>
              {'Are you sure you want to cancel the upload of '}
              <strong style={{ color: '#fff' }}>{step3DeletePending.fileName}</strong>
              ?
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button type="button" onClick={() => setStep3DeletePending(null)} style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', color: '#fff', padding: '8px 18px', fontFamily: "'Montserrat', sans-serif", fontWeight: 600, fontSize: '0.85rem' }}>
                No
              </button>
              <button
                type="button"
                onClick={() => {
                  if (step3DeletePending.onConfirm) step3DeletePending.onConfirm();
                  setStep3DeletePending(null);
                }}
                style={{ cursor: 'pointer', background: '#e53935', border: 'none', borderRadius: '8px', color: '#fff', padding: '8px 18px', fontFamily: "'Montserrat', sans-serif", fontWeight: 600, fontSize: '0.85rem' }}
              >
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Album photos modal for grid view */}
      {step3AlbumModal && (
        <div
          className={styles.albumGridOverlay}
          role="button"
          tabIndex={0}
          onClick={() => setStep3AlbumModal(null)}
          onKeyDown={(e) => { if (e.key === 'Escape') setStep3AlbumModal(null); }}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            role="dialog"
            aria-modal="true"
            className={styles.albumGridModal}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <button type="button" className={styles.albumGridModalClose} onClick={() => setStep3AlbumModal(null)}>
              &times;
            </button>
            <div className={styles.albumGridModalHeader}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#5aa5ef"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
              {`${step3AlbumModal.upload?.fileName || 'Album Photos'} (${step3AlbumModal.photos?.length || 0} photos)`}
            </div>
            <div className={styles.albumGridModalBody}>
              {step3AlbumModal.photos && step3AlbumModal.photos.length > 0 ? (
                <div className={styles.albumGridPhotos}>
                  {step3AlbumModal.photos.map((photo) => (
                    <div key={photo.id} className={styles.albumGridPhotoItem}>
                      {(photo.file || photo.previewUrl) ? (
                        <img
                          src={photo.previewUrl || URL.createObjectURL(photo.file)}
                          alt={photo.name}
                        />
                      ) : photo.cloudFile || photo.cloudSource ? (
                        <div className={styles.albumGridPhotoShimmer}>
                          <div className={styles.shimmerAnimation} />
                        </div>
                      ) : (
                        <div className={styles.albumGridPhotoPlaceholder}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                        </div>
                      )}
                      <span className={styles.albumGridPhotoName}>
                        {photo.name}
                      </span>
                      <button
                        type="button"
                        className={styles.albumGridPhotoDelete}
                        onClick={() => {
                          const albumFileId = step3AlbumModal.albumMatch?.id;
                          const theUpload = step3AlbumModal.upload;
                          handleSoftDeletePhoto({
                            source: photo.photo_id ? 'registered' : 'unregistered',
                            type: 'photo',
                            uploadId: theUpload?.id,
                            registeredId: photo.photo_id || photo.id,
                            albumFileId,
                            fileName: photo.name,
                            thumbnail: photo.previewUrl || photo.imagekit_url || null,
                            photoData: { ...photo },
                          });
                          // Update modal photos
                          setStep3AlbumModal((prev) => {
                            if (!prev) return prev;
                            const remaining = prev.photos.filter((p) => p.id !== photo.id);
                            if (remaining.length === 0) return null;
                            return { ...prev, photos: remaining };
                          });
                        }}
                        title="Remove photo"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#999', textAlign: 'center', padding: '20px', fontFamily: "'Montserrat', sans-serif" }}>No photos in this album</p>
              )}
            </div>
          </div>
        </div>
      )}

      {currentStep === 0 && (
        <>
          <div
            aria-hidden="true"
            onClick={handleBackClick}
            className={`${styles.backLink} ${styles.fadeIn} ${styles.delay1}`}
          >
            BACK
          </div>
          <a href="https://www.projectorstream.com/" className={`${styles.logoLink} ${styles.logoFadeIn} ${styles.delay1}`}>
            <Logo className={styles.logo} isLarge isLight />
          </a>
        </>
      )}
      <div className={styles.giftPageContent}>
        {currentStep > 0 && (
          <>
            {/* Hide back button on choose content (step 3), upload progress (step 5), and payment (step 4) when connected to mobile */}
            {currentStep !== 3 && currentStep !== 5 && !(currentStep === 1 && step1SubStep === 1 && !showSenderInline) && !(currentStep === 4 && uploads.some((u) => u.isMobileUpload)) && (
              <div
                aria-hidden="true"
                onClick={handleBackClick}
                className={styles.backLink}
              >
                {currentStep === 4 ? 'Back to Uploads' : 'BACK'}
              </div>
            )}
            {/* NEXT button - commented out
            {currentStep === 1 && (
              (step1SubStep === 1 && formData.firstname && formData.email)
              || (step1SubStep === 2 && cloudflareToken && !cloudflareVerifying)
            ) && (
              <div
                aria-hidden="true"
                onClick={handleLoginClick}
                className={`${styles.loginLink} ${styles.fadeIn} ${styles.delay1}`}
              >
                NEXT
              </div>
            )}
            */}
            <a href="https://www.projectorstream.com/">
              <Logo className={styles.logo} isLarge isLight />
            </a>
          </>
        )}

        {currentStep === 0 ? (
          <>
            <div ref={heroSectionRef} className={styles.heroSection} style={{ backgroundImage: `url(${heroBackground})` }}>
              <div className={styles.heroContent}>
                <h1 className={`${styles.introHeading} ${styles.fadeInUp} ${styles.delay1}`}>Give Projector!</h1>
                <h2 className={`${styles.subHeading} ${styles.fadeInUp} ${styles.delay2}`}>Where Memories Live</h2>
                {/* <p className={styles.description}>
                  From weddings to newborns, Projector is where new and old
                  memories live. Whether it's a baby's first photo or decades worth
                  of family videos and albums, Projector is where everything lives!
                </p> */}

                <div className={`${styles.featuresRow} ${styles.fadeInUp} ${styles.delay3}`}>
                  <span className={styles.featureText}>Upload once. Watch forever. Share To Anyone</span>
                  <p className={`${styles.tvText} ${styles.fadeInUp} ${styles.delay4}`}>Turn your TV into your family&apos;s private memory channel.</p>
                  {/* <span className={styles.featureText}>Share To Anyone</span> */}
                </div>

                {/* <p className={`${styles.tvText} ${styles.fadeInUp} ${styles.delay4}`}>Turn your TV into your family&apos;s private memory channel.</p> */}

                <button className={`${styles.startGiftButton} ${styles.fadeInScale} ${styles.delay5}`} onClick={() => animateToStep(1)}>
                  Start Gifting
                </button>

                <p className={`${styles.givingText} ${styles.fadeInUp} ${styles.delay6}`}>You are giving the place where memories live</p>
              </div>
            </div>

            <div
              ref={bottomSectionRef}
              className={`${styles.imageGalleryWrapper} ${styles.fadeIn} ${styles.delay4} ${bottomSectionUp ? styles.imageGalleryWrapperUp : styles.imageGalleryWrapperDown}`}
            >
              <div ref={imageGalleryRef} className={styles.imageGallery}>
                <div className={`${styles.imageCard} ${galleryVisible ? styles.scrollSlideUp : styles.scrollHidden} ${styles.scrollDelay1}`}>
                  <img src={image01} alt="Memory showcase 1" className={styles.galleryImage} />
                </div>
                <div className={`${styles.imageCard} ${galleryVisible ? styles.scrollSlideUp : styles.scrollHidden} ${styles.scrollDelay2}`}>
                  <img src={image02} alt="Memory showcase 2" className={styles.galleryImage} />
                </div>
                <div className={`${styles.imageCard} ${galleryVisible ? styles.scrollSlideUp : styles.scrollHidden} ${styles.scrollDelay3}`}>
                  <img src={image03} alt="Memory showcase 3" className={styles.galleryImage} />
                </div>
                <div className={`${styles.imageCard} ${galleryVisible ? styles.scrollSlideUp : styles.scrollHidden} ${styles.scrollDelay4}`}>
                  <img src={image04} alt="Memory showcase 4" className={styles.galleryImage} />
                </div>
              </div>

              <div ref={featuresSectionRef} className={styles.featuresSection}>
                <div className={`${styles.featureCard} ${featuresVisible ? styles.scrollZoomIn : styles.scrollHidden} ${styles.scrollDelay1}`}>
                  <div className={styles.featureIcon}>📺</div>
                  <h3 className={styles.featureTitle}>Watch Anywhere</h3>
                  <p className={styles.featureDescription}>Stream your memories on any device, from TV to mobile</p>
                </div>
                <div className={`${styles.featureCard} ${featuresVisible ? styles.scrollZoomIn : styles.scrollHidden} ${styles.scrollDelay2}`}>
                  <div className={styles.featureIcon}>☁️</div>
                  <h3 className={styles.featureTitle}>Cloud Storage</h3>
                  <p className={styles.featureDescription}>Securely store unlimited photos and videos forever</p>
                </div>
                <div className={`${styles.featureCard} ${featuresVisible ? styles.scrollZoomIn : styles.scrollHidden} ${styles.scrollDelay3}`}>
                  <div className={styles.featureIcon}>👥</div>
                  <h3 className={styles.featureTitle}>Easy Sharing</h3>
                  <p className={styles.featureDescription}>Share special moments with family and friends instantly</p>
                </div>
                <div className={`${styles.featureCard} ${featuresVisible ? styles.scrollZoomIn : styles.scrollHidden} ${styles.scrollDelay4}`}>
                  <div className={styles.featureIcon}>🔒</div>
                  <h3 className={styles.featureTitle}>Private & Secure</h3>
                  <p className={styles.featureDescription}>Your memories stay private with enterprise-grade security</p>
                </div>
              </div>
            </div>
          </>
        ) : currentStep === 1 ? (
          <>
            <div className={`${styles.contentContainer} ${getAnimationClass()}`}>
              {/* <h1 className={styles.heading}>Step 1</h1> */}

              {step1SubStep === 1 ? (
                <>
                  {showSenderInline ? (
                    <>
                      <h2 className={styles.stepSubHeading}>Who Is This Gift From?</h2>
                      <p className={styles.stepSubText}>
                        Enter your name and email so the recipient knows who sent this gift.
                      </p>
                      <div className={styles.formContainer} style={{ gap: 0 }}>
                        <h3 className={styles.featureTitle} style={{ margin: '0 0 15px 0' }}>From:</h3>
                        <input
                          type="text"
                          className={styles.inputField}
                          placeholder="Enter your full name"
                          value={senderName}
                          onChange={(e) => setSenderName(capitalizeWords(e.target.value))}
                          onBlur={() => setSenderNameTouched(true)}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && senderFullNameValid && senderEmailValid) {
                              localStorage.setItem('senderName', senderName.trim());
                              localStorage.setItem('senderEmail', senderEmail.trim());
                              handleInputChange('cardholderEmail', senderEmail.trim());
                              setShowSenderInline(false);
                              setStep1SubStep(2);
                            }
                          }}
                          style={{ marginBottom: senderNameTouched && senderNameValidReady && senderName && !senderFullNameValid ? '4px' : '16px' }}
                        />
                        {senderNameTouched && senderNameValidReady && senderName && senderNameNoSpaces.length < 4 && (
                          <p style={{ color: '#ff6b6b', fontSize: '0.8rem', margin: '0 0 12px 0', textAlign: 'left', width: '100%', alignSelf: 'flex-start' }}>
                            Please enter a name with at least 4 characters
                          </p>
                        )}
                        {senderNameTouched && senderNameValidReady && senderName && senderNameNoSpaces.length >= 4 && !senderHasSpace && (
                          <p style={{ color: '#ff6b6b', fontSize: '0.8rem', margin: '0 0 12px 0', textAlign: 'left', width: '100%', alignSelf: 'flex-start' }}>
                            Last name is required
                          </p>
                        )}
                        <input
                          type="email"
                          className={styles.inputField}
                          placeholder="Enter your email"
                          value={senderEmail}
                          onChange={(e) => setSenderEmail(e.target.value)}
                          onBlur={() => setSenderEmailTouched(true)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && senderFullNameValid && senderEmailValid) {
                              localStorage.setItem('senderName', senderName.trim());
                              localStorage.setItem('senderEmail', senderEmail.trim());
                              handleInputChange('cardholderEmail', senderEmail.trim());
                              setShowSenderInline(false);
                              setStep1SubStep(2);
                            }
                          }}
                        />
                        {senderEmailTouched && senderEmailValidReady && senderEmail && !senderEmailValid && (
                          <p style={{ color: '#ff6b6b', fontSize: '0.8rem', margin: '0 0 12px 0', textAlign: 'left', width: '100%', alignSelf: 'flex-start' }}>
                            Please enter a valid email address
                          </p>
                        )}
                        {senderEmailTouched && senderEmailValidReady && !senderEmail && (
                          <p style={{ color: '#ff6b6b', fontSize: '0.8rem', margin: '0 0 12px 0', textAlign: 'left', width: '100%', alignSelf: 'flex-start' }}>
                            Email is required
                          </p>
                        )}
                        <button
                          className={styles.startGiftButton}
                          disabled={!senderFullNameValid || !senderEmailValid}
                          onClick={() => {
                            localStorage.setItem('senderName', senderName.trim());
                            localStorage.setItem('senderEmail', senderEmail.trim());
                            handleInputChange('cardholderEmail', senderEmail.trim());
                            setShowSenderInline(false);
                            setStep1SubStep(2);
                          }}
                          style={{ marginTop: '32px', opacity: (!senderFullNameValid || !senderEmailValid) ? 0.5 : 1, cursor: (!senderFullNameValid || !senderEmailValid) ? 'not-allowed' : 'pointer' }}
                        >
                          Continue
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h2 className={styles.stepSubHeading}>Who Are You Gifting Projector To?</h2>

                      <div className={styles.formContainer} style={{ gap: 0 }}>
                        <h3 className={styles.featureTitle} style={{ margin: '0 0 15px 0' }}>To:</h3>
                    <input
                      ref={toNameInputRef}
                      type="text"
                      className={styles.inputField}
                      placeholder="Full Name"
                      value={formData.firstname}
                      onChange={(e) => handleInputChange('firstname', e.target.value)}
                      onBlur={() => setNameFieldTouched(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !loading && fullNameValid && formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && cloudflareToken && !cloudflareVerifying && !error) {
                          handleLoginClick();
                        }
                      }}
                      style={{ marginBottom: nameFieldTouched && nameValidationReady && formData.firstname && !fullNameValid ? '4px' : '16px' }}
                    />
                    {nameFieldTouched && nameValidationReady && formData.firstname && nameWithoutSpaces.length < 4 && (
                      <p style={{ color: '#ff6b6b', fontSize: '0.8rem', margin: '0 0 12px 0', textAlign: 'left', width: '100%', alignSelf: 'flex-start' }}>
                        Please enter a name with at least 4 characters
                      </p>
                    )}
                    {nameFieldTouched && nameValidationReady && formData.firstname && nameWithoutSpaces.length >= 4 && !hasSpace && (
                      <p style={{ color: '#ff6b6b', fontSize: '0.8rem', margin: '0 0 12px 0', textAlign: 'left', width: '100%', alignSelf: 'flex-start' }}>
                        Last name is required
                      </p>
                    )}
                    <input
                      type="email"
                      className={styles.inputField}
                      placeholder="Enter Recipient's Email Address"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !loading && fullNameValid && formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && cloudflareToken && !cloudflareVerifying && !error) {
                          handleLoginClick();
                        }
                      }}
                    />

                    {/* From section - commented out
                    <h3 className={styles.featureTitle} style={{ margin: '24px 0 0 0' }}>
                      <span>From: </span>
                      <span style={{ fontSize: '0.95rem' }}>(Give 2, Get Yours For Free)</span>
                    </h3>
                    <input
                      type="email"
                      className={styles.inputField}
                      placeholder="Enter Your Email Address"
                      value={formData.senderEmail}
                      onChange={(e) => {
                        handleInputChange('senderEmail', e.target.value);
                        handleInputChange('cardholderEmail', e.target.value);
                      }}
                      style={{ marginTop: '6px', marginBottom: '10px' }}
                    />

                    */}

                    <div
                      ref={turnstileRef}
                      style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-start', alignSelf: 'flex-start', height: '67px' }}
                    />

                    <button
                      className={styles.startGiftButton}
                      onClick={handleLoginClick}
                      disabled={loading || !fullNameValid || !formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) || !cloudflareToken || cloudflareVerifying || error}
                      style={{ marginTop: '32px', opacity: (loading || !fullNameValid || !formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) || !cloudflareToken || cloudflareVerifying || error) ? 0.5 : 1, cursor: (loading || !fullNameValid || !formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) || !cloudflareToken || cloudflareVerifying || error) ? 'not-allowed' : 'pointer' }}
                    >
                      {loading ? <span className={styles.buttonSpinner} /> : 'Continue'}
                    </button>

                    {/* <p style={{ color: '#fff', fontSize: '0.95rem', fontWeight: '600', margin: '16px 0 8px 0', textAlign: 'center' }}>
                      Create a Projector Account To Get Credit!
                    </p>
                    <div
                      aria-hidden="true"
                      style={{ fontSize: '1rem', fontWeight: '600', cursor: 'pointer', margin: '0', textAlign: 'center' }}
                      onClick={() => navigate('/login')}
                    >
                      <span style={{ color: '#5aa5ef' }}>Create Account</span>
                      <span style={{ color: '#fff' }}> or </span>
                      <span style={{ color: '#5aa5ef' }}>Log In</span>
                    </div> */}

                    {error && (
                      <div style={{ color: '#ff6b6b', marginTop: '16px', textAlign: 'center', width: '100%' }}>
                        {error}
                      </div>
                    )}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <>
                  <h2 className={styles.stepSubHeading}>Is This For A Special Occasion?</h2>
                  <p className={styles.stepSubText}>You Can Select One Or More Occasion</p>

                  {/* Occasion Grid */}
                  <div className={styles.occasionGrid}>
                    {occasions.map((occasion, index) => {
                      const isSelected = formData.occasions && formData.occasions.includes(occasion);
                      return (
                        <button
                          key={occasion}
                          className={`${styles.occasionButton} ${styles.buttonFadeIn}`}
                          onClick={() => handleOccasionClick(occasion)}
                          style={{
                            backgroundColor: isSelected ? '#5aa5ef' : '#4E5360',
                            animationDelay: `${0.8 + (index * 0.2)}s`,
                            position: 'relative'
                          }}
                        >
                          {occasion}
                        </button>
                      );
                    })}
                  </div>

                  {/* Other Reason input - commented out
                  {formData.occasion === 'Other Milestone' && (
                    <div className={styles.formContainer}>
                      <input
                        type="text"
                        className={styles.inputField}
                        placeholder="Other Reason?"
                        value={formData.otherReason || ''}
                        onChange={(e) => handleInputChange('otherReason', e.target.value)}
                      />
                    </div>
                  )}
                  */}

                  {/* Note textarea - only shows for Other Milestone */}
                  {gridAnimationComplete && formData.occasions && formData.occasions.includes('Other Milestone') && (
                    <div className={styles.formContainer}>
                      <textarea
                        ref={noteTextareaRef}
                        className={`${styles.inputField} ${styles.textArea} ${noteAnimating ? styles.notePopIn : ''}`}
                        placeholder="Other reason?"
                        value={formData.note}
                        onChange={(e) => {
                          handleInputChange('note', e.target.value);
                          const textarea = e.target;
                          textarea.style.height = '85px';
                          textarea.style.height = `${Math.max(85, textarea.scrollHeight)}px`;
                        }}
                        style={{ height: '85px', minHeight: '85px', overflow: 'hidden', resize: 'none' }}
                      />
                      {error && (
                        <div style={{ color: '#ff6b6b', marginTop: '10px', textAlign: 'left', width: '100%' }}>
                          {error}
                        </div>
                      )}
                    </div>
                  )}

                  {gridAnimationComplete && (
                    <>
                      <button
                        className={styles.startGiftButton}
                        onClick={handleLoginClick}
                        disabled={!formData.occasions || formData.occasions.length === 0 || (formData.occasions.includes('Other Milestone') && !formData.note)}
                        style={{ marginTop: '50px', opacity: (!formData.occasions || formData.occasions.length === 0 || (formData.occasions.includes('Other Milestone') && !formData.note)) ? 0.5 : 1, cursor: (!formData.occasions || formData.occasions.length === 0 || (formData.occasions.includes('Other Milestone') && !formData.note)) ? 'not-allowed' : 'pointer' }}
                      >
                        Continue
                      </button>
                      {error && (
                        <div style={{ color: '#ff6b6b', marginTop: '16px', textAlign: 'center', width: '100%' }}>
                          {error}
                        </div>
                      )}
                    </>
                  )}

                  {/* Horizontal scroll carousel - commented out */}
                  {/* <div className={styles.occasionCarouselContainer}>
                    <button
                      className={`${styles.carouselArrow} ${styles.carouselArrowLeft} ${!canScrollLeft ? styles.hidden : ''}`}
                      onClick={handleOccasionScrollLeft}
                      aria-label="Scroll left"
                    >
                      ‹
                    </button>
                    <div className={styles.occasionCarousel} ref={occasionCarouselRef}>
                      <div
                        className={styles.occasionTrack}
                        style={{
                          transform: `translateX(calc(-${occasionScrollIndex} * (298px + 12px)))`
                        }}
                      >
                        {occasions.map((occasion, index) => (
                          <button
                            key={occasion}
                            className={`${styles.occasionButton} ${styles.buttonFadeIn} ${styles[`btnDelay${index + 1}`]}`}
                            onClick={() => handleOccasionClick(occasion)}
                            style={{
                              backgroundColor: formData.occasion === occasion ? '#5aa5ef' : '#4E5360'
                            }}
                          >
                            {occasion}
                          </button>
                        ))}
                      </div>
                    </div>
                    <button
                      className={`${styles.carouselArrow} ${styles.carouselArrowRight} ${!canScrollRight ? styles.hidden : ''}`}
                      onClick={handleOccasionScrollRight}
                      aria-label="Scroll right"
                    >
                      ›
                    </button>
                  </div> */}
                </>
              )}
            </div>
          </>
        ) : currentStep === 2 ? (
          <>
            <div className={`${styles.contentContainer} ${getAnimationClass()}`}>
              {/* <h1 className={styles.heading}>Step 2</h1> */}
              <h2 className={styles.stepSubHeading}>Add Content</h2>

              <div className={styles.choiceRow}>
                <div className={`${styles.choiceBox} ${styles.choiceSlideLeft}`}>
                  <h3 className={styles.choiceHeading}>Choose Content</h3>
                  <p className={styles.choiceDescription}>
                    Upload content from your computer, Google Photos, Apple Photos or your mobile device.
                  </p>
                  <button
                    className={styles.startGiftButton}
                    onClick={() => handleChoiceClick('you-help-build')}
                    style={{ marginTop: '50px', fontSize: '1.5rem', padding: '15px 40px', height: 'auto' }}
                  >
                    Add
                  </button>
                </div>
              </div>

              {!urlParams.share && (
                <>
                  <div
                    aria-hidden="true"
                    className={styles.skipLink}
                    onClick={() => handleChoiceClick('they-build')}
                  >
                    Skip
                  </div>
                  <p className={styles.skipDescription} style={{ marginTop: '20px' }}>No content to upload? (Recipient Adds Their Own Content)</p>
                </>
              )}

              {/* Old two-box layout - commented out
              <div className={styles.choiceRow}>
                <div
                  aria-hidden="true"
                  className={`${styles.choiceBox} ${styles.choiceSlideLeft}`}
                  onClick={() => handleChoiceClick('they-build')}
                >
                  <h3 className={styles.choiceHeading}>They Build!</h3>
                  <p className={styles.choiceDescription}>
                    Give a place where all
                    their most special memories
                    live, viewed and  get stored
                    forever.
                  </p>
                </div>
                <div
                  aria-hidden="true"
                  className={`${styles.choiceBox} ${styles.choiceSlideRight}`}
                  onClick={() => handleChoiceClick('you-help-build')}
                >
                  <h3 className={styles.choiceHeading}>Help Create!</h3>
                  <p className={styles.choiceDescription}>
                    Add a single video or photo
                    to inspire them to add to their
                    account. They can add and
                    remove any files themselves.
                  </p>
                </div>
              </div>
              */}
            </div>
          </>
        ) : currentStep === 3 ? (
          <>
            <div className={`${styles.contentContainer} ${getAnimationClass()}`}>
              {/* <h1 className={styles.heading}>Step 3</h1> */}
              {!(isMobileUploadMode || isShareMobileUI) && (
                <>
                  <h2 className={styles.stepSubHeading}>Add Media To Their Projector</h2>
                  {selectedFiles.length === 0 && (
                    <p className={styles.stepSubText}>You can choose one or more</p>
                  )}
                </>
              )}

              {/* Hidden file inputs */}
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*,.mp4,.mov,.avi,.mkv,.webm,.flv,.wmv,.m4v,.mpeg,.mpg"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => handleFileSelect(e, 'video')}
              />
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*,.jpg,.jpeg,.png,.webp,.heic,.heif,.gif"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => handleFileSelect(e, 'photo')}
              />
              <input
                id="mobile-file-input"
                ref={filesInputRef}
                type="file"
                accept={FILE_ACCEPT}
                multiple
                style={{ display: 'none' }}
                onChange={handleCombinedFileSelect}
              />

              {/* Mobile upload UI: show only Choose Files option centered (small screens) */}
              {(isMobileUploadMode || isShareMobileUI) && selectedFiles.length === 0 && (
                <div className={styles.mobileUploadCenter}>
                  <div
                    aria-hidden="true"
                    className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay1}`}
                    onClick={() => filesInputRef.current?.click()}
                  >
                    <div className={styles.uploadOptionIcon}>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M14 2V8H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12 18V12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9 15L12 12L15 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <span className={styles.uploadOptionText}>Choose Files from Gallery</span>
                  </div>
                </div>
              )}

              {/* Flip card container - Upload options on front, QR code on back */}
              {/* Desktop/large screen mode: show all upload options */}
              {!(isMobileUploadMode || isShareMobileUI) && (selectedFiles.length === 0 || showAddMore) && (
                <>
                  <div className={`${styles.flipCardContainer} ${showQrCode ? styles.flipped : ''}`}>
                    {/* Front - Upload Options Grid */}
                    <div className={styles.flipCardFront}>
                      <div className={styles.uploadOptionsGrid}>
                        {/* Box 1: Upload Files */}
                        <div
                          aria-hidden="true"
                          className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay1}`}
                          onClick={() => filesInputRef.current?.click()}
                        >
                          <div className={styles.uploadOptionIcon}>
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M14 2V8H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M12 18V12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M9 15L12 12L15 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                          <span className={styles.uploadOptionText}>
                            <span className={styles.hideOnMobile}>Choose Local Files</span>
                            <span className={styles.showOnMobileOnly}>Choose From Gallery</span>
                          </span>
                        </div>
                        {/* Box 2: Upload from Phone (QR) - hide on mobile UI and small screens */}
                        {!(isMobileUploadMode || isShareMobileUI) && (
                          <div
                            aria-hidden="true"
                            className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay2} ${styles.hideOnMobile}`}
                            onClick={() => setShowQrCode(true)}
                          >
                            <div className={styles.uploadOptionIcon}>
                              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="5" y="1" width="14" height="22" rx="3" stroke="white" strokeWidth="1.5" />
                                <line x1="10" y1="3.5" x2="14" y2="3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                                <circle cx="12" cy="19" r="1" fill="white" />
                              </svg>
                            </div>
                            <span className={styles.uploadOptionText}>Upload From Mobile</span>
                          </div>
                        )}
                        {/* Box: Apple Photos - hidden on mobile */}
                        <div
                          aria-hidden="true"
                          className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${isMobileUploadMode ? styles.optionDelay3 : styles.optionDelay4} ${styles.hideOnMobile}`}
                          onClick={() => setShowQrCode(true)}
                        >
                          <div className={styles.uploadOptionIcon}>
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M17.05 20.28c-.98.95-2.05.79-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="#999" />
                              </svg>
                          </div>
                          <span className={styles.uploadOptionText}>Apple Photos</span>
                        </div>
                        {/* Box 3: Google Photos */}
                        <div
                          aria-hidden="true"
                          className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${isMobileUploadMode ? styles.optionDelay2 : styles.optionDelay3}`}
                          onClick={() => handleCloudConnect('googlephotos')}
                        >
                          <div className={styles.uploadOptionIcon}>
                            <svg width="40" height="40" viewBox="0 0 122.88 122.88" xmlns="http://www.w3.org/2000/svg">
                              <path d="M30.72,27.97c16.95,0,30.72,13.77,30.72,30.72v2.83H2.83c-1.52,0-2.75-1.23-2.83-2.83 C0,41.73,13.77,27.97,30.72,27.97L30.72,27.97z" fill="#F6B704" />
                              <path d="M94.91,30.72c0,16.95-13.77,30.72-30.72,30.72l-2.75,0V2.83C61.44,1.3,62.67,0,64.19,0 C81.22,0,94.91,13.77,94.91,30.72L94.91,30.72z" fill="#E54335" />
                              <path d="M92.16,94.99c-16.95,0-30.72-13.77-30.72-30.72v-2.83h58.61c1.52,0,2.83,1.23,2.83,2.75 C122.88,81.22,109.11,94.99,92.16,94.99L92.16,94.99L92.16,94.99z" fill="#4280EF" />
                              <path d="M27.97,92.16c0-16.95,13.77-30.72,30.72-30.72h2.75v58.61c0,1.52-1.23,2.75-2.83,2.83 C41.66,122.88,27.97,109.11,27.97,92.16L27.97,92.16L27.97,92.16z" fill="#34A353" />
                            </svg>
                          </div>
                          <span className={styles.uploadOptionText}>Google Photos</span>
                        </div>

                        {/* Box 4: Dropbox */}
                        <div
                          aria-hidden="true"
                          className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${isMobileUploadMode ? styles.optionDelay3 : styles.optionDelay4}`}
                          onClick={() => handleCloudConnect('dropbox')}
                        >
                          <div className={styles.uploadOptionIcon}>
                            <svg width="40" height="40" viewBox="0 0 43 40" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12.5 0L0 8.1l8.6 6.9 12.5-7.8L12.5 0zM0 21.9l12.5 8.1 8.6-7.1-12.5-7.8L0 21.9zM21.1 22.9l8.6 7.1 12.5-8.1-8.6-6.8-12.5 7.8zM42.2 8.1L29.7 0l-8.6 7.2 12.5 7.8 8.6-6.9zM21.2 24.6l-8.6 7.1-4 -2.6v2.9l12.6 7.6 12.6-7.6v-2.9l-4 2.6-8.6-7.1z" fill="#0061ff" />
                            </svg>
                          </div>
                          <span className={styles.uploadOptionText}>Dropbox</span>
                        </div>

                        {/* Box 4: Dropbox */}
                        <div
                          aria-hidden="true"
                          className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${isMobileUploadMode ? styles.optionDelay3 : styles.optionDelay4}`}
                          onClick={() => { handleCloudConnect('googledrive'); setShowAddMoreOptions(false); }}
                        >
                          <div className={styles.uploadOptionIcon}>
                            <svg width="32" height="32" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
                              <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#0066DA" />
                              <path d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L1.2 52.35c-.8 1.4-1.2 2.95-1.2 4.5h27.5L43.65 25z" fill="#00AC47" />
                              <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75L86.1 57c.8-1.4 1.2-2.95 1.2-4.5H59.85L73.55 76.8z" fill="#EA4335" />
                              <path d="M43.65 25L57.4 1.2c-1.35-.8-2.9-1.2-4.5-1.2H34.35c-1.6 0-3.15.45-4.5 1.2L43.65 25z" fill="#00832D" />
                              <path d="M59.85 53H27.5l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.55c1.6 0 3.15-.45 4.5-1.2L59.85 53z" fill="#2684FC" />
                              <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.2 28h27.5c0-1.55-.4-3.1-1.2-4.5l-12.75-22z" fill="#FFBA00" />
                            </svg>
                          </div>
                          <span className={styles.uploadOptionText}>Google Drive</span>
                        </div>
                      </div>
                    </div>

                    {/* Back - Full QR Code */}
                    {!(isMobileUploadMode || isShareMobileUI) && (
                      <div className={styles.flipCardBack}>
                        <div className={styles.qrCodeFlipContent}>
                          <QRCodeComponent
                            url={urlParams.share
                              ? (shortShareUrl || `${window.location.origin}/${buildSharePath()}`)
                              : `${window.location.origin}/gift?mobile=true&session=${sessionId}${giftUserUid ? `&giftUid=${giftUserUid}` : ''}${localStorage.getItem('token') ? `&token=${localStorage.getItem('token')}` : ''}${localStorage.getItem('uploader_token') ? `&access=${localStorage.getItem('uploader_token')}` : ''}`}
                            size={200}
                          />
                          <p className={styles.qrCodeHint}>Scan with your phone camera to upload files from your mobile device</p>
                          <button
                            className={styles.flipBackButton}
                            onClick={() => setShowQrCode(false)}
                            type="button"
                          >
                            ← Back to Upload Options
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Skip button - allows skipping content upload (hidden when QR code is shown) */}
                  {!urlParams.share && !showQrCode && (
                    <>
                      <div
                        aria-hidden="true"
                        className={`${styles.uploadOptionBox} ${styles.noMediaBox} ${styles.optionSlideIn} ${styles.optionDelay1}`}
                        onClick={() => {
                          setSelectedChoice('they-build');
                          animateToStep(4);
                        }}
                      >
                        <span className={styles.uploadOptionText} style={{ fontSize: '1.25rem' }}>No Media To Upload</span>
                        <span style={{ color: '#ccc', fontSize: '1.1rem', fontFamily: "'Montserrat', sans-serif", fontWeight: 500, textAlign: 'center', lineHeight: 1.6, marginTop: '8px' }}>
                          It&apos;s completely ok to send Projector as a blank canvas!
                          <br className={styles.noMediaBreak} />
                          <span className={styles.noMediaSecondLine}>They can add as much media as they would like later!</span>
                        </span>
                      </div>
                    </>
                  )}

                  {/* Toggle button for flip - only show on desktop */}
                  {/* {!isMobileUploadMode && (
                    <button
                      className={styles.flipToggleButton}
                      onClick={() => setShowQrCode(!showQrCode)}
                    >
                      {showQrCode ? '← Show Upload Options' : 'Scan QR Code to Upload from Mobile →'}
                    </button>
                  )} */}

                  {/* Already uploaded link - for users who uploaded via mobile QR */}
                  {/* {!isMobileUploadMode && (
                    <span
                      className={styles.alreadyUploadedLink}
                      onClick={() => {
                        setMobileUploadCompleted(true);
                        animateToStep(4); // Go to payment section first
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setMobileUploadCompleted(true);
                          animateToStep(4); // Go to payment section first
                        }
                      }}
                    >
                      Already uploaded?
                    </span>
                  )} */}
                </>
              )}

              {/* Back to files button when adding more */}
              {showAddMore && selectedFiles.length > 0 && (
                <div className={styles.backToFilesContainer}>
                  <span className={styles.backToFilesText}>
                    {`${selectedFiles.reduce((count, f) => count + (f.type === 'album' ? (f.photoCount || 0) : 1), 0)} ${selectedFiles.reduce((count, f) => count + (f.type === 'album' ? (f.photoCount || 0) : 1), 0) === 1 ? 'file' : 'files'} already selected`}
                  </span>
                  <button
                    className={styles.backToFilesButton}
                    onClick={() => setShowAddMore(false)}
                  >
                    Back to Files
                  </button>
                </div>
              )}

              {/* Selected files list - before upload */}
              {selectedFiles.length > 0 && !showAddMore && (
                <div className={styles.selectedFilesContainer} style={(isMobileUploadMode || isShareMobileUI) ? { maxHeight: 'none' } : undefined}>
                  {/* Quick add options row */}
                  <div className={styles.quickAddRow}>
                    {/* Content filter tabs - left side */}
                    <div className={styles.contentFilterTabs}>
                      {[{ label: 'All', value: 'all' }, { label: 'Videos', value: 'videos' }, { label: 'Photos', value: 'photos' }, ...(deletedItems.length > 0 ? [{ label: 'Restore', value: 'restore', badge: deletedItems.length }] : [])].map((tab) => (
                        <button
                          key={tab.value}
                          className={`${styles.contentFilterTab} ${contentFilter === tab.value ? styles.contentFilterTabActive : ''} ${tab.value === 'restore' ? styles.restoreTab : ''}`}
                          onClick={() => setContentFilter(tab.value)}
                        >
                          {tab.label}
                          {tab.badge && <span className={styles.restoreBadge}>{tab.badge}</span>}
                        </button>
                      ))}
                    </div>
                    {/* Add content to Add media icons - right side */}
                    <div className={styles.quickAddRight}>
                      <p style={{ color: '#fff', fontSize: (isMobileUploadMode || isShareMobileUI) ? '0.65rem' : '1rem', fontWeight: '600', fontFamily: "'Montserrat', sans-serif", margin: '0 0 4px 0' }}>Add More Media</p>
                      <div className={styles.quickAddIcons}>
                        <div
                          role="button"
                          tabIndex={0}
                          className={styles.quickAddOption}
                          onClick={() => filesInputRef.current?.click()}
                          onKeyDown={(e) => { if (e.key === 'Enter') filesInputRef.current?.click(); }}
                        >
                          <svg className={styles.quickAddSvg} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M14 2V8H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12 18V12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M9 15L12 12L15 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                        {!(isMobileUploadMode || isShareMobileUI) && (
                          <div
                            role="button"
                            tabIndex={0}
                            className={`${styles.quickAddOption} ${styles.hideOnMobile}`}
                            onClick={() => setShowQrCode(true)}
                            onKeyDown={(e) => { if (e.key === 'Enter') setShowQrCode(true); }}
                          >
                            <svg className={styles.quickAddSvg} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="5" y="1" width="14" height="22" rx="3" stroke="white" strokeWidth="1.5" />
                              <line x1="10" y1="3.5" x2="14" y2="3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                              <circle cx="12" cy="19" r="1" fill="white" />
                            </svg>
                          </div>
                        )}
                        {!(isMobileUploadMode || isShareMobileUI) && (
                        <>
                        <div
                          role="button"
                          tabIndex={0}
                          className={styles.quickAddOption}
                          onClick={() => setShowQrCode(true)}
                          onKeyDown={(e) => { if (e.key === 'Enter') setShowQrCode(true); }}
                        >
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.05 20.28c-.98.95-2.05.79-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="#999" />
                          </svg>
                        </div>
                        <div
                          role="button"
                          tabIndex={0}
                          className={styles.quickAddOption}
                          onClick={() => handleCloudConnect('googledrive')}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleCloudConnect('googledrive'); }}
                        >
                          <svg className={styles.quickAddSvg} width="24" height="24" viewBox="0 0 122.88 122.88" xmlns="http://www.w3.org/2000/svg">
                            <path d="M30.72,27.97c16.95,0,30.72,13.77,30.72,30.72v2.83H2.83c-1.52,0-2.75-1.23-2.83-2.83 C0,41.73,13.77,27.97,30.72,27.97L30.72,27.97z" fill="#F6B704" />
                            <path d="M94.91,30.72c0,16.95-13.77,30.72-30.72,30.72l-2.75,0V2.83C61.44,1.3,62.67,0,64.19,0 C81.22,0,94.91,13.77,94.91,30.72L94.91,30.72z" fill="#E54335" />
                            <path d="M92.16,94.99c-16.95,0-30.72-13.77-30.72-30.72v-2.83h58.61c1.52,0,2.83,1.23,2.83,2.75 C122.88,81.22,109.11,94.99,92.16,94.99L92.16,94.99L92.16,94.99z" fill="#4280EF" />
                            <path d="M27.97,92.16c0-16.95,13.77-30.72,30.72-30.72h2.75v58.61c0,1.52-1.23,2.75-2.83,2.83 C41.66,122.88,27.97,109.11,27.97,92.16L27.97,92.16L27.97,92.16z" fill="#34A353" />
                          </svg>
                        </div>
                        <div
                          role="button"
                          tabIndex={0}
                          className={styles.quickAddOption}
                          onClick={() => handleCloudConnect('dropbox')}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleCloudConnect('dropbox'); }}
                        >
                          <svg className={styles.quickAddSvg} width="24" height="24" viewBox="0 0 43 40" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.5 0L0 8.1l8.6 6.9 12.5-7.8L12.5 0zM0 21.9l12.5 8.1 8.6-7.1-12.5-7.8L0 21.9zM21.1 22.9l8.6 7.1 12.5-8.1-8.6-6.8-12.5 7.8zM42.2 8.1L29.7 0l-8.6 7.2 12.5 7.8 8.6-6.9zM21.2 24.6l-8.6 7.1-4 -2.6v2.9l12.6 7.6 12.6-7.6v-2.9l-4 2.6-8.6-7.1z" fill="#0061ff" />
                          </svg>
                        </div>
                        <div
                          role="button"
                          tabIndex={0}
                          className={styles.quickAddOption}
                          onClick={() => { handleCloudConnect('googledrive'); setShowAddMoreOptions(false); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleCloudConnect('googledrive'); }}
                        >
                          <svg width="32" height="32" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#0066DA" />
                            <path d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L1.2 52.35c-.8 1.4-1.2 2.95-1.2 4.5h27.5L43.65 25z" fill="#00AC47" />
                            <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75L86.1 57c.8-1.4 1.2-2.95 1.2-4.5H59.85L73.55 76.8z" fill="#EA4335" />
                            <path d="M43.65 25L57.4 1.2c-1.35-.8-2.9-1.2-4.5-1.2H34.35c-1.6 0-3.15.45-4.5 1.2L43.65 25z" fill="#00832D" />
                            <path d="M59.85 53H27.5l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.55c1.6 0 3.15-.45 4.5-1.2L59.85 53z" fill="#2684FC" />
                            <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.2 28h27.5c0-1.55-.4-3.1-1.2-4.5l-12.75-22z" fill="#FFBA00" />
                          </svg>
                        </div>
                        </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={`${styles.uploadProgressList} ${contentFilter !== 'all' ? styles.filteredGrid : ''}`} style={(isMobileUploadMode || isShareMobileUI) ? { maxHeight: '55vh' } : undefined}>
                    {contentFilter === 'restore' ? (
                      deletedItems.length > 0 ? (
                        deletedItems.map((item) => (
                          <div key={`restore-${item.type}-${item.uploadId}-${item.fileName}`} className={styles.filteredCard}>
                            <div className={styles.filteredCardThumb}>
                              {item.type === 'album' && item.selectedFileData?.photos && item.selectedFileData.photos.length > 0 ? (
                                <div className={getAlbumGridClass(item.selectedFileData.photos.length)}>
                                  {item.selectedFileData.photos.slice(0, item.selectedFileData.photos.length > 9 ? 8 : 9).map((photo, idx) => (
                                    <div key={photo.id || idx}>
                                      {(photo.previewUrl || photo.file) ? (
                                        <img src={photo.previewUrl || URL.createObjectURL(photo.file)} alt={photo.name || `Photo ${idx + 1}`} />
                                      ) : null}
                                    </div>
                                  ))}
                                  {item.selectedFileData.photos.length > 9 && (
                                    <div className={styles.albumFolderGridCount}>{`+${item.selectedFileData.photos.length - 8}`}</div>
                                  )}
                                </div>
                              ) : item.thumbnail ? (
                                <img src={item.thumbnail} alt={item.fileName} onError={(e) => { e.target.src = image04; }} />
                              ) : (
                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.05)' }}>
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" /></svg>
                                </div>
                              )}
                            </div>
                            <div className={styles.filteredCardInfo}>
                              <div className={styles.filteredCardTitleRow}>
                                <span className={styles.filteredCardTitle}>
                                  {item.fileName.length > 18 ? `${item.fileName.substring(0, 18)}...` : item.fileName}
                                </span>
                                <span className={styles.filteredCardTypeIcon}>
                                  {item.type === 'video' ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polygon points="23 7 16 12 23 17 23 7" />
                                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                                    </svg>
                                  ) : item.type === 'photo' ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                      <circle cx="8.5" cy="8.5" r="1.5" />
                                      <polyline points="21 15 16 10 5 21" />
                                    </svg>
                                  ) : (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                      <circle cx="8.5" cy="8.5" r="1.5" />
                                      <polyline points="21 15 16 10 5 21" />
                                    </svg>
                                  )}
                                </span>
                              </div>
                              <button
                                type="button"
                                className={styles.restoreCardButton}
                                onClick={() => handleRestore(item)}
                              >
                                Restore
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px', paddingTop: '120px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Montserrat', sans-serif", fontSize: '1.3rem', gridColumn: '1 / -1', width: '100%', textAlign: 'center' }}>
                          No items to restore
                        </div>
                      )
                    ) : uploads.length > 0 ? (() => {
                      // Apply content filter to uploads
                      const filteredUploads = contentFilter === 'all' ? uploads : uploads.filter((u) => {
                        const isAlbum = u.type === 'album' || u.fileName === ALL_PHOTOS_ALBUM_NAME || u.fileName.startsWith('All photos (');
                        if (!isAlbum) {
                          const ext = `.${u.fileName.split('.').pop().toLowerCase()}`;
                          const isImage = IMAGE_EXTENSIONS.includes(ext);
                          if (contentFilter === 'videos') return !isImage && !isAlbum;
                          if (contentFilter === 'photos') return isImage;
                        }
                        return contentFilter === 'photos';
                      });
                      // Group all "All photos" album entries under a single heading
                      const allPhotosUploadIds = new Set();
                      let firstAllPhotosId = null;
                      let totalAllPhotosCount = 0;
                      filteredUploads.forEach((u) => {
                        if (u.fileName === ALL_PHOTOS_ALBUM_NAME || u.fileName.startsWith('All photos (')) {
                          allPhotosUploadIds.add(u.id);
                          if (!firstAllPhotosId) firstAllPhotosId = u.id;
                          const m = u.fileName.match(/\((\d+)\)/);
                          totalAllPhotosCount += m ? parseInt(m[1], 10) : 0;
                        }
                      });
                      if (filteredUploads.length === 0) {
                        return (
                          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px', paddingTop: '120px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Montserrat', sans-serif", fontSize: '1.3rem', gridColumn: '1 / -1', width: '100%', textAlign: 'center' }}>
                            {contentFilter === 'videos' ? 'No videos' : 'No photos'}
                          </div>
                        );
                      }
                      return filteredUploads.map((upload) => {
                      const albumMatch = upload.fileName === ALL_PHOTOS_ALBUM_NAME || upload.fileName.startsWith('All photos (')
                        ? selectedFiles.find((f) => f.type === 'album' && f.id === upload.id)
                          || (upload.linkedAlbumId && selectedFiles.find((f) => f.id === upload.linkedAlbumId))
                          || selectedFiles.find((f) => f.type === 'album' && f.name === ALL_PHOTOS_ALBUM_NAME)
                          || selectedFiles.find((f) => f.type === 'album' && f.source === 'mobile-preview')
                        : null;
                      const matchedFile = selectedFiles.find((f) => f.id === upload.id)
                        || (upload.isMobileUpload && upload.mobileUid && selectedFiles.find((f) => f.source === 'mobile-preview' && f.mobileUid === upload.mobileUid))
                        || (upload.isMobileUpload && selectedFiles.find((f) => f.source === 'mobile-preview' && f.name === upload.fileName))
                        || (upload.isShareUpload && selectedFiles.find((f) => f.source === 'share-preview' && f.id === upload.id));
                      if (!matchedFile?.posterUrl && !matchedFile?.previewUrl) {
                        console.log('[THUMB_DEBUG] Step5 - No thumbnail for:', upload.fileName, {
                          uploadId: upload.id,
                          matchedFileFound: !!matchedFile,
                          matchedFileId: matchedFile?.id,
                          hasPosterUrl: !!matchedFile?.posterUrl,
                          hasPreviewUrl: !!matchedFile?.previewUrl,
                          fileType: matchedFile?.type,
                          source: matchedFile?.source,
                          selectedFilesCount: selectedFiles.length,
                        });
                      }
                      const isPhotosFilterAlbum = contentFilter === 'photos' && albumMatch && albumMatch.photos && albumMatch.photos.length > 0;
                      // Group "All photos" entries: hide non-first, combine count for first
                      const isAllPhotosAlbum = upload.fileName === ALL_PHOTOS_ALBUM_NAME || upload.fileName.startsWith('All photos (');
                      const isGroupedNonFirst = contentFilter === 'all' && isAllPhotosAlbum && allPhotosUploadIds.size > 1 && upload.id !== firstAllPhotosId;
                      const isGroupedFirst = contentFilter === 'all' && isAllPhotosAlbum && allPhotosUploadIds.size > 1 && upload.id === firstAllPhotosId;
                      const displayFileName = isGroupedFirst ? ALL_PHOTOS_ALBUM_NAME : upload.fileName;
                      // Non-first "All photos" entries: skip entirely, their photos are shown under the first entry
                      if (isGroupedNonFirst) return null;
                      // For first grouped entry, combine photos from all "All photos" album entries
                      let effectiveAlbumMatch = albumMatch;
                      if (isGroupedFirst && albumMatch) {
                        const combinedPhotos = [];
                        const seenPhotoIds = new Set();
                        filteredUploads.forEach((u) => {
                          if (!allPhotosUploadIds.has(u.id)) return;
                          const match = selectedFiles.find((f) => f.type === 'album' && f.id === u.id)
                            || (u.linkedAlbumId && selectedFiles.find((f) => f.id === u.linkedAlbumId))
                            || selectedFiles.find((f) => f.type === 'album' && f.name === ALL_PHOTOS_ALBUM_NAME)
                            || selectedFiles.find((f) => f.type === 'album' && f.source === 'mobile-preview');
                          if (match?.photos) {
                            match.photos.forEach((p) => {
                              if (!seenPhotoIds.has(p.id)) {
                                seenPhotoIds.add(p.id);
                                combinedPhotos.push({ ...p, srcAlbumId: match.id, srcUploadId: u.id, srcIsMobile: !!u.isMobileUpload });
                              }
                            });
                          }
                        });
                        if (combinedPhotos.length > 0) {
                          effectiveAlbumMatch = { ...albumMatch, photos: combinedPhotos };
                        }
                      }
                      // === GRID CARD VIEW when filter is active ===
                      if (contentFilter !== 'all') {
                        if (isPhotosFilterAlbum && albumMatch?.photos) {
                          return albumMatch.photos.map((photo) => (
                            <div key={`${upload.id}-${photo.id}`} className={styles.filteredCard}>
                              <div className={styles.filteredCardThumb}>
                                {(photo.file || photo.previewUrl) ? (
                                  <img
                                    src={photo.previewUrl || URL.createObjectURL(photo.file)}
                                    alt={photo.name}
                                  />
                                ) : photo.cloudFile || photo.cloudSource ? (
                                  <div className={styles.albumGridPhotoShimmer}>
                                    <div className={styles.shimmerAnimation} />
                                  </div>
                                ) : (
                                  <div className={styles.filteredCardPlaceholder}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  className={styles.filteredCardDelete}
                                  onClick={() => {
                                    handleSoftDeletePhoto({
                                      source: photo.photo_id ? 'registered' : 'unregistered',
                                      type: 'photo',
                                      uploadId: upload.id,
                                      registeredId: photo.photo_id || photo.id,
                                      albumFileId: albumMatch.id,
                                      fileName: photo.name,
                                      thumbnail: photo.previewUrl || photo.imagekit_url || null,
                                      photoData: { ...photo },
                                    });
                                  }}
                                  title="Remove"
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M19 6L18.1 20.1C18 21.2 17.1 22 16 22H8C6.9 22 6 21.2 5.9 20.1L5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M10 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M14 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </button>
                              </div>
                              <div className={styles.filteredCardInfo}>
                                <div className={styles.filteredCardTitleRow}>
                                  <span className={styles.filteredCardTitle}>{photo.name.length > 18 ? `${photo.name.substring(0, 18)}...` : photo.name}</span>
                                  <span className={styles.filteredCardTypeIcon}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                      <circle cx="8.5" cy="8.5" r="1.5" />
                                      <polyline points="21 15 16 10 5 21" />
                                    </svg>
                                  </span>
                                </div>
                              </div>
                            </div>
                          ));
                        }
                        return (
                          <div key={upload.id} className={`${styles.filteredCard} ${upload.status === UPLOAD_STATUS.UPLOADING || upload.status === UPLOAD_STATUS.PENDING ? styles.filteredCardUploading : ''}`}>
                            <div className={styles.filteredCardThumb}>
                              {matchedFile?.posterUrl || matchedFile?.previewUrl ? (
                                matchedFile.posterUrl || matchedFile.type !== 'video' ? (
                                  <img src={matchedFile.posterUrl || matchedFile.previewUrl} alt={upload.fileName} />
                                ) : (
                                  <video src={matchedFile.previewUrl} muted playsInline preload="metadata" poster={matchedFile.posterUrl || ''} />
                                )
                              ) : (
                                <div className={styles.filteredCardPlaceholder}>
                                  {contentFilter === 'videos' ? (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" /></svg>
                                  ) : (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                                  )}
                                </div>
                              )}
                              {contentFilter === 'videos' ? (
                                (matchedFile?.previewUrl || matchedFile?.s3Url || (upload.status === UPLOAD_STATUS.COMPLETED && upload.registeredContentId)) && (
                                  <div
                                    className={styles.filteredVideoPlayOverlay}
                                    style={{ cursor: 'pointer' }}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => (upload.registeredContentId ? handleUploadVideoPreview(upload.registeredContentId) : handleLocalVideoPreview(matchedFile))}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { if (upload.registeredContentId) handleUploadVideoPreview(upload.registeredContentId); else handleLocalVideoPreview(matchedFile); } }}
                                  >
                                    <span className={styles.filteredVideoPlayIcon}>
                                      <svg width="22" height="22" viewBox="0 0 24 24" fill="#000" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8 5v14l11-7z" />
                                      </svg>
                                    </span>
                                  </div>
                                )
                              ) : (
                                <button type="button" className={styles.filteredPreviewEyeButton} title="Preview">
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <circle cx="12" cy="12" r="3" stroke="white" strokeWidth="2" />
                                  </svg>
                                </button>
                              )}
                              {(upload.status === UPLOAD_STATUS.UPLOADING || upload.status === UPLOAD_STATUS.PENDING) && (
                                <>
                                  <div className={styles.filteredCardShimmer} />
                                  <div className={styles.filteredCardProgress}>
                                    {upload.isMobileUpload || upload.progress === 0 ? (
                                      <div className={styles.filteredCardProgressIndeterminate} />
                                    ) : (
                                      <div className={styles.filteredCardProgressBar} style={{ width: `${upload.progress}%` }} />
                                    )}
                                  </div>
                                </>
                              )}
                              {upload.status === UPLOAD_STATUS.COMPLETED && (
                                <button
                                  type="button"
                                  className={styles.filteredCardDelete}
                                  onClick={() => {
                                    const isAlbum = upload.fileName.startsWith('All photos');
                                    const isRegistered = !!(upload.registeredContentId || upload.registeredAlbumId);
                                    const matchedFile = selectedFiles.find((f) => f.id === upload.id)
                                      || (upload.linkedAlbumId && selectedFiles.find((f) => f.id === upload.linkedAlbumId))
                                      || (upload.mobileUid && selectedFiles.find((f) => f.mobileUid === upload.mobileUid))
                                      || selectedFiles.find((f) => f.name === upload.fileName);
                                    handleSoftDelete({
                                      source: isRegistered ? 'registered' : 'unregistered',
                                      type: isRegistered ? (upload.registeredContentId ? 'video' : 'album') : (isAlbum ? 'album' : 'video'),
                                      uploadId: upload.id,
                                      registeredId: upload.registeredContentId || upload.registeredAlbumId || null,
                                      fileName: upload.fileName,
                                      thumbnail: matchedFile?.posterUrl || matchedFile?.previewUrl || (matchedFile?.type === 'album' && matchedFile?.photos?.[0]?.previewUrl) || (matchedFile?.type === 'album' && matchedFile?.photos?.[0]?.imagekit_url) || upload.thumbnail || null,
                                      uploadData: { ...upload },
                                      selectedFileData: matchedFile ? { ...matchedFile } : null,
                                    });
                                  }}
                                  title="Delete"
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M19 6L18.1 20.1C18 21.2 17.1 22 16 22H8C6.9 22 6 21.2 5.9 20.1L5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M10 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M14 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            <div className={styles.filteredCardInfo}>
                              <div className={styles.filteredCardTitleRow}>
                                <span className={styles.filteredCardTitle}>{upload.fileName.length > 18 ? `${upload.fileName.substring(0, 18)}...` : upload.fileName}</span>
                                <span className={styles.filteredCardTypeIcon}>
                                  {contentFilter === 'videos' ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polygon points="23 7 16 12 23 17 23 7" />
                                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                                    </svg>
                                  ) : (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                      <circle cx="8.5" cy="8.5" r="1.5" />
                                      <polyline points="21 15 16 10 5 21" />
                                    </svg>
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div key={upload.id} className={`${styles.uploadProgressItem}${(upload.status === UPLOAD_STATUS.S3_COMPLETE || (upload.status === UPLOAD_STATUS.COMPLETED && !upload.registeredContentId && !upload.registeredAlbumId)) ? ` ${styles.previewLoading}` : ''}`} style={isGroupedNonFirst ? { display: 'none' } : undefined}>
                          {/* File preview thumbnail - use first album photo for "All photos" items */}
                          {!isPhotosFilterAlbum && !isGroupedNonFirst && (() => {
                            const albumPhotos = (effectiveAlbumMatch || albumMatch)?.photos;
                            const firstAlbumPhoto = albumPhotos?.[0];
                            if (matchedFile?.posterUrl || matchedFile?.previewUrl) {
                              return matchedFile.posterUrl || matchedFile.source === 'share-preview' || matchedFile.type !== 'video' ? (
                                <img
                                  src={matchedFile.posterUrl || matchedFile.previewUrl}
                                  alt={upload.fileName}
                                />
                              ) : (
                                <video
                                  src={matchedFile.previewUrl}
                                  poster={matchedFile.posterUrl || ''}
                                  muted
                                  playsInline
                                  preload="metadata"
                                />
                              );
                            }
                            if (albumPhotos && albumPhotos.length > 0) {
                              return (
                                <div className={getAlbumGridClass(albumPhotos.length)}>
                                  {albumPhotos.slice(0, albumPhotos.length > 9 ? 8 : 9).map((photo, idx) => (
                                    <div key={photo.id || idx}>
                                      {(photo.previewUrl || photo.file) ? (
                                        <img src={photo.previewUrl || URL.createObjectURL(photo.file)} alt={photo.name || `Photo ${idx + 1}`} />
                                      ) : (photo.cloudFile || photo.cloudSource) ? (
                                        <div className={styles.albumGridPhotoShimmer} style={{ width: '100%', height: '100%', borderRadius: '4px' }}>
                                          <div className={styles.shimmerAnimation} />
                                        </div>
                                      ) : null}
                                    </div>
                                  ))}
                                  {albumPhotos.length > 9 && (
                                    <div className={styles.albumFolderGridCount}>{`+${albumPhotos.length - 8}`}</div>
                                  )}
                                </div>
                              );
                            }
                            // Show shimmer for cloud photos loading (160px matches image height)
                            if (firstAlbumPhoto?.cloudFile || firstAlbumPhoto?.cloudSource) {
                              return (
                                <div className={styles.albumGridPhotoShimmer} style={{ width: '100%', height: '160px' }}>
                                  <div className={styles.shimmerAnimation} />
                                </div>
                              );
                            }
                            return (
                              <div className={styles.gridPlaceholderThumb}>
                                {matchedFile?.type === 'video' || (upload.fileName && VIDEO_EXTENSIONS.includes(`.${upload.fileName.split('.').pop().toLowerCase()}`)) ? (
                                  <svg width="28" height="28" viewBox="0 0 24 24" fill="#555"><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" /></svg>
                                ) : (
                                  <svg width="28" height="28" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                                )}
                              </div>
                            );
                          })()}
                          {/* Preview overlay on thumbnail */}
                          {!isPhotosFilterAlbum && !isGroupedNonFirst && (() => {
                            const isVideo = matchedFile?.type === 'video' || (upload.fileName && VIDEO_EXTENSIONS.includes(`.${upload.fileName.split('.').pop().toLowerCase()}`));
                            const isAlbumItem = upload.type === 'album' || albumMatch || upload.fileName === ALL_PHOTOS_ALBUM_NAME || upload.fileName.startsWith('All photos (');
                            if (isVideo && !isAlbumItem && (matchedFile?.previewUrl || matchedFile?.s3Url || (upload.status === UPLOAD_STATUS.COMPLETED && upload.registeredContentId))) {
                              return (
                                <div
                                  className={styles.filteredVideoPlayOverlay}
                                  style={{ cursor: 'pointer' }}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => (upload.registeredContentId ? handleUploadVideoPreview(upload.registeredContentId) : handleLocalVideoPreview(matchedFile))}
                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { if (upload.registeredContentId) handleUploadVideoPreview(upload.registeredContentId); else handleLocalVideoPreview(matchedFile); } }}
                                >
                                  <span className={styles.filteredVideoPlayIcon}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="#000" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M8 5v14l11-7z" />
                                    </svg>
                                  </span>
                                </div>
                              );
                            }
                            if (isAlbumItem) {
                              return (
                                <button
                                  type="button"
                                  className={styles.filteredPreviewEyeButton}
                                  title="Preview"
                                  onClick={() => setStep3AlbumModal({ upload, albumMatch: effectiveAlbumMatch || albumMatch, photos: (effectiveAlbumMatch || albumMatch)?.photos })}
                                >
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <circle cx="12" cy="12" r="3" stroke="white" strokeWidth="2" />
                                  </svg>
                                </button>
                              );
                            }
                            return null;
                          })()}
                          {/* Delete overlay on thumbnail */}
                          {!isPhotosFilterAlbum && !isGroupedNonFirst && upload.status === UPLOAD_STATUS.COMPLETED && (
                            <button
                              type="button"
                              className={styles.filteredCardDelete}
                              onClick={() => {
                                const isAlbum = upload.fileName.startsWith('All photos');
                                const isRegistered = !!(upload.registeredContentId || upload.registeredAlbumId);
                                const matchedFile = selectedFiles.find((f) => f.id === upload.id)
                                  || (upload.linkedAlbumId && selectedFiles.find((f) => f.id === upload.linkedAlbumId))
                                  || (upload.mobileUid && selectedFiles.find((f) => f.mobileUid === upload.mobileUid))
                                  || selectedFiles.find((f) => f.name === upload.fileName);
                                handleSoftDelete({
                                  source: isRegistered ? 'registered' : 'unregistered',
                                  type: isRegistered ? (upload.registeredContentId ? 'video' : 'album') : (isAlbum ? 'album' : 'video'),
                                  uploadId: upload.id,
                                  registeredId: upload.registeredContentId || upload.registeredAlbumId || null,
                                  fileName: upload.fileName,
                                  thumbnail: matchedFile?.posterUrl || matchedFile?.previewUrl || (matchedFile?.type === 'album' && matchedFile?.photos?.[0]?.previewUrl) || (matchedFile?.type === 'album' && matchedFile?.photos?.[0]?.imagekit_url) || upload.thumbnail || null,
                                  uploadData: { ...upload },
                                  selectedFileData: matchedFile ? { ...matchedFile } : null,
                                });
                              }}
                              title="Delete"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M19 6L18.1 20.1C18 21.2 17.1 22 16 22H8C6.9 22 6 21.2 5.9 20.1L5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M10 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M14 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          )}
                          <div style={{ padding: '8px 10px' }}>
                          {!isPhotosFilterAlbum && !isGroupedNonFirst && (
                            <div className={styles.uploadProgressHeader}>
                              <div className={styles.filteredCardTitleRow} style={{ flex: 1, minWidth: 0 }}>
                                <span className={styles.uploadFileName}>
                                  {displayFileName.length > 30 ? `${displayFileName.substring(0, 30)}...` : displayFileName}
                                </span>
                                {upload.status === UPLOAD_STATUS.COMPLETED && (upload.registeredContentId || upload.registeredAlbumId) && (
                                  <span className={styles.filteredCardTypeIcon}>
                                    {(() => {
                                      const isVideo = matchedFile?.type === 'video' || (upload.fileName && VIDEO_EXTENSIONS.includes(`.${upload.fileName.split('.').pop().toLowerCase()}`));
                                      const isAlbumItem = upload.type === 'album' || albumMatch || upload.fileName === ALL_PHOTOS_ALBUM_NAME || upload.fileName.startsWith('All photos (');
                                      if (isVideo && !isAlbumItem) {
                                        return (
                                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polygon points="23 7 16 12 23 17 23 7" />
                                            <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                                          </svg>
                                        );
                                      }
                                      return (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                          <circle cx="8.5" cy="8.5" r="1.5" />
                                          <polyline points="21 15 16 10 5 21" />
                                        </svg>
                                      );
                                    })()}
                                  </span>
                                )}
                              </div>
                            <div className={styles.uploadStatusActions}>
                              {/* {urlParams.share && (upload.status === UPLOAD_STATUS.S3_COMPLETE || (upload.status === UPLOAD_STATUS.COMPLETED && !upload.registeredContentId && !upload.registeredAlbumId)) && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '6px', color: '#5aa5ef', fontSize: '0.7rem', fontFamily: "'Montserrat', sans-serif" }}>
                                  <span style={{ width: '12px', height: '12px', border: '2px solid rgba(90,165,239,0.25)', borderTop: '2px solid #5aa5ef', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                                  {upload.status === UPLOAD_STATUS.S3_COMPLETE ? 'Registering...' : 'Preview Loading'}
                                </span>
                              )} */}
                              <span className={styles.uploadStatus}>
                                {upload.status === UPLOAD_STATUS.FAILED && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setStep3DeletePending({
                                        type: 'failed',
                                        fileName: upload.fileName,
                                        onConfirm: () => handleCancelUpload(upload.id),
                                      });
                                    }}
                                    style={{ cursor: 'pointer', background: 'none', border: 'none', color: 'inherit', font: 'inherit', padding: 0 }}
                                    title="Remove"
                                  >
                                    ✕
                                  </button>
                                )}
                                {upload.status === UPLOAD_STATUS.UPLOADING && !upload.isMobileUpload && `${upload.progress}%`}
                                {upload.status === UPLOAD_STATUS.UPLOADING && upload.isMobileUpload && (
                                  <span className={styles.mobileUploadStatus}>
                                    Uploading from Mobile...
                                    <span className={styles.mobileUploadSpinner} />
                                  </span>
                                )}
                                {upload.status === UPLOAD_STATUS.PAUSED && 'Paused'}
                                {upload.status === UPLOAD_STATUS.NETWORK_ERROR && 'Network Error - Will Resume'}
                                {upload.status === UPLOAD_STATUS.PENDING && 'Waiting...'}
                                {upload.status === UPLOAD_STATUS.CANCELLING && 'Cancelling...'}
                                {upload.status === UPLOAD_STATUS.CANCELLED && 'Cancelled'}
                              </span>
                              {!upload.isMobileUpload && (upload.status === UPLOAD_STATUS.UPLOADING || upload.status === UPLOAD_STATUS.PAUSED || upload.status === UPLOAD_STATUS.NETWORK_ERROR) && (
                                <div className={styles.uploadControls}>
                                  {upload.status === UPLOAD_STATUS.UPLOADING && (
                                    <button
                                      type="button"
                                      className={styles.uploadControlButton}
                                      onClick={() => handlePauseUpload(upload.id)}
                                      title="Pause upload"
                                    >
                                      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                                      </svg>
                                    </button>
                                  )}
                                  {(upload.status === UPLOAD_STATUS.PAUSED || upload.status === UPLOAD_STATUS.NETWORK_ERROR) && (
                                    <button
                                      type="button"
                                      className={styles.uploadControlButton}
                                      onClick={() => handleResumeUpload(upload.id)}
                                      title={upload.status === UPLOAD_STATUS.NETWORK_ERROR ? 'Resume when online' : 'Resume upload'}
                                    >
                                      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M8 5v14l11-7z" />
                                      </svg>
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className={styles.uploadControlButtonCancel}
                                    onClick={() => {
                                      setStep3DeletePending({
                                        type: 'video',
                                        fileName: upload.fileName,
                                        isCancel: true,
                                        onConfirm: () => handleCancelUpload(upload.id),
                                      });
                                    }}
                                    title="Cancel upload"
                                  >
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                                    </svg>
                                  </button>
                                </div>
                              )}
                              {upload.isMobileUpload && upload.status === UPLOAD_STATUS.UPLOADING && (
                                <button
                                  type="button"
                                  className={styles.uploadControlButtonCancel}
                                  onClick={() => {
                                    setStep3DeletePending({
                                      type: 'video',
                                      fileName: upload.fileName,
                                      isCancel: true,
                                      onConfirm: () => handleCancelUpload(upload.id),
                                    });
                                  }}
                                  title="Cancel upload"
                                >
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            </div>
                          )}
                          {!isPhotosFilterAlbum && !isGroupedNonFirst && (!upload.isMobileUpload || upload.status === UPLOAD_STATUS.UPLOADING || upload.status === UPLOAD_STATUS.COMPLETED) && (
                            <>
                              <div className={styles.uploadProgressBarContainer}>
                                <div
                                  className={`${styles.uploadProgressBar} ${upload.status === UPLOAD_STATUS.COMPLETED ? styles.uploadProgressComplete : ''} ${upload.status === UPLOAD_STATUS.FAILED ? styles.uploadProgressFailed : ''} ${upload.status === UPLOAD_STATUS.PAUSED ? styles.uploadProgressPaused : ''} ${upload.isMobileUpload && upload.status === UPLOAD_STATUS.UPLOADING ? styles.uploadProgressIndeterminate : ''}`}
                                  style={{ width: upload.isMobileUpload && upload.status !== UPLOAD_STATUS.COMPLETED ? '100%' : `${upload.progress}%` }}
                                />
                              </div>
                              <div className={styles.uploadProgressDetails}>
                                {!upload.isMobileUpload && !upload.isShareUpload && (
                                  <span>{`${formatFileSize(upload.bytesUploaded)} / ${formatFileSize(upload.fileSize)}`}</span>
                                )}
                                {upload.isMobileUpload && !isAllPhotosAlbum && upload.status !== UPLOAD_STATUS.COMPLETED && (
                                  <span>Uploading from mobile</span>
                                )}
                                {upload.status === UPLOAD_STATUS.COMPLETED && (
                                  <span className={styles.uploadCompleteText}>
                                    {upload.isShareUpload
                                      ? `Uploaded by ${upload.contributorName || 'Contributor'}${upload.shareFileSize && !Number.isNaN(parseFloat(upload.shareFileSize)) ? ` · ${formatFileSize(parseFloat(upload.shareFileSize) * 1024 * 1024)}` : ''}`
                                      : upload.isMobileUpload && !isAllPhotosAlbum ? 'Uploaded from mobile' : 'Complete'}
                                  </span>
                                )}
                                {upload.status === UPLOAD_STATUS.FAILED && (
                                  <span className={styles.uploadFailedText}>Failed</span>
                                )}
                              </div>
                            </>
                          )}
                          {/* Album photos hidden in grid - shown via modal instead */}
                          {(effectiveAlbumMatch || albumMatch) && (effectiveAlbumMatch || albumMatch).photos && (isPhotosFilterAlbum || expandedAlbums[`upload-${upload.id}`] !== false) && (
                            <div style={{ display: 'none' }}>
                              {(effectiveAlbumMatch || albumMatch).photos.map((photo) => (
                                <div key={photo.id} className={styles.uploadProgressItem} style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '10px 12px' }}>
                                  {(photo.file || photo.previewUrl) ? (
                                    <img
                                      src={photo.previewUrl || URL.createObjectURL(photo.file)}
                                      alt={photo.name}
                                      style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0, background: '#000' }}
                                    />
                                  ) : photo.cloudFile || photo.cloudSource ? (
                                    <div className={styles.albumGridPhotoShimmer} style={{ width: '48px', height: '48px', borderRadius: '6px', flexShrink: 0 }}>
                                      <div className={styles.shimmerAnimation} />
                                    </div>
                                  ) : (
                                    <div style={{ width: '48px', height: '48px', borderRadius: '6px', flexShrink: 0, background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <svg width="20" height="20" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                                    </div>
                                  )}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className={styles.uploadProgressHeader}>
                                      <span className={styles.uploadFileName}>
                                        {photo.name.length > 30 ? `${photo.name.substring(0, 30)}...` : photo.name}
                                      </span>
                                    </div>
                                    {!(photo.srcIsMobile ?? upload.isMobileUpload) && photo.size > 0 && (
                                      <div className={styles.uploadProgressDetails}>
                                        <span>{formatFileSize(photo.size)}</span>
                                      </div>
                                    )}
                                  </div>
                                  {/* Delete individual photo from album */}
                                  <button
                                    type="button"
                                    title="Remove photo"
                                    style={{ cursor: 'pointer', background: 'rgba(229,57,53,0.12)', border: '1px solid rgba(229,57,53,0.35)', borderRadius: '6px', color: '#e53935', padding: '3px 7px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                                    onClick={() => {
                                      const photoUploadId = photo.srcUploadId || upload.id;
                                      const photoAlbumId = photo.srcAlbumId || (effectiveAlbumMatch || albumMatch).id;
                                      handleSoftDeletePhoto({
                                        source: photo.photo_id ? 'registered' : 'unregistered',
                                        type: 'photo',
                                        uploadId: photoUploadId,
                                        registeredId: photo.photo_id || photo.id,
                                        albumFileId: photoAlbumId,
                                        fileName: photo.name,
                                        thumbnail: photo.previewUrl || photo.imagekit_url || null,
                                        photoData: { ...photo },
                                      });
                                    }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M19 6L18.1 20.1C18 21.2 17.1 22 16 22H8C6.9 22 6 21.2 5.9 20.1L5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M10 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M14 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          </div>
                        </div>
                      );
                      });
                    })() : selectedFiles.map((file) => (
                      <div key={file.id} className={styles.uploadProgressItem}>
                        <div style={{ position: 'relative' }}>
                          {(file.posterUrl || file.previewUrl) ? (
                            file.posterUrl || file.type !== 'video' ? (
                              <img src={file.posterUrl || file.previewUrl} alt={file.name} />
                            ) : (
                              <video src={file.previewUrl} muted preload="metadata" />
                            )
                          ) : file.type === 'album' && file.photos && file.photos.length > 0 ? (
                            <div className={getAlbumGridClass(file.photos.length)}>
                              {file.photos.slice(0, file.photos.length > 9 ? 8 : 9).map((photo, idx) => (
                                <div key={photo.id || idx}>
                                  {(photo.previewUrl || photo.file) ? (
                                    <img src={photo.previewUrl || URL.createObjectURL(photo.file)} alt={photo.name || `Photo ${idx + 1}`} />
                                  ) : null}
                                </div>
                              ))}
                              {file.photos.length > 9 && (
                                <div className={styles.albumFolderGridCount}>{`+${file.photos.length - 8}`}</div>
                              )}
                            </div>
                          ) : (
                            <div className={styles.gridPlaceholderThumb}>
                              <svg width="28" height="28" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                            </div>
                          )}
                          {file.type === 'video' && (file.previewUrl || file.s3Url) && (
                            <button
                              type="button"
                              className={styles.filteredVideoPlayOverlay}
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleLocalVideoPreview(file)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleLocalVideoPreview(file); }}
                            >
                              <span className={styles.filteredVideoPlayIcon}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="#000" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              </span>
                            </button>
                          )}
                        </div>
                        <div style={{ padding: '8px 10px' }}>
                          <div className={styles.uploadProgressHeader}>
                            <span className={styles.uploadFileName}>
                              {file.name.length > 20 ? `${file.name.substring(0, 20)}...` : file.name}
                            </span>
                            <span className={styles.uploadStatus}>
                              Waiting...
                            </span>
                          </div>
                          <div className={styles.uploadProgressBarContainer}>
                            <div className={styles.uploadProgressBar} style={{ width: '0%' }} />
                          </div>
                          <div className={styles.uploadProgressDetails}>
                            <span>{formatFileSize(file.size)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className={styles.uploadActionsRow}>
                    {/* <button
                      className={styles.sendEmailButton}
                      onClick={() => setShowAddMore(true)}
                    >
                      + Add content to Add media
                    </button> */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {!isMobileUploadMode && !urlParams.share && currentStep === 3 && (
                      <span style={{ color: '#fff', fontSize: '0.85rem', fontFamily: "'Montserrat', sans-serif", textAlign: 'center', marginBottom: '12px' }}>
                        You do not need to wait for uploads to complete to continue.
                      </span>
                    )}
                    <button
                      className={styles.startUploadButton}
                      disabled={(!urlParams.share && (isRegistering || uploads.some((u) => u.status === UPLOAD_STATUS.S3_COMPLETE))) || (urlParams.share && (uploads.length === 0 || !uploads.every((u) => u.status === UPLOAD_STATUS.COMPLETED || u.status === UPLOAD_STATUS.S3_COMPLETE || u.status === UPLOAD_STATUS.FAILED || u.status === UPLOAD_STATUS.CANCELLED))) || (isMobileUploadMode && uploads.length > 0 && !uploads.every((u) => u.status === UPLOAD_STATUS.COMPLETED || u.status === UPLOAD_STATUS.S3_COMPLETE || u.status === UPLOAD_STATUS.FAILED || u.status === UPLOAD_STATUS.CANCELLED))}
                      style={((!urlParams.share && (isRegistering || uploads.some((u) => u.status === UPLOAD_STATUS.S3_COMPLETE))) || (urlParams.share && (uploads.length === 0 || !uploads.every((u) => u.status === UPLOAD_STATUS.COMPLETED || u.status === UPLOAD_STATUS.S3_COMPLETE || u.status === UPLOAD_STATUS.FAILED || u.status === UPLOAD_STATUS.CANCELLED))) || (isMobileUploadMode && uploads.length > 0 && !uploads.every((u) => u.status === UPLOAD_STATUS.COMPLETED || u.status === UPLOAD_STATUS.S3_COMPLETE || u.status === UPLOAD_STATUS.FAILED || u.status === UPLOAD_STATUS.CANCELLED))) ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                      onClick={async () => {
                        await flushDeletedItems();
                        if (isMobileUploadMode) {
                          // Show upload complete popup
                          setShowMobileUploadCompletePopup(true);

                          // Start polling for paymentDone from desktop (SSE may miss it)
                          if (!paymentDonePollRef.current && !paymentDoneRef.current) {
                            let pdPollCount = 0;
                            paymentDonePollRef.current = setInterval(async () => {
                              pdPollCount += 1;
                              if (pdPollCount > 120 || paymentDoneRef.current) {
                                clearInterval(paymentDonePollRef.current);
                                paymentDonePollRef.current = null;
                                return;
                              }
                              try {
                                const statusRes = await getGiftStatus(giftUserUid);
                                const statusData = statusRes?.data?.data || statusRes?.data || {};
                                const pushStr = statusData.push || '';
                                if (pushStr.includes && pushStr.includes('paymentDone') && pushStr.includes('mobileUpload:false')) {
                                  console.log('[MOBILE_FLOW] Poll detected paymentDone from desktop');
                                  const tokenMatch = pushStr.match(/token:'([^']+)'/);
                                  const pollToken = tokenMatch ? tokenMatch[1] : null;
                                  if (pollToken) {
                                    localStorage.setItem('token', pollToken);
                                    console.log('[MOBILE_FLOW] Token stored from poll');
                                  } else {
                                    console.error('[MOBILE_FLOW] No token in paymentDone poll!');
                                  }
                                  paymentDoneRef.current = true;
                                  paywebRequestedRef.current = false;
                                  // Send metadata to desktop so it can register — mobile should not register itself
                                  buildMobileUploadMetadata().then((meta) => {
                                    sendMobilePushMessage('allUploadsComplete', [], null, null, meta);
                                  });
                                  setSnackbar({
                                    isVisible: true,
                                    message: 'Payment complete! Registering videos...',
                                    type: 'success'
                                  });
                                  clearInterval(paymentDonePollRef.current);
                                  paymentDonePollRef.current = null;
                                }
                              } catch (err) {
                                console.warn('[MOBILE_FLOW] PaymentDone poll error:', err);
                              }
                            }, 5000);
                          }
                        } else if (urlParams.share) {
                          // Share contributor flow — clear tokens and show tick popup after step 3
                          localStorage.removeItem('token');
                          localStorage.removeItem('uploader_token');
                          localStorage.removeItem('uploader_token_expiry');
                          setShowMobileUploadCompletePopup(true);
                        } else {
                          handleStartUpload();
                        }
                      }}
                    >
                      {isMobileUploadMode ? 'Finalize Upload From Mobile' : 'Continue'}
                    </button>
                    {isMobileUploadMode && (
                      <p className={styles.continueSubText}>Please stay on the screen until uploads are fully completed</p>
                    )}
                    </div>
                    <span className={styles.filesCount}>
                      {(() => {
                        const photoCount = selectedFiles.reduce((count, f) => count + (f.type === 'album' ? (f.photoCount || 0) : 0), 0);
                        const videoCount = selectedFiles.filter((f) => f.type === 'video').length;
                        const parts = [];
                        if (photoCount > 0) parts.push(`${photoCount} ${photoCount === 1 ? 'photo' : 'photos'}`);
                        if (videoCount > 0) parts.push(`${videoCount} ${videoCount === 1 ? 'video' : 'videos'}`);
                        return parts.length > 0 ? parts.join(', ') : '0 files';
                      })()}
                    </span>
                  </div>
                </div>
              )}

              {error && (
                <div className={styles.uploadError}>{error}</div>
              )}
            </div>
          </>
        ) : currentStep === 5 ? (
          <>
            {/* Step 4 - Upload Progress with Sidebar Tabs */}
            <div className={`${styles.contentContainer} ${getAnimationClass()}`}>
              {/* <h1 className={styles.heading}>Step 4</h1> */}
              <div style={{ marginBottom: '20px' }}>
                <h2 className={styles.stepSubHeading} style={{ margin: 0 }}>
                  {isUploading || uploads.some((u) => u.status === UPLOAD_STATUS.UPLOADING || u.status === UPLOAD_STATUS.PENDING || u.status === UPLOAD_STATUS.PAUSED) ? 'Uploading Your Content' : 'Finalize Projector'}
                </h2>
                <p className={styles.stepSubText} style={{ marginTop: '8px' }}>Add More Media Or Invite Others To Add Media</p>
              </div>

              {/* Sidebar Layout Container */}
              <div className={styles.sidebarLayout}>
                {/* Left Sidebar - Tab Navigation - REMOVED */}
                {false && (
                  <div className={styles.tabSidebar}>
                    <span className={styles.tabSidebarTitle}>Customize</span>
                    <button
                      className={`${styles.tabButton} ${activeTab === 'upload' ? styles.tabButtonActive : ''}`}
                      onClick={() => setActiveTab('upload')}
                    >
                      <span className={styles.tabButtonIcon}>&#x2B06;</span>
                      Upload
                    </button>
                    <button
                      className={`${styles.tabButton} ${activeTab === 'layout' ? styles.tabButtonActive : ''}`}
                      onClick={() => setActiveTab('layout')}
                    >
                      <span className={styles.tabButtonIcon}>&#x25A6;</span>
                      Layout
                    </button>
                    <button
                      className={`${styles.tabButton} ${activeTab === 'content' ? styles.tabButtonActive : ''}`}
                      onClick={() => setActiveTab('content')}
                    >
                      <span className={styles.tabButtonIcon}>&#x270E;</span>
                      Content
                    </button>
                  </div>
                )}

                {/* Right Content Area */}
                <div className={styles.tabContentArea}>
                  {/* Upload Tab Content */}
                  {activeTab === 'upload' && (
                    <>
                      {/* Upload progress section */}
                      <div className={styles.selectedFilesContainer}>
                          {/* Hidden combined file input for adding more content */}
                          <input
                            ref={filesInputRef}
                            type="file"
                            accept={FILE_ACCEPT}
                            multiple
                            style={{ display: 'none' }}
                            onChange={handleCombinedFileSelect}
                          />
                          {/* Top row: Tabs on left, Upload options on right */}
                          <div className={styles.progressTopRow}>
                            {/* Content filter tabs - left side */}
                            <div className={styles.contentFilterTabs}>
                              {[{ label: 'All', value: 'all' }, { label: 'Videos', value: 'videos' }, { label: 'Photos', value: 'photos' }, ...(deletedItems.length > 0 ? [{ label: 'Restore', value: 'restore', badge: deletedItems.length }] : [])].map((tab) => (
                                <button
                                  key={tab.value}
                                  className={`${styles.contentFilterTab} ${contentFilter === tab.value ? styles.contentFilterTabActive : ''} ${tab.value === 'restore' ? styles.restoreTab : ''}`}
                                  onClick={() => setContentFilter(tab.value)}
                                >
                                  {tab.label}
                                  {tab.badge && <span className={styles.restoreBadge}>{tab.badge}</span>}
                                </button>
                              ))}
                            </div>
                            {/* Add content to Add media icons - right side */}
                            <div className={styles.quickAddRight}>
                              <p style={{ color: '#fff', fontSize: isMobileUploadMode ? '0.65rem' : '1rem', fontWeight: '600', fontFamily: "'Montserrat', sans-serif", margin: '0 0 4px 0' }}>Add More Media</p>
                              <div className={styles.progressUploadOptions}>
                                {/* 1. Choose Files */}
                                <div
                                  role="button"
                                  tabIndex={0}
                                  className={styles.progressUploadOption}
                                  onClick={() => filesInputRef.current?.click()}
                                  onKeyDown={(e) => { if (e.key === 'Enter') filesInputRef.current?.click(); }}
                                >
                                  <svg className={styles.progressUploadSvg} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M14 2V8H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M12 18V12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M9 15L12 12L15 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </div>
                                {/* 2. Scan from Mobile (QR) - desktop only */}
                                {!isMobileUploadMode && (
                                  <div
                                    role="button"
                                    tabIndex={0}
                                    className={styles.progressUploadOption}
                                    onClick={() => setShowQrCode(!showQrCode)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') setShowQrCode(!showQrCode); }}
                                  >
                                    <svg className={styles.progressUploadSvg} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect x="5" y="1" width="14" height="22" rx="3" stroke="white" strokeWidth="1.5" />
                                      <line x1="10" y1="3.5" x2="14" y2="3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                                      <circle cx="12" cy="19" r="1" fill="white" />
                                    </svg>
                                  </div>
                                )}
                                {!isMobileUploadMode && (
                                <>
                                {/* 3. Apple Photos */}
                                <div
                                  role="button"
                                  tabIndex={0}
                                  className={styles.progressUploadOption}
                                  onClick={() => setShowQrCode(true)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') setShowQrCode(true); }}
                                >
                                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17.05 20.28c-.98.95-2.05.79-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="#999" />
                                  </svg>
                                </div>
                                {/* 4. Google Photos */}
                                <div
                                  role="button"
                                  tabIndex={0}
                                  className={styles.progressUploadOption}
                                  onClick={() => handleCloudConnect('googlephotos')}
                                  onKeyDown={(e) => { if (e.key === 'Enter') handleCloudConnect('googlephotos'); }}
                                >
                                  <svg className={styles.progressUploadSvg} width="24" height="24" viewBox="0 0 122.88 122.88" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M30.72,27.97c16.95,0,30.72,13.77,30.72,30.72v2.83H2.83c-1.52,0-2.75-1.23-2.83-2.83 C0,41.73,13.77,27.97,30.72,27.97L30.72,27.97z" fill="#F6B704" />
                                    <path d="M94.91,30.72c0,16.95-13.77,30.72-30.72,30.72l-2.75,0V2.83C61.44,1.3,62.67,0,64.19,0 C81.22,0,94.91,13.77,94.91,30.72L94.91,30.72z" fill="#E54335" />
                                    <path d="M92.16,94.99c-16.95,0-30.72-13.77-30.72-30.72v-2.83h58.61c1.52,0,2.83,1.23,2.83,2.75 C122.88,81.22,109.11,94.99,92.16,94.99L92.16,94.99L92.16,94.99z" fill="#4280EF" />
                                    <path d="M27.97,92.16c0-16.95,13.77-30.72,30.72-30.72h2.75v58.61c0,1.52-1.23,2.75-2.83,2.83 C41.66,122.88,27.97,109.11,27.97,92.16L27.97,92.16L27.97,92.16z" fill="#34A353" />
                                  </svg>
                                </div>
                                {/* 5. Dropbox */}
                                <div
                                  role="button"
                                  tabIndex={0}
                                  className={styles.progressUploadOption}
                                  onClick={() => handleCloudConnect('dropbox')}
                                  onKeyDown={(e) => { if (e.key === 'Enter') handleCloudConnect('dropbox'); }}
                                >
                                  <svg className={styles.progressUploadSvg} width="24" height="24" viewBox="0 0 43 40" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12.5 0L0 8.1l8.6 6.9 12.5-7.8L12.5 0zM0 21.9l12.5 8.1 8.6-7.1-12.5-7.8L0 21.9zM21.1 22.9l8.6 7.1 12.5-8.1-8.6-6.8-12.5 7.8zM42.2 8.1L29.7 0l-8.6 7.2 12.5 7.8 8.6-6.9zM21.2 24.6l-8.6 7.1-4 -2.6v2.9l12.6 7.6 12.6-7.6v-2.9l-4 2.6-8.6-7.1z" fill="#0061ff" />
                                  </svg>
                                </div>
                                {/* 6. Google Drive */}
                                <div
                                  role="button"
                                  tabIndex={0}
                                  className={styles.progressUploadOption}
                                  onClick={() => handleCloudConnect('googledrive')}
                                  onKeyDown={(e) => { if (e.key === 'Enter') handleCloudConnect('googledrive'); }}
                                >
                                  <svg width="32" height="32" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#0066DA" />
                                    <path d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L1.2 52.35c-.8 1.4-1.2 2.95-1.2 4.5h27.5L43.65 25z" fill="#00AC47" />
                                    <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75L86.1 57c.8-1.4 1.2-2.95 1.2-4.5H59.85L73.55 76.8z" fill="#EA4335" />
                                    <path d="M43.65 25L57.4 1.2c-1.35-.8-2.9-1.2-4.5-1.2H34.35c-1.6 0-3.15.45-4.5 1.2L43.65 25z" fill="#00832D" />
                                    <path d="M59.85 53H27.5l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.55c1.6 0 3.15-.45 4.5-1.2L59.85 53z" fill="#2684FC" />
                                    <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.2 28h27.5c0-1.55-.4-3.1-1.2-4.5l-12.75-22z" fill="#FFBA00" />
                                  </svg>
                                </div>
                                </>
                                )}
                              </div>
                            </div>
                          </div>
                          {/* File list */}
                          <div className={`${styles.uploadProgressList} ${contentFilter !== 'all' ? styles.filteredGrid : ''}`}>
                            {contentFilter === 'restore' ? (
                              deletedItems.length > 0 ? (
                                deletedItems.map((item) => (
                                  <div key={`restore-${item.type}-${item.uploadId}-${item.fileName}`} className={styles.filteredCard}>
                                    <div className={styles.filteredCardThumb}>
                                      {item.type === 'album' && item.selectedFileData?.photos && item.selectedFileData.photos.length > 0 ? (
                                        <div className={getAlbumGridClass(item.selectedFileData.photos.length)}>
                                          {item.selectedFileData.photos.slice(0, item.selectedFileData.photos.length > 9 ? 8 : 9).map((photo, idx) => (
                                            <div key={photo.id || idx}>
                                              {(photo.previewUrl || photo.file) ? (
                                                <img src={photo.previewUrl || URL.createObjectURL(photo.file)} alt={photo.name || `Photo ${idx + 1}`} />
                                              ) : null}
                                            </div>
                                          ))}
                                          {item.selectedFileData.photos.length > 9 && (
                                            <div className={styles.albumFolderGridCount}>{`+${item.selectedFileData.photos.length - 8}`}</div>
                                          )}
                                        </div>
                                      ) : item.thumbnail ? (
                                        <img src={item.thumbnail} alt={item.fileName} onError={(e) => { e.target.src = image04; }} />
                                      ) : (
                                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.05)' }}>
                                          <svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" /></svg>
                                        </div>
                                      )}
                                    </div>
                                    <div className={styles.filteredCardInfo}>
                                      <div className={styles.filteredCardTitleRow}>
                                        <span className={styles.filteredCardTitle}>
                                          {item.fileName.length > 18 ? `${item.fileName.substring(0, 18)}...` : item.fileName}
                                        </span>
                                        <span className={styles.filteredCardTypeIcon}>
                                          {item.type === 'video' ? (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <polygon points="23 7 16 12 23 17 23 7" />
                                              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                                            </svg>
                                          ) : item.type === 'photo' ? (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                              <circle cx="8.5" cy="8.5" r="1.5" />
                                              <polyline points="21 15 16 10 5 21" />
                                            </svg>
                                          ) : (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                              <circle cx="8.5" cy="8.5" r="1.5" />
                                              <polyline points="21 15 16 10 5 21" />
                                            </svg>
                                          )}
                                        </span>
                                      </div>
                                      <button
                                        type="button"
                                        className={styles.restoreCardButton}
                                        onClick={() => handleRestore(item)}
                                      >
                                        Restore
                                      </button>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 20px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Montserrat', sans-serif", fontSize: '1.5rem', gridColumn: '1 / -1', minHeight: '300px', width: '100%', textAlign: 'center' }}>
                                  No items to restore
                                </div>
                              )
                            ) : (() => {
                              const filteredUploads = contentFilter === 'all' ? uploads : uploads.filter((u) => {
                                const isAlbum = u.type === 'album' || u.fileName === ALL_PHOTOS_ALBUM_NAME || u.fileName.startsWith('All photos (');
                                if (!isAlbum) {
                                  const ext = `.${u.fileName.split('.').pop().toLowerCase()}`;
                                  const isImage = IMAGE_EXTENSIONS.includes(ext);
                                  if (contentFilter === 'videos') return !isImage && !isAlbum;
                                  if (contentFilter === 'photos') return isImage;
                                }
                                return contentFilter === 'photos';
                              });
                              // Group all "All photos" album entries under a single heading
                              const allPhotosUploadIds = new Set();
                              let firstAllPhotosId = null;
                              let totalAllPhotosCount = 0;
                              filteredUploads.forEach((u) => {
                                if (u.fileName === ALL_PHOTOS_ALBUM_NAME || u.fileName.startsWith('All photos (')) {
                                  allPhotosUploadIds.add(u.id);
                                  if (!firstAllPhotosId) firstAllPhotosId = u.id;
                                  const m = u.fileName.match(/\((\d+)\)/);
                                  totalAllPhotosCount += m ? parseInt(m[1], 10) : 0;
                                }
                              });
                              if (filteredUploads.length === 0) {
                                return (
                                  <div style={{ display: 'flex', justifyContent: 'center', padding: '20px', paddingTop: '120px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Montserrat', sans-serif", fontSize: '1.3rem', gridColumn: '1 / -1', width: '100%', textAlign: 'center' }}>
                                    {contentFilter === 'videos' ? 'No videos' : 'No photos'}
                                  </div>
                                );
                              }
                              return filteredUploads.map((upload) => {
                              const albumMatch = upload.fileName === ALL_PHOTOS_ALBUM_NAME || upload.fileName.startsWith('All photos (')
                                ? selectedFiles.find((f) => f.type === 'album' && f.id === upload.id)
                                  || (upload.linkedAlbumId && selectedFiles.find((f) => f.id === upload.linkedAlbumId))
                                  || selectedFiles.find((f) => f.type === 'album' && f.name === ALL_PHOTOS_ALBUM_NAME)
                                  || selectedFiles.find((f) => f.type === 'album' && f.source === 'mobile-preview')
                                : null;
                              const matchedFile = selectedFiles.find((f) => f.id === upload.id)
                                || (upload.isMobileUpload && upload.mobileUid && selectedFiles.find((f) => f.source === 'mobile-preview' && f.mobileUid === upload.mobileUid))
                                || (upload.isMobileUpload && selectedFiles.find((f) => f.source === 'mobile-preview' && f.name === upload.fileName))
                                || (upload.isShareUpload && selectedFiles.find((f) => f.source === 'share-preview' && f.id === upload.id));
                              const isPhotosFilterAlbum = contentFilter === 'photos' && albumMatch && albumMatch.photos && albumMatch.photos.length > 0;
                              // Group "All photos" entries: hide non-first, combine count for first
                              const isAllPhotosAlbum = upload.fileName === ALL_PHOTOS_ALBUM_NAME || upload.fileName.startsWith('All photos (');
                              const isGroupedNonFirst = contentFilter === 'all' && isAllPhotosAlbum && allPhotosUploadIds.size > 1 && upload.id !== firstAllPhotosId;
                              const isGroupedFirst = contentFilter === 'all' && isAllPhotosAlbum && allPhotosUploadIds.size > 1 && upload.id === firstAllPhotosId;
                              const displayFileName = isGroupedFirst ? ALL_PHOTOS_ALBUM_NAME : upload.fileName;
                              // Non-first "All photos" entries: skip entirely, their photos are shown under the first entry
                              if (isGroupedNonFirst) return null;
                              // For first grouped entry, combine photos from all "All photos" album entries
                              let effectiveAlbumMatch = albumMatch;
                              if (isGroupedFirst && albumMatch) {
                                const combinedPhotos = [];
                                const seenPhotoIds = new Set();
                                filteredUploads.forEach((u) => {
                                  if (!allPhotosUploadIds.has(u.id)) return;
                                  const match = selectedFiles.find((f) => f.type === 'album' && f.id === u.id)
                                    || (u.linkedAlbumId && selectedFiles.find((f) => f.id === u.linkedAlbumId))
                                    || selectedFiles.find((f) => f.type === 'album' && f.name === ALL_PHOTOS_ALBUM_NAME)
                                    || selectedFiles.find((f) => f.type === 'album' && f.source === 'mobile-preview');
                                  if (match?.photos) {
                                    match.photos.forEach((p) => {
                                      if (!seenPhotoIds.has(p.id)) {
                                        seenPhotoIds.add(p.id);
                                        combinedPhotos.push({ ...p, srcAlbumId: match.id, srcUploadId: u.id, srcIsMobile: !!u.isMobileUpload });
                                      }
                                    });
                                  }
                                });
                                if (combinedPhotos.length > 0) {
                                  effectiveAlbumMatch = { ...albumMatch, photos: combinedPhotos };
                                }
                              }
                              // === GRID CARD VIEW when filter is active ===
                              if (contentFilter !== 'all') {
                                if (isPhotosFilterAlbum && albumMatch?.photos) {
                                  return albumMatch.photos.map((photo) => (
                                    <div key={`${upload.id}-${photo.id}`} className={styles.filteredCard}>
                                      <div className={styles.filteredCardThumb}>
                                        {(photo.file || photo.previewUrl) ? (
                                          <img
                                            src={photo.previewUrl || URL.createObjectURL(photo.file)}
                                            alt={photo.name}
                                          />
                                        ) : photo.cloudFile || photo.cloudSource ? (
                                          <div className={styles.albumGridPhotoShimmer}>
                                            <div className={styles.shimmerAnimation} />
                                          </div>
                                        ) : (
                                          <div className={styles.filteredCardPlaceholder}>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                                          </div>
                                        )}
                                        {photo.photo_id && (
                                          <button
                                            type="button"
                                            className={styles.filteredCardDelete}
                                            onClick={() => {
                                              handleSoftDeletePhoto({
                                                source: 'registered',
                                                type: 'photo',
                                                uploadId: upload.id,
                                                registeredId: photo.photo_id,
                                                albumFileId: albumMatch.id,
                                                fileName: photo.name,
                                                thumbnail: photo.previewUrl || photo.imagekit_url || null,
                                                photoData: { ...photo },
                                              });
                                            }}
                                            title="Delete photo"
                                          >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                              <polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M19 6L18.1 20.1C18 21.2 17.1 22 16 22H8C6.9 22 6 21.2 5.9 20.1L5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M10 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M14 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          </button>
                                        )}
                                      </div>
                                      <div className={styles.filteredCardInfo}>
                                        <div className={styles.filteredCardTitleRow}>
                                          <span className={styles.filteredCardTitle}>{photo.name.length > 18 ? `${photo.name.substring(0, 18)}...` : photo.name}</span>
                                          <span className={styles.filteredCardTypeIcon}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                              <circle cx="8.5" cy="8.5" r="1.5" />
                                              <polyline points="21 15 16 10 5 21" />
                                            </svg>
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  ));
                                }
                                // Server-fetched album photos (registered albums)
                                if (contentFilter === 'photos' && upload.type === 'album' && fetchedAlbumPhotos[upload.id]?.photos?.length > 0) {
                                  return fetchedAlbumPhotos[upload.id].photos.map((photo, photoIdx) => {
                                    const photoThumb = photo.imagekit_url || photo.photo_file || null;
                                    const photoName = photo.title || photo.name || `Photo ${photoIdx + 1}`;
                                    return (
                                      <div key={`${upload.id}-fetched-${photo.id || photoIdx}`} className={styles.filteredCard}>
                                        <div className={styles.filteredCardThumb}>
                                          {photoThumb ? (
                                            <img src={photoThumb} alt={photoName} onError={(e) => { e.target.style.display = 'none'; }} />
                                          ) : (
                                            <div className={styles.filteredCardPlaceholder}>
                                              <svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                                            </div>
                                          )}
                                          <button
                                            type="button"
                                            className={styles.filteredCardDelete}
                                            onClick={() => {
                                              handleSoftDeletePhoto({
                                                source: 'registered',
                                                type: 'photo',
                                                uploadId: upload.id,
                                                registeredId: photo.id,
                                                albumFileId: albumMatch ? albumMatch.id : undefined,
                                                fileName: photoName,
                                                thumbnail: photo.imagekit_url || photo.photo_file || null,
                                                photoData: { ...photo },
                                              });
                                            }}
                                            title="Delete photo"
                                          >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                              <polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M19 6L18.1 20.1C18 21.2 17.1 22 16 22H8C6.9 22 6 21.2 5.9 20.1L5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M10 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M14 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          </button>
                                        </div>
                                        <div className={styles.filteredCardInfo}>
                                          <div className={styles.filteredCardTitleRow}>
                                            <span className={styles.filteredCardTitle}>{photoName.length > 18 ? `${photoName.substring(0, 18)}...` : photoName}</span>
                                            <span className={styles.filteredCardTypeIcon}>
                                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                                <circle cx="8.5" cy="8.5" r="1.5" />
                                                <polyline points="21 15 16 10 5 21" />
                                              </svg>
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  });
                                }
                                return (
                                  <div key={upload.id} className={`${styles.filteredCard} ${upload.status === UPLOAD_STATUS.UPLOADING || upload.status === UPLOAD_STATUS.PENDING ? styles.filteredCardUploading : ''}`}>
                                    <div className={styles.filteredCardThumb}>
                                      {matchedFile?.posterUrl || matchedFile?.previewUrl ? (
                                        matchedFile.posterUrl || matchedFile.type !== 'video' ? (
                                          <img src={matchedFile.posterUrl || matchedFile.previewUrl} alt={upload.fileName} />
                                        ) : (
                                          <video src={matchedFile.previewUrl} muted preload="metadata" poster={matchedFile.posterUrl || ''} />
                                        )
                                      ) : (
                                        <div className={styles.filteredCardPlaceholder}>
                                          {contentFilter === 'videos' ? (
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" /></svg>
                                          ) : (
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                                          )}
                                        </div>
                                      )}
                                      {contentFilter === 'videos' ? (
                                        (matchedFile?.previewUrl || matchedFile?.s3Url || (upload.status === UPLOAD_STATUS.COMPLETED && upload.registeredContentId)) && (
                                          <div
                                            className={styles.filteredVideoPlayOverlay}
                                            style={{ cursor: 'pointer' }}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => (upload.registeredContentId ? handleUploadVideoPreview(upload.registeredContentId) : handleLocalVideoPreview(matchedFile))}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { if (upload.registeredContentId) handleUploadVideoPreview(upload.registeredContentId); else handleLocalVideoPreview(matchedFile); } }}
                                          >
                                            <span className={styles.filteredVideoPlayIcon}>
                                              <svg width="22" height="22" viewBox="0 0 24 24" fill="#000" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M8 5v14l11-7z" />
                                              </svg>
                                            </span>
                                          </div>
                                        )
                                      ) : (
                                        <button type="button" className={styles.filteredPreviewEyeButton} title="Preview">
                                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <circle cx="12" cy="12" r="3" stroke="white" strokeWidth="2" />
                                          </svg>
                                        </button>
                                      )}
                                      {(upload.status === UPLOAD_STATUS.UPLOADING || upload.status === UPLOAD_STATUS.PENDING) && (
                                        <>
                                          <div className={styles.filteredCardShimmer} />
                                          <div className={styles.filteredCardProgress}>
                                            {upload.isMobileUpload || upload.progress === 0 ? (
                                              <div className={styles.filteredCardProgressIndeterminate} />
                                            ) : (
                                              <div className={styles.filteredCardProgressBar} style={{ width: `${upload.progress}%` }} />
                                            )}
                                          </div>
                                        </>
                                      )}
                                      {upload.status === UPLOAD_STATUS.COMPLETED && (upload.registeredContentId || upload.registeredAlbumId) && (
                                        <button
                                          type="button"
                                          className={styles.filteredCardDelete}
                                          onClick={() => {
                                            const matchedFile = selectedFiles.find((f) => f.id === upload.id)
                                              || (upload.linkedAlbumId && selectedFiles.find((f) => f.id === upload.linkedAlbumId))
                                              || (upload.mobileUid && selectedFiles.find((f) => f.mobileUid === upload.mobileUid))
                                              || selectedFiles.find((f) => f.name === upload.fileName);
                                            handleSoftDelete({
                                              source: 'registered',
                                              type: upload.registeredContentId ? 'video' : 'album',
                                              uploadId: upload.id,
                                              registeredId: upload.registeredContentId || upload.registeredAlbumId,
                                              fileName: upload.fileName,
                                              thumbnail: matchedFile?.posterUrl || matchedFile?.previewUrl || (matchedFile?.type === 'album' && matchedFile?.photos?.[0]?.previewUrl) || (matchedFile?.type === 'album' && matchedFile?.photos?.[0]?.imagekit_url) || upload.thumbnail || null,
                                              uploadData: { ...upload },
                                              selectedFileData: matchedFile ? { ...matchedFile } : null,
                                            });
                                          }}
                                          title="Delete"
                                        >
                                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M19 6L18.1 20.1C18 21.2 17.1 22 16 22H8C6.9 22 6 21.2 5.9 20.1L5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M10 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M14 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                        </button>
                                      )}
                                    </div>
                                    <div className={styles.filteredCardInfo}>
                                      <div className={styles.filteredCardTitleRow}>
                                        <span className={styles.filteredCardTitle}>{upload.fileName.length > 18 ? `${upload.fileName.substring(0, 18)}...` : upload.fileName}</span>
                                        <span className={styles.filteredCardTypeIcon}>
                                          {contentFilter === 'videos' ? (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <polygon points="23 7 16 12 23 17 23 7" />
                                              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                                            </svg>
                                          ) : (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                              <circle cx="8.5" cy="8.5" r="1.5" />
                                              <polyline points="21 15 16 10 5 21" />
                                            </svg>
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                              return (
                              <div key={upload.id} className={`${styles.uploadProgressItem}${(upload.status === UPLOAD_STATUS.COMPLETED && !upload.registeredContentId && !upload.registeredAlbumId) ? ` ${styles.previewLoading}` : ''}`} style={isGroupedNonFirst ? { display: 'none' } : undefined}>
                                {/* File preview thumbnail - use first album photo for "All photos" items */}
                                {!isPhotosFilterAlbum && !isGroupedNonFirst && (() => {
                                  const albumPhotos = (effectiveAlbumMatch || albumMatch)?.photos;
                                  const firstAlbumPhoto = albumPhotos?.[0];
                                  if (matchedFile?.posterUrl || matchedFile?.previewUrl) {
                                    return matchedFile.posterUrl || matchedFile.source === 'share-preview' || matchedFile.type !== 'video' ? (
                                      <img
                                        src={matchedFile.posterUrl || matchedFile.previewUrl}
                                        alt={upload.fileName}
                                      />
                                    ) : (
                                      <video
                                        src={matchedFile.previewUrl}
                                        poster={matchedFile.posterUrl || ''}
                                        muted
                                        preload="metadata"
                                      />
                                    );
                                  }
                                  if (albumPhotos && albumPhotos.length > 0) {
                                    return (
                                      <div className={getAlbumGridClass(albumPhotos.length)}>
                                        {albumPhotos.slice(0, albumPhotos.length > 9 ? 8 : 9).map((photo, idx) => (
                                          <div key={photo.id || idx}>
                                            {(photo.previewUrl || photo.file) ? (
                                              <img src={photo.previewUrl || URL.createObjectURL(photo.file)} alt={photo.name || `Photo ${idx + 1}`} />
                                            ) : (photo.cloudFile || photo.cloudSource) ? (
                                              <div className={styles.albumGridPhotoShimmer} style={{ width: '100%', height: '100%', borderRadius: '4px' }}>
                                                <div className={styles.shimmerAnimation} />
                                              </div>
                                            ) : null}
                                          </div>
                                        ))}
                                        {albumPhotos.length > 9 && (
                                          <div className={styles.albumFolderGridCount}>{`+${albumPhotos.length - 8}`}</div>
                                        )}
                                      </div>
                                    );
                                  }
                                  // Show shimmer for cloud photos loading (160px matches image height)
                                  if (firstAlbumPhoto?.cloudFile || firstAlbumPhoto?.cloudSource) {
                                    return (
                                      <div className={styles.albumGridPhotoShimmer} style={{ width: '100%', height: '160px' }}>
                                        <div className={styles.shimmerAnimation} />
                                      </div>
                                    );
                                  }
                                  return (
                                    <div className={styles.gridPlaceholderThumb}>
                                      {matchedFile?.type === 'video' || (upload.fileName && VIDEO_EXTENSIONS.includes(`.${upload.fileName.split('.').pop().toLowerCase()}`)) ? (
                                        <svg width="28" height="28" viewBox="0 0 24 24" fill="#555"><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" /></svg>
                                      ) : (
                                        <svg width="28" height="28" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                                      )}
                                    </div>
                                  );
                                })()}
                                {/* Preview overlay on thumbnail */}
                                {!isPhotosFilterAlbum && !isGroupedNonFirst && (() => {
                                  const isVideo = matchedFile?.type === 'video' || (upload.fileName && VIDEO_EXTENSIONS.includes(`.${upload.fileName.split('.').pop().toLowerCase()}`));
                                  const isAlbumItem = upload.type === 'album' || albumMatch || upload.fileName === ALL_PHOTOS_ALBUM_NAME || upload.fileName.startsWith('All photos (');
                                  if (isVideo && !isAlbumItem && (matchedFile?.previewUrl || matchedFile?.s3Url || (upload.status === UPLOAD_STATUS.COMPLETED && upload.registeredContentId))) {
                                    return (
                                      <div
                                        className={styles.filteredVideoPlayOverlay}
                                        style={{ cursor: 'pointer' }}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => (upload.registeredContentId ? handleUploadVideoPreview(upload.registeredContentId) : handleLocalVideoPreview(matchedFile))}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { if (upload.registeredContentId) handleUploadVideoPreview(upload.registeredContentId); else handleLocalVideoPreview(matchedFile); } }}
                                      >
                                        <span className={styles.filteredVideoPlayIcon}>
                                          <svg width="22" height="22" viewBox="0 0 24 24" fill="#000" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M8 5v14l11-7z" />
                                          </svg>
                                        </span>
                                      </div>
                                    );
                                  }
                                  if (isAlbumItem) {
                                    return (
                                      <button
                                        type="button"
                                        className={styles.filteredPreviewEyeButton}
                                        title="Preview"
                                        onClick={() => setStep3AlbumModal({ upload, albumMatch: effectiveAlbumMatch || albumMatch, photos: (effectiveAlbumMatch || albumMatch)?.photos })}
                                      >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                          <circle cx="12" cy="12" r="3" stroke="white" strokeWidth="2" />
                                        </svg>
                                      </button>
                                    );
                                  }
                                  return null;
                                })()}
                                {/* Delete overlay on thumbnail */}
                                {!isPhotosFilterAlbum && !isGroupedNonFirst && upload.status === UPLOAD_STATUS.COMPLETED && (upload.registeredContentId || upload.registeredAlbumId) && (
                                  <button
                                    type="button"
                                    className={styles.filteredCardDelete}
                                    onClick={() => {
                                      const matchedFile = selectedFiles.find((f) => f.id === upload.id)
                                        || (upload.linkedAlbumId && selectedFiles.find((f) => f.id === upload.linkedAlbumId))
                                        || (upload.mobileUid && selectedFiles.find((f) => f.mobileUid === upload.mobileUid))
                                        || selectedFiles.find((f) => f.name === upload.fileName);
                                      handleSoftDelete({
                                        source: 'registered',
                                        type: upload.registeredContentId ? 'video' : 'album',
                                        uploadId: upload.id,
                                        registeredId: upload.registeredContentId || upload.registeredAlbumId,
                                        fileName: upload.fileName,
                                        thumbnail: matchedFile?.posterUrl || matchedFile?.previewUrl || (matchedFile?.type === 'album' && matchedFile?.photos?.[0]?.previewUrl) || (matchedFile?.type === 'album' && matchedFile?.photos?.[0]?.imagekit_url) || upload.thumbnail || null,
                                        uploadData: { ...upload },
                                        selectedFileData: matchedFile ? { ...matchedFile } : null,
                                      });
                                    }}
                                    title="Delete"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M19 6L18.1 20.1C18 21.2 17.1 22 16 22H8C6.9 22 6 21.2 5.9 20.1L5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M10 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M14 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </button>
                                )}
                                <div style={{ padding: '8px 10px' }}>
                                {!isPhotosFilterAlbum && !isGroupedNonFirst && (
                                  <>
                                  <div className={styles.uploadProgressHeader}>
                                    <div className={styles.filteredCardTitleRow} style={{ flex: 1, minWidth: 0 }}>
                                      <span className={styles.uploadFileName}>
                                        {displayFileName.length > 30 ? `${displayFileName.substring(0, 30)}...` : displayFileName}
                                      </span>
                                      {upload.status === UPLOAD_STATUS.COMPLETED && (upload.registeredContentId || upload.registeredAlbumId) && (
                                        <span className={styles.filteredCardTypeIcon}>
                                          {(() => {
                                            const isVideo = matchedFile?.type === 'video' || (upload.fileName && VIDEO_EXTENSIONS.includes(`.${upload.fileName.split('.').pop().toLowerCase()}`));
                                            const isAlbumItem = upload.type === 'album' || albumMatch || upload.fileName === ALL_PHOTOS_ALBUM_NAME || upload.fileName.startsWith('All photos (');
                                            if (isVideo && !isAlbumItem) {
                                              return (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                  <polygon points="23 7 16 12 23 17 23 7" />
                                                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                                                </svg>
                                              );
                                            }
                                            return (
                                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                                <circle cx="8.5" cy="8.5" r="1.5" />
                                                <polyline points="21 15 16 10 5 21" />
                                              </svg>
                                            );
                                          })()}
                                        </span>
                                      )}
                                    </div>
                                    <div className={styles.uploadStatusActions}>
                                    {/* {upload.status === UPLOAD_STATUS.COMPLETED && !upload.registeredContentId && !upload.registeredAlbumId && (
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '6px', color: '#5aa5ef', fontSize: '0.7rem', fontFamily: "'Montserrat', sans-serif" }}>
                                        <span style={{ width: '12px', height: '12px', border: '2px solid rgba(90,165,239,0.25)', borderTop: '2px solid #5aa5ef', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                                        Preview Loading
                                      </span>
                                    )} */}
                                    <span className={styles.uploadStatus}>
                                      {upload.status === UPLOAD_STATUS.FAILED && (
                                        <button
                                          type="button"
                                          onClick={() => handleCancelUpload(upload.id)}
                                          style={{ cursor: 'pointer', background: 'none', border: 'none', color: 'inherit', font: 'inherit', padding: 0 }}
                                          title="Remove"
                                        >
                                          ✕
                                        </button>
                                      )}
                                      {upload.status === UPLOAD_STATUS.UPLOADING && !upload.isMobileUpload && `${upload.progress}%`}
                                      {upload.status === UPLOAD_STATUS.UPLOADING && upload.isMobileUpload && (
                                        <span className={styles.mobileUploadStatus}>
                                          {upload.progress > 0 ? `${upload.progress}% (Mobile)` : 'Uploading from Mobile ...'}
                                          <span className={styles.mobileUploadSpinner} />
                                        </span>
                                      )}
                                      {upload.status === UPLOAD_STATUS.PAUSED && 'Paused'}
                                      {upload.status === UPLOAD_STATUS.NETWORK_ERROR && 'Network Error - Will Resume'}
                                      {upload.status === UPLOAD_STATUS.PENDING && 'Waiting...'}
                                      {upload.status === UPLOAD_STATUS.CANCELLING && 'Cancelling...'}
                                      {upload.status === UPLOAD_STATUS.CANCELLED && 'Cancelled'}
                                    </span>
                                    {/* Pause/Resume/Cancel controls (not shown for mobile uploads) */}
                                    {!upload.isMobileUpload && (upload.status === UPLOAD_STATUS.UPLOADING || upload.status === UPLOAD_STATUS.PAUSED || upload.status === UPLOAD_STATUS.NETWORK_ERROR) && (
                                      <div className={styles.uploadControls}>
                                        {upload.status === UPLOAD_STATUS.UPLOADING && (
                                          <button
                                            type="button"
                                            className={styles.uploadControlButton}
                                            onClick={() => handlePauseUpload(upload.id)}
                                            title="Pause upload"
                                          >
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                                            </svg>
                                          </button>
                                        )}
                                        {(upload.status === UPLOAD_STATUS.PAUSED || upload.status === UPLOAD_STATUS.NETWORK_ERROR) && (
                                          <button
                                            type="button"
                                            className={styles.uploadControlButton}
                                            onClick={() => handleResumeUpload(upload.id)}
                                            title={upload.status === UPLOAD_STATUS.NETWORK_ERROR ? 'Resume when online' : 'Resume upload'}
                                          >
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                              <path d="M8 5v14l11-7z" />
                                            </svg>
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          className={styles.uploadControlButtonCancel}
                                          onClick={() => {
                                            setStep3DeletePending({
                                              type: 'video',
                                              fileName: upload.fileName,
                                              isCancel: true,
                                              onConfirm: () => handleCancelUpload(upload.id),
                                            });
                                          }}
                                          title="Cancel upload"
                                        >
                                          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                                          </svg>
                                        </button>
                                      </div>
                                    )}
                                    {upload.isMobileUpload && upload.status === UPLOAD_STATUS.UPLOADING && (
                                      <button
                                        type="button"
                                        className={styles.uploadControlButtonCancel}
                                        onClick={() => {
                                          setStep3DeletePending({
                                            type: 'video',
                                            fileName: upload.fileName,
                                            isCancel: true,
                                            onConfirm: () => handleCancelUpload(upload.id),
                                          });
                                        }}
                                        title="Cancel upload"
                                      >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                                        </svg>
                                      </button>
                                    )}
                                    </div>
                                  </div>
                                  {(!upload.isMobileUpload || upload.progress > 0 || upload.status === UPLOAD_STATUS.COMPLETED) && (
                                    <>
                                      <div className={styles.uploadProgressBarContainer}>
                                        <div
                                          className={`${styles.uploadProgressBar} ${upload.status === UPLOAD_STATUS.COMPLETED ? styles.uploadProgressComplete : ''} ${upload.status === UPLOAD_STATUS.FAILED ? styles.uploadProgressFailed : ''} ${upload.status === UPLOAD_STATUS.PAUSED ? styles.uploadProgressPaused : ''}`}
                                          style={{ width: `${upload.progress}%` }}
                                        />
                                      </div>
                                      <div className={styles.uploadProgressDetails}>
                                        {!upload.isMobileUpload && !upload.isShareUpload && (
                                          <span>{`${formatFileSize(upload.bytesUploaded)} / ${formatFileSize(upload.fileSize)}`}</span>
                                        )}
                                        {upload.isMobileUpload && !isAllPhotosAlbum && upload.status !== UPLOAD_STATUS.COMPLETED && (
                                          <span>Uploading from mobile</span>
                                        )}
                                        {upload.status === UPLOAD_STATUS.COMPLETED && (
                                          <span className={styles.uploadCompleteText}>
                                            {upload.isShareUpload
                                              ? `Uploaded by ${upload.contributorName || 'Contributor'}${upload.shareFileSize && !Number.isNaN(parseFloat(upload.shareFileSize)) ? ` · ${formatFileSize(parseFloat(upload.shareFileSize) * 1024 * 1024)}` : ''}`
                                              : upload.isMobileUpload && !isAllPhotosAlbum ? 'Uploaded from mobile' : 'Complete'}
                                          </span>
                                        )}
                                        {upload.status === UPLOAD_STATUS.FAILED && (
                                          <span className={styles.uploadFailedText}>Failed</span>
                                        )}
                                      </div>
                                    </>
                                  )}
                                  </>
                                )}
                                {/* Album photos hidden in grid - shown via modal instead */}
                                {upload.type === 'album' && !albumMatch?.photos?.length && (isPhotosFilterAlbum || expandedAlbums[`upload-${upload.id}`] !== false) && fetchedAlbumPhotos[upload.id] && (
                                  <div style={{ display: 'none' }}>
                                    {fetchedAlbumPhotos[upload.id].loading ? (
                                      <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', padding: '8px 0', display: 'block' }}>Loading photos...</span>
                                    ) : (fetchedAlbumPhotos[upload.id].photos || []).map((photo, photoIdx) => {
                                      const photoThumb = photo.imagekit_url || photo.photo_file || null;
                                      const photoName = photo.title || photo.name || `Photo ${photoIdx + 1}`;
                                      return (
                                        <div key={photo.id || photoIdx} className={styles.uploadProgressItem} style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '10px 12px' }}>
                                          {photoThumb ? (
                                            <img src={photoThumb} alt={photoName} style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0, background: '#000' }} onError={(e) => { e.target.style.display = 'none'; }} />
                                          ) : (
                                            <div style={{ width: '48px', height: '48px', borderRadius: '6px', flexShrink: 0, background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                              <svg width="20" height="20" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                                            </div>
                                          )}
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <span className={styles.uploadFileName}>{photoName.length > 30 ? `${photoName.substring(0, 30)}...` : photoName}</span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                                {(effectiveAlbumMatch || albumMatch) && (expandedAlbums[`upload-${upload.id}`] !== false) && (effectiveAlbumMatch || albumMatch).photos && (
                                  <div style={{ display: 'none' }}>
                                    {(effectiveAlbumMatch || albumMatch).photos.map((photo) => (
                                      <div key={photo.id} className={styles.uploadProgressItem} style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '10px 12px' }}>
                                        {(photo.file || photo.previewUrl) ? (
                                          <img
                                            src={photo.previewUrl || URL.createObjectURL(photo.file)}
                                            alt={photo.name}
                                            style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0, background: '#000' }}
                                          />
                                        ) : photo.cloudFile || photo.cloudSource ? (
                                          <div className={styles.albumGridPhotoShimmer} style={{ width: '48px', height: '48px', borderRadius: '6px', flexShrink: 0 }}>
                                            <div className={styles.shimmerAnimation} />
                                          </div>
                                        ) : (
                                          <div style={{ width: '48px', height: '48px', borderRadius: '6px', flexShrink: 0, background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                                          </div>
                                        )}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <div className={styles.uploadProgressHeader}>
                                            <span className={styles.uploadFileName}>
                                              {photo.name.length > 30 ? `${photo.name.substring(0, 30)}...` : photo.name}
                                            </span>
                                          </div>
                                          {!(photo.srcIsMobile ?? upload.isMobileUpload) && photo.size > 0 && (
                                            <div className={styles.uploadProgressDetails}>
                                              <span>{formatFileSize(photo.size)}</span>
                                            </div>
                                          )}
                                        </div>
                                        <button
                                          type="button"
                                          title="Delete photo"
                                          style={{ cursor: 'pointer', background: 'rgba(229,57,53,0.12)', border: '1px solid rgba(229,57,53,0.35)', borderRadius: '6px', color: '#e53935', padding: '3px 7px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                                          onClick={() => {
                                            if (!photo.photo_id) return;
                                            handleSoftDeletePhoto({
                                              source: 'registered',
                                              type: 'photo',
                                              uploadId: photo.srcUploadId || upload.id,
                                              registeredId: photo.photo_id,
                                              albumFileId: photo.srcAlbumId || (effectiveAlbumMatch || albumMatch).id,
                                              fileName: photo.name,
                                              thumbnail: photo.previewUrl || photo.imagekit_url || null,
                                              photoData: { ...photo },
                                            });
                                          }}
                                        >
                                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M19 6L18.1 20.1C18 21.2 17.1 22 16 22H8C6.9 22 6 21.2 5.9 20.1L5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M10 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M14 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                </div>
                              </div>
                              );
                              });
                            })()}
                          </div>
                          <div className={styles.step5BottomSection}>
                          <div className={styles.progressStatusContainer}>
                            <span className={styles.uploadStatItem}>
                              {(() => {
                                const pCount = selectedFiles.reduce((count, f) => count + (f.type === 'album' ? (f.photoCount || 0) : 0), 0);
                                const vCount = selectedFiles.filter((f) => f.type === 'video').length;
                                const parts = [];
                                if (pCount > 0) parts.push(`${pCount} ${pCount === 1 ? 'photo' : 'photos'}`);
                                if (vCount > 0) parts.push(`${vCount} ${vCount === 1 ? 'video' : 'videos'}`);
                                return parts.length > 0 ? parts.join(', ') : '0 photos, 0 videos';
                              })()}
                            </span>
                            {uploads.filter((u) => u.status === UPLOAD_STATUS.PENDING).length > 0 && (
                              <span className={styles.uploadStatItem}>
                                {`${uploads.filter((u) => u.status === UPLOAD_STATUS.PENDING).length} in queue`}
                              </span>
                            )}
                            {uploads.filter((u) => u.status === UPLOAD_STATUS.PAUSED).length > 0 && (
                              <span className={styles.uploadStatItem}>
                                {`${uploads.filter((u) => u.status === UPLOAD_STATUS.PAUSED).length} paused`}
                              </span>
                            )}
                            {/* {uploads.filter((u) => u.status === UPLOAD_STATUS.COMPLETED).length > 0 && (
                              <span className={styles.uploadStatItem}>
                                {`${uploads.filter((u) => u.status === UPLOAD_STATUS.COMPLETED).length} completed`}
                              </span>
                            )} */}
                            {isUploading && uploads.some((u) => u.status === UPLOAD_STATUS.UPLOADING && !u.isMobileUpload) && (
                              <span className={styles.uploadStatSpeed}>
                                {`${formatFileSize(uploads.filter((u) => u.status === UPLOAD_STATUS.UPLOADING && !u.isMobileUpload).reduce((sum, u) => sum + (u.speed || 0), 0))}/s`}
                              </span>
                            )}
                          </div>
                          <div className={styles.uploadCompleteActions}>
                              <div className={styles.uploadCompleteButtonGroup} style={{ justifyContent: isMobileUploadMode ? 'center' : 'flex-start' }}>
                                {!isMobileUploadMode && !isUploading && (() => {
                                  const hasUnregistered = isRegistering || uploads.some((u) => u.status !== UPLOAD_STATUS.COMPLETED && u.status !== UPLOAD_STATUS.FAILED && u.status !== UPLOAD_STATUS.CANCELLED);
                                  return (
                                    <button
                                      className={`${styles.viewProjectorButton}${hasUnregistered ? ` ${styles.viewProjectorButtonShimmer}` : ''}`}
                                      disabled={hasUnregistered}
                                      onClick={() => { if (!hasUnregistered) window.open('/profile?fromGift=true', '_blank'); }}
                                    >
                                      View their Projector
                                    </button>
                                  );
                                })()}
                                {!isUploading && (isRegistering || uploads.some((u) => u.status !== UPLOAD_STATUS.COMPLETED && u.status !== UPLOAD_STATUS.FAILED && u.status !== UPLOAD_STATUS.CANCELLED)) && (
                                  <span style={{ flex: 1, textAlign: 'center', color: '#fff', fontSize: '0.95rem', fontFamily: "'Montserrat', sans-serif", marginLeft: '5px' }}>
                                    Almost ready! Just setting up their projector.
                                  </span>
                                )}
                                {/* Glass Share Button */}
                                <button
                                  onClick={async () => {
                                    // Clear the last push on the server so new contributors don't see stale data
                                    if (giftUserUid) {
                                      updateGiftStatus({ uuid: giftUserUid, push: '', mobileUpload: false }).catch(() => {});
                                    }
                                    const isMobileBrowser = window.innerWidth <= 1024;
                                    if (isMobileBrowser) {
                                      const shareUrl = await getShortenedShareUrl();
                                      if (navigator.share) {
                                        try {
                                          await navigator.share({
                                            title: 'Projector Gift',
                                            text: 'Join me in contributing to Projector Gift',
                                            url: shareUrl,
                                          });
                                          return;
                                        } catch (err) {
                                          if (err.name === 'AbortError') return;
                                        }
                                      }
                                      try {
                                        await navigator.clipboard.writeText(shareUrl);
                                        setSnackbar({ isVisible: true, message: 'Link copied to clipboard!', type: 'success' });
                                      } catch (clipErr) {
                                        const textArea = document.createElement('textarea');
                                        textArea.value = shareUrl;
                                        textArea.style.position = 'fixed';
                                        textArea.style.left = '-9999px';
                                        document.body.appendChild(textArea);
                                        textArea.select();
                                        document.execCommand('copy');
                                        document.body.removeChild(textArea);
                                        setSnackbar({ isVisible: true, message: 'Link copied to clipboard!', type: 'success' });
                                      }
                                    } else {
                                      // Pre-fetch the short link before showing modal
                                      getShortenedShareUrl();
                                      setShowShareModal(true);
                                    }
                                  }}
                                  style={{
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    backdropFilter: 'blur(20px)',
                                    WebkitBackdropFilter: 'blur(20px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    borderRadius: '25px',
                                    padding: '12px 20px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    flexShrink: 0,
                                    marginRight: '0',
                                    marginLeft: 'auto',
                                    color: '#fff',
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 600,
                                    fontSize: '0.95rem'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.3)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                    e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.2)';
                                  }}
                                  title="Share"
                                >
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="18" cy="5" r="3" stroke="white" strokeWidth="2" />
                                    <circle cx="6" cy="12" r="3" stroke="white" strokeWidth="2" />
                                    <circle cx="18" cy="19" r="3" stroke="white" strokeWidth="2" />
                                    <path d="M8.59 13.51L15.42 17.49M15.41 6.51L8.59 10.49" stroke="white" strokeWidth="2" />
                                  </svg>
                                  Invite Others To Add Media
                                </button>
                              </div>
                          </div>
                          </div>
                      </div>
                      {/* Complete button - grey when uploading, blue when done */}
                      {!isMobileUploadMode && (() => {
                        const registering = isRegistering || uploads.some((u) => u.status === UPLOAD_STATUS.S3_COMPLETE);
                        const uploading = isUploading || registering || uploads.some((u) => u.status === UPLOAD_STATUS.UPLOADING || u.status === UPLOAD_STATUS.PENDING || u.status === UPLOAD_STATUS.PAUSED);
                        return (
                          <div
                            role="button"
                            tabIndex={uploading ? -1 : 0}
                            className={`${styles.completeButton} ${uploading ? styles.completeButtonShimmer : ''}`}
                            onClick={() => {
                              if (uploading) return;
                              flushDeletedItems();
                              setShowSuccessSection(true);
                            }}
                            onKeyDown={(e) => {
                              if (uploading) return;
                              if (e.key === 'Enter' || e.key === ' ') {
                                flushDeletedItems();
                                setShowSuccessSection(true);
                              }
                            }}
                          >
                            Continue
                          </div>
                        );
                      })()}
                    </>
                  )}

                  {/* Layout Tab Content */}
                  {activeTab === 'layout' && (
                    <div className={styles.layoutTabContent}>
                      {/* Header */}
                      <div className={styles.layoutHeader}>
                        <h2 className={styles.layoutTitle}>Choose Order/Edit</h2>
                      </div>

                      {/* Content Items List with Drag and Drop */}
                      <DragDropContext
                        onDragEnd={(result) => {
                          if (!result.destination) return;
                          if (result.destination.index === result.source.index) return;

                          // Reorder items locally
                          const items = Array.from(layoutItems);
                          const [reorderedItem] = items.splice(result.source.index, 1);
                          items.splice(result.destination.index, 0, reorderedItem);
                          const newItems = items.map((item, index) => ({ ...item, order: index + 1 }));

                          // Build changedItems object (only items that changed position)
                          const changedItems = {};
                          layoutItems.forEach((item, idx) => {
                            if (item.id !== newItems[idx]?.id) {
                              changedItems[newItems[idx].id] = idx + 1;
                            }
                          });

                          // Update local state
                          setLayoutItems(newItems);

                          // Trigger API call via useSWR
                          setNewLayoutOrder({ items: changedItems });
                        }}
                      >
                        <Droppable droppableId="layoutItems">
                          {(droppableProvided, droppableSnapshot) => (
                            <div
                              className={`${styles.layoutItemsList} ${droppableSnapshot.isDraggingOver ? styles.layoutItemsListDraggingOver : ''}`}
                              {...droppableProvided.droppableProps}
                              ref={droppableProvided.innerRef}
                            >
                              {layoutItems.map((item, index) => (
                                <Draggable key={item.id} draggableId={item.id} index={index}>
                                  {(draggableProvided, snapshot) => (
                                    <div
                                      ref={draggableProvided.innerRef}
                                      {...draggableProvided.draggableProps}
                                      {...draggableProvided.dragHandleProps}
                                      className={`${styles.layoutItemBox} ${snapshot.isDragging ? styles.layoutItemDragging : ''}`}
                                      style={{
                                        ...draggableProvided.draggableProps.style,
                                      }}
                                    >
                                      <div className={styles.layoutItemDragHandle}>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <circle cx="9" cy="6" r="1.5" fill="currentColor" />
                                          <circle cx="15" cy="6" r="1.5" fill="currentColor" />
                                          <circle cx="9" cy="12" r="1.5" fill="currentColor" />
                                          <circle cx="15" cy="12" r="1.5" fill="currentColor" />
                                          <circle cx="9" cy="18" r="1.5" fill="currentColor" />
                                          <circle cx="15" cy="18" r="1.5" fill="currentColor" />
                                        </svg>
                                      </div>
                                      <div className={styles.layoutItemTitle}>{item.title}</div>
                                      <button
                                        className={styles.layoutItemEditBtn}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        Edit
                                      </button>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {droppableProvided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>

                      {/* Info text */}
                      <p className={styles.layoutInfoText}>
                        Drag items to reorder how content appears on your gift page
                      </p>
                    </div>
                  )}

                  {/* Content Tab Content */}
                  {activeTab === 'content' && (
                    <div className={styles.contentTabContent}>
                      {/* Content Type Tabs */}
                      <div className={styles.contentTypeTabs}>
                        <button
                          className={`${styles.contentTypeTab} ${contentTabType === 'videos' ? styles.contentTypeTabActive : ''}`}
                          onClick={() => {
                            setContentTabType('videos');
                            setSelectedContentItems([]);
                          }}
                        >
                          Videos
                        </button>
                        <button
                          className={`${styles.contentTypeTab} ${contentTabType === 'photos' ? styles.contentTypeTabActive : ''}`}
                          onClick={() => {
                            setContentTabType('photos');
                            setSelectedContentItems([]);
                          }}
                        >
                          Photo Albums
                        </button>
                      </div>

                      {/* Action Bar */}
                      <div className={styles.contentActionBar}>
                        <button
                          className={styles.selectAllBtn}
                          onClick={selectAllContentItems}
                        >
                          {selectedContentItems.length === (contentTabType === 'videos' ? userVideos.length : userAlbums.length)
                            ? 'Deselect All'
                            : 'Select All'}
                        </button>
                        {selectedContentItems.length > 0 && (
                          <div className={styles.contentActionButtons}>
                            {selectedContentItems.length === 1 && (
                              <button
                                className={styles.editBtn}
                                onClick={() => {
                                  const items = contentTabType === 'videos' ? userVideos : userAlbums;
                                  const item = items.find((i) => i.id === selectedContentItems[0]);
                                  handleEditContentItem(item);
                                }}
                              >
                                Edit
                              </button>
                            )}
                            {selectedContentItems.length > 1 && (
                              <button
                                className={styles.editBtn}
                                onClick={() => {
                                  initializeBulkEditItems(selectedContentItems);
                                  setShowEditModal(true);
                                }}
                              >
                                {`Edit (${selectedContentItems.length})`}
                              </button>
                            )}
                            <button
                              className={styles.deleteBtn}
                              onClick={handleDeleteContentItems}
                            >
                              {`Delete${selectedContentItems.length > 1 ? ` (${selectedContentItems.length})` : ''}`}
                            </button>
                          </div>
                        )}
                        <span className={styles.contentCount}>
                          {contentTabType === 'videos' ? `${userVideos.length} Videos` : `${userAlbums.length} Albums`}
                        </span>
                      </div>

                      {/* Content Grid */}
                      <div className={styles.contentGrid}>
                        {contentTabType === 'videos' ? (
                          userVideos.map((video) => (
                            <div
                              key={video.id}
                              role="button"
                              tabIndex={0}
                              className={`${styles.contentCard} ${selectedContentItems.includes(video.id) ? styles.contentCardSelected : ''}`}
                              onClick={() => toggleContentItemSelection(video.id)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleContentItemSelection(video.id); }}
                            >
                              <div className={styles.contentCardCheckbox}>
                                <input
                                  type="checkbox"
                                  checked={selectedContentItems.includes(video.id)}
                                  onChange={() => toggleContentItemSelection(video.id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              <div className={styles.contentCardThumbnail}>
                                <img src={video.thumbnail} alt={video.title} />
                                <span className={styles.videoDuration}>{video.duration}</span>
                              </div>
                              <div className={styles.contentCardInfo}>
                                <h4 className={styles.contentCardTitle}>{video.title}</h4>
                                <div className={styles.contentCardMeta}>
                                  <span>{video.category}</span>
                                  <span>{video.date}</span>
                                </div>
                              </div>
                              <button
                                className={styles.contentCardEditBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditContentItem(video);
                                }}
                              >
                                Edit
                              </button>
                            </div>
                          ))
                        ) : (
                          userAlbums.map((album) => (
                            <div
                              key={album.id}
                              role="button"
                              tabIndex={0}
                              className={`${styles.contentCard} ${selectedContentItems.includes(album.id) ? styles.contentCardSelected : ''}`}
                              onClick={() => toggleContentItemSelection(album.id)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleContentItemSelection(album.id); }}
                            >
                              <div className={styles.contentCardCheckbox}>
                                <input
                                  type="checkbox"
                                  checked={selectedContentItems.includes(album.id)}
                                  onChange={() => toggleContentItemSelection(album.id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              <div className={styles.contentCardThumbnail}>
                                <img src={album.thumbnail} alt={album.title} />
                                <span className={styles.photoCount}>{`${album.photoCount} photos`}</span>
                              </div>
                              <div className={styles.contentCardInfo}>
                                <h4 className={styles.contentCardTitle}>{album.title}</h4>
                                <div className={styles.contentCardMeta}>
                                  <span>{album.date}</span>
                                </div>
                              </div>
                              <button
                                className={styles.contentCardEditBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditContentItem(album);
                                }}
                              >
                                Edit
                              </button>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Edit Modal - Same structure as VideoEditModal */}
                      {showEditModal && (
                        <div
                          role="button"
                          tabIndex={0}
                          className={styles.editModalOverlay}
                          onClick={() => setShowEditModal(false)}
                          onKeyDown={(e) => { if (e.key === 'Escape') setShowEditModal(false); }}
                        >
                          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                          <div
                            role="dialog"
                            aria-modal="true"
                            className={`${styles.editModalContent} ${selectedContentItems.length > 1 ? styles.editModalBulk : ''}`}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            {/* Modal Header - Same as VideoEditModal */}
                            <div className={styles.editModalHeader}>
                              <div className={styles.editModalHeaderLeft}>
                                <div className={styles.editModalHeaderIcon}>
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="#5aa5ef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#5aa5ef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </div>
                                <h3 className={styles.editModalTitle}>
                                  {selectedContentItems.length > 1
                                    ? `Edit ${selectedContentItems.length} ${contentTabType === 'videos' ? 'Videos' : 'Albums'}`
                                    : `Edit ${contentTabType === 'videos' ? 'Video' : 'Album'} Details`}
                                </h3>
                              </div>
                              <button
                                className={styles.editModalCloseBtn}
                                onClick={() => {
                                  setShowEditModal(false);
                                  setEditingItem(null);
                                  setShowCreateCategory(false);
                                  setNewCategoryName('');
                                }}
                                type="button"
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                            </div>

                            {/* SINGLE VIDEO EDIT - Two Column Layout like VideoEditModal */}
                            {selectedContentItems.length <= 1 && contentTabType === 'videos' && (
                              <div className={styles.editModalBodyNew}>
                                {/* Left Side: Form Controls - 45% */}
                                <div className={styles.editModalLeftColumn}>
                                  {/* Title */}
                                  <div className={styles.editModalFieldGroup}>
                                    <label className={styles.editModalFieldLabel}>VIDEO TITLE</label>
                                    <input
                                      type="text"
                                      className={styles.editModalFieldInput}
                                      value={editTitle}
                                      onChange={(e) => setEditTitle(e.target.value)}
                                      placeholder="Enter an engaging title..."
                                    />
                                  </div>

                                  {/* Category */}
                                  <div className={styles.editModalFieldGroup}>
                                    <label className={styles.editModalFieldLabel}>CATEGORY</label>
                                    {!showCreateCategory ? (
                                      <select
                                        className={styles.editModalFieldSelect}
                                        value={editCategory}
                                        onChange={(e) => {
                                          if (e.target.value === '__create_new__') {
                                            setShowCreateCategory(true);
                                          } else {
                                            setEditCategory(e.target.value);
                                          }
                                        }}
                                      >
                                        <option value="__create_new__">+ Create New Category</option>
                                        {userCategories.map((cat) => (
                                          <option key={cat.id} value={cat.id}>{cat.title}</option>
                                        ))}
                                      </select>
                                    ) : (
                                      <div className={styles.createCategoryInline}>
                                        <input
                                          type="text"
                                          className={styles.editModalFieldInput}
                                          value={newCategoryName}
                                          onChange={(e) => setNewCategoryName(e.target.value)}
                                          placeholder="Enter category name..."
                                          autoFocus
                                        />
                                        <div className={styles.createCategoryActions}>
                                          <button
                                            type="button"
                                            className={styles.createCategoryCancelBtn}
                                            onClick={() => {
                                              setShowCreateCategory(false);
                                              setNewCategoryName('');
                                            }}
                                            disabled={creatingCategory}
                                          >
                                            Cancel
                                          </button>
                                          <button
                                            type="button"
                                            className={styles.createCategoryCreateBtn}
                                            onClick={handleCreateCategory}
                                            disabled={creatingCategory || !newCategoryName.trim()}
                                          >
                                            {creatingCategory ? 'Creating...' : 'Create'}
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  {/* Visibility */}
                                  <div className={styles.editModalFieldGroup}>
                                    <label className={styles.editModalFieldLabel}>VISIBILITY</label>
                                    <select
                                      className={styles.editModalFieldSelect}
                                      value={editVisibility}
                                      onChange={(e) => setEditVisibility(e.target.value)}
                                    >
                                      <option value="2">All Viewers</option>
                                      <option value="1">Private</option>
                                      <option value="3">Group</option>
                                    </select>
                                  </div>

                                  {/* Description */}
                                  <div className={styles.editModalFieldGroup}>
                                    <label className={styles.editModalFieldLabel}>DESCRIPTION</label>
                                    <textarea
                                      className={styles.editModalFieldTextarea}
                                      placeholder="Add a compelling description for your video..."
                                      rows={4}
                                      value={editDescription}
                                      onChange={(e) => setEditDescription(e.target.value)}
                                    />
                                  </div>

                                  {/* Playlist Selection */}
                                  <div className={styles.editModalFieldGroup}>
                                    <label className={styles.editModalFieldLabel}>PLAYLIST</label>
                                    <select
                                      className={styles.editModalFieldSelect}
                                      defaultValue=""
                                    >
                                      <option value="">Select playlist...</option>
                                      {userPlaylists.map((playlist) => (
                                        <option key={playlist.id} value={playlist.id}>{playlist.title}</option>
                                      ))}
                                    </select>
                                  </div>

                                </div>

                                {/* Right Side: Thumbnail Preview - 55% */}
                                <div className={styles.editModalRightColumn}>
                                  {/* Main Video Preview */}
                                  <div className={styles.editModalVideoPreview}>
                                    <img
                                      src={editingItem?.thumbnail || image04}
                                      alt="Video preview"
                                      className={styles.editModalVideoPreviewImg}
                                    />
                                    {editingItem?.duration && (
                                      <span className={styles.editModalVideoDuration}>{editingItem.duration}</span>
                                    )}
                                  </div>

                                  {/* Thumbnail Selection Grid */}
                                  <div className={styles.editModalThumbnailSection}>
                                    <h4 className={styles.editModalThumbnailSectionTitle}>CHOOSE THUMBNAIL</h4>
                                    <div className={styles.editModalThumbnailGridNew}>
                                      {/* Custom Upload Option */}
                                      <div className={styles.editModalThumbnailOptionNew}>
                                        <div className={styles.editModalThumbnailUploadNew}>
                                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <polyline points="17,8 12,3 7,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                          <span>Custom</span>
                                        </div>
                                      </div>
                                      {/* Auto-generated thumbnails */}
                                      <div className={`${styles.editModalThumbnailOptionNew} ${styles.editModalThumbnailSelectedNew}`}>
                                        <img src={editingItem?.thumbnail || image03} alt="Thumbnail 1" />
                                      </div>
                                      <div className={styles.editModalThumbnailOptionNew}>
                                        <img src={image01} alt="Thumbnail 2" />
                                      </div>
                                      <div className={styles.editModalThumbnailOptionNew}>
                                        <img src={image02} alt="Thumbnail 3" />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* SINGLE ALBUM EDIT - Layout like PhotoEditModal (dark theme) */}
                            {selectedContentItems.length <= 1 && contentTabType === 'photos' && (
                              <div className={styles.albumEditModalBody}>
                                {/* Album Thumbnail Preview */}
                                <div className={styles.albumEditPreview}>
                                  <img
                                    src={editingItem?.thumbnail || editingItem?.icon || image04}
                                    alt="Album preview"
                                    className={styles.albumEditPreviewImg}
                                  />
                                  {editingItem?.photo_count && (
                                    <span className={styles.albumEditPhotoCount}>
                                      {`${editingItem.photo_count} photo${editingItem.photo_count !== 1 ? 's' : ''}`}
                                    </span>
                                  )}
                                </div>

                                {/* Album Title */}
                                <div className={styles.albumEditFieldGroup}>
                                  <label className={styles.albumEditFieldLabel}>Title</label>
                                  <input
                                    type="text"
                                    className={styles.albumEditFieldInput}
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    placeholder="Enter album title..."
                                  />
                                </div>

                                {/* Description and Visibility Row */}
                                <div className={styles.albumEditFieldRow}>
                                  {/* Description */}
                                  <div className={styles.albumEditFieldGroup}>
                                    <label className={styles.albumEditFieldLabel}>Description</label>
                                    <input
                                      type="text"
                                      className={styles.albumEditFieldInput}
                                      value={editDescription}
                                      onChange={(e) => setEditDescription(e.target.value)}
                                      placeholder="Add a description for your album..."
                                    />
                                  </div>

                                  {/* Visibility */}
                                  <div className={styles.albumEditFieldGroup}>
                                    <label className={styles.albumEditFieldLabel}>Visibility</label>
                                    <select
                                      className={styles.albumEditFieldSelect}
                                      value={editVisibility}
                                      onChange={(e) => setEditVisibility(e.target.value)}
                                    >
                                      <option value="2">All Viewers</option>
                                      <option value="1">Private</option>
                                      <option value="3">Group</option>
                                    </select>
                                  </div>
                                </div>

                                {/* Album Info Footer */}
                                <div className={styles.albumEditInfo}>
                                  <span className={styles.albumEditInfoText}>
                                    {editingItem?.photo_count
                                      ? `${editingItem.photo_count} photo${editingItem.photo_count !== 1 ? 's' : ''} in this album`
                                      : 'Edit album details'}
                                  </span>
                                </div>
                              </div>
                            )}

                            {/* MULTIPLE ITEMS EDIT - Table Layout like MultipleVideoEditModal */}
                            {selectedContentItems.length > 1 && (
                              <div className={styles.bulkEditTableContainer}>
                                {/* Table Header */}
                                <div className={styles.bulkEditTableHeader}>
                                  <div className={styles.bulkEditTableHeaderCell} style={{ width: '100px' }}>Thumbnail</div>
                                  <div className={styles.bulkEditTableHeaderCell} style={{ flex: 1 }}>Title</div>
                                  <div className={styles.bulkEditTableHeaderCell} style={{ width: '160px' }}>Category</div>
                                  <div className={styles.bulkEditTableHeaderCell} style={{ width: '140px' }}>Visibility</div>
                                  <div className={styles.bulkEditTableHeaderCell} style={{ width: '90px' }}>Action</div>
                                </div>

                                {/* Table Body */}
                                <div className={styles.bulkEditTableBody}>
                                  {bulkEditItems.map((item) => (
                                    <React.Fragment key={item.id}>
                                    <div className={styles.bulkEditTableRow}>
                                      {/* Thumbnail */}
                                      <div className={styles.bulkEditTableCell} style={{ width: '100px' }}>
                                        <div className={styles.bulkEditThumbnail}>
                                          <img src={item.thumbnail} alt={item.title} />
                                          {item.duration && (
                                            <span className={styles.bulkEditThumbnailDuration}>{item.duration}</span>
                                          )}
                                        </div>
                                      </div>

                                      {/* Title */}
                                      <div className={styles.bulkEditTableCell} style={{ flex: 1 }}>
                                        <input
                                          type="text"
                                          className={styles.bulkEditInput}
                                          value={item.title}
                                          onChange={(e) => updateBulkEditItem(item.id, 'title', e.target.value)}
                                          placeholder="Video title"
                                        />
                                      </div>

                                      {/* Category */}
                                      <div className={styles.bulkEditTableCell} style={{ width: '160px' }}>
                                        <select
                                          className={styles.bulkEditSelect}
                                          value={item.category_id}
                                          onChange={(e) => {
                                            if (e.target.value === '__create_new__') {
                                              setShowCreateCategory(true);
                                            } else {
                                              updateBulkEditItem(item.id, 'category_id', e.target.value);
                                            }
                                          }}
                                        >
                                          <option value="__create_new__">+ Create New</option>
                                          {userCategories.map((cat) => (
                                            <option key={cat.id} value={cat.id}>{cat.title}</option>
                                          ))}
                                        </select>
                                      </div>

                                      {/* Visibility */}
                                      <div className={styles.bulkEditTableCell} style={{ width: '140px' }}>
                                        <select
                                          className={styles.bulkEditSelect}
                                          value={item.visibility}
                                          onChange={(e) => updateBulkEditItem(item.id, 'visibility', e.target.value)}
                                        >
                                          <option value="2">All Viewers</option>
                                          <option value="1">Private</option>
                                          <option value="3">Group</option>
                                        </select>
                                      </div>

                                      {/* Actions - Expand & Remove */}
                                      <div className={styles.bulkEditTableCell} style={{ width: '90px' }}>
                                        <div className={styles.bulkEditActions}>
                                          <button
                                            className={`${styles.bulkEditExpandBtn} ${expandedItems[item.id] ? styles.expanded : ''}`}
                                            onClick={() => toggleExpandedItem(item.id)}
                                            title="More options"
                                            type="button"
                                          >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                              {expandedItems[item.id] ? (
                                                <path d="M18 15l-6-6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              ) : (
                                                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              )}
                                            </svg>
                                          </button>
                                          <button
                                            className={styles.bulkEditRemoveBtn}
                                            onClick={() => removeBulkEditItem(item.id)}
                                            title="Remove from edit"
                                            type="button"
                                          >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                          </button>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Expanded Row Content */}
                                    {expandedItems[item.id] && (
                                      <div className={styles.bulkEditExpandedRow}>
                                        {/* Thumbnail Selection */}
                                        {item.thumbnails && item.thumbnails.length > 0 && (
                                          <div className={styles.bulkEditExpandedSection}>
                                            <div className={styles.bulkEditExpandedLabel}>Select Thumbnail</div>
                                            <div className={styles.bulkEditThumbnailGrid}>
                                              {item.thumbnails.map((thumb, idx) => (
                                                <div
                                                  key={idx}
                                                  className={`${styles.bulkEditThumbnailOption} ${item.selectedThumbnail === idx ? styles.selected : ''}`}
                                                  onClick={() => updateBulkEditItem(item.id, 'selectedThumbnail', idx)}
                                                  onKeyDown={(e) => { if (e.key === 'Enter') updateBulkEditItem(item.id, 'selectedThumbnail', idx); }}
                                                  role="button"
                                                  tabIndex={0}
                                                >
                                                  <img src={thumb} alt={`Thumbnail ${idx + 1}`} />
                                                  {item.selectedThumbnail === idx && (
                                                    <div className={styles.bulkEditThumbnailCheck}>
                                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                                      </svg>
                                                    </div>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {/* Additional Fields Row */}
                                        <div className={styles.bulkEditExpandedFields}>
                                          {/* Description */}
                                          <div className={styles.bulkEditExpandedFieldGroup}>
                                            <label className={styles.bulkEditExpandedLabel}>Description</label>
                                            <input
                                              type="text"
                                              className={styles.bulkEditInput}
                                              value={item.description}
                                              onChange={(e) => updateBulkEditItem(item.id, 'description', e.target.value)}
                                              placeholder="Add description..."
                                            />
                                          </div>

                                          {/* Subcategory */}
                                          <div className={styles.bulkEditExpandedFieldGroup}>
                                            <label className={styles.bulkEditExpandedLabel}>Subcategory</label>
                                            <select
                                              className={styles.bulkEditSelect}
                                              value={item.subcategory_id}
                                              onChange={(e) => updateBulkEditItem(item.id, 'subcategory_id', e.target.value)}
                                            >
                                              <option value="">None</option>
                                              {userCategories.map((cat) => (
                                                <option key={cat.id} value={cat.id}>{cat.title}</option>
                                              ))}
                                            </select>
                                          </div>

                                          {/* Playlist */}
                                          <div className={styles.bulkEditExpandedFieldGroup}>
                                            <label className={styles.bulkEditExpandedLabel}>Playlist</label>
                                            <select
                                              className={styles.bulkEditSelect}
                                              value={item.selectedPlaylists?.[0] || ''}
                                              onChange={(e) => updateBulkEditItem(item.id, 'selectedPlaylists', e.target.value ? [e.target.value] : [])}
                                            >
                                              <option value="">None</option>
                                              {userPlaylists.map((pl) => (
                                                <option key={pl.id} value={pl.id}>{pl.title}</option>
                                              ))}
                                            </select>
                                          </div>
                                        </div>

                                        {/* Group Selection (only show when visibility is Group) */}
                                        {item.visibility === '3' && (
                                          <div className={styles.bulkEditExpandedSection}>
                                            <div className={styles.bulkEditExpandedLabel}>Select Groups</div>
                                            <div className={styles.bulkEditGroupNote}>
                                              Group visibility requires selecting at least one group
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    </React.Fragment>
                                  ))}
                                </div>

                                {/* Empty State */}
                                {bulkEditItems.length === 0 && (
                                  <div className={styles.bulkEditEmpty}>
                                    <p>No items to edit</p>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Modal Footer */}
                            <div className={styles.editModalActions}>
                              <button
                                className={styles.editModalCancelBtn}
                                onClick={() => {
                                  setShowEditModal(false);
                                  setEditingItem(null);
                                  setShowCreateCategory(false);
                                  setNewCategoryName('');
                                  setBulkEditItems([]);
                                  setExpandedItems({});
                                }}
                                disabled={editLoading}
                              >
                                Cancel
                              </button>
                              <button
                                className={styles.editModalSaveBtn}
                                onClick={bulkEditItems.length > 0 ? saveBulkEditContentItems : saveEditContentItem}
                                disabled={editLoading || (bulkEditItems.length > 0 && bulkEditItems.length === 0)}
                              >
                                {editLoading ? 'Saving...' : (bulkEditItems.length > 0 ? `Save ${bulkEditItems.length} Items` : 'Save Changes')}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Delete Confirmation Modal */}
                      {showDeleteConfirm && (
                        <div
                          className={styles.editModalOverlay}
                          onClick={() => setShowDeleteConfirm(false)}
                          onKeyDown={(e) => { if (e.key === 'Escape') setShowDeleteConfirm(false); }}
                          role="presentation"
                        >
                          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
                          <div
                            className={styles.deleteModalContent}
                            onClick={(e) => e.stopPropagation()}
                            role="dialog"
                            aria-modal="true"
                          >
                            <div className={styles.deleteModalHeader}>
                              <h3 className={styles.deleteModalTitle}>
                                {`Delete ${selectedContentItems.length} ${selectedContentItems.length > 1 ? 'Items' : 'Item'}?`}
                              </h3>
                              <button
                                className={styles.editModalCloseBtn}
                                onClick={() => setShowDeleteConfirm(false)}
                                type="button"
                              >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                  <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                              </button>
                            </div>
                            <div className={styles.deleteModalBody}>
                              <p className={styles.deleteModalText}>
                                {`Are you sure you want to delete ${selectedContentItems.length > 1 ? 'these items' : 'this item'}? This action cannot be undone.`}
                              </p>
                              <div className={styles.deleteModalItems}>
                                {selectedContentItems.slice(0, 4).map((item) => (
                                  <div key={item.id} className={styles.deleteModalItem}>
                                    <img src={item.thumbnail || image04} alt={item.title} className={styles.deleteModalItemThumb} />
                                    <span className={styles.deleteModalItemTitle}>{item.title}</span>
                                  </div>
                                ))}
                                {selectedContentItems.length > 4 && (
                                  <div className={styles.deleteModalMoreItems}>
                                    {`+${selectedContentItems.length - 4} more items`}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className={styles.deleteModalActions}>
                              <button
                                className={styles.deleteModalCancelBtn}
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={deleteLoading}
                              >
                                Cancel
                              </button>
                              <button
                                className={styles.deleteModalDeleteBtn}
                                onClick={confirmDeleteContentItems}
                                disabled={deleteLoading}
                              >
                                {deleteLoading ? 'Deleting...' : 'Delete'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Projector View Overlay */}
            {showProjectorView && (
              <div className={styles.projectorViewOverlay}>
                <div className={styles.projectorViewContainer}>
                  {/* Header */}
                  <div className={styles.projectorViewHeader}>
                    <h2 className={styles.projectorViewTitle}>Preview Your Projector</h2>
                  </div>

                  {/* Projector Content - renders sections based on layoutItems order (same pattern as main app profile/index.jsx) */}
                  <div className={styles.projectorViewContent}>
                    {layoutItems
                      .sort((a, b) => a.order - b.order)
                      .map((layoutItem) => {
                        // Content type map similar to main app (content_type_id: 1=Categories, 2=Playlists, 3=Photo Albums, 4=Videos)
                        const contentTypeId = Number(layoutItem.id);

                        // Categories Section (content_type_id: 1) - displays category cards + videos inside each category
                        if (contentTypeId === 1 && userCategories.length > 0) {
                          return (
                            <div key={layoutItem.id} className={styles.projectorSection}>
                              {/* Category Cards Grid */}
                              <h3 className={styles.projectorSectionTitle}>Categories</h3>
                              <div className={styles.projectorCategoryGrid}>
                                {userCategories.map((category) => (
                                  <div
                                    key={category.id}
                                    className={styles.projectorCategoryCard}
                                    style={{
                                      backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${category.icon || defaultBackgroundImage || image04})`,
                                      backgroundRepeat: 'no-repeat',
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center',
                                    }}
                                  >
                                    <span className={styles.projectorCategoryName}>{category.title}</span>
                                  </div>
                                ))}
                              </div>

                              {/* Videos inside each category */}
                              {userCategories.map((category) => {
                                // Filter videos that belong to this category
                                const categoryVideos = userVideos.filter(
                                  (video) => video.category === category.title || video.category === category.id
                                );

                                if (categoryVideos.length === 0) return null;

                                return (
                                  <div key={`${category.id}-videos`} className={styles.projectorCategorySection}>
                                    <h3 className={styles.projectorSectionTitle}>{category.title}</h3>
                                    <div className={styles.projectorCarousel}>
                                      {categoryVideos.map((video) => (
                                        <div key={video.id} className={styles.projectorVideoCard}>
                                          <div className={styles.projectorVideoThumb}>
                                            <img src={video.thumbnail} alt={video.title} />
                                            {video.duration && (
                                              <span className={styles.projectorVideoDuration}>{video.duration}</span>
                                            )}
                                            <div className={styles.projectorVideoPlayOverlay}>
                                              <span className={styles.playIconSmall}>&#9658;</span>
                                            </div>
                                          </div>
                                          <div className={styles.projectorVideoInfo}>
                                            <h4 className={styles.projectorVideoTitle}>{video.title}</h4>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        }

                        // Playlists Section (content_type_id: 2)
                        if (contentTypeId === 2 && userPlaylists.length > 0) {
                          return (
                            <div key={layoutItem.id} className={styles.projectorSection}>
                              <h3 className={styles.projectorSectionTitle}>Playlists</h3>
                              <div className={styles.projectorCarousel}>
                                {userPlaylists.map((playlist) => (
                                  <div key={playlist.id} className={styles.projectorPlaylistCard}>
                                    <div className={styles.projectorPlaylistThumb}>
                                      <img src={playlist.icon || defaultBackgroundImage || image04} alt={playlist.title} />
                                      <div className={styles.projectorPlaylistOverlay}>
                                        <span className={styles.playlistIcon}>&#9835;</span>
                                      </div>
                                    </div>
                                    <div className={styles.projectorPlaylistInfo}>
                                      <h4 className={styles.projectorPlaylistTitle}>{playlist.title}</h4>
                                      <span className={styles.projectorPlaylistCount}>{`${playlist.videoCount} videos`}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }

                        // Photo Albums Section (content_type_id: 3)
                        if (contentTypeId === 3 && userAlbums.length > 0) {
                          return (
                            <div key={layoutItem.id} className={styles.projectorSection}>
                              <h3 className={styles.projectorSectionTitle}>Photo Albums</h3>
                              <div className={styles.projectorCarousel}>
                                {userAlbums.map((album) => (
                                  <div key={album.id} className={styles.projectorAlbumCard}>
                                    <div className={styles.projectorAlbumThumb}>
                                      <img src={album.thumbnail} alt={album.title} />
                                    </div>
                                    <div className={styles.projectorAlbumInfo}>
                                      <h4 className={styles.projectorAlbumTitle}>{album.title}</h4>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }

                        // Videos Section (content_type_id: 4) - shows uncategorized videos (All Videos)
                        if (contentTypeId === 4 && userVideos.length > 0) {
                          // Get category titles to filter out categorized videos
                          const categoryTitles = userCategories.map((cat) => cat.title);
                          // Filter videos that don't belong to any specific category
                          const uncategorizedVideos = userVideos.filter(
                            (video) => !categoryTitles.includes(video.category) || video.category === 'All Videos'
                          );

                          if (uncategorizedVideos.length === 0) return null;

                          return (
                            <div key={layoutItem.id} className={styles.projectorSection}>
                              <h3 className={styles.projectorSectionTitle}>All Videos</h3>
                              <div className={styles.projectorCarousel}>
                                {uncategorizedVideos.map((video) => (
                                  <div key={video.id} className={styles.projectorVideoCard}>
                                    <div className={styles.projectorVideoThumb}>
                                      <img src={video.thumbnail} alt={video.title} />
                                      {video.duration && (
                                        <span className={styles.projectorVideoDuration}>{video.duration}</span>
                                      )}
                                      <div className={styles.projectorVideoPlayOverlay}>
                                        <span className={styles.playIconSmall}>&#9658;</span>
                                      </div>
                                    </div>
                                    <div className={styles.projectorVideoInfo}>
                                      <h4 className={styles.projectorVideoTitle}>{video.title}</h4>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }

                        return null;
                      })}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className={`${styles.contentContainer} ${getAnimationClass()}`}>
              {/* <h1 className={styles.heading}>Step 3</h1> */}
              {/* <h2 className={styles.stepSubHeading}>Send Projector</h2> */}

              {error && (
                <div style={{ color: '#ff6b6b', marginTop: '20px', textAlign: 'center' }}>
                  {error}
                </div>
              )}

              {/* Centered Plan Selection - shown before plan is selected */}
              {!selectedPlan && (
                <div className={styles.planSelectionCentered}>
                  <h3 className={styles.accountInfoHeading} style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, textAlign: 'center' }}>Gift Plan</h3>
                  <p style={{ color: '#aaa', fontSize: '0.95rem', fontFamily: "'Montserrat', sans-serif", margin: '8px 0 30px 0', textAlign: 'center' }}>Choose a plan to continue</p>
                  <div className={styles.planGridContainer}>
                    {loading ? (
                      <p className={styles.planLoadingText}>Loading plan details...</p>
                    ) : (
                      <>
                      <div className={styles.planGridCentered} ref={planCarouselCallbackRef} style={{ '--carousel-offset': `${planCarouselIndex * -100}%` }}>
                        {giftPlans.length > 0 ? (
                          giftPlans.map((plan, planIdx) => (
                            <div
                              key={plan.id}
                              className={`${styles.planCard} ${styles.unselectedPlanCard} ${planIdx > 0 ? styles.planCardNotFirst : ''}`}
                              onClick={() => {
                                const desc = (plan.price_description || '').toLowerCase();
                                setPlanToggle(desc.includes('lifetime') ? 'Lifetime' : '2 Years');
                                handleChoosePlan(plan);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  const desc = (plan.price_description || '').toLowerCase();
                                  setPlanToggle(desc.includes('lifetime') ? 'Lifetime' : '2 Years');
                                  handleChoosePlan(plan);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className={styles.planBox}>
                                {plan.amount_without_discount && parseFloat(plan.amount_without_discount) > 0 && (
                                  <span style={{ color: '#5aa5ef', fontFamily: "'Montserrat', sans-serif", fontWeight: 600, fontSize: '0.85rem', padding: '5px 0', display: 'block', textAlign: 'left' }}>Limited time only</span>
                                )}
                                <div className={styles.planHeader}>
                                  <div className={styles.planPrice}>
                                    <p style={{ display: 'flex', alignItems: 'baseline', gap: '6px', margin: 0 }}>
                                      {plan.amount_without_discount && parseFloat(plan.amount_without_discount) > 0 && (
                                        <span style={{ textDecoration: 'line-through', opacity: 0.5, fontSize: '1.2rem', fontWeight: 400 }}>
                                          $
                                          {parseFloat(plan.amount_without_discount).toFixed(2)}
                                        </span>
                                      )}
                                      <span>
                                        $
                                        {plan.amount ? parseFloat(plan.amount).toFixed(2) : '0.00'}
                                      </span>
                                    </p>
                                    {plan.price_description && (
                                      <p style={{ fontSize: '1.1rem' }}>
                                        {plan.price_description.replace(/^\/+/, '').trim()}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <p className={styles.planAbout}>
                                  {(plan.price_description || '').toLowerCase().includes('lifetime')
                                    ? 'The perfect forever gift, a lifetime of memories with unlimited access to store, share and stream every moment on all their devices.'
                                    : 'A thoughtful gift to stream, share and preserve cherished memories. 2 full years of secure storage and streaming for all their videos & photos.'}
                                </p>
                                <button type="button" className={styles.planGetStartedBtn}>Select Plan</button>
                                <p className={styles.planIncludedHeading}>What&apos;s included:</p>
                                <ul className={styles.planFeatures}>
                                  <li>
                                    <span>
                                      {(plan.price_description || '').toLowerCase().includes('lifetime')
                                        ? 'Lifetime Subscription'
                                        : '2 Years Built-in Subscription'}
                                    </span>
                                    <span className={styles.checkMark}>{getFeatureIcon((plan.price_description || '').toLowerCase().includes('lifetime') ? 'Lifetime Subscription' : '2 Years Built-in Subscription')}</span>
                                  </li>
                                  {(plan.price_description || '').toLowerCase().includes('lifetime') && (
                                    <li>
                                      <span>Firestick included</span>
                                      <span className={styles.checkMark}>{getFeatureIcon('firestick')}</span>
                                    </li>
                                  )}
                                  {plan.description && plan.description.split(',').map((feature, idx) => {
                                    const correctedFeature = feature.trim().replace('Unlimited sharing text', 'Unlimited sharing').replace(/^Unlimited Uploads$/, 'Unlimited Uploads (Videos & Photos)');
                                    return (
                                      <li key={idx}>
                                        <span>{correctedFeature}</span>
                                        <span className={styles.checkMark}>{getFeatureIcon(correctedFeature)}</span>
                                      </li>
                                    );
                                  })}
                                  <li>
                                    <span>Unlimited Streaming</span>
                                    <span className={styles.checkMark}>{getFeatureIcon('Unlimited Streaming')}</span>
                                  </li>
                                  <li>
                                    <span>Unlimited Viewers</span>
                                    <span className={styles.checkMark}>{getFeatureIcon('Unlimited Viewers')}</span>
                                  </li>
                                  <li>
                                    <span>Unlimited number of sign-ins across all TV, web & mobile apps</span>
                                    <span className={styles.checkMark}>{getFeatureIcon('sign-ins TV mobile apps')}</span>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className={styles.planCard}>
                            <div className={styles.planBox}>
                              <div className={styles.planHeader}>
                                <div className={styles.planPrice}>
                                  <p>$9.99</p>
                                  <p>/ year</p>
                                </div>
                              </div>
                              <p className={styles.planAbout}>
                                A thoughtful gift to stream, share and preserve cherished memories. 2 full years of secure storage and streaming for all their videos & photos.
                              </p>
                              <ul className={styles.planFeatures}>
                                <li>
                                  <span>500GB Storage</span>
                                  <span className={styles.checkMark}>{getFeatureIcon('storage')}</span>
                                </li>
                                <li>
                                  <span>Unlimited Viewers</span>
                                  <span className={styles.checkMark}>{getFeatureIcon('Unlimited Viewers')}</span>
                                </li>
                                <li>
                                  <span>All Devices</span>
                                  <span className={styles.checkMark}>{getFeatureIcon('device')}</span>
                                </li>
                                <li>
                                  <span>Unlimited Streaming</span>
                                  <span className={styles.checkMark}>{getFeatureIcon('Unlimited Streaming')}</span>
                                </li>
                                <li>
                                  <span>Unlimited Viewers</span>
                                  <span className={styles.checkMark}>{getFeatureIcon('Unlimited Viewers')}</span>
                                </li>
                                <li>
                                  <span>Unlimited number of sign-ins across all TV, web & mobile apps</span>
                                  <span className={styles.checkMark}>{getFeatureIcon('sign-ins TV mobile apps')}</span>
                                </li>
                                <li>
                                  <span>2 Years Built-in Subscription</span>
                                  <span className={styles.checkMark}>{getFeatureIcon('2 Years Built-in Subscription')}</span>
                                </li>
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                      </>
                    )}
                  </div>
                  {/* Carousel dots */}
                  <div className={styles.planCarouselDotsWrapper} style={{ marginTop: '22px' }}>
                    {giftPlans.map((_, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={`${styles.carouselDot} ${planCarouselIndex === idx ? styles.carouselDotActive : ''}`}
                        onClick={() => setPlanCarouselIndex(idx)}
                        aria-label={`Go to plan ${idx + 1}`}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Two-column layout for payment - shown after plan is selected */}
              {selectedPlan && (
              <div className={styles.paymentTwoColumnLayout}>
                {/* Left Section - Heading with toggle, Promo + Account Info */}
                <div className={styles.paymentLeftSection}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 8px 0' }}>
                    <h3 className={styles.accountInfoHeading} style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>Projector Gift</h3>
                    <button
                      type="button"
                      onClick={() => {
                        const next = planToggle === '2 Years' ? 'Lifetime' : '2 Years';
                        setPlanToggle(next);
                        const matchPlan = giftPlans.find((p) => {
                          const desc = (p.price_description || '').toLowerCase();
                          return next === 'Lifetime' ? desc.includes('lifetime') : (desc.includes('2 year') || desc.includes('year'));
                        });
                        if (matchPlan) {
                          setSelectedPlan(null);
                          setPaymentData(null);
                          handleChoosePlan(matchPlan);
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '105px',
                        height: '32px',
                        borderRadius: '16px',
                        border: 'none',
                        cursor: 'pointer',
                        backgroundColor: '#5aa5ef',
                        padding: '3px',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          left: planToggle === '2 Years' ? '0' : '32px',
                          width: planToggle === '2 Years' ? '76px' : '73px',
                          textAlign: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          fontFamily: "'Montserrat', sans-serif",
                          color: '#fff',
                          whiteSpace: 'nowrap',
                          userSelect: 'none',
                          transition: 'left 0.3s ease, width 0.3s ease',
                        }}
                      >
                        {planToggle}
                      </span>
                      <span
                        style={{
                          width: '26px',
                          height: '26px',
                          borderRadius: '50%',
                          backgroundColor: '#fff',
                          position: 'absolute',
                          top: '3px',
                          left: planToggle === '2 Years' ? '76px' : '3px',
                          transition: 'left 0.3s ease',
                        }}
                      />
                    </button>
                  </div>
                  <p style={{ color: '#aaa', fontSize: '0.95rem', fontFamily: "'Montserrat', sans-serif", margin: '0 0 20px 0', textAlign: 'left' }}>Submit your payment information</p>

                  {/* Promo Code + Total */}
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '10px 0 0 0' }}>
                      <span style={{ color: '#fff', fontSize: '1.5rem', fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}>
                        {selectedPlan.amount_without_discount && parseFloat(selectedPlan.amount_without_discount) > 0 && (
                          <span style={{ textDecoration: 'line-through', opacity: 0.5, fontSize: '1.1rem', fontWeight: 400, marginRight: '8px' }}>
                            {`$${parseFloat(selectedPlan.amount_without_discount).toFixed(2)}`}
                          </span>
                        )}
                        {`$${selectedPlan.amount ? parseFloat(selectedPlan.amount).toFixed(2) : '0.00'} For ${selectedPlan.price_description ? selectedPlan.price_description.replace(/^\/+/, '').trim() : ''}`}
                      </span>
                    </div>

                    {/* Projector plan info section */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0', padding: '14px', background: 'rgba(255,255,255,0.06)', borderRadius: '12px' }}>
                      <img src={logoRound} alt="Projector" style={{ width: '50px', height: '50px', borderRadius: '50%' }} />
                      <div style={{ flex: 1, fontFamily: "'Montserrat', sans-serif" }}>
                        <p style={{ color: '#fff', fontSize: '1rem', fontWeight: 700, margin: 0 }}>Projector</p>
                        <p style={{ color: '#aaa', fontSize: '0.85rem', fontWeight: 500, margin: '2px 0 0 0' }}>
                          {selectedPlan.price_description
                            ? (selectedPlan.price_description.replace(/^\/+/, '').trim().toLowerCase().includes('lifetime')
                              ? 'Billed for a Lifetime'
                              : `Billed for ${selectedPlan.price_description.replace(/^\/+/, '').trim()}`)
                            : ''}
                        </p>
                        <p style={{ color: '#fff', fontSize: '1rem', fontWeight: 700, margin: '4px 0 0 0' }}>Subtotal</p>
                      </div>
                      <div style={{ textAlign: 'right', fontFamily: "'Montserrat', sans-serif" }}>
                        <p style={{ color: '#fff', fontSize: '1rem', fontWeight: 700, margin: 0 }}>
                          {selectedPlan.amount_without_discount && parseFloat(selectedPlan.amount_without_discount) > 0 && (
                            <span style={{ textDecoration: 'line-through', opacity: 0.5, fontSize: '0.85rem', fontWeight: 400, marginRight: '6px' }}>
                              {`$${parseFloat(selectedPlan.amount_without_discount).toFixed(2)}`}
                            </span>
                          )}
                          {`$${selectedPlan.amount ? parseFloat(selectedPlan.amount).toFixed(2) : '0.00'}`}
                        </p>
                        <p style={{ color: '#fff', fontSize: '1rem', fontWeight: 700, margin: '26px 0 0 0' }}>
                          {`$${selectedPlan.amount ? parseFloat(selectedPlan.amount).toFixed(2) : '0.00'}`}
                        </p>
                      </div>
                    </div>

                    {/* Promo Code - only visible on localhost */}
                    {/* (window.location.hostname === 'localhost' || window.location.origin.includes('preprod')) && (
                      <div style={{ marginTop: '10px', textAlign: 'left' }}>
                        {!promoInputVisible ? (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              if (!selectedPlan) return;
                              setPromoInputVisible(true);
                              setTimeout(() => promoInputRef.current?.focus(), 50);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                if (!selectedPlan) return;
                                setPromoInputVisible(true);
                                setTimeout(() => promoInputRef.current?.focus(), 50);
                              }
                            }}
                            style={{ color: '#5aa5ef', fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}
                          >
                            Have a promo code?
                          </span>
                        ) : (
                          <input
                            ref={promoInputRef}
                            type="text"
                            placeholder="Enter Promo Code"
                            style={{ width: '100%', fontWeight: 700, background: 'transparent', border: 'none', borderBottom: '1.5px solid rgba(255,255,255,0.4)', outline: 'none', color: '#fff', fontFamily: "'Montserrat', sans-serif", fontSize: '0.9rem', padding: '6px 0', letterSpacing: '0.5px' }}
                            value={promoCodeNew}
                            onChange={handlePromoCodeChange}
                            onBlur={() => setPromoFieldTouched(true)}
                            maxLength={14}
                          />
                        )}
                        {promoCodeNew?.length > 0 && promoCodeNew?.length < 6 && (
                          <p style={{ color: '#ff4d4d', fontSize: '0.8rem', margin: '6px 0 0 0' }}>
                            *Promo code requires at least 6 characters!
                          </p>
                        )}
                        {promoActive && promoLoading && (
                          <p style={{ color: '#ccc', fontSize: '0.8rem', margin: '6px 0 0 0' }}>
                            Loading...
                          </p>
                        )}
                        {promoActive && !promoLoading && !promoErrMsg && promoResponse?.data?.name && (
                          <p style={{ color: '#22bb33', fontSize: '0.85rem', fontWeight: 600, margin: '6px 0 0 0' }}>
                            {`${promoResponse.data.name} Applied!`}
                          </p>
                        )}
                        {promoActive && !promoLoading && promoErrMsg && (
                          <p style={{ color: '#ff4d4d', fontSize: '0.8rem', margin: '6px 0 0 0' }}>
                            {promoErrMsg}
                          </p>
                        )}
                      </div>
                    ) */}
                    {promoInputVisible && (
                      <div style={{ marginTop: '10px', textAlign: 'left' }}>
                        <input
                          ref={promoInputRef}
                          type="text"
                          placeholder="Enter Promo Code"
                          style={{ width: '100%', fontWeight: 700, background: 'transparent', border: 'none', borderBottom: '1.5px solid rgba(255,255,255,0.4)', outline: 'none', color: '#fff', fontFamily: "'Montserrat', sans-serif", fontSize: '0.9rem', padding: '6px 0', letterSpacing: '0.5px' }}
                          value={promoCodeNew}
                          onChange={handlePromoCodeChange}
                          onBlur={() => setPromoFieldTouched(true)}
                          maxLength={14}
                        />
                        {promoCodeNew?.length > 0 && promoCodeNew?.length < 6 && (
                          <p style={{ color: '#ff4d4d', fontSize: '0.8rem', margin: '6px 0 0 0' }}>
                            *Promo code requires at least 6 characters!
                          </p>
                        )}
                        {promoActive && promoLoading && (
                          <p style={{ color: '#ccc', fontSize: '0.8rem', margin: '6px 0 0 0' }}>
                            Loading...
                          </p>
                        )}
                        {promoActive && !promoLoading && !promoErrMsg && promoResponse?.data?.name && (
                          <p style={{ color: '#22bb33', fontSize: '0.85rem', fontWeight: 600, margin: '6px 0 0 0' }}>
                            {`${promoResponse.data.name} Applied!`}
                          </p>
                        )}
                        {promoActive && !promoLoading && promoErrMsg && (
                          <p style={{ color: '#ff4d4d', fontSize: '0.8rem', margin: '6px 0 0 0' }}>
                            {promoErrMsg}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Total - full breakdown when promo applied */}
                    <div className={styles.promoDiscountSection}>
                      {discountedPrice !== '' ? (
                        <>
                          <div className={styles.promoDiscountRow}>
                            <span className={styles.promoDiscountLabel}>Regular Rate</span>
                            <span className={styles.promoDiscountOriginal}>
                              {`$${selectedPlan?.amount ? parseFloat(selectedPlan.amount).toFixed(2) : '0.00'}`}
                            </span>
                          </div>
                          <div className={styles.promoDiscountRow}>
                            <span className={styles.promoDiscountName}>{promoResponse?.data?.name}</span>
                            <span className={styles.promoDiscountAmount}>{`-$${calculatedDiscount}`}</span>
                          </div>
                          <div className={`${styles.promoDiscountRow} ${styles.promoDiscountTotal}`}>
                            <span className={styles.promoDiscountLabel} onDoubleClick={() => { if (!selectedPlan) return; setPromoInputVisible(true); setTimeout(() => promoInputRef.current?.focus(), 50); }}>Total Due Today</span>
                            <span className={styles.promoDiscountLabel}>{`$${discountedPrice}`}</span>
                          </div>
                        </>
                      ) : (
                        <div className={`${styles.promoDiscountRow} ${styles.promoDiscountTotal}`}>
                          <span className={styles.promoDiscountLabel} onDoubleClick={() => { if (!selectedPlan) return; setPromoInputVisible(true); setTimeout(() => promoInputRef.current?.focus(), 50); }}>Total Due Today</span>
                          <span className={styles.promoDiscountLabel}>
                            {`$${selectedPlan?.amount ? parseFloat(selectedPlan.amount).toFixed(2) : '0.00'}`}
                          </span>
                        </div>
                      )}
                    </div>
                  </>

                  {/* Cardholder Information */}
                  <h3 className={styles.accountInfoHeading} style={{ fontSize: '1.5rem', fontWeight: 700 }}>Cardholder Information</h3>
                  <div className={styles.formContainer} style={{ marginTop: '10px', maxWidth: '100%' }}>
                    <input
                      ref={cardholderNameRef}
                      type="text"
                      className={styles.inputFieldSmall}
                      style={paymentValidationErrors.cardholderName ? { border: '1px solid #ff4444' } : {}}
                      placeholder="Cardholders Name"
                      value={formData.cardholderName}
                      onChange={(e) => {
                        handleInputChange('cardholderName', e.target.value);
                        if (paymentValidationErrors.cardholderName) setPaymentValidationErrors((prev) => ({ ...prev, cardholderName: false }));
                      }}
                    />
                    <input
                      type="email"
                      className={styles.inputFieldSmall}
                      placeholder="Cardholders Email"
                      value={formData.cardholderEmail}
                      onChange={(e) => handleInputChange('cardholderEmail', e.target.value)}
                      onBlur={() => setEmailFieldTouched(true)}
                    />
                    {emailFieldTouched && emailValidationReady && formData.cardholderEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.cardholderEmail) && (
                      <p style={{ color: '#ff6b6b', fontSize: '0.8rem', margin: '0 0 12px 0', textAlign: 'left', width: '100%', alignSelf: 'flex-start' }}>
                        Please enter a valid email address
                      </p>
                    )}
                  </div>

                  {/* Delivery Date */}
                  <div style={{ marginTop: '10px', textAlign: 'left' }}>
                    <label className={styles.inputLabel}>Delivery Date</label>
                    <div style={{ position: 'relative', marginTop: '8px' }}>
                      <input
                        type="text"
                        className={styles.inputFieldSmall}
                        style={{ cursor: 'pointer', paddingRight: '44px', ...(paymentValidationErrors.deliveryDate ? { border: '1px solid #ff4444' } : {}) }}
                        placeholder="mm/dd/yyyy"
                        readOnly
                        value={deliveryDate ? `${(new Date(`${deliveryDate}T00:00:00`).getMonth() + 1).toString().padStart(2, '0')}/${new Date(`${deliveryDate}T00:00:00`).getDate().toString().padStart(2, '0')}/${new Date(`${deliveryDate}T00:00:00`).getFullYear()}` : ''}
                      />
                      <svg
                        style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', width: '20px', height: '20px' }}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#8e8e8e"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <input
                        id="giftDeliveryDate"
                        type="date"
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                        value={deliveryDate}
                        min={new Date().toISOString().split('T')[0]}
                        onClick={(e) => { try { e.target.showPicker(); } catch (err) { /* iOS handles natively */ } }}
                        onChange={(e) => {
                          setDeliveryDate(e.target.value);
                          if (paymentValidationErrors.deliveryDate) setPaymentValidationErrors((prev) => ({ ...prev, deliveryDate: false }));
                        }}
                      />
                    </div>
                  </div>

                  {/* Gift Note - visible when occasion selected */}
                  {formData.occasions && formData.occasions.length > 0 && (
                    <div style={{ marginTop: '10px', textAlign: 'left' }}>
                      <label className={styles.inputLabel} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        Add A Special Note
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M16.474 5.408l2.118 2.117m-.756-3.982L12.109 9.27a2.118 2.118 0 00-.58 1.082L11 13l2.648-.53c.41-.082.786-.283 1.082-.579l5.727-5.727a1.853 1.853 0 10-2.621-2.621z" stroke="#5aa5ef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M19 15v3a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h3" stroke="#5aa5ef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </label>
                      <textarea
                        ref={paymentNoteRef}
                        className={`${styles.inputFieldSmall} ${styles.textAreaSmall}`}
                        style={{ marginTop: '8px', height: '115px' }}
                        placeholder="Write a personal note for your gift..."
                        value={formData.note}
                        onChange={(e) => handleInputChange('note', e.target.value)}
                      />
                    </div>
                  )}

                </div>

                {/* Right Section - Stripe Payment */}
                <div className={`${styles.paymentRightSection} ${styles.paymentRightSelected}`}>
                  {paymentData ? (
                    <Elements
                      key={paymentData.clientSecret}
                      stripe={stripePromise}
                      options={{
                        clientSecret: paymentData.clientSecret,
                        appearance: {
                          theme: 'night',
                          variables: {
                            colorPrimary: '#5aa5ef',
                            colorBackground: '#4E5360',
                            colorText: '#ffffff',
                            colorDanger: '#ff6b6b',
                            fontFamily: 'Montserrat, sans-serif',
                            borderRadius: '8px',
                          },
                          rules: {
                            '.Input': {
                              backgroundColor: '#3a3f4a',
                              border: '1px solid #5a5f6d',
                              color: '#ffffff',
                            },
                            '.Input:focus': {
                              border: '1px solid #5aa5ef',
                              boxShadow: '0 0 0 1px #5aa5ef',
                            },
                            '.Label': {
                              color: '#ccc',
                            },
                          },
                        },
                      }}
                    >
                      <InlinePaymentForm
                        clientSecret={paymentData.clientSecret}
                        giftUserUid={giftUserUid}
                        planId={paymentData.planId}
                        onSuccess={handlePaymentSuccess}
                        onError={handlePaymentError}
                        fromName={formData.cardholderName}
                        fromEmail={formData.cardholderEmail}
                        deliveryDate={deliveryDate}
                        promoCode={promoCodeNew}
                        onValidationError={(errors) => {
                          setPaymentValidationErrors(errors);
                          const missing = [];
                          if (errors.cardholderName) missing.push('Cardholder Name');
                          if (errors.deliveryDate) missing.push('Delivery Date');
                          if (missing.length) {
                            setSnackbar({ isVisible: true, message: `Please enter ${missing.join(' and ')}`, type: 'validation' });
                          }
                        }}
                      />
                    </Elements>
                  ) : (
                    <p style={{ color: '#ccc', textAlign: 'center', marginTop: '40px' }}>
                      Loading payment...
                    </p>
                  )}
                </div>
              </div>
              )}

              {/* Old blue box layout - commented out
              <div className={styles.formContainer}>
                <input
                  type="text"
                  className={styles.inputField}
                  placeholder="Cardholders Name"
                  value={formData.cardholderName}
                  onChange={(e) => handleInputChange('cardholderName', e.target.value)}
                />
                <input
                  type="email"
                  className={styles.inputField}
                  placeholder="Cardholders Email"
                  value={formData.cardholderEmail}
                  onChange={(e) => handleInputChange('cardholderEmail', e.target.value)}
                  onBlur={() => setEmailFieldTouched(true)}
                />
                {emailFieldTouched && emailValidationReady && formData.cardholderEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.cardholderEmail) && (
                  <p style={{ color: '#ff6b6b', fontSize: '0.8rem', margin: '0 0 12px 0', textAlign: 'left', width: '100%', alignSelf: 'flex-start' }}>
                    Please enter a valid email address
                  </p>
                )}
              </div>

              <div className={styles.planPaymentContainer}>
                <div className={`${styles.planSection} ${showPayment ? styles.planSlideLeft : ''}`}>
                  {loading ? (
                    <div className={styles.shimmerPlanBox}>
                      <div className={styles.shimmerHeader}>
                        <div className={styles.shimmerTitle} />
                        <div className={styles.shimmerPrice} />
                      </div>
                      <div className={styles.shimmerFeature} />
                      <div className={styles.shimmerFeature} />
                      <div className={styles.shimmerFeature} />
                      <div className={styles.shimmerButton} />
                    </div>
                  ) : (
                    <>
                      {giftPlans.length > 0 ? (
                        giftPlans.map((plan) => (
                          <div key={plan.id} className={styles.planBox}>
                            {plan.amount_without_discount && parseFloat(plan.amount_without_discount) > 0 && (
                              <span style={{ color: '#5aa5ef', fontFamily: "'Montserrat', sans-serif", fontWeight: 600, fontSize: '0.85rem', padding: '5px 0', display: 'block', textAlign: 'left' }}>Limited time only</span>
                            )}
                            <div className={styles.planHeader}>
                              <div className={styles.planName}>
                                <p>Projector Gift</p>
                                {plan.has_trial === '1' && plan.total_trial_days && plan.total_trial_days !== '0' && (
                                  <span>
                                    {plan.total_trial_days}
                                    {' days free trial'}
                                  </span>
                                )}
                              </div>
                              <div className={styles.planPrice}>
                                <p style={{ display: 'flex', alignItems: 'baseline', gap: '6px', margin: 0 }}>
                                  {plan.amount_without_discount && parseFloat(plan.amount_without_discount) > 0 && (
                                    <span style={{ textDecoration: 'line-through', opacity: 0.5, fontSize: '1.2rem', fontWeight: 400 }}>
                                      $
                                      {parseFloat(plan.amount_without_discount).toFixed(2)}
                                    </span>
                                  )}
                                  <span>
                                    $
                                    {plan.amount ? parseFloat(plan.amount).toFixed(2) : '0.00'}
                                  </span>
                                </p>
                                {plan.price_description && (
                                  <p>
                                    /
                                    {plan.price_description}
                                  </p>
                                )}
                              </div>
                            </div>
                            <ul className={styles.planFeatures}>
                              {plan.description && plan.description.split(',').map((feature, idx) => {
                                const correctedFeature = feature.trim().replace('Unlimited sharing text', 'Unlimited sharing');
                                return (
                                  <li key={idx}>
                                    <span>{correctedFeature}</span>
                                    <span className={styles.checkMark}>✓</span>
                                  </li>
                                );
                              })}
                            </ul>

                            {showPayment && (
                              <div className={styles.planUserInfo}>
                                <div className={styles.planInfoGroup}>
                                  <label className={styles.planInfoLabel}>Name:</label>
                                  <input
                                    type="text"
                                    className={styles.planInfoField}
                                    value={formData.firstname}
                                    readOnly
                                  />
                                </div>
                                <div className={styles.planInfoGroup}>
                                  <label className={styles.planInfoLabel}>Email:</label>
                                  <input
                                    type="email"
                                    className={styles.planInfoField}
                                    value={formData.email}
                                    readOnly
                                  />
                                </div>
                              </div>
                            )}

                            {!showPayment && formData.cardholderName && formData.cardholderEmail && (
                              <div>
                                {(window.location.hostname === 'localhost' || window.location.origin.includes('preprod')) && <div className={styles.promoCodeSection}>
                                  <input
                                    type="text"
                                    className={styles.promoCodeInput}
                                    placeholder="Promo Code"
                                  />
                                  <button type="button" className={styles.promoApplyButton}>
                                    Apply
                                  </button>
                                </div>}
                                <div className={styles.promoDiscountSection}>
                                  <div className={styles.promoDiscountRow}>
                                    <span className={styles.promoDiscountLabel}>Regular Rate</span>
                                    <span className={styles.promoDiscountOriginal}>
                                      {'$' + (plan.amount ? parseFloat(plan.amount).toFixed(2) : '0.00')}
                                    </span>
                                  </div>
                                  <div className={styles.promoDiscountRow}>
                                    <span className={styles.promoDiscountName}>Promo Discount</span>
                                    <span className={styles.promoDiscountAmount}>-$0.00</span>
                                  </div>
                                  <div className={styles.promoDiscountRow + ' ' + styles.promoDiscountTotal}>
                                    <span className={styles.promoDiscountLabel}>Total Due Today</span>
                                    <span className={styles.promoDiscountLabel}>
                                      {'$' + (plan.amount ? parseFloat(plan.amount).toFixed(2) : '0.00')}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  className={styles.buyNowButton}
                                  onClick={() => handlePlanPurchase(plan.id)}
                                  disabled={loading}
                                >
                                  {loading ? 'Processing...' : 'Buy Now'}
                                </button>
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className={styles.planBox}>
                          <div className={styles.planHeader}>
                            <div className={styles.planName}>
                              <p>Projector Gift</p>
                            </div>
                            <div className={styles.planPrice}>
                              <p>$9.99</p>
                            </div>
                          </div>
                          <ul className={styles.planFeatures}>
                            <li>
                              <span>500GB Storage</span>
                              <span className={styles.checkMark}>✓</span>
                            </li>
                            <li>
                              <span>Unlimited Viewers</span>
                              <span className={styles.checkMark}>✓</span>
                            </li>
                            <li>
                              <span>All Devices</span>
                              <span className={styles.checkMark}>✓</span>
                            </li>
                          </ul>
                          <button className={styles.buyNowButton} disabled>
                            No plans available
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className={`${styles.paymentSection} ${showPayment ? styles.paymentSlideIn : ''}`}>
                  {showPayment && paymentData && (
                    <>
                      <Elements
                        key={paymentData.clientSecret}
                        stripe={stripePromise}
                        options={{ clientSecret: paymentData.clientSecret }}
                      >
                        <InlinePaymentForm
                          clientSecret={paymentData.clientSecret}
                          giftUserUid={giftUserUid}
                          planId={paymentData.planId}
                          onSuccess={handlePaymentSuccess}
                          onError={handlePaymentError}
                          fromName={formData.cardholderName}
                          fromEmail={formData.cardholderEmail}
                          deliveryDate={deliveryDate}
                          promoCode={promoCodeNew}
                          onValidationError={(errors) => {
                          setPaymentValidationErrors(errors);
                          const missing = [];
                          if (errors.cardholderName) missing.push('Cardholder Name');
                          if (errors.deliveryDate) missing.push('Delivery Date');
                          if (missing.length) {
                            setSnackbar({ isVisible: true, message: `Please enter ${missing.join(' and ')}`, type: 'validation' });
                          }
                        }}
                        />
                      </Elements>
                    </>
                  )}
                </div>
              </div>
              */}
            </div>
          </>
        )}
      </div>

      {/* Success Modal */}
      {showSuccessModal && giftExpressData && (
        <SuccessPopUp
          expressData={giftExpressData}
          paymentStatus="success"
          isGift
          onHide={() => {
            setShowSuccessModal(false);
            setShowSuccessSection(true);
          }}
          paymentNavigation={() => {
            setShowSuccessModal(false);
            setShowSuccessSection(true);
          }}
          setOpenPaymentLoader={() => { }}
        />
      )}

      {/* Toast/Snackbar for validation messages */}
      <Snackbar
        isVisible={snackbar.isVisible}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar((prev) => ({ ...prev, isVisible: false }))}
        autoHideDuration={5000}
      />

      {/* Mobile Upload Complete Popup */}
      {showMobileUploadCompletePopup && (isMobileUploadMode || urlParams.share) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            style={{
              backgroundColor: '#2a2d36',
              borderRadius: '16px',
              padding: '32px',
              textAlign: 'center',
              maxWidth: '90%',
              width: '400px'
            }}
          >
            <div style={{ width: '72px', height: '72px', borderRadius: '50%', border: '3px solid #4CAF50', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '36px', color: '#4CAF50' }}>&#10003;</div>
            <h3 className={styles.mobileUploadSuccessTitle}>Completed</h3>
            <p className={styles.mobileUploadSuccessMessage}>
              {isMobileUploadMode
                ? 'Your files have been uploaded successfully. Please complete the remaining steps from the web to finish your gift.'
                : 'Your files have been uploaded successfully. Thank you for contributing!'}
            </p>
            <button
              type="button"
              onClick={(e) => {
                const btn = e.currentTarget;
                btn.disabled = true;
                btn.style.cursor = 'not-allowed';
                btn.innerHTML = '<div style="width:20px;height:20px;border:2px solid rgba(255,255,255,0.3);border-top-color:#fff;border-radius:50%;animation:spin .6s linear infinite;margin:0 auto"></div>';
                window.location.href = '/newSign-in';
              }}
              style={{
                marginTop: '20px',
                width: '100%',
                padding: '0 24px',
                backgroundColor: '#5aa5ef',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                fontFamily: "'Montserrat', sans-serif",
                cursor: 'pointer',
              }}
            >
              Create Your Projector
            </button>
          </div>
        </div>
      )}

      {/* Mobile Payment Popup */}
      {showMobilePaymentPopup && isMobileUploadMode && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            style={{
              backgroundColor: '#2a2d36',
              borderRadius: '16px',
              padding: '32px',
              textAlign: 'center',
              maxWidth: '90%',
              width: '400px'
            }}
          >
            <h3 className={styles.mobileUploadSuccessTitle}>Ready to Upload</h3>
            <p className={styles.mobileUploadSuccessMessage}>
              Where would you like to complete payment?
            </p>
            <div className={styles.uploadCompleteButtonGroup}>
              <button
                className={styles.viewProjectorButton}
                onClick={async () => {
                  setShowMobilePaymentPopup(false);
                  paywebRequestedRef.current = true;

                  // Send push message to desktop to navigate to payment
                  console.log('[DEBUG_PAYWEB] Mobile: "Pay from Web" button clicked, sending payweb push. giftUserUid:', giftUserUid);
                  await sendMobilePushMessage('payweb', []);
                  console.log('[DEBUG_PAYWEB] Mobile: payweb push sent successfully');

                  // Go directly to progress view (no waiting screen)
                  animateToStep(5);

                  // Start polling for paymentDone from desktop (SSE may miss it)
                  if (!paymentDonePollRef.current && !paymentDoneRef.current) {
                    let pdPollCount = 0;
                    paymentDonePollRef.current = setInterval(async () => {
                      pdPollCount += 1;
                      if (pdPollCount > 120 || paymentDoneRef.current) { // Stop after 10 minutes or when payment done
                        clearInterval(paymentDonePollRef.current);
                        paymentDonePollRef.current = null;
                        return;
                      }
                      try {
                        const statusRes = await getGiftStatus(giftUserUid);
                        const statusData = statusRes?.data?.data || statusRes?.data || {};
                        const pushStr = statusData.push || '';
                        if (pushStr.includes && pushStr.includes('paymentDone') && pushStr.includes('mobileUpload:false')) {
                          console.log('[MOBILE_FLOW] Poll detected paymentDone from desktop');
                          // Extract token from push string
                          const tokenMatch = pushStr.match(/token:'([^']+)'/);
                          const pollToken = tokenMatch ? tokenMatch[1] : null;
                          if (pollToken) {
                            localStorage.setItem('token', pollToken);
                            console.log('[MOBILE_FLOW] Token stored from poll');
                          } else {
                            console.error('[MOBILE_FLOW] No token in paymentDone poll!');
                          }
                          paymentDoneRef.current = true;
                          paywebRequestedRef.current = false;
                          // Send metadata to desktop so it can register — mobile should not register itself
                          buildMobileUploadMetadata().then((meta) => {
                            sendMobilePushMessage('allUploadsComplete', [], null, null, meta);
                          });
                          setSnackbar({
                            isVisible: true,
                            message: 'Payment complete! Registering videos...',
                            type: 'success'
                          });
                          clearInterval(paymentDonePollRef.current);
                          paymentDonePollRef.current = null;
                        }
                      } catch (err) {
                        console.warn('[MOBILE_FLOW] PaymentDone poll error:', err);
                      }
                    }, 5000);
                  }
                }}
              >
                Pay from Web
              </button>
              <button
                className={styles.sendGiftButton}
                onClick={() => {
                  setShowMobilePaymentPopup(false);
                  animateToStep(4); // Navigate to payment page, upload happens after payment
                }}
              >
                Pay Here
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Waiting for Web Payment Screen (Mobile Upload Mode) */}
      {showWaitingForPayment && isMobileUploadMode && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#1a1d24',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001
          }}
        >
          <div
            style={{
              textAlign: 'center',
              padding: '32px',
              maxWidth: '90%',
              width: '400px'
            }}
          >
            {/* Animated Loading Spinner */}
            <div
              style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 32px',
                border: '4px solid rgba(90, 165, 239, 0.2)',
                borderTop: '4px solid #5aa5ef',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}
            />
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>

            <h2
              style={{
                color: '#ffffff',
                fontSize: '24px',
                fontWeight: '600',
                marginBottom: '16px'
              }}
            >
              Waiting for Payment
            </h2>
            <p
              style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '16px',
                lineHeight: '1.5',
                marginBottom: '32px'
              }}
            >
              Please complete the payment on your desktop browser. This screen will update automatically once payment is confirmed.
            </p>

            {/* QR Code for desktop access */}
            {!isMobileUploadMode && (
              <div
                style={{
                  backgroundColor: '#2a2d36',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '24px'
                }}
              >
                <p
                  style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '14px',
                    marginBottom: '16px'
                  }}
                >
                  Scan to open payment page on desktop
                </p>
                <QRCodeComponent
                  url={`${window.location.origin}/gift?giftUid=${giftUserUid}${localStorage.getItem('token') ? `&token=${localStorage.getItem('token')}` : ''}${localStorage.getItem('uploader_token') ? `&access=${localStorage.getItem('uploader_token')}` : ''}${urlParams.share ? '&share=true' : ''}${formData.firstname.trim() ? `&recipientName=${encodeURIComponent(formData.firstname.trim())}` : ''}`}
                  size={150}
                />
              </div>
            )}

            <button
              style={{
                backgroundColor: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: 'rgba(255, 255, 255, 0.7)',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              onClick={() => setShowWaitingForPayment(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Album Assignment Popup - Same design as CloudPhotoAlbumModal */}
      {showAlbumPopup && (
        <div className={styles.albumModalOverlay}>
          <div className={styles.albumModal}>
            {/* Header */}
            <div className={styles.albumModalHeader}>
              <div className={styles.albumModalHeaderLeft}>
                {/* PhotoLibraryIcon */}
                <svg width="24" height="24" viewBox="0 0 24 24" style={{ color: '#5aa5ef', marginRight: 12 }}>
                  <path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" fill="currentColor" />
                </svg>
                <h2 className={styles.albumModalTitle}>
                  {`Upload ${pendingImages.length} Photo${pendingImages.length !== 1 ? 's' : ''} to Album`}
                </h2>
              </div>
              <button className={styles.albumModalCloseBtn} onClick={handleSkipAlbum} type="button">
                {/* CloseIcon */}
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className={styles.albumModalContent}>
              <div className={styles.albumModalSection}>
                <div className={styles.albumModalSectionHeader}>
                  {/* AddCircleOutlineIcon */}
                  <svg width="22" height="22" viewBox="0 0 24 24" style={{ color: '#5aa5ef', marginRight: 10 }}>
                    <path d="M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4V7zm-1-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="currentColor" />
                  </svg>
                  <span className={styles.albumModalSectionTitle}>Create New Album</span>
                </div>
                <div className={styles.albumModalSectionContent}>
                  <p className={styles.albumModalDescription}>
                    Create a new album to organize your photos
                  </p>
                  <input
                    type="text"
                    value={albumName}
                    onChange={(e) => setAlbumName(e.target.value)}
                    placeholder="Enter album title..."
                    className={styles.albumModalInput}
                    onKeyDown={(e) => e.key === 'Enter' && handleAlbumAssign()}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={handleAlbumAssign}
                    disabled={!albumName.trim()}
                    className={styles.albumModalCreateBtn}
                  >
                    Create & Upload
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add More Options Modal */}
      {showAddMoreOptions && (
        <div
          className={styles.albumModalOverlay}
          onClick={() => setShowAddMoreOptions(false)}
          onKeyDown={(e) => { if (e.key === 'Escape') setShowAddMoreOptions(false); }}
          role="button"
          tabIndex={0}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div className={styles.addMoreModal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className={styles.addMoreModalHeader}>
              <h2 className={styles.addMoreModalTitle}>More Upload Options</h2>
              <button className={styles.albumModalCloseBtn} onClick={() => setShowAddMoreOptions(false)} type="button">
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor" />
                </svg>
              </button>
            </div>
            <div className={styles.addMoreModalContent}>
              <div
                className={styles.addMoreModalOption}
                onClick={() => { handleCloudConnect('googledrive'); setShowAddMoreOptions(false); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') { handleCloudConnect('googledrive'); setShowAddMoreOptions(false); } }}
              >
                <div className={styles.addMoreModalIcon}>
                  <svg width="32" height="32" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#0066DA" />
                    <path d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L1.2 52.35c-.8 1.4-1.2 2.95-1.2 4.5h27.5L43.65 25z" fill="#00AC47" />
                    <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75L86.1 57c.8-1.4 1.2-2.95 1.2-4.5H59.85L73.55 76.8z" fill="#EA4335" />
                    <path d="M43.65 25L57.4 1.2c-1.35-.8-2.9-1.2-4.5-1.2H34.35c-1.6 0-3.15.45-4.5 1.2L43.65 25z" fill="#00832D" />
                    <path d="M59.85 53H27.5l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.55c1.6 0 3.15-.45 4.5-1.2L59.85 53z" fill="#2684FC" />
                    <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.2 28h27.5c0-1.55-.4-3.1-1.2-4.5l-12.75-22z" fill="#FFBA00" />
                  </svg>
                </div>
                <div className={styles.addMoreModalOptionText}>
                  <span className={styles.addMoreModalOptionTitle}>Google Drive</span>
                  <span className={styles.addMoreModalOptionDesc}>Import from your Drive</span>
                </div>
              </div>
              <div
                className={styles.addMoreModalOption}
                onClick={() => { handleCloudConnect('applephotos'); setShowAddMoreOptions(false); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') { handleCloudConnect('applephotos'); setShowAddMoreOptions(false); } }}
              >
                <div className={styles.addMoreModalIcon}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.05 20.28c-.98.95-2.05.79-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="#999" />
                  </svg>
                </div>
                <div className={styles.addMoreModalOptionText}>
                  <span className={styles.addMoreModalOptionTitle}>Apple Photos</span>
                  <span className={styles.addMoreModalOptionDesc}>Import from iCloud</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Popup Modal */}
      {showQrCode && !isMobileUploadMode && (currentStep !== 3 || selectedFiles.length > 0) && (
        <div
          className={styles.albumModalOverlay}
          onClick={() => setShowQrCode(false)}
          onKeyDown={(e) => { if (e.key === 'Escape') setShowQrCode(false); }}
          role="button"
          tabIndex={0}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div className={styles.qrModal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            {/* Header */}
            <div className={styles.albumModalHeader}>
              <div className={styles.albumModalHeaderLeft}>
                {/* Mobile Upload Icon */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 12 }}>
                  <rect x="5" y="1" width="14" height="22" rx="3" stroke="#5aa5ef" strokeWidth="1.5" />
                  <line x1="10" y1="3.5" x2="14" y2="3.5" stroke="#5aa5ef" strokeWidth="1.5" strokeLinecap="round" />
                  <circle cx="12" cy="19" r="1" fill="#5aa5ef" />
                </svg>
                <h2 className={styles.albumModalTitle}>Scan to Upload</h2>
              </div>
              <button className={styles.albumModalCloseBtn} onClick={() => setShowQrCode(false)} type="button">
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor" />
                </svg>
              </button>
            </div>
            {/* Content */}
            <div className={styles.qrModalContent}>
              <div className={styles.qrModalQrWrapper}>
                <QRCodeComponent
                  url={urlParams.share
                    ? (shortShareUrl || `${window.location.origin}/${buildSharePath()}`)
                    : `${window.location.origin}/gift?mobile=true&session=${sessionId}${giftUserUid ? `&giftUid=${giftUserUid}` : ''}${localStorage.getItem('token') ? `&token=${localStorage.getItem('token')}` : ''}${localStorage.getItem('uploader_token') ? `&access=${localStorage.getItem('uploader_token')}` : ''}`}
                  size={200}
                />
              </div>
              <p className={styles.qrModalHint}>Scan with your phone camera to upload files from your mobile device</p>
            </div>
          </div>
        </div>
      )}

      {/* Cloud Upload Modal (same as main app) */}
      <CloudUploadModal
        open={cloudModalOpen}
        onClose={handleCloudModalClose}
        service={selectedCloudService}
        onFilesSelected={handleCloudFilesSelected}
        isUploading={false}
        onShowMessage={(message) => {
          setSnackbar({
            isVisible: true,
            message,
            type: 'error'
          });
        }}
      />

      {/* Share Modal - Bottom Sheet on mobile, Centered modal on desktop */}
      {showShareModal && (() => {
        const shareUrl = shortShareUrl || `${window.location.origin}/${buildSharePath()}`;
        const shareText = 'Upload photos and videos to this gift!';
        return (
        <div
          role="button"
          tabIndex={0}
          className={styles.shareModalOverlay}
          onClick={() => setShowShareModal(false)}
          onKeyDown={(e) => { if (e.key === 'Escape') setShowShareModal(false); }}
        >
          <div
            role="presentation"
            className={styles.shareModalContent}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {/* Drag handle for mobile bottom sheet */}
            <div className={styles.shareModalDragHandle}>
              <div className={styles.shareModalDragBar} />
            </div>

            <button
              onClick={() => setShowShareModal(false)}
              className={styles.shareModalCloseBtn}
            >
              ×
            </button>

            <p className={styles.successSendText} style={{ marginBottom: '16px' }}>Share With Others To Add Media</p>

            {/* Share apps grid - visible on mobile */}
            <div className={styles.shareAppsGrid}>
              {/* Messages / SMS */}
              <a
                href={`sms:?&body=${encodeURIComponent(`${shareText} ${shareUrl}`)}`}
                className={styles.shareAppItem}
              >
                <div className={styles.shareAppIcon} style={{ background: '#34C759' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M21 11.5C21 16.75 16.75 21 11.5 21C9.8 21 8.2 20.6 6.8 19.8L2 21L3.2 16.2C2.4 14.8 2 13.2 2 11.5C2 6.25 6.25 2 11.5 2C16.75 2 21 6.25 21 11.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span className={styles.shareAppLabel}>Messages</span>
              </a>

              {/* WhatsApp */}
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.shareAppItem}
              >
                <div className={styles.shareAppIcon} style={{ background: '#25D366' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </div>
                <span className={styles.shareAppLabel}>WhatsApp</span>
              </a>

              {/* Facebook Messenger */}
              <a
                href={`fb-messenger://share/?link=${encodeURIComponent(shareUrl)}`}
                className={styles.shareAppItem}
                onClick={(e) => {
                  // Fallback to web messenger if app not installed
                  setTimeout(() => {
                    window.open(`https://www.facebook.com/dialog/send?link=${encodeURIComponent(shareUrl)}&app_id=291494419107518&redirect_uri=${encodeURIComponent(window.location.origin)}`, '_blank');
                  }, 500);
                }}
              >
                <div className={styles.shareAppIcon} style={{ background: 'linear-gradient(135deg, #00B2FF, #006AFF)' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.906 1.453 5.497 3.723 7.19V22l3.405-1.868c.91.252 1.875.388 2.872.388 5.523 0 10-4.145 10-9.243S17.523 2 12 2zm.994 12.462l-2.547-2.72-4.97 2.72 5.467-5.803 2.61 2.72 4.907-2.72-5.467 5.803z" />
                  </svg>
                </div>
                <span className={styles.shareAppLabel}>Messenger</span>
              </a>

              {/* Facebook */}
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.shareAppItem}
              >
                <div className={styles.shareAppIcon} style={{ background: '#1877F2' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </div>
                <span className={styles.shareAppLabel}>Facebook</span>
              </a>

              {/* X / Twitter */}
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.shareAppItem}
              >
                <div className={styles.shareAppIcon} style={{ background: '#000' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </div>
                <span className={styles.shareAppLabel}>X</span>
              </a>

              {/* Telegram */}
              <a
                href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.shareAppItem}
              >
                <div className={styles.shareAppIcon} style={{ background: '#0088cc' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                    <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0 12 12 0 0011.944 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                  </svg>
                </div>
                <span className={styles.shareAppLabel}>Telegram</span>
              </a>

              {/* Email */}
              <a
                href={`mailto:?subject=${encodeURIComponent('Contribute to a Gift!')}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`}
                className={styles.shareAppItem}
              >
                <div className={styles.shareAppIcon} style={{ background: '#EA4335' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M22 6L12 13L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span className={styles.shareAppLabel}>Email</span>
              </a>

              {/* Copy Link */}
              <button
                className={styles.shareAppItem}
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl);
                  setSnackbar({ isVisible: true, message: 'Link copied to clipboard!', type: 'success' });
                  if (giftUserUid) {
                    updateGiftStatus({ uuid: giftUserUid, push: '' }).catch(() => {});
                  }
                }}
              >
                <div className={styles.shareAppIcon} style={{ background: '#8E8E93' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span className={styles.shareAppLabel}>Copy Link</span>
              </button>

              {/* More - native share API */}
              {navigator.share && (
                <button
                  className={styles.shareAppItem}
                  onClick={() => {
                    navigator.share({
                      title: 'Add Media to a Gift',
                      text: shareText,
                      url: shareUrl,
                    }).catch(() => {});
                  }}
                >
                  <div className={styles.shareAppIcon} style={{ background: '#007AFF' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <circle cx="5" cy="12" r="2" fill="white" />
                      <circle cx="12" cy="12" r="2" fill="white" />
                      <circle cx="19" cy="12" r="2" fill="white" />
                    </svg>
                  </div>
                  <span className={styles.shareAppLabel}>More</span>
                </button>
              )}
            </div>

            {/* Copy URL section */}
            <div className={styles.shareModalUrlSection}>
              <div className={styles.successUrlInputWrapper}>
                <input
                  type="text"
                  className={styles.successUrlInput}
                  value={shareUrl}
                  disabled
                  readOnly
                />
                <button
                  className={styles.successUrlCopyButton}
                  onClick={() => {
                    navigator.clipboard.writeText(shareUrl);
                    setSnackbar({
                      isVisible: true,
                      message: 'Link copied to clipboard!',
                      type: 'success'
                    });
                    if (giftUserUid) {
                      updateGiftStatus({ uuid: giftUserUid, push: '' }).catch(() => {});
                    }
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="9" y="9" width="13" height="13" rx="2" stroke="white" strokeWidth="2" />
                    <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="white" strokeWidth="2" />
                  </svg>
                </button>
              </div>
              <p style={{ color: '#aaa', fontFamily: "'Montserrat', sans-serif", fontSize: '0.8rem', fontWeight: 500, margin: '8px 0 0 0', textAlign: 'center' }}>Copy and save this link to add more content to Projector</p>
            </div>

            {/* QR Code - only on desktop */}
            <div className={styles.shareModalQrSection}>
              <p className={styles.successSendText} style={{ marginBottom: '16px', marginTop: '20px', fontSize: '0.85rem' }}>Or Send QR Code</p>
              <div className={styles.successQrContainer}>
                <div className={styles.successQrWrapper}>
                  <QRCodeComponent
                    url={shareUrl}
                    size={150}
                  />
                </div>
                <button
                  className={styles.successQrDownloadButton}
                  onClick={(e) => {
                    const modalContent = e.target.closest('[role="presentation"]');
                    const canvas = modalContent?.querySelector('canvas');
                    if (canvas) {
                      const link = document.createElement('a');
                      link.download = 'gift-qr-code.png';
                      link.href = canvas.toDataURL('image/png');
                      link.click();
                      setSnackbar({
                        isVisible: true,
                        message: 'QR code downloaded! Share it to let others Add Media.',
                        type: 'success'
                      });
                    }
                  }}
                >
                  <span style={{ fontSize: '0.65rem', fontWeight: '600', letterSpacing: '0.02em' }}>Download</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default GiftFlowIntro;
